import { ResponseContext, RequestContext, HttpInfo } from '../http/http';
import type { HttpFile } from '../http/http';
import { retry } from '../util';
import type { Configuration} from '../configuration'

import { AccessCondition } from '../models/AccessCondition';
import { AccessConditionEffect } from '../models/AccessConditionEffect';
import { AccessConditionalExpression } from '../models/AccessConditionalExpression';
import { Account } from '../models/Account';
import { AccountConfigInput } from '../models/AccountConfigInput';
import { AccountIdCondition } from '../models/AccountIdCondition';
import { AccountIdFilters } from '../models/AccountIdFilters';
import { AccountInstallationMethod } from '../models/AccountInstallationMethod';
import { AccountMetricsDestination } from '../models/AccountMetricsDestination';
import { AccountNameFilters } from '../models/AccountNameFilters';
import { AccountState } from '../models/AccountState';
import { AccountStatusFilters } from '../models/AccountStatusFilters';
import { AccountUpdateInput } from '../models/AccountUpdateInput';
import { AccountVersion } from '../models/AccountVersion';
import { ActionFilters } from '../models/ActionFilters';
import { ActionRequiredDetails } from '../models/ActionRequiredDetails';
import { ActivateAwsOrganizationResponse } from '../models/ActivateAwsOrganizationResponse';
import { ActivateGcpFolderResponse } from '../models/ActivateGcpFolderResponse';
import { ActivateGcpOrganizationResponse } from '../models/ActivateGcpOrganizationResponse';
import { Ami } from '../models/Ami';
import { AmiBlockDevice } from '../models/AmiBlockDevice';
import { AnnuallyConfig } from '../models/AnnuallyConfig';
import { ApiCredentials } from '../models/ApiCredentials';
import { ApiCredentialsProperties } from '../models/ApiCredentialsProperties';
import { App } from '../models/App';
import { AppFilters } from '../models/AppFilters';
import { ApplicationOverride } from '../models/ApplicationOverride';
import { AppsCondition } from '../models/AppsCondition';
import { AppsDetails } from '../models/AppsDetails';
import { AtlasProject } from '../models/AtlasProject';
import { AtlasProjects } from '../models/AtlasProjects';
import { AuditLog } from '../models/AuditLog';
import { AuditLogAction } from '../models/AuditLogAction';
import { AuditLogEntitiesInner } from '../models/AuditLogEntitiesInner';
import { AuditLogFilterConditions } from '../models/AuditLogFilterConditions';
import { AuthParams } from '../models/AuthParams';
import { AuthResponse } from '../models/AuthResponse';
import { AwsAccountConfigInput } from '../models/AwsAccountConfigInput';
import { AwsAccountMetricsDestination } from '../models/AwsAccountMetricsDestination';
import { AwsDatabaseDestination } from '../models/AwsDatabaseDestination';
import { AwsDynamoDBDestination } from '../models/AwsDynamoDBDestination';
import { AwsDynamoDbResource } from '../models/AwsDynamoDbResource';
import { AwsDynamoDbSnapshotProperties } from '../models/AwsDynamoDbSnapshotProperties';
import { AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInner } from '../models/AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInner';
import { AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerOnDemandThroughput } from '../models/AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerOnDemandThroughput';
import { AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerProjection } from '../models/AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerProjection';
import { AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerProvisionedThroughput } from '../models/AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerProvisionedThroughput';
import { AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerWarmThroughput } from '../models/AwsDynamoDbSnapshotPropertiesGlobalSecondaryIndexesInnerWarmThroughput';
import { AwsDynamoDbSnapshotPropertiesLocalSecondaryIndexesInner } from '../models/AwsDynamoDbSnapshotPropertiesLocalSecondaryIndexesInner';
import { AwsDynamoDbSnapshotPropertiesLocalSecondaryIndexesInnerKeySchemaInner } from '../models/AwsDynamoDbSnapshotPropertiesLocalSecondaryIndexesInnerKeySchemaInner';
import { AwsDynamoDbSnapshotPropertiesLocalSecondaryIndexesInnerProjection } from '../models/AwsDynamoDbSnapshotPropertiesLocalSecondaryIndexesInnerProjection';
import { AwsEbsSnapshotConvertResult } from '../models/AwsEbsSnapshotConvertResult';
import { AwsEbsVolumeResource } from '../models/AwsEbsVolumeResource';
import { AwsEbsVolumeRestoreData } from '../models/AwsEbsVolumeRestoreData';
import { AwsEbsVolumeRestoreResult } from '../models/AwsEbsVolumeRestoreResult';
import { AwsEbsVolumeSnapshotProperties } from '../models/AwsEbsVolumeSnapshotProperties';
import { AwsEc2AmiConvertResult } from '../models/AwsEc2AmiConvertResult';
import { AwsEc2InstanceRestoreDestination } from '../models/AwsEc2InstanceRestoreDestination';
import { AwsEc2InstanceRestoreResult } from '../models/AwsEc2InstanceRestoreResult';
import { AwsEc2InstanceRestoreTarget } from '../models/AwsEc2InstanceRestoreTarget';
import { AwsEc2Resource } from '../models/AwsEc2Resource';
import { AwsEc2SnapshotProperties } from '../models/AwsEc2SnapshotProperties';
import { AwsEksNamespaceResource } from '../models/AwsEksNamespaceResource';
import { AwsEksNamespaceSnapshotProperties } from '../models/AwsEksNamespaceSnapshotProperties';
import { AwsFsxResource } from '../models/AwsFsxResource';
import { AwsKeyspacesResource } from '../models/AwsKeyspacesResource';
import { AwsNativePitrBackupPolicyPlan } from '../models/AwsNativePitrBackupPolicyPlan';
import { AwsNativePitrBackupResourceType } from '../models/AwsNativePitrBackupResourceType';
import { AwsNativePitrConfig } from '../models/AwsNativePitrConfig';
import { AwsOrganization } from '../models/AwsOrganization';
import { AwsRdsEngine } from '../models/AwsRdsEngine';
import { AwsRdsResource } from '../models/AwsRdsResource';
import { AwsRdsResourceAurora } from '../models/AwsRdsResourceAurora';
import { AwsRdsResourceStorageSettings } from '../models/AwsRdsResourceStorageSettings';
import { AwsRestoreAccountAttributes } from '../models/AwsRestoreAccountAttributes';
import { AwsRestoreAccountAttributesInput } from '../models/AwsRestoreAccountAttributesInput';
import { AwsRestoreAccountConnectivityConfig } from '../models/AwsRestoreAccountConnectivityConfig';
import { AwsSourceAccountAttributes } from '../models/AwsSourceAccountAttributes';
import { AwsSourceAccountAttributesInput } from '../models/AwsSourceAccountAttributesInput';
import { AwsVaultConfig } from '../models/AwsVaultConfig';
import { AwsVaultConfigInput } from '../models/AwsVaultConfigInput';
import { AwsVpcConnectivityConfig } from '../models/AwsVpcConnectivityConfig';
import { AzureAccountConfigInput } from '../models/AzureAccountConfigInput';
import { AzureAksNamespaceResource } from '../models/AzureAksNamespaceResource';
import { AzureBackupApplicationsResponse } from '../models/AzureBackupApplicationsResponse';
import { AzureConsentUrlResponse } from '../models/AzureConsentUrlResponse';
import { AzureDisk } from '../models/AzureDisk';
import { AzureDiskRestoreData } from '../models/AzureDiskRestoreData';
import { AzureDiskRestoreDestination } from '../models/AzureDiskRestoreDestination';
import { AzureDiskRestoreResult } from '../models/AzureDiskRestoreResult';
import { AzureDiskSettings } from '../models/AzureDiskSettings';
import { AzureDiskTarget } from '../models/AzureDiskTarget';
import { AzureEonApplication } from '../models/AzureEonApplication';
import { AzureListResourceGroupsResponse } from '../models/AzureListResourceGroupsResponse';
import { AzureListSubscriptionsResponse } from '../models/AzureListSubscriptionsResponse';
import { AzureManagementGroup } from '../models/AzureManagementGroup';
import { AzureManagementGroups } from '../models/AzureManagementGroups';
import { AzureMarketplaceWebhookInput } from '../models/AzureMarketplaceWebhookInput';
import { AzureMarketplaceWebhookInputSubscription } from '../models/AzureMarketplaceWebhookInputSubscription';
import { AzureMarketplaceWebhookInputSubscriptionBeneficiary } from '../models/AzureMarketplaceWebhookInputSubscriptionBeneficiary';
import { AzureMarketplaceWebhookInputSubscriptionPurchaser } from '../models/AzureMarketplaceWebhookInputSubscriptionPurchaser';
import { AzureMarketplaceWebhookInputSubscriptionTerm } from '../models/AzureMarketplaceWebhookInputSubscriptionTerm';
import { AzureMssqlSnapshotProperties } from '../models/AzureMssqlSnapshotProperties';
import { AzureOnboardedTenantsResponse } from '../models/AzureOnboardedTenantsResponse';
import { AzureOnboardingStatus } from '../models/AzureOnboardingStatus';
import { AzureResourceGroup } from '../models/AzureResourceGroup';
import { AzureResourceGroups } from '../models/AzureResourceGroups';
import { AzureRestoreAccountAttributes } from '../models/AzureRestoreAccountAttributes';
import { AzureRestoreAccountAttributesInput } from '../models/AzureRestoreAccountAttributesInput';
import { AzureRestorePointData } from '../models/AzureRestorePointData';
import { AzureRestorePointSpec } from '../models/AzureRestorePointSpec';
import { AzureSapHanaResource } from '../models/AzureSapHanaResource';
import { AzureScanningAccountSetupStatus200Response } from '../models/AzureScanningAccountSetupStatus200Response';
import { AzureSourceAccountAttributes } from '../models/AzureSourceAccountAttributes';
import { AzureSourceAccountAttributesInput } from '../models/AzureSourceAccountAttributesInput';
import { AzureSqlDatabaseRestoreDestination } from '../models/AzureSqlDatabaseRestoreDestination';
import { AzureSqlDatabaseRestoreTarget } from '../models/AzureSqlDatabaseRestoreTarget';
import { AzureStorageAccount } from '../models/AzureStorageAccount';
import { AzureStorageAccounts } from '../models/AzureStorageAccounts';
import { AzureSubscription } from '../models/AzureSubscription';
import { AzureTenant } from '../models/AzureTenant';
import { AzureVaultConfig } from '../models/AzureVaultConfig';
import { AzureVaultConfigInput } from '../models/AzureVaultConfigInput';
import { AzureVaultType } from '../models/AzureVaultType';
import { AzureVmInstanceRestoreDestination } from '../models/AzureVmInstanceRestoreDestination';
import { AzureVmInstanceRestoreResult } from '../models/AzureVmInstanceRestoreResult';
import { AzureVmInstanceRestoreTarget } from '../models/AzureVmInstanceRestoreTarget';
import { AzureVmSnapshotProperties } from '../models/AzureVmSnapshotProperties';
import { BackedUpByResourceType } from '../models/BackedUpByResourceType';
import { BackedUpByResourceTypeDataInner } from '../models/BackedUpByResourceTypeDataInner';
import { BackedUpStorageDateHistogram } from '../models/BackedUpStorageDateHistogram';
import { BackupControlViolations } from '../models/BackupControlViolations';
import { BackupJob } from '../models/BackupJob';
import { BackupJobResponse } from '../models/BackupJobResponse';
import { BackupJobSortField } from '../models/BackupJobSortField';
import { BackupJobType } from '../models/BackupJobType';
import { BackupJobTypeFilters } from '../models/BackupJobTypeFilters';
import { BackupJobsFilterConditions } from '../models/BackupJobsFilterConditions';
import { BackupPoliciesFilters } from '../models/BackupPoliciesFilters';
import { BackupPoliciesSecurityConfig } from '../models/BackupPoliciesSecurityConfig';
import { BackupPolicy } from '../models/BackupPolicy';
import { BackupPolicyAppliesNotificationArguments } from '../models/BackupPolicyAppliesNotificationArguments';
import { BackupPolicyConfig } from '../models/BackupPolicyConfig';
import { BackupPolicyExpression } from '../models/BackupPolicyExpression';
import { BackupPolicyGroupCondition } from '../models/BackupPolicyGroupCondition';
import { BackupPolicyNotificationArguments } from '../models/BackupPolicyNotificationArguments';
import { BackupPolicyPlan } from '../models/BackupPolicyPlan';
import { BackupPolicyResourceSelector } from '../models/BackupPolicyResourceSelector';
import { BackupPolicySecurityConfig } from '../models/BackupPolicySecurityConfig';
import { BackupPolicyType } from '../models/BackupPolicyType';
import { BackupRegionsFilters } from '../models/BackupRegionsFilters';
import { BackupSchedule } from '../models/BackupSchedule';
import { BackupScope } from '../models/BackupScope';
import { BackupScopeSummary } from '../models/BackupScopeSummary';
import { BackupStatus } from '../models/BackupStatus';
import { BackupStatusFilters } from '../models/BackupStatusFilters';
import { BackupVault } from '../models/BackupVault';
import { BatchApplicationOverrides } from '../models/BatchApplicationOverrides';
import { BatchOperationResponse } from '../models/BatchOperationResponse';
import { BatchResourceValidationResult } from '../models/BatchResourceValidationResult';
import { BatchValidateResourceConfigurationRequest } from '../models/BatchValidateResourceConfigurationRequest';
import { BatchValidateResourceConfigurationResponse } from '../models/BatchValidateResourceConfigurationResponse';
import { BigQueryDatasetDestination } from '../models/BigQueryDatasetDestination';
import { BigQueryIntegrationConfig } from '../models/BigQueryIntegrationConfig';
import { BillingPeriod } from '../models/BillingPeriod';
import { BillingTransactionHistory } from '../models/BillingTransactionHistory';
import { BooleanFilters } from '../models/BooleanFilters';
import { CancelExclusionFromBackupResponse } from '../models/CancelExclusionFromBackupResponse';
import { CheckAzureVMNameExists200Response } from '../models/CheckAzureVMNameExists200Response';
import { CheckAzureVMNameExistsRequest } from '../models/CheckAzureVMNameExistsRequest';
import { Classifications } from '../models/Classifications';
import { CloudAccountIdFilters } from '../models/CloudAccountIdFilters';
import { CloudAccountNotificationArguments } from '../models/CloudAccountNotificationArguments';
import { CloudAccountType } from '../models/CloudAccountType';
import { CloudProviderCondition } from '../models/CloudProviderCondition';
import { CloudProviderFilters } from '../models/CloudProviderFilters';
import { CloudRegion } from '../models/CloudRegion';
import { CloudResourceAttachmentEvent } from '../models/CloudResourceAttachmentEvent';
import { CloudResourceAttachmentEventType } from '../models/CloudResourceAttachmentEventType';
import { CloudResourceConfiguration } from '../models/CloudResourceConfiguration';
import { CloudResourceConfigurationCdcBackup } from '../models/CloudResourceConfigurationCdcBackup';
import { CloudResourceConfigurationInventoryBackup } from '../models/CloudResourceConfigurationInventoryBackup';
import { CloudResourceSecurityProperties } from '../models/CloudResourceSecurityProperties';
import { CloudSnapshotsPerVolume } from '../models/CloudSnapshotsPerVolume';
import { ClusterNameFilters } from '../models/ClusterNameFilters';
import { ColumnMetadata } from '../models/ColumnMetadata';
import { Condition } from '../models/Condition';
import { ConditionOperatorEnum } from '../models/ConditionOperatorEnum';
import { ConnectAwsOrganizationRequest } from '../models/ConnectAwsOrganizationRequest';
import { ConnectAwsOrganizationResponse } from '../models/ConnectAwsOrganizationResponse';
import { ConnectAzureBackupAccountRequest } from '../models/ConnectAzureBackupAccountRequest';
import { ConnectAzureBackupApplicationsRequest } from '../models/ConnectAzureBackupApplicationsRequest';
import { ConnectAzureRestoreAccountRequest } from '../models/ConnectAzureRestoreAccountRequest';
import { ConnectAzureSourceAccountRequest } from '../models/ConnectAzureSourceAccountRequest';
import { ConnectAzureVaultAccountRequest } from '../models/ConnectAzureVaultAccountRequest';
import { ConnectBYOAzureBackupApplicationsRequest } from '../models/ConnectBYOAzureBackupApplicationsRequest';
import { ConnectCustomerVaultAccountRequest } from '../models/ConnectCustomerVaultAccountRequest';
import { ConnectCustomerVaultAccountResponse } from '../models/ConnectCustomerVaultAccountResponse';
import { ConnectGcpFolderRequest } from '../models/ConnectGcpFolderRequest';
import { ConnectGcpFolderResponse } from '../models/ConnectGcpFolderResponse';
import { ConnectGcpOrganizationRequest } from '../models/ConnectGcpOrganizationRequest';
import { ConnectGcpOrganizationResponse } from '../models/ConnectGcpOrganizationResponse';
import { ConnectRestoreAccountRequest } from '../models/ConnectRestoreAccountRequest';
import { ConnectRestoreAccountResponse } from '../models/ConnectRestoreAccountResponse';
import { ConnectSourceAccountRequest } from '../models/ConnectSourceAccountRequest';
import { ConnectSourceAccountResponse } from '../models/ConnectSourceAccountResponse';
import { Control } from '../models/Control';
import { ControlRules } from '../models/ControlRules';
import { ControlViolation } from '../models/ControlViolation';
import { ControlViolations } from '../models/ControlViolations';
import { ControlViolationsPerSeverity } from '../models/ControlViolationsPerSeverity';
import { ControlViolationsPerSeverityViolatedControlsInner } from '../models/ControlViolationsPerSeverityViolatedControlsInner';
import { ControlsFilters } from '../models/ControlsFilters';
import { ConvertEbsSnapshotConfig } from '../models/ConvertEbsSnapshotConfig';
import { ConvertEc2ImageConfig } from '../models/ConvertEc2ImageConfig';
import { ConvertOtherSnapshotsOnDemandRequest } from '../models/ConvertOtherSnapshotsOnDemandRequest';
import { ConvertOtherSnapshotsOnDemandRequestSnapshotDetailsInner } from '../models/ConvertOtherSnapshotsOnDemandRequestSnapshotDetailsInner';
import { ConvertResourceSnapshotsRequest } from '../models/ConvertResourceSnapshotsRequest';
import { ConvertResourceSnapshotsResponse } from '../models/ConvertResourceSnapshotsResponse';
import { Cost } from '../models/Cost';
import { CostDataFilters } from '../models/CostDataFilters';
import { CostDataGroupBy } from '../models/CostDataGroupBy';
import { CostDataRecord } from '../models/CostDataRecord';
import { CostDataRecordCost } from '../models/CostDataRecordCost';
import { CostDataRecordDimensions } from '../models/CostDataRecordDimensions';
import { CostGranularity } from '../models/CostGranularity';
import { CostUnit } from '../models/CostUnit';
import { CreateAccountInput } from '../models/CreateAccountInput';
import { CreateApiCredentialsRequest } from '../models/CreateApiCredentialsRequest';
import { CreateApiCredentialsResponse } from '../models/CreateApiCredentialsResponse';
import { CreateBackupPolicyRequest } from '../models/CreateBackupPolicyRequest';
import { CreateBackupPolicyResponse } from '../models/CreateBackupPolicyResponse';
import { CreateDlsgPolicyRequest } from '../models/CreateDlsgPolicyRequest';
import { CreateDlsgPolicyResponse } from '../models/CreateDlsgPolicyResponse';
import { CreateFabricTableShortcutDemoRequest } from '../models/CreateFabricTableShortcutDemoRequest';
import { CreateFabricTableShortcutDemoRequestShortcutsInner } from '../models/CreateFabricTableShortcutDemoRequestShortcutsInner';
import { CreateFabricTableShortcutDemoResponse } from '../models/CreateFabricTableShortcutDemoResponse';
import { CreateFabricTableShortcutDemoResponseResultsInner } from '../models/CreateFabricTableShortcutDemoResponseResultsInner';
import { CreateInfectedExcludePathInput } from '../models/CreateInfectedExcludePathInput';
import { CreateIntegrationRequest } from '../models/CreateIntegrationRequest';
import { CreateIntegrationResponse } from '../models/CreateIntegrationResponse';
import { CreateLlgPolicyRequest } from '../models/CreateLlgPolicyRequest';
import { CreateLlgPolicyResponse } from '../models/CreateLlgPolicyResponse';
import { CreatePolicyInput } from '../models/CreatePolicyInput';
import { CreatePolicyScheduleInput } from '../models/CreatePolicyScheduleInput';
import { CreateProjectInput } from '../models/CreateProjectInput';
import { CreatePulumiConfigurationRequest } from '../models/CreatePulumiConfigurationRequest';
import { CreateRedshiftAccessPointRequest } from '../models/CreateRedshiftAccessPointRequest';
import { CreateRedshiftAccessPointResponse } from '../models/CreateRedshiftAccessPointResponse';
import { CreateRoleRequest } from '../models/CreateRoleRequest';
import { CreateRoleResponse } from '../models/CreateRoleResponse';
import { CreateSamlIdentityProviderConfigInput } from '../models/CreateSamlIdentityProviderConfigInput';
import { CreateScanJobInput } from '../models/CreateScanJobInput';
import { CreateScanJobResponse } from '../models/CreateScanJobResponse';
import { CreateSessionRequest } from '../models/CreateSessionRequest';
import { CreateUserInput } from '../models/CreateUserInput';
import { CreateVaultRequest } from '../models/CreateVaultRequest';
import { CreateVaultResponse } from '../models/CreateVaultResponse';
import { CrossResourceQueryRequest } from '../models/CrossResourceQueryRequest';
import { CrossResourceQueryRequestResourcesInner } from '../models/CrossResourceQueryRequestResourcesInner';
import { CustomTagsInner } from '../models/CustomTagsInner';
import { CustomerVaultAccount } from '../models/CustomerVaultAccount';
import { DBResourceMetadata } from '../models/DBResourceMetadata';
import { DBServerRecord } from '../models/DBServerRecord';
import { DBSnapshotMetadata } from '../models/DBSnapshotMetadata';
import { DBSnapshotType } from '../models/DBSnapshotType';
import { DailyConfig } from '../models/DailyConfig';
import { DailySnapshotData } from '../models/DailySnapshotData';
import { DailyStorageSummaries } from '../models/DailyStorageSummaries';
import { DailyStorageSummary } from '../models/DailyStorageSummary';
import { DataClass } from '../models/DataClass';
import { DataClassesCondition } from '../models/DataClassesCondition';
import { DataClassesDetails } from '../models/DataClassesDetails';
import { DataClassesFilters } from '../models/DataClassesFilters';
import { DataClassificationEntity } from '../models/DataClassificationEntity';
import { DataShareIntegrationConfig } from '../models/DataShareIntegrationConfig';
import { DatabaseDestination } from '../models/DatabaseDestination';
import { DatabaseRecord } from '../models/DatabaseRecord';
import { DatabricksIntegrationConfig } from '../models/DatabricksIntegrationConfig';
import { DayOfWeek } from '../models/DayOfWeek';
import { DeactivateAccountRequest } from '../models/DeactivateAccountRequest';
import { DeactivateAwsOrganizationResponse } from '../models/DeactivateAwsOrganizationResponse';
import { DeactivateGcpFolderResponse } from '../models/DeactivateGcpFolderResponse';
import { DeactivateGcpOrganizationResponse } from '../models/DeactivateGcpOrganizationResponse';
import { DestinationDetails } from '../models/DestinationDetails';
import { DestinationProviderAttributes } from '../models/DestinationProviderAttributes';
import { DestinationProviderAttributesAzure } from '../models/DestinationProviderAttributesAzure';
import { DestinationRestoreTypeAttributes } from '../models/DestinationRestoreTypeAttributes';
import { DisableResourceOverrideLimitsResponse } from '../models/DisableResourceOverrideLimitsResponse';
import { DisconnectCustomerVaultAccountResponse } from '../models/DisconnectCustomerVaultAccountResponse';
import { DisconnectRestoreAccountResponse } from '../models/DisconnectRestoreAccountResponse';
import { DisconnectSourceAccountResponse } from '../models/DisconnectSourceAccountResponse';
import { DiscoveryInvokeParams } from '../models/DiscoveryInvokeParams';
import { DiscoveryJob } from '../models/DiscoveryJob';
import { DiscoveryStatusResponse } from '../models/DiscoveryStatusResponse';
import { DlsgPolicy } from '../models/DlsgPolicy';
import { DlsgPolicyConfig } from '../models/DlsgPolicyConfig';
import { DlsgPolicyFilters } from '../models/DlsgPolicyFilters';
import { DlsgPolicyPlan } from '../models/DlsgPolicyPlan';
import { DlsgSettings } from '../models/DlsgSettings';
import { DriftProtectionPolicy } from '../models/DriftProtectionPolicy';
import { DriftProtectionSummary } from '../models/DriftProtectionSummary';
import { DriftProtectionTimePoint } from '../models/DriftProtectionTimePoint';
import { DynamoDBAdvancedIndexConfig } from '../models/DynamoDBAdvancedIndexConfig';
import { DynamoDBRestoreMethodAvailabilityRequest } from '../models/DynamoDBRestoreMethodAvailabilityRequest';
import { DynamoDBRestoreMethodAvailabilityResponse } from '../models/DynamoDBRestoreMethodAvailabilityResponse';
import { DynamoDbBackupConfig } from '../models/DynamoDbBackupConfig';
import { DynamoDbBackupConfigApplication } from '../models/DynamoDbBackupConfigApplication';
import { DynamoDbBillingMode } from '../models/DynamoDbBillingMode';
import { DynamodbTableRestoreDestination } from '../models/DynamodbTableRestoreDestination';
import { EFSRestoreDestination } from '../models/EFSRestoreDestination';
import { EbsRestoreDestination } from '../models/EbsRestoreDestination';
import { EbsSnapshotRestoreDestination } from '../models/EbsSnapshotRestoreDestination';
import { EbsSnapshotTarget } from '../models/EbsSnapshotTarget';
import { EbsTarget } from '../models/EbsTarget';
import { EfsSnapshotProperties } from '../models/EfsSnapshotProperties';
import { EksCluster } from '../models/EksCluster';
import { EksClusterStatus } from '../models/EksClusterStatus';
import { EksNamespaceRestoreDestination } from '../models/EksNamespaceRestoreDestination';
import { EksNamespaceRestoreResult } from '../models/EksNamespaceRestoreResult';
import { EksVolumeRestoreResult } from '../models/EksVolumeRestoreResult';
import { EmrIntegrationConfig } from '../models/EmrIntegrationConfig';
import { EnableFromLimitExceededResponse } from '../models/EnableFromLimitExceededResponse';
import { EnableResourceOverrideLimitsResponse } from '../models/EnableResourceOverrideLimitsResponse';
import { EnableRestoreAccountMetricsConfigRequest } from '../models/EnableRestoreAccountMetricsConfigRequest';
import { EnableRestoreAccountMetricsConfigResponse } from '../models/EnableRestoreAccountMetricsConfigResponse';
import { EnableSourceAccountMetricsConfigRequest } from '../models/EnableSourceAccountMetricsConfigRequest';
import { EnableSourceAccountMetricsConfigResponse } from '../models/EnableSourceAccountMetricsConfigResponse';
import { EncryptionKey } from '../models/EncryptionKey';
import { EncryptionKeys } from '../models/EncryptionKeys';
import { EndTimeDateFilters } from '../models/EndTimeDateFilters';
import { Environment } from '../models/Environment';
import { EnvironmentCondition } from '../models/EnvironmentCondition';
import { EnvironmentDetails } from '../models/EnvironmentDetails';
import { EnvironmentFilters } from '../models/EnvironmentFilters';
import { EonAccount } from '../models/EonAccount';
import { EonAccountIdFilters } from '../models/EonAccountIdFilters';
import { EonAccountStatus } from '../models/EonAccountStatus';
import { ErrorCode } from '../models/ErrorCode';
import { ExcludeFromBackupResponse } from '../models/ExcludeFromBackupResponse';
import { ExportCostData400Response } from '../models/ExportCostData400Response';
import { ExportCostDataRequest } from '../models/ExportCostDataRequest';
import { FSxRestoreDestination } from '../models/FSxRestoreDestination';
import { FSxRestoreDestinationOntapRestoreOptions } from '../models/FSxRestoreDestinationOntapRestoreOptions';
import { FileExplorerCountSecurityAlertsInResourceResponse } from '../models/FileExplorerCountSecurityAlertsInResourceResponse';
import { FileExplorerResponse } from '../models/FileExplorerResponse';
import { FilePath } from '../models/FilePath';
import { FileRestoreData } from '../models/FileRestoreData';
import { FileSearchRecord } from '../models/FileSearchRecord';
import { FileSnapshot } from '../models/FileSnapshot';
import { ForceDeleteSnapshotsRequest } from '../models/ForceDeleteSnapshotsRequest';
import { FreeTextFilters } from '../models/FreeTextFilters';
import { FsxBackupData } from '../models/FsxBackupData';
import { FsxOntapVolume } from '../models/FsxOntapVolume';
import { FsxSnapshotProperties } from '../models/FsxSnapshotProperties';
import { FsxVolumeBackupInfo } from '../models/FsxVolumeBackupInfo';
import { GCSBucket } from '../models/GCSBucket';
import { GCSBuckets } from '../models/GCSBuckets';
import { GCSRestoreDestination } from '../models/GCSRestoreDestination';
import { GCSRestoreTarget } from '../models/GCSRestoreTarget';
import { GcpAccountConfigInput } from '../models/GcpAccountConfigInput';
import { GcpBigQueryResource } from '../models/GcpBigQueryResource';
import { GcpBigQueryResourceMaterializedViewsInner } from '../models/GcpBigQueryResourceMaterializedViewsInner';
import { GcpBigQueryResourceRoutinesInner } from '../models/GcpBigQueryResourceRoutinesInner';
import { GcpBigQueryResourceTablesInner } from '../models/GcpBigQueryResourceTablesInner';
import { GcpBigQueryResourceViewsInner } from '../models/GcpBigQueryResourceViewsInner';
import { GcpCloudSqlResource } from '../models/GcpCloudSqlResource';
import { GcpCloudSqlResourceStorageSettings } from '../models/GcpCloudSqlResourceStorageSettings';
import { GcpCloudSqlRestoreDestination } from '../models/GcpCloudSqlRestoreDestination';
import { GcpCloudSqlSnapshotProperties } from '../models/GcpCloudSqlSnapshotProperties';
import { GcpCloudSqlTarget } from '../models/GcpCloudSqlTarget';
import { GcpDiskResource } from '../models/GcpDiskResource';
import { GcpDiskRestoreData } from '../models/GcpDiskRestoreData';
import { GcpDiskRestoreDestination } from '../models/GcpDiskRestoreDestination';
import { GcpDiskSettings } from '../models/GcpDiskSettings';
import { GcpDiskSnapshotProperties } from '../models/GcpDiskSnapshotProperties';
import { GcpDiskTarget } from '../models/GcpDiskTarget';
import { GcpDiskTypesResponse } from '../models/GcpDiskTypesResponse';
import { GcpEncryptionKey } from '../models/GcpEncryptionKey';
import { GcpEncryptionKeys } from '../models/GcpEncryptionKeys';
import { GcpGkeNamespaceResource } from '../models/GcpGkeNamespaceResource';
import { GcpMachineTypesResponse } from '../models/GcpMachineTypesResponse';
import { GcpNetworkConnectivityConfig } from '../models/GcpNetworkConnectivityConfig';
import { GcpNetworkType } from '../models/GcpNetworkType';
import { GcpOrganization } from '../models/GcpOrganization';
import { GcpRestoreAccountAttributes } from '../models/GcpRestoreAccountAttributes';
import { GcpRestoreAccountAttributesInput } from '../models/GcpRestoreAccountAttributesInput';
import { GcpRestoreAccountConnectivityConfig } from '../models/GcpRestoreAccountConnectivityConfig';
import { GcpSourceAccountAttributes } from '../models/GcpSourceAccountAttributes';
import { GcpSourceAccountAttributesInput } from '../models/GcpSourceAccountAttributesInput';
import { GcpSubnet } from '../models/GcpSubnet';
import { GcpSubnetPerRegion } from '../models/GcpSubnetPerRegion';
import { GcpVaultConfig } from '../models/GcpVaultConfig';
import { GcpVaultConfigInput } from '../models/GcpVaultConfigInput';
import { GcpVmInstanceRestoreDestination } from '../models/GcpVmInstanceRestoreDestination';
import { GcpVmInstanceRestoreTarget } from '../models/GcpVmInstanceRestoreTarget';
import { GcpVmResource } from '../models/GcpVmResource';
import { GcpVmSnapshotProperties } from '../models/GcpVmSnapshotProperties';
import { GenericSnapshotData } from '../models/GenericSnapshotData';
import { GetAWSStackDetailsResponse } from '../models/GetAWSStackDetailsResponse';
import { GetBackupJobResponse } from '../models/GetBackupJobResponse';
import { GetBackupPolicyResponse } from '../models/GetBackupPolicyResponse';
import { GetBillingPeriodsResponse } from '../models/GetBillingPeriodsResponse';
import { GetBillingTransactionHistoryRequest } from '../models/GetBillingTransactionHistoryRequest';
import { GetBillingTransactionHistoryResponse } from '../models/GetBillingTransactionHistoryResponse';
import { GetCloudResourceSecurityPropertiesResponse } from '../models/GetCloudResourceSecurityPropertiesResponse';
import { GetCredits400Response } from '../models/GetCredits400Response';
import { GetCreditsResponse } from '../models/GetCreditsResponse';
import { GetDlsgPolicyResponse } from '../models/GetDlsgPolicyResponse';
import { GetGcsServiceAgentResponse } from '../models/GetGcsServiceAgentResponse';
import { GetIntegrationResponse } from '../models/GetIntegrationResponse';
import { GetLimitsResponseValue } from '../models/GetLimitsResponseValue';
import { GetLlgPolicyResponse } from '../models/GetLlgPolicyResponse';
import { GetProjectConfigurationKeyResponse } from '../models/GetProjectConfigurationKeyResponse';
import { GetProjectConfigurationKeysResponse } from '../models/GetProjectConfigurationKeysResponse';
import { GetResourceResponse } from '../models/GetResourceResponse';
import { GetRestoreAccountConnectivityConfigResponse } from '../models/GetRestoreAccountConnectivityConfigResponse';
import { GetRestoreAccountMetricsConfigResponse } from '../models/GetRestoreAccountMetricsConfigResponse';
import { GetRestoreJobResponse } from '../models/GetRestoreJobResponse';
import { GetRoleResponse } from '../models/GetRoleResponse';
import { GetScanJobResponse } from '../models/GetScanJobResponse';
import { GetSnapshotResponse } from '../models/GetSnapshotResponse';
import { GetSourceAccountMetricsConfigResponse } from '../models/GetSourceAccountMetricsConfigResponse';
import { GetSqlVmNetworkInfoRequest } from '../models/GetSqlVmNetworkInfoRequest';
import { GetSqlVmNetworkInfoResponse } from '../models/GetSqlVmNetworkInfoResponse';
import { GetVaultResponse } from '../models/GetVaultResponse';
import { GroupToRoleMapping } from '../models/GroupToRoleMapping';
import { HighFrequencyBackupPolicyPlan } from '../models/HighFrequencyBackupPolicyPlan';
import { HighFrequencyBackupResourceType } from '../models/HighFrequencyBackupResourceType';
import { HighFrequencyBackupScheduleConfig } from '../models/HighFrequencyBackupScheduleConfig';
import { HighFrequencyBackupScheduleFrequency } from '../models/HighFrequencyBackupScheduleFrequency';
import { HighFrequencyBackupSchedules } from '../models/HighFrequencyBackupSchedules';
import { HighFrequencyIntervalConfig } from '../models/HighFrequencyIntervalConfig';
import { IDs } from '../models/IDs';
import { IdFilters } from '../models/IdFilters';
import { Identifier } from '../models/Identifier';
import { IdentityProvidersNames } from '../models/IdentityProvidersNames';
import { ImportDlsgCertificateRequest } from '../models/ImportDlsgCertificateRequest';
import { IndexRestoreMode } from '../models/IndexRestoreMode';
import { InfectedDataByClassification } from '../models/InfectedDataByClassification';
import { InfectedDataClassificationSize } from '../models/InfectedDataClassificationSize';
import { InfectedDataOverTime } from '../models/InfectedDataOverTime';
import { InfectedDateSize } from '../models/InfectedDateSize';
import { InfectedExcludePath } from '../models/InfectedExcludePath';
import { InfectedResourcesCountResponse } from '../models/InfectedResourcesCountResponse';
import { InitAuthInput } from '../models/InitAuthInput';
import { Initiator } from '../models/Initiator';
import { InitiatorNameFilters } from '../models/InitiatorNameFilters';
import { IntegConfig } from '../models/IntegConfig';
import { IntegType } from '../models/IntegType';
import { IntegrationConfig } from '../models/IntegrationConfig';
import { InventoryFilterConditions } from '../models/InventoryFilterConditions';
import { InventoryGcpDisk } from '../models/InventoryGcpDisk';
import { InventoryResource } from '../models/InventoryResource';
import { InventoryResourceMetadata } from '../models/InventoryResourceMetadata';
import { InventorySnapshotVolume } from '../models/InventorySnapshotVolume';
import { InventoryVault } from '../models/InventoryVault';
import { InventoryVolume } from '../models/InventoryVolume';
import { IssueMutedNotificationArguments } from '../models/IssueMutedNotificationArguments';
import { JobExecutionDetails } from '../models/JobExecutionDetails';
import { JobFailedNotificationArguments } from '../models/JobFailedNotificationArguments';
import { JobIdFilters } from '../models/JobIdFilters';
import { JobInitiationResponse } from '../models/JobInitiationResponse';
import { JobMetricsReportFailedArguments } from '../models/JobMetricsReportFailedArguments';
import { JobSnapshotDetails } from '../models/JobSnapshotDetails';
import { JobStatus } from '../models/JobStatus';
import { JobStatusFilters } from '../models/JobStatusFilters';
import { JobStep } from '../models/JobStep';
import { JobStepName } from '../models/JobStepName';
import { JobStepStatus } from '../models/JobStepStatus';
import { JobSucceededNotificationArguments } from '../models/JobSucceededNotificationArguments';
import { KubeAccessStatus } from '../models/KubeAccessStatus';
import { KubernetesAccessMode } from '../models/KubernetesAccessMode';
import { LimitData } from '../models/LimitData';
import { ListAccountsResponse } from '../models/ListAccountsResponse';
import { ListApiCredentialsResponse } from '../models/ListApiCredentialsResponse';
import { ListApplicationOverrideResponse } from '../models/ListApplicationOverrideResponse';
import { ListAuditLogRequest } from '../models/ListAuditLogRequest';
import { ListAuditLogResponse } from '../models/ListAuditLogResponse';
import { ListAuditLogsResponse } from '../models/ListAuditLogsResponse';
import { ListAwsOrganizationsResponse } from '../models/ListAwsOrganizationsResponse';
import { ListAzureRegions200Response } from '../models/ListAzureRegions200Response';
import { ListAzureSKUs200Response } from '../models/ListAzureSKUs200Response';
import { ListAzureSKUs200ResponseSkusInner } from '../models/ListAzureSKUs200ResponseSkusInner';
import { ListAzureSKUsRequest } from '../models/ListAzureSKUsRequest';
import { ListAzureStorageAccountContainers200Response } from '../models/ListAzureStorageAccountContainers200Response';
import { ListAzureStorageAccountContainersRequest } from '../models/ListAzureStorageAccountContainersRequest';
import { ListAzureUnassignedNetworkInterfaces200Response } from '../models/ListAzureUnassignedNetworkInterfaces200Response';
import { ListAzureUnassignedNetworkInterfaces200ResponseNetworkInterfacesInner } from '../models/ListAzureUnassignedNetworkInterfaces200ResponseNetworkInterfacesInner';
import { ListAzureUnassignedNetworkInterfacesRequest } from '../models/ListAzureUnassignedNetworkInterfacesRequest';
import { ListBackupJobTypesResponse } from '../models/ListBackupJobTypesResponse';
import { ListBackupJobsInternal200Response } from '../models/ListBackupJobsInternal200Response';
import { ListBackupJobsRequest } from '../models/ListBackupJobsRequest';
import { ListBackupJobsResponse } from '../models/ListBackupJobsResponse';
import { ListBackupPoliciesResponse } from '../models/ListBackupPoliciesResponse';
import { ListBackupPolicyResponse } from '../models/ListBackupPolicyResponse';
import { ListBackupVaultResponse } from '../models/ListBackupVaultResponse';
import { ListCloudSnapshotsResponse } from '../models/ListCloudSnapshotsResponse';
import { ListControlViolationsResponse } from '../models/ListControlViolationsResponse';
import { ListControlsResponse } from '../models/ListControlsResponse';
import { ListDailySnapshotsResponse } from '../models/ListDailySnapshotsResponse';
import { ListDataClassificationEntitiesResponse } from '../models/ListDataClassificationEntitiesResponse';
import { ListDatabaseSnapshotsResponse } from '../models/ListDatabaseSnapshotsResponse';
import { ListDlsgPoliciesResponse } from '../models/ListDlsgPoliciesResponse';
import { ListDynamoDbBackupConfigsResponse } from '../models/ListDynamoDbBackupConfigsResponse';
import { ListEksClustersResponse } from '../models/ListEksClustersResponse';
import { ListGcpFoldersResponse } from '../models/ListGcpFoldersResponse';
import { ListGcpNetworksResponse } from '../models/ListGcpNetworksResponse';
import { ListGcpNetworksResponseNetworksInner } from '../models/ListGcpNetworksResponseNetworksInner';
import { ListGcpOrganizationsResponse } from '../models/ListGcpOrganizationsResponse';
import { ListGcpSubnetsResponse } from '../models/ListGcpSubnetsResponse';
import { ListInfectedExcludePathsResponse } from '../models/ListInfectedExcludePathsResponse';
import { ListInstanceProfilesResponse } from '../models/ListInstanceProfilesResponse';
import { ListInstanceTypesResponse } from '../models/ListInstanceTypesResponse';
import { ListIntegrationsResponse } from '../models/ListIntegrationsResponse';
import { ListInventoryAppsResponse } from '../models/ListInventoryAppsResponse';
import { ListInventoryBackupRegionsResponse } from '../models/ListInventoryBackupRegionsResponse';
import { ListInventoryEnvironmentsResponse } from '../models/ListInventoryEnvironmentsResponse';
import { ListInventoryGlobalDatabasesResponse } from '../models/ListInventoryGlobalDatabasesResponse';
import { ListInventoryItemSnapshotsResponse } from '../models/ListInventoryItemSnapshotsResponse';
import { ListInventoryNetworksResponse } from '../models/ListInventoryNetworksResponse';
import { ListInventoryRequest } from '../models/ListInventoryRequest';
import { ListInventoryResourceGroupsResponse } from '../models/ListInventoryResourceGroupsResponse';
import { ListInventoryResourceIdentifiersResponse } from '../models/ListInventoryResourceIdentifiersResponse';
import { ListInventoryResourceTypesResponse } from '../models/ListInventoryResourceTypesResponse';
import { ListInventoryResourcesResponse } from '../models/ListInventoryResourcesResponse';
import { ListInventorySnapshotsRequest } from '../models/ListInventorySnapshotsRequest';
import { ListInventorySnapshotsResponse } from '../models/ListInventorySnapshotsResponse';
import { ListInventorySourceRegionsResponse } from '../models/ListInventorySourceRegionsResponse';
import { ListInventorySubnetsResponse } from '../models/ListInventorySubnetsResponse';
import { ListInventoryTagsResponse } from '../models/ListInventoryTagsResponse';
import { ListJobErrorCodesResponse } from '../models/ListJobErrorCodesResponse';
import { ListLlgPoliciesResponse } from '../models/ListLlgPoliciesResponse';
import { ListLogSourcesResponse } from '../models/ListLogSourcesResponse';
import { ListNotificationPoliciesResponse } from '../models/ListNotificationPoliciesResponse';
import { ListNotificationsResponse } from '../models/ListNotificationsResponse';
import { ListObjectStoreExclusionRulesResponse } from '../models/ListObjectStoreExclusionRulesResponse';
import { ListOperators } from '../models/ListOperators';
import { ListPermissionsResponse } from '../models/ListPermissionsResponse';
import { ListProjectsResponse } from '../models/ListProjectsResponse';
import { ListResourceAttachmentEventsResponse } from '../models/ListResourceAttachmentEventsResponse';
import { ListResourcesResponse } from '../models/ListResourcesResponse';
import { ListRestoreAccountsRequest } from '../models/ListRestoreAccountsRequest';
import { ListRestoreAccountsResponse } from '../models/ListRestoreAccountsResponse';
import { ListRestoreJobTypesResponse } from '../models/ListRestoreJobTypesResponse';
import { ListRestoreJobsInternal200Response } from '../models/ListRestoreJobsInternal200Response';
import { ListRestoreJobsRequest } from '../models/ListRestoreJobsRequest';
import { ListRestoreJobsResponse } from '../models/ListRestoreJobsResponse';
import { ListRolesResponse } from '../models/ListRolesResponse';
import { ListScanJobTypesResponse } from '../models/ListScanJobTypesResponse';
import { ListScanJobsInternal200Response } from '../models/ListScanJobsInternal200Response';
import { ListScanJobsRequest } from '../models/ListScanJobsRequest';
import { ListScanJobsResponse } from '../models/ListScanJobsResponse';
import { ListSourceAccountsRequest } from '../models/ListSourceAccountsRequest';
import { ListSourceAccountsResponse } from '../models/ListSourceAccountsResponse';
import { ListStorageGatewaysResponse } from '../models/ListStorageGatewaysResponse';
import { ListSubnetsResponse } from '../models/ListSubnetsResponse';
import { ListSubnetsResponseV2 } from '../models/ListSubnetsResponseV2';
import { ListSubnetsResponseV2SubnetsInner } from '../models/ListSubnetsResponseV2SubnetsInner';
import { ListUsersResponse } from '../models/ListUsersResponse';
import { LlgPolicy } from '../models/LlgPolicy';
import { LlgPolicyPlan } from '../models/LlgPolicyPlan';
import { LogRecord } from '../models/LogRecord';
import { LogSource } from '../models/LogSource';
import { LogSourceLocation } from '../models/LogSourceLocation';
import { LogSourceLocationCloudTrail } from '../models/LogSourceLocationCloudTrail';
import { LogSourceLocationCloudWatchLogs } from '../models/LogSourceLocationCloudWatchLogs';
import { LogSourceLocationDatadog } from '../models/LogSourceLocationDatadog';
import { LogSourceLocationSplunkDdss } from '../models/LogSourceLocationSplunkDdss';
import { LogicalOperator } from '../models/LogicalOperator';
import { LogsSchema } from '../models/LogsSchema';
import { LogsSchemaColumn } from '../models/LogsSchemaColumn';
import { LogsSchemaEntry } from '../models/LogsSchemaEntry';
import { LogsSchemasResponse } from '../models/LogsSchemasResponse';
import { LuceneQueryInput } from '../models/LuceneQueryInput';
import { MaxRetentionRule } from '../models/MaxRetentionRule';
import { Message } from '../models/Message';
import { MeteringDimension } from '../models/MeteringDimension';
import { MicrosoftFabricIntegrationConfig } from '../models/MicrosoftFabricIntegrationConfig';
import { MinRetentionRule } from '../models/MinRetentionRule';
import { ModelError } from '../models/ModelError';
import { MonthlyConfig } from '../models/MonthlyConfig';
import { MonthlySnapshotData } from '../models/MonthlySnapshotData';
import { MsaAcceptanceResponse } from '../models/MsaAcceptanceResponse';
import { NativeStandardBackupPolicyPlan } from '../models/NativeStandardBackupPolicyPlan';
import { NativeStandardBackupSchedules } from '../models/NativeStandardBackupSchedules';
import { Notification } from '../models/Notification';
import { NotificationArguments } from '../models/NotificationArguments';
import { NotificationPolicy } from '../models/NotificationPolicy';
import { NotificationPolicyConfig } from '../models/NotificationPolicyConfig';
import { NotificationPolicyConfigEmail } from '../models/NotificationPolicyConfigEmail';
import { NotificationPolicyConfigServiceNow } from '../models/NotificationPolicyConfigServiceNow';
import { NotificationPolicyConfigSlack } from '../models/NotificationPolicyConfigSlack';
import { NotificationPolicyConfigSns } from '../models/NotificationPolicyConfigSns';
import { NotificationPolicyConfigSplunk } from '../models/NotificationPolicyConfigSplunk';
import { NotificationPolicyConfigWebhook } from '../models/NotificationPolicyConfigWebhook';
import { NotificationStatus } from '../models/NotificationStatus';
import { NotificationType } from '../models/NotificationType';
import { NumberOfCopiesRule } from '../models/NumberOfCopiesRule';
import { Oauth2TokenResponse } from '../models/Oauth2TokenResponse';
import { ObjectStorageDestination } from '../models/ObjectStorageDestination';
import { ObjectStoreExclusionRule } from '../models/ObjectStoreExclusionRule';
import { OnboardingAzureResourceGroup } from '../models/OnboardingAzureResourceGroup';
import { OperationIdFilters } from '../models/OperationIdFilters';
import { OrganizationIdFilters } from '../models/OrganizationIdFilters';
import { OverrideDataClassificationsRequest } from '../models/OverrideDataClassificationsRequest';
import { OverrideDataClassificationsResponse } from '../models/OverrideDataClassificationsResponse';
import { OverrideEnvironmentRequest } from '../models/OverrideEnvironmentRequest';
import { OverrideEnvironmentResponse } from '../models/OverrideEnvironmentResponse';
import { Pagination } from '../models/Pagination';
import { PartialRestoreData } from '../models/PartialRestoreData';
import { PartialRestoreType } from '../models/PartialRestoreType';
import { Path } from '../models/Path';
import { PathSnapshotsRequest } from '../models/PathSnapshotsRequest';
import { PathSnapshotsResponse } from '../models/PathSnapshotsResponse';
import { PathType } from '../models/PathType';
import { Permission } from '../models/Permission';
import { PermissionCategory } from '../models/PermissionCategory';
import { PermissionGrant } from '../models/PermissionGrant';
import { PermissionGrantInput } from '../models/PermissionGrantInput';
import { PermissionType } from '../models/PermissionType';
import { PersistentVolumeClaimProperties } from '../models/PersistentVolumeClaimProperties';
import { PitrBackupPolicyPlan } from '../models/PitrBackupPolicyPlan';
import { PitrBackupResourceType } from '../models/PitrBackupResourceType';
import { PitrBackupSchedules } from '../models/PitrBackupSchedules';
import { PitrIntervalConfig } from '../models/PitrIntervalConfig';
import { Preferences } from '../models/Preferences';
import { Project } from '../models/Project';
import { ProjectConfigurationKeyEnum } from '../models/ProjectConfigurationKeyEnum';
import { ProjectIdFilters } from '../models/ProjectIdFilters';
import { Provider } from '../models/Provider';
import { ProviderAccountIdFilters } from '../models/ProviderAccountIdFilters';
import { ProviderOrganizationIdFilters } from '../models/ProviderOrganizationIdFilters';
import { ProviderRestoreAccountConnectivityConfig } from '../models/ProviderRestoreAccountConnectivityConfig';
import { PulumiConfiguration } from '../models/PulumiConfiguration';
import { PulumiConfigurations } from '../models/PulumiConfigurations';
import { PulumiConfigurationsFilterConditions } from '../models/PulumiConfigurationsFilterConditions';
import { PulumiConfigurationsRequest } from '../models/PulumiConfigurationsRequest';
import { QueryCostData400Response } from '../models/QueryCostData400Response';
import { QueryCostDataRequest } from '../models/QueryCostDataRequest';
import { QueryCostDataResponse } from '../models/QueryCostDataResponse';
import { QueryDBRequest } from '../models/QueryDBRequest';
import { QueryDBResponse } from '../models/QueryDBResponse';
import { QueryDBRestoreDestination } from '../models/QueryDBRestoreDestination';
import { QueryDBRestoreDestinationType } from '../models/QueryDBRestoreDestinationType';
import { QueryDBResultResponse } from '../models/QueryDBResultResponse';
import { QueryDBStatus } from '../models/QueryDBStatus';
import { QueryDBStatusResponse } from '../models/QueryDBStatusResponse';
import { QuotaStatus } from '../models/QuotaStatus';
import { QuotaStatusFilters } from '../models/QuotaStatusFilters';
import { RansomwareResourceNotificationArguments } from '../models/RansomwareResourceNotificationArguments';
import { RansomwareSnapshotNotificationArguments } from '../models/RansomwareSnapshotNotificationArguments';
import { RdsNativePitrAdvancedConfig } from '../models/RdsNativePitrAdvancedConfig';
import { RdsNativePitrClusterAdvancedConfig } from '../models/RdsNativePitrClusterAdvancedConfig';
import { RdsNativePitrNetworkConfig } from '../models/RdsNativePitrNetworkConfig';
import { RdsNativePitrWindow } from '../models/RdsNativePitrWindow';
import { ReactivateAccountRequest } from '../models/ReactivateAccountRequest';
import { ReconnectCustomerVaultAccountResponse } from '../models/ReconnectCustomerVaultAccountResponse';
import { ReconnectRestoreAccountResponse } from '../models/ReconnectRestoreAccountResponse';
import { ReconnectSourceAccountResponse } from '../models/ReconnectSourceAccountResponse';
import { RecordRestoreResult } from '../models/RecordRestoreResult';
import { RedshiftCluster } from '../models/RedshiftCluster';
import { RedshiftClusters } from '../models/RedshiftClusters';
import { RedshiftIntegrationConfig } from '../models/RedshiftIntegrationConfig';
import { RegionCondition } from '../models/RegionCondition';
import { RegisterInput } from '../models/RegisterInput';
import { RegisterInputBillingToken } from '../models/RegisterInputBillingToken';
import { RegisterResponse } from '../models/RegisterResponse';
import { RelatedGlobalDbCluster } from '../models/RelatedGlobalDbCluster';
import { RequestDetails } from '../models/RequestDetails';
import { ResourceDetails } from '../models/ResourceDetails';
import { ResourceGroupNameCondition } from '../models/ResourceGroupNameCondition';
import { ResourceIdCondition } from '../models/ResourceIdCondition';
import { ResourceIdFilters } from '../models/ResourceIdFilters';
import { ResourceIdentifiers } from '../models/ResourceIdentifiers';
import { ResourceNameCondition } from '../models/ResourceNameCondition';
import { ResourceNameFilters } from '../models/ResourceNameFilters';
import { ResourceProperties } from '../models/ResourceProperties';
import { ResourceSelectorMode } from '../models/ResourceSelectorMode';
import { ResourceSnapshot } from '../models/ResourceSnapshot';
import { ResourceSnapshotProperties } from '../models/ResourceSnapshotProperties';
import { ResourceSortField } from '../models/ResourceSortField';
import { ResourceTag } from '../models/ResourceTag';
import { ResourceType } from '../models/ResourceType';
import { ResourceTypeCondition } from '../models/ResourceTypeCondition';
import { ResourceTypeFilters } from '../models/ResourceTypeFilters';
import { ResourceTypeToSecurityGroup } from '../models/ResourceTypeToSecurityGroup';
import { ResourceValidationError } from '../models/ResourceValidationError';
import { RestoreAccount } from '../models/RestoreAccount';
import { RestoreAccountAttributesInput } from '../models/RestoreAccountAttributesInput';
import { RestoreAccountCloudAttributes } from '../models/RestoreAccountCloudAttributes';
import { RestoreAccountConnectivityConfig } from '../models/RestoreAccountConnectivityConfig';
import { RestoreAccountMetricsConfig } from '../models/RestoreAccountMetricsConfig';
import { RestoreAccountRegions } from '../models/RestoreAccountRegions';
import { RestoreAccountSortField } from '../models/RestoreAccountSortField';
import { RestoreAccountsFilterConditions } from '../models/RestoreAccountsFilterConditions';
import { RestoreAtlasClusterInput } from '../models/RestoreAtlasClusterInput';
import { RestoreAvailabilityZones } from '../models/RestoreAvailabilityZones';
import { RestoreAwsEc2InstanceRequest } from '../models/RestoreAwsEc2InstanceRequest';
import { RestoreAzureDiskConfig } from '../models/RestoreAzureDiskConfig';
import { RestoreAzureDiskRequest } from '../models/RestoreAzureDiskRequest';
import { RestoreAzureInstanceDiskInput } from '../models/RestoreAzureInstanceDiskInput';
import { RestoreAzureSqlDatabaseRequest } from '../models/RestoreAzureSqlDatabaseRequest';
import { RestoreAzureVMInstanceConfig } from '../models/RestoreAzureVMInstanceConfig';
import { RestoreAzureVmInstanceRequest } from '../models/RestoreAzureVmInstanceRequest';
import { RestoreBigQueryDatasetRequest } from '../models/RestoreBigQueryDatasetRequest';
import { RestoreBucketRequest } from '../models/RestoreBucketRequest';
import { RestoreDbToRdsInstanceRequest } from '../models/RestoreDbToRdsInstanceRequest';
import { RestoreDbToRdsSnapshotRequest } from '../models/RestoreDbToRdsSnapshotRequest';
import { RestoreDynamoDBTableRequest } from '../models/RestoreDynamoDBTableRequest';
import { RestoreDynamoDBToExistingTableRequest } from '../models/RestoreDynamoDBToExistingTableRequest';
import { RestoreDynamoDbTableConfig } from '../models/RestoreDynamoDbTableConfig';
import { RestoreEFSRequest } from '../models/RestoreEFSRequest';
import { RestoreEbsVolumeConfig } from '../models/RestoreEbsVolumeConfig';
import { RestoreEc2InstanceConfig } from '../models/RestoreEc2InstanceConfig';
import { RestoreEksNamespaceConfig } from '../models/RestoreEksNamespaceConfig';
import { RestoreEksNamespaceInput } from '../models/RestoreEksNamespaceInput';
import { RestoreEksNamespaceVolumeInput } from '../models/RestoreEksNamespaceVolumeInput';
import { RestoreFSxRequest } from '../models/RestoreFSxRequest';
import { RestoreFilesRequest } from '../models/RestoreFilesRequest';
import { RestoreGceDiskConfig } from '../models/RestoreGceDiskConfig';
import { RestoreGceInstanceConfig } from '../models/RestoreGceInstanceConfig';
import { RestoreGcpCloudSqlDatabaseConfig } from '../models/RestoreGcpCloudSqlDatabaseConfig';
import { RestoreGcpCloudSqlRequest } from '../models/RestoreGcpCloudSqlRequest';
import { RestoreGcpDiskRequest } from '../models/RestoreGcpDiskRequest';
import { RestoreGcpInstanceDiskInput } from '../models/RestoreGcpInstanceDiskInput';
import { RestoreGcpVmInstanceRequest } from '../models/RestoreGcpVmInstanceRequest';
import { RestoreImageImageDeviceInput } from '../models/RestoreImageImageDeviceInput';
import { RestoreImageImageInput } from '../models/RestoreImageImageInput';
import { RestoreImageInput } from '../models/RestoreImageInput';
import { RestoreImageVolumeInput } from '../models/RestoreImageVolumeInput';
import { RestoreInstanceImageDeviceInput } from '../models/RestoreInstanceImageDeviceInput';
import { RestoreInstanceImageInput } from '../models/RestoreInstanceImageInput';
import { RestoreInstanceVolumeInput } from '../models/RestoreInstanceVolumeInput';
import { RestoreJob } from '../models/RestoreJob';
import { RestoreJobConfiguration } from '../models/RestoreJobConfiguration';
import { RestoreJobInitiationResponse } from '../models/RestoreJobInitiationResponse';
import { RestoreJobSortField } from '../models/RestoreJobSortField';
import { RestoreJobType } from '../models/RestoreJobType';
import { RestoreJobTypeFilters } from '../models/RestoreJobTypeFilters';
import { RestoreJobsFilterConditions } from '../models/RestoreJobsFilterConditions';
import { RestoreMethod } from '../models/RestoreMethod';
import { RestoreMethodAvailabilityReason } from '../models/RestoreMethodAvailabilityReason';
import { RestoreRdsInstanceClasses } from '../models/RestoreRdsInstanceClasses';
import { RestoreRdsInstanceConfig } from '../models/RestoreRdsInstanceConfig';
import { RestoreRdsNativePitrRequest } from '../models/RestoreRdsNativePitrRequest';
import { RestoreRdsSubnetGroups } from '../models/RestoreRdsSubnetGroups';
import { RestoreRdsSubnetGroupsRdsSubnetGroupsInner } from '../models/RestoreRdsSubnetGroupsRdsSubnetGroupsInner';
import { RestoreResult } from '../models/RestoreResult';
import { RestoreSecurityGroups } from '../models/RestoreSecurityGroups';
import { RestoreSecurityGroupsSecurityGroupsInner } from '../models/RestoreSecurityGroupsSecurityGroupsInner';
import { RestoreVolumeToEbsRequest } from '../models/RestoreVolumeToEbsRequest';
import { RestoreVolumeToEbsSnapshotRequest } from '../models/RestoreVolumeToEbsSnapshotRequest';
import { Role } from '../models/Role';
import { RoleAccessGroupCondition } from '../models/RoleAccessGroupCondition';
import { RoleInstallationMethod } from '../models/RoleInstallationMethod';
import { S3Bucket } from '../models/S3Bucket';
import { S3Buckets } from '../models/S3Buckets';
import { S3RestoreDestination } from '../models/S3RestoreDestination';
import { S3RestoreTarget } from '../models/S3RestoreTarget';
import { SAMLCallback400Response } from '../models/SAMLCallback400Response';
import { SAMLCallback401Response } from '../models/SAMLCallback401Response';
import { SAMLCallback500Response } from '../models/SAMLCallback500Response';
import { SamlIdentityProvider } from '../models/SamlIdentityProvider';
import { SamlIdentityProviderConfigsResponse } from '../models/SamlIdentityProviderConfigsResponse';
import { SapHanaInstance } from '../models/SapHanaInstance';
import { SaveCloudResourceSecurityPropertiesRequest } from '../models/SaveCloudResourceSecurityPropertiesRequest';
import { ScalarOperators } from '../models/ScalarOperators';
import { ScanJob } from '../models/ScanJob';
import { ScanJobType } from '../models/ScanJobType';
import { ScheduleMode } from '../models/ScheduleMode';
import { ScheduleType } from '../models/ScheduleType';
import { SchemaRecord } from '../models/SchemaRecord';
import { SearchDBResponse } from '../models/SearchDBResponse';
import { SearchFilesResponse } from '../models/SearchFilesResponse';
import { SearchInput } from '../models/SearchInput';
import { SearchLogsInput } from '../models/SearchLogsInput';
import { SearchLogsResponse } from '../models/SearchLogsResponse';
import { SearchResources200Response } from '../models/SearchResources200Response';
import { SearchResources200ResponseTablesInner } from '../models/SearchResources200ResponseTablesInner';
import { SearchResources200ResponseTablesInnerResource } from '../models/SearchResources200ResponseTablesInnerResource';
import { SearchResources200ResponseTablesInnerSnapshots } from '../models/SearchResources200ResponseTablesInnerSnapshots';
import { SearchResources200ResponseTablesInnerSource } from '../models/SearchResources200ResponseTablesInnerSource';
import { SearchResources200ResponseTablesInnerTablesInner } from '../models/SearchResources200ResponseTablesInnerTablesInner';
import { SearchResources200ResponseTablesInnerTablesInnerColumnsInner } from '../models/SearchResources200ResponseTablesInnerTablesInnerColumnsInner';
import { SearchResourcesRequest } from '../models/SearchResourcesRequest';
import { SecurityAssignmentStatus } from '../models/SecurityAssignmentStatus';
import { SecurityInformation } from '../models/SecurityInformation';
import { SecurityPlan } from '../models/SecurityPlan';
import { SecurityPlanEnabledResponse } from '../models/SecurityPlanEnabledResponse';
import { SecurityScan } from '../models/SecurityScan';
import { SecurityScanExample } from '../models/SecurityScanExample';
import { SecurityScanResult } from '../models/SecurityScanResult';
import { SecurityScansSummaryResponse } from '../models/SecurityScansSummaryResponse';
import { SecuritySummary } from '../models/SecuritySummary';
import { SendMessageToAI400Response } from '../models/SendMessageToAI400Response';
import { SendMessageToAIRequest } from '../models/SendMessageToAIRequest';
import { ServiceProviderDetails } from '../models/ServiceProviderDetails';
import { Session } from '../models/Session';
import { Severity } from '../models/Severity';
import { SeverityFilters } from '../models/SeverityFilters';
import { Snapshot } from '../models/Snapshot';
import { SnapshotDateFilters } from '../models/SnapshotDateFilters';
import { SnapshotFilterConditions } from '../models/SnapshotFilterConditions';
import { SnapshotIdFilters } from '../models/SnapshotIdFilters';
import { SnapshotLocation } from '../models/SnapshotLocation';
import { SnapshotReference } from '../models/SnapshotReference';
import { SnapshotSortField } from '../models/SnapshotSortField';
import { SnapshotStatus } from '../models/SnapshotStatus';
import { SnapshotStorage } from '../models/SnapshotStorage';
import { SnapshotsSummaryResponse } from '../models/SnapshotsSummaryResponse';
import { SnowflakeIntegrationConfig } from '../models/SnowflakeIntegrationConfig';
import { SortBackupJobBy } from '../models/SortBackupJobBy';
import { SortLogsBy } from '../models/SortLogsBy';
import { SortOrder } from '../models/SortOrder';
import { SortResourceBy } from '../models/SortResourceBy';
import { SortRestoreAccountsBy } from '../models/SortRestoreAccountsBy';
import { SortRestoreJobBy } from '../models/SortRestoreJobBy';
import { SortSnapshotsBy } from '../models/SortSnapshotsBy';
import { SortSourceAccountsBy } from '../models/SortSourceAccountsBy';
import { SourceAccount } from '../models/SourceAccount';
import { SourceAccountAttributesInput } from '../models/SourceAccountAttributesInput';
import { SourceAccountCloudAttributes } from '../models/SourceAccountCloudAttributes';
import { SourceAccountMetricsConfig } from '../models/SourceAccountMetricsConfig';
import { SourceAccountProviderIdFilters } from '../models/SourceAccountProviderIdFilters';
import { SourceAccountSortField } from '../models/SourceAccountSortField';
import { SourceAccountsFilterConditions } from '../models/SourceAccountsFilterConditions';
import { SourceRegionFilters } from '../models/SourceRegionFilters';
import { SourceStorage } from '../models/SourceStorage';
import { SourceStorageBackupStatus } from '../models/SourceStorageBackupStatus';
import { SqlVmNetworkInfo } from '../models/SqlVmNetworkInfo';
import { SsoIntegrationSettings } from '../models/SsoIntegrationSettings';
import { StackNameFilters } from '../models/StackNameFilters';
import { StandardBackupPolicyPlan } from '../models/StandardBackupPolicyPlan';
import { StandardBackupScheduleConfig } from '../models/StandardBackupScheduleConfig';
import { StandardBackupScheduleFrequency } from '../models/StandardBackupScheduleFrequency';
import { StandardBackupSchedules } from '../models/StandardBackupSchedules';
import { StandardIntervalConfig } from '../models/StandardIntervalConfig';
import { StartTimeDateFilters } from '../models/StartTimeDateFilters';
import { StatusCodeFilters } from '../models/StatusCodeFilters';
import { StorageAccountContainerRestoreDestination } from '../models/StorageAccountContainerRestoreDestination';
import { StorageAccountRestoreTarget } from '../models/StorageAccountRestoreTarget';
import { StorageGateway } from '../models/StorageGateway';
import { StorageGatewayDetails } from '../models/StorageGatewayDetails';
import { StorageGatewayStatus } from '../models/StorageGatewayStatus';
import { SubnetFilters } from '../models/SubnetFilters';
import { SubnetPerAvailabilityZone } from '../models/SubnetPerAvailabilityZone';
import { SubnetsCondition } from '../models/SubnetsCondition';
import { SupportedProviders } from '../models/SupportedProviders';
import { TableRecord } from '../models/TableRecord';
import { TagKeyValue } from '../models/TagKeyValue';
import { TagKeyValuesCondition } from '../models/TagKeyValuesCondition';
import { TagKeyValuesFilters } from '../models/TagKeyValuesFilters';
import { TagKeysCondition } from '../models/TagKeysCondition';
import { TagKeysFilters } from '../models/TagKeysFilters';
import { TakeNativeSnapshotRequest } from '../models/TakeNativeSnapshotRequest';
import { TakeSnapshotRequest } from '../models/TakeSnapshotRequest';
import { TakeSnapshotResponse } from '../models/TakeSnapshotResponse';
import { TimeFrame } from '../models/TimeFrame';
import { TimeOfDay } from '../models/TimeOfDay';
import { TimeOfYear } from '../models/TimeOfYear';
import { TokenResponse } from '../models/TokenResponse';
import { UnifiedDBRecord } from '../models/UnifiedDBRecord';
import { UnifiedDBServerRecord } from '../models/UnifiedDBServerRecord';
import { UnifiedDatabaseRecord } from '../models/UnifiedDatabaseRecord';
import { UnifiedSchemaRecord } from '../models/UnifiedSchemaRecord';
import { UnifiedTableRecord } from '../models/UnifiedTableRecord';
import { UpPulumiStacksResponse } from '../models/UpPulumiStacksResponse';
import { UpdateApiCredentialsRequest } from '../models/UpdateApiCredentialsRequest';
import { UpdateBackupPolicyRequest } from '../models/UpdateBackupPolicyRequest';
import { UpdateBackupPolicyResponse } from '../models/UpdateBackupPolicyResponse';
import { UpdateCloudResourceConfigurationRequest } from '../models/UpdateCloudResourceConfigurationRequest';
import { UpdateCloudResourceConfigurationRequestCdcBackup } from '../models/UpdateCloudResourceConfigurationRequestCdcBackup';
import { UpdateCloudResourceConfigurationRequestInventoryBackup } from '../models/UpdateCloudResourceConfigurationRequestInventoryBackup';
import { UpdateControlViolationInput } from '../models/UpdateControlViolationInput';
import { UpdateDlsgPolicyRequest } from '../models/UpdateDlsgPolicyRequest';
import { UpdateDlsgPolicyResponse } from '../models/UpdateDlsgPolicyResponse';
import { UpdateIntegrationRequest } from '../models/UpdateIntegrationRequest';
import { UpdateIntegrationResponse } from '../models/UpdateIntegrationResponse';
import { UpdateLlgPolicyRequest } from '../models/UpdateLlgPolicyRequest';
import { UpdateLlgPolicyResponse } from '../models/UpdateLlgPolicyResponse';
import { UpdateProjectConfigurationKeyRequest } from '../models/UpdateProjectConfigurationKeyRequest';
import { UpdatePulumiConfigurationRequest } from '../models/UpdatePulumiConfigurationRequest';
import { UpdateResourceSecurityScansExclusionRequest } from '../models/UpdateResourceSecurityScansExclusionRequest';
import { UpdateRestoreAccountConnectivityConfigRequest } from '../models/UpdateRestoreAccountConnectivityConfigRequest';
import { UpdateRestoreAccountConnectivityConfigResponse } from '../models/UpdateRestoreAccountConnectivityConfigResponse';
import { UpdateRoleRequest } from '../models/UpdateRoleRequest';
import { UpdateRoleResponse } from '../models/UpdateRoleResponse';
import { UpdateSamlIdentityProviderInput } from '../models/UpdateSamlIdentityProviderInput';
import { UpdateSessionRequest } from '../models/UpdateSessionRequest';
import { UpdateShouldFullBackupResourceRequest } from '../models/UpdateShouldFullBackupResourceRequest';
import { UpdateUserInput } from '../models/UpdateUserInput';
import { UpdateVaultRequest } from '../models/UpdateVaultRequest';
import { UpdateVaultResponse } from '../models/UpdateVaultResponse';
import { UpdateViewerRoleRequest } from '../models/UpdateViewerRoleRequest';
import { Usage } from '../models/Usage';
import { UsageUnit } from '../models/UsageUnit';
import { User } from '../models/User';
import { UserIdFilters } from '../models/UserIdFilters';
import { ValidateResourceConfigurationRequest } from '../models/ValidateResourceConfigurationRequest';
import { ValidateResourceConfigurationResponse } from '../models/ValidateResourceConfigurationResponse';
import { VaultFilters } from '../models/VaultFilters';
import { VaultMetadataReplicationConfig } from '../models/VaultMetadataReplicationConfig';
import { VaultNotificationArguments } from '../models/VaultNotificationArguments';
import { VaultProviderAttributes } from '../models/VaultProviderAttributes';
import { VaultProviderAttributesInput } from '../models/VaultProviderAttributesInput';
import { VaultSnapshotCount } from '../models/VaultSnapshotCount';
import { Viewer } from '../models/Viewer';
import { ViolationsDetectionNotificationArguments } from '../models/ViolationsDetectionNotificationArguments';
import { VirtualMachineRestoreDestination } from '../models/VirtualMachineRestoreDestination';
import { VisualizationHistogramSeries } from '../models/VisualizationHistogramSeries';
import { VisualizationHistogramTimePoint } from '../models/VisualizationHistogramTimePoint';
import { VisualizationLabel } from '../models/VisualizationLabel';
import { VolumeSettings } from '../models/VolumeSettings';
import { VpcCondition } from '../models/VpcCondition';
import { VpcPropertyFilters } from '../models/VpcPropertyFilters';
import { WeeklyConfig } from '../models/WeeklyConfig';
import { YearlySnapshotDistribution } from '../models/YearlySnapshotDistribution';


async function requestWithRetries(requestContext: RequestContext, configuration: Configuration): Promise<ResponseContext> {
  if (configuration.retryNonIdempotent || requestContext.isIdempotent()) {
    return retry(() => requestWithMiddleware(requestContext, configuration), configuration.retries);
  }
  return requestWithMiddleware(requestContext, configuration);
}

async function requestWithMiddleware(requestContext: RequestContext, configuration: Configuration): Promise<ResponseContext> {
  for (let middleware of configuration.middleware!) {
    requestContext = await middleware.pre(requestContext);
  }
  let signal = requestContext.signal;
  if (configuration.signal && 'any' in AbortSignal) {
    signal = AbortSignal.any([configuration.signal, signal!]);
  }
  const resp = await fetch(requestContext.getUrl(), {
    method: requestContext.getHttpMethod(),
    body: requestContext.getBody(),
    headers: requestContext.getHeaders(),
    credentials: "same-origin",
    signal: signal
  });
  
  let headers: { [key: string]: string } = {};
  resp.headers.forEach((value, key) => {
    headers[key] = value;
  });

  let responseContext = new ResponseContext(resp.status, headers, {
    text: () => resp.text(), // TODO benji remove this indirection 
    binary: () => resp.blob(),
    json: () => resp.json(),
  });

  for (let middleware of configuration.middleware!) {
    responseContext = await middleware.post(responseContext, requestContext);
  }
  return responseContext;
}
import { AcceptMsaApiRequestFactory, AcceptMsaApiResponseProcessor} from "../apis/AcceptMsaApi";

export class PromiseAcceptMsaApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AcceptMsaApiRequestFactory = new AcceptMsaApiRequestFactory(configuration),
    private responseProcessor: AcceptMsaApiResponseProcessor = new AcceptMsaApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Accept the MSA (Master Service Agreement) for the current account. The signing user is determined from the authenticated viewer.
   * Accept MSA
   */
  public async acceptMsaWithHttpInfo(_options?: Configuration): Promise<HttpInfo<MsaAcceptanceResponse>> {
    let requestContextValue = await this.requestFactory.acceptMsa(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.acceptMsaWithHttpInfo(responseContext)
  }

  /**
   * Description: Accept the MSA (Master Service Agreement) for the current account. The signing user is determined from the authenticated viewer.
   * Accept MSA
   */
  public async acceptMsa(_options?: Configuration): Promise<MsaAcceptanceResponse> {
    const responseWithInfo = await this.acceptMsaWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { AccountsApiRequestFactory, AccountsApiResponseProcessor} from "../apis/AccountsApi";

export class PromiseAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AccountsApiRequestFactory = new AccountsApiRequestFactory(configuration),
    private responseProcessor: AccountsApiResponseProcessor = new AccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Activate an aws organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the aws organization to. 
   * @param id ID of the aws organization to activate
   */
  public async activateAwsOrganizationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<ActivateAwsOrganizationResponse>> {
    let requestContextValue = await this.requestFactory.activateAwsOrganization(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.activateAwsOrganizationWithHttpInfo(responseContext)
  }

  /**
   * Activate an aws organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the aws organization to. 
   * @param id ID of the aws organization to activate
   */
  public async activateAwsOrganization(projectId: string, id: string, _options?: Configuration): Promise<ActivateAwsOrganizationResponse> {
    const responseWithInfo = await this.activateAwsOrganizationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Activate Azure Management Group
   * @param projectId [ID of the project](/global-settings/projects) you want to modify the azure management group for. 
   * @param id ID of the azure management group to activate
   */
  public async activateAzureManagementGroupWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<AzureManagementGroup>> {
    let requestContextValue = await this.requestFactory.activateAzureManagementGroup(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.activateAzureManagementGroupWithHttpInfo(responseContext)
  }

  /**
   * Activate Azure Management Group
   * @param projectId [ID of the project](/global-settings/projects) you want to modify the azure management group for. 
   * @param id ID of the azure management group to activate
   */
  public async activateAzureManagementGroup(projectId: string, id: string, _options?: Configuration): Promise<AzureManagementGroup> {
    const responseWithInfo = await this.activateAzureManagementGroupWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Activate a gcp folder and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp folder to. 
   * @param id ID of the gcp folder to activate
   */
  public async activateGcpFolderWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<ActivateGcpFolderResponse>> {
    let requestContextValue = await this.requestFactory.activateGcpFolder(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.activateGcpFolderWithHttpInfo(responseContext)
  }

  /**
   * Activate a gcp folder and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp folder to. 
   * @param id ID of the gcp folder to activate
   */
  public async activateGcpFolder(projectId: string, id: string, _options?: Configuration): Promise<ActivateGcpFolderResponse> {
    const responseWithInfo = await this.activateGcpFolderWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Activate an gcp organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp organization to. 
   * @param id ID of the gcp organization to activate
   */
  public async activateGcpOrganizationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<ActivateGcpOrganizationResponse>> {
    let requestContextValue = await this.requestFactory.activateGcpOrganization(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.activateGcpOrganizationWithHttpInfo(responseContext)
  }

  /**
   * Activate an gcp organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp organization to. 
   * @param id ID of the gcp organization to activate
   */
  public async activateGcpOrganization(projectId: string, id: string, _options?: Configuration): Promise<ActivateGcpOrganizationResponse> {
    const responseWithInfo = await this.activateGcpOrganizationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Connects an AWS organization to the given project.
   * Connect Aws Organization
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the aws organization to. 
   * @param connectAwsOrganizationRequest 
   */
  public async connectAwsOrganizationWithHttpInfo(projectId: string, connectAwsOrganizationRequest: ConnectAwsOrganizationRequest, _options?: Configuration): Promise<HttpInfo<ConnectAwsOrganizationResponse>> {
    let requestContextValue = await this.requestFactory.connectAwsOrganization(projectId, connectAwsOrganizationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectAwsOrganizationWithHttpInfo(responseContext)
  }

  /**
   * Description: Connects an AWS organization to the given project.
   * Connect Aws Organization
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the aws organization to. 
   * @param connectAwsOrganizationRequest 
   */
  public async connectAwsOrganization(projectId: string, connectAwsOrganizationRequest: ConnectAwsOrganizationRequest, _options?: Configuration): Promise<ConnectAwsOrganizationResponse> {
    const responseWithInfo = await this.connectAwsOrganizationWithHttpInfo(projectId, connectAwsOrganizationRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Connects a customer-owned vault account.
   * Connect Customer-owned Vault Account
   * @param connectCustomerVaultAccountRequest 
   */
  public async connectCustomerVaultAccountWithHttpInfo(connectCustomerVaultAccountRequest: ConnectCustomerVaultAccountRequest, _options?: Configuration): Promise<HttpInfo<ConnectCustomerVaultAccountResponse>> {
    let requestContextValue = await this.requestFactory.connectCustomerVaultAccount(connectCustomerVaultAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectCustomerVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Connects a customer-owned vault account.
   * Connect Customer-owned Vault Account
   * @param connectCustomerVaultAccountRequest 
   */
  public async connectCustomerVaultAccount(connectCustomerVaultAccountRequest: ConnectCustomerVaultAccountRequest, _options?: Configuration): Promise<ConnectCustomerVaultAccountResponse> {
    const responseWithInfo = await this.connectCustomerVaultAccountWithHttpInfo(connectCustomerVaultAccountRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Connects a GCP folder to the given project.
   * Connect Gcp Folder
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp folder to. 
   * @param connectGcpFolderRequest 
   */
  public async connectGcpFolderWithHttpInfo(projectId: string, connectGcpFolderRequest: ConnectGcpFolderRequest, _options?: Configuration): Promise<HttpInfo<ConnectGcpFolderResponse>> {
    let requestContextValue = await this.requestFactory.connectGcpFolder(projectId, connectGcpFolderRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectGcpFolderWithHttpInfo(responseContext)
  }

  /**
   * Description: Connects a GCP folder to the given project.
   * Connect Gcp Folder
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp folder to. 
   * @param connectGcpFolderRequest 
   */
  public async connectGcpFolder(projectId: string, connectGcpFolderRequest: ConnectGcpFolderRequest, _options?: Configuration): Promise<ConnectGcpFolderResponse> {
    const responseWithInfo = await this.connectGcpFolderWithHttpInfo(projectId, connectGcpFolderRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Connects an GCP organization to the given project.
   * Connect Gcp Organization
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp organization to. 
   * @param connectGcpOrganizationRequest 
   */
  public async connectGcpOrganizationWithHttpInfo(projectId: string, connectGcpOrganizationRequest: ConnectGcpOrganizationRequest, _options?: Configuration): Promise<HttpInfo<ConnectGcpOrganizationResponse>> {
    let requestContextValue = await this.requestFactory.connectGcpOrganization(projectId, connectGcpOrganizationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectGcpOrganizationWithHttpInfo(responseContext)
  }

  /**
   * Description: Connects an GCP organization to the given project.
   * Connect Gcp Organization
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp organization to. 
   * @param connectGcpOrganizationRequest 
   */
  public async connectGcpOrganization(projectId: string, connectGcpOrganizationRequest: ConnectGcpOrganizationRequest, _options?: Configuration): Promise<ConnectGcpOrganizationResponse> {
    const responseWithInfo = await this.connectGcpOrganizationWithHttpInfo(projectId, connectGcpOrganizationRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Connects a restore account to the given project. 
   * Connect Restore Account
   * @param projectId ID of the project you want to connect the source account to. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param connectRestoreAccountRequest 
   */
  public async connectRestoreAccountWithHttpInfo(projectId: string, connectRestoreAccountRequest: ConnectRestoreAccountRequest, _options?: Configuration): Promise<HttpInfo<ConnectRestoreAccountResponse>> {
    let requestContextValue = await this.requestFactory.connectRestoreAccount(projectId, connectRestoreAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Connects a restore account to the given project. 
   * Connect Restore Account
   * @param projectId ID of the project you want to connect the source account to. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param connectRestoreAccountRequest 
   */
  public async connectRestoreAccount(projectId: string, connectRestoreAccountRequest: ConnectRestoreAccountRequest, _options?: Configuration): Promise<ConnectRestoreAccountResponse> {
    const responseWithInfo = await this.connectRestoreAccountWithHttpInfo(projectId, connectRestoreAccountRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Connects a source cloud account to the given project.
   * Connect Source Account
   * @param projectId ID of the project you want to connect the source account to. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param connectSourceAccountRequest 
   */
  public async connectSourceAccountWithHttpInfo(projectId: string, connectSourceAccountRequest: ConnectSourceAccountRequest, _options?: Configuration): Promise<HttpInfo<ConnectSourceAccountResponse>> {
    let requestContextValue = await this.requestFactory.connectSourceAccount(projectId, connectSourceAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Connects a source cloud account to the given project.
   * Connect Source Account
   * @param projectId ID of the project you want to connect the source account to. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param connectSourceAccountRequest 
   */
  public async connectSourceAccount(projectId: string, connectSourceAccountRequest: ConnectSourceAccountRequest, _options?: Configuration): Promise<ConnectSourceAccountResponse> {
    const responseWithInfo = await this.connectSourceAccountWithHttpInfo(projectId, connectSourceAccountRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Deactivate an aws organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the aws organization to. 
   * @param id ID of the aws organization to deactivate
   */
  public async deactivateAwsOrganizationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<DeactivateAwsOrganizationResponse>> {
    let requestContextValue = await this.requestFactory.deactivateAwsOrganization(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deactivateAwsOrganizationWithHttpInfo(responseContext)
  }

  /**
   * Deactivate an aws organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the aws organization to. 
   * @param id ID of the aws organization to deactivate
   */
  public async deactivateAwsOrganization(projectId: string, id: string, _options?: Configuration): Promise<DeactivateAwsOrganizationResponse> {
    const responseWithInfo = await this.deactivateAwsOrganizationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Deactivate Azure Management Group
   * @param projectId [ID of the project](/global-settings/projects) you want to modify the azure management group for. 
   * @param id ID of the azure management group to deactivate
   */
  public async deactivateAzureManagementGroupWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<AzureManagementGroup>> {
    let requestContextValue = await this.requestFactory.deactivateAzureManagementGroup(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deactivateAzureManagementGroupWithHttpInfo(responseContext)
  }

  /**
   * Deactivate Azure Management Group
   * @param projectId [ID of the project](/global-settings/projects) you want to modify the azure management group for. 
   * @param id ID of the azure management group to deactivate
   */
  public async deactivateAzureManagementGroup(projectId: string, id: string, _options?: Configuration): Promise<AzureManagementGroup> {
    const responseWithInfo = await this.deactivateAzureManagementGroupWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Deactivate a gcp folder and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp folder to. 
   * @param id ID of the gcp folder to deactivate
   */
  public async deactivateGcpFolderWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<DeactivateGcpFolderResponse>> {
    let requestContextValue = await this.requestFactory.deactivateGcpFolder(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deactivateGcpFolderWithHttpInfo(responseContext)
  }

  /**
   * Deactivate a gcp folder and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp folder to. 
   * @param id ID of the gcp folder to deactivate
   */
  public async deactivateGcpFolder(projectId: string, id: string, _options?: Configuration): Promise<DeactivateGcpFolderResponse> {
    const responseWithInfo = await this.deactivateGcpFolderWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Deactivate an gcp organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp organization to. 
   * @param id ID of the gcp organization to deactivate
   */
  public async deactivateGcpOrganizationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<DeactivateGcpOrganizationResponse>> {
    let requestContextValue = await this.requestFactory.deactivateGcpOrganization(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deactivateGcpOrganizationWithHttpInfo(responseContext)
  }

  /**
   * Deactivate an gcp organization and its accounts
   * @param projectId [ID of the project](/global-settings/projects) you want to connect the gcp organization to. 
   * @param id ID of the gcp organization to deactivate
   */
  public async deactivateGcpOrganization(projectId: string, id: string, _options?: Configuration): Promise<DeactivateGcpOrganizationResponse> {
    const responseWithInfo = await this.deactivateGcpOrganizationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Deletes the restore account connectivity configuration, reverting to the default settings. 
   * Delete Restore Account Connectivity Configuration
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account connectivity configuration you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteRestoreAccountConnectivityConfigWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteRestoreAccountConnectivityConfig(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteRestoreAccountConnectivityConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes the restore account connectivity configuration, reverting to the default settings. 
   * Delete Restore Account Connectivity Configuration
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account connectivity configuration you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteRestoreAccountConnectivityConfig(accountId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteRestoreAccountConnectivityConfigWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Disables metrics for the specified restore account. 
   * Disable Restore Account Metrics
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account metrics you want to disable. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async disableRestoreAccountMetricsConfigWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.disableRestoreAccountMetricsConfig(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.disableRestoreAccountMetricsConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Disables metrics for the specified restore account. 
   * Disable Restore Account Metrics
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account metrics you want to disable. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async disableRestoreAccountMetricsConfig(accountId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.disableRestoreAccountMetricsConfigWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Disables metrics for the specified source account. 
   * Disable Source Account Metrics
   * @param accountId Eon-assigned ID of the source account.
   * @param projectId ID of the project whose source account metrics configuration you want to disable. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async disableSourceAccountMetricsConfigWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.disableSourceAccountMetricsConfig(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.disableSourceAccountMetricsConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Disables metrics for the specified source account. 
   * Disable Source Account Metrics
   * @param accountId Eon-assigned ID of the source account.
   * @param projectId ID of the project whose source account metrics configuration you want to disable. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async disableSourceAccountMetricsConfig(accountId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.disableSourceAccountMetricsConfigWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Disconnects a customer owned vault account from the given project.  After disconnecting, data and resources can no longer be backed up to the vault account. 
   * Disconnect Customer Vault Account
   * @param accountId Eon-assigned ID of the customer-owned vault account to disconnect.
   */
  public async disconnectCustomerVaultAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<DisconnectCustomerVaultAccountResponse>> {
    let requestContextValue = await this.requestFactory.disconnectCustomerVaultAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.disconnectCustomerVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Disconnects a customer owned vault account from the given project.  After disconnecting, data and resources can no longer be backed up to the vault account. 
   * Disconnect Customer Vault Account
   * @param accountId Eon-assigned ID of the customer-owned vault account to disconnect.
   */
  public async disconnectCustomerVaultAccount(accountId: string, _options?: Configuration): Promise<DisconnectCustomerVaultAccountResponse> {
    const responseWithInfo = await this.disconnectCustomerVaultAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Disconnects a restore account from the given project.  After disconnecting, data and resources can no longer be restored to the restore account. 
   * Disconnect Restore Account
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the restore account to disconnect.
   */
  public async disconnectRestoreAccountWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<DisconnectRestoreAccountResponse>> {
    let requestContextValue = await this.requestFactory.disconnectRestoreAccount(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.disconnectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Disconnects a restore account from the given project.  After disconnecting, data and resources can no longer be restored to the restore account. 
   * Disconnect Restore Account
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the restore account to disconnect.
   */
  public async disconnectRestoreAccount(projectId: string, accountId: string, _options?: Configuration): Promise<DisconnectRestoreAccountResponse> {
    const responseWithInfo = await this.disconnectRestoreAccountWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Disconnects a source account from the given project.  After disconnecting, resources in the source account will no longer be backed up. Resources with snapshots will continue to be displayed in the inventory, and you can still explore, query, and restore them. Resources with no remaining snapshots are removed from the inventory. 
   * Disconnect Source Account
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the source account to disconnect.
   */
  public async disconnectSourceAccountWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<DisconnectSourceAccountResponse>> {
    let requestContextValue = await this.requestFactory.disconnectSourceAccount(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.disconnectSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Disconnects a source account from the given project.  After disconnecting, resources in the source account will no longer be backed up. Resources with snapshots will continue to be displayed in the inventory, and you can still explore, query, and restore them. Resources with no remaining snapshots are removed from the inventory. 
   * Disconnect Source Account
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the source account to disconnect.
   */
  public async disconnectSourceAccount(projectId: string, accountId: string, _options?: Configuration): Promise<DisconnectSourceAccountResponse> {
    const responseWithInfo = await this.disconnectSourceAccountWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Enables or updates the metrics configuration of the specified restore account. 
   * Configure Restore Account Metrics
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account metrics you want to configure. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param enableRestoreAccountMetricsConfigRequest 
   */
  public async enableRestoreAccountMetricsConfigWithHttpInfo(accountId: string, projectId: string, enableRestoreAccountMetricsConfigRequest: EnableRestoreAccountMetricsConfigRequest, _options?: Configuration): Promise<HttpInfo<EnableRestoreAccountMetricsConfigResponse>> {
    let requestContextValue = await this.requestFactory.enableRestoreAccountMetricsConfig(accountId, projectId, enableRestoreAccountMetricsConfigRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.enableRestoreAccountMetricsConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Enables or updates the metrics configuration of the specified restore account. 
   * Configure Restore Account Metrics
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account metrics you want to configure. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param enableRestoreAccountMetricsConfigRequest 
   */
  public async enableRestoreAccountMetricsConfig(accountId: string, projectId: string, enableRestoreAccountMetricsConfigRequest: EnableRestoreAccountMetricsConfigRequest, _options?: Configuration): Promise<EnableRestoreAccountMetricsConfigResponse> {
    const responseWithInfo = await this.enableRestoreAccountMetricsConfigWithHttpInfo(accountId, projectId, enableRestoreAccountMetricsConfigRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Enables or updates the metrics configuration of the specified source account. 
   * Configure Source Account Metrics
   * @param accountId Eon-assigned ID of the source account.
   * @param projectId ID of the project whose source account metrics you want to configure. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param enableSourceAccountMetricsConfigRequest 
   */
  public async enableSourceAccountMetricsConfigWithHttpInfo(accountId: string, projectId: string, enableSourceAccountMetricsConfigRequest: EnableSourceAccountMetricsConfigRequest, _options?: Configuration): Promise<HttpInfo<EnableSourceAccountMetricsConfigResponse>> {
    let requestContextValue = await this.requestFactory.enableSourceAccountMetricsConfig(accountId, projectId, enableSourceAccountMetricsConfigRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.enableSourceAccountMetricsConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Enables or updates the metrics configuration of the specified source account. 
   * Configure Source Account Metrics
   * @param accountId Eon-assigned ID of the source account.
   * @param projectId ID of the project whose source account metrics you want to configure. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param enableSourceAccountMetricsConfigRequest 
   */
  public async enableSourceAccountMetricsConfig(accountId: string, projectId: string, enableSourceAccountMetricsConfigRequest: EnableSourceAccountMetricsConfigRequest, _options?: Configuration): Promise<EnableSourceAccountMetricsConfigResponse> {
    const responseWithInfo = await this.enableSourceAccountMetricsConfigWithHttpInfo(accountId, projectId, enableSourceAccountMetricsConfigRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Get onboarded Azure tenants for an Eon account
   * @param projectId The project ID
   */
  public async getAzureOnboardedTenantsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<AzureOnboardedTenantsResponse>> {
    let requestContextValue = await this.requestFactory.getAzureOnboardedTenants(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAzureOnboardedTenantsWithHttpInfo(responseContext)
  }

  /**
   * Get onboarded Azure tenants for an Eon account
   * @param projectId The project ID
   */
  public async getAzureOnboardedTenants(projectId: string, _options?: Configuration): Promise<AzureOnboardedTenantsResponse> {
    const responseWithInfo = await this.getAzureOnboardedTenantsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Returns the GCS service agent email for a GCP scanning account. This is used for CMEK setup to grant the service agent access to the KMS key. 
   * Get GCS Service Agent
   * @param accountId ID of the GCP scanning account. 
   */
  public async getGcsServiceAgentWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<GetGcsServiceAgentResponse>> {
    let requestContextValue = await this.requestFactory.getGcsServiceAgent(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getGcsServiceAgentWithHttpInfo(responseContext)
  }

  /**
   * Description: Returns the GCS service agent email for a GCP scanning account. This is used for CMEK setup to grant the service agent access to the KMS key. 
   * Get GCS Service Agent
   * @param accountId ID of the GCP scanning account. 
   */
  public async getGcsServiceAgent(accountId: string, _options?: Configuration): Promise<GetGcsServiceAgentResponse> {
    const responseWithInfo = await this.getGcsServiceAgentWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves the connectivity configuration of the specified restore account. 
   * Get Restore Account Connectivity Configuration
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account connectivity configuration you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getRestoreAccountConnectivityConfigWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetRestoreAccountConnectivityConfigResponse>> {
    let requestContextValue = await this.requestFactory.getRestoreAccountConnectivityConfig(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRestoreAccountConnectivityConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the connectivity configuration of the specified restore account. 
   * Get Restore Account Connectivity Configuration
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account connectivity configuration you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getRestoreAccountConnectivityConfig(accountId: string, projectId: string, _options?: Configuration): Promise<GetRestoreAccountConnectivityConfigResponse> {
    const responseWithInfo = await this.getRestoreAccountConnectivityConfigWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves the metrics configuration of the specified restore account. 
   * Get Restore Account Metrics Configuration
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account metrics configuration you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getRestoreAccountMetricsConfigWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetRestoreAccountMetricsConfigResponse>> {
    let requestContextValue = await this.requestFactory.getRestoreAccountMetricsConfig(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRestoreAccountMetricsConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the metrics configuration of the specified restore account. 
   * Get Restore Account Metrics Configuration
   * @param accountId Eon-assigned ID of the restore account.
   * @param projectId ID of the project whose restore account metrics configuration you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getRestoreAccountMetricsConfig(accountId: string, projectId: string, _options?: Configuration): Promise<GetRestoreAccountMetricsConfigResponse> {
    const responseWithInfo = await this.getRestoreAccountMetricsConfigWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves the metrics configuration of the specified source account. 
   * Get Source Account Metrics Configuration
   * @param accountId Eon-assigned ID of the source account.
   * @param projectId ID of the project whose source account metrics configuration you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getSourceAccountMetricsConfigWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetSourceAccountMetricsConfigResponse>> {
    let requestContextValue = await this.requestFactory.getSourceAccountMetricsConfig(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSourceAccountMetricsConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the metrics configuration of the specified source account. 
   * Get Source Account Metrics Configuration
   * @param accountId Eon-assigned ID of the source account.
   * @param projectId ID of the project whose source account metrics configuration you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getSourceAccountMetricsConfig(accountId: string, projectId: string, _options?: Configuration): Promise<GetSourceAccountMetricsConfigResponse> {
    const responseWithInfo = await this.getSourceAccountMetricsConfigWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieve a list of all customer aws organizations.
   * List Aws Organizations
   * @param projectId [ID of the project](/global-settings/projects) you want to list the aws organizations for. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listAwsOrganizationsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListAwsOrganizationsResponse>> {
    let requestContextValue = await this.requestFactory.listAwsOrganizations(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsOrganizationsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all customer aws organizations.
   * List Aws Organizations
   * @param projectId [ID of the project](/global-settings/projects) you want to list the aws organizations for. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listAwsOrganizations(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListAwsOrganizationsResponse> {
    const responseWithInfo = await this.listAwsOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieve a list of all customer aws organizations.
   * List Azure Management Groups
   * @param projectId [ID of the project](/global-settings/projects) you want to list the azure management groups for. 
   */
  public async listAzureManagementGroupsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<AzureManagementGroups>> {
    let requestContextValue = await this.requestFactory.listAzureManagementGroups(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureManagementGroupsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all customer aws organizations.
   * List Azure Management Groups
   * @param projectId [ID of the project](/global-settings/projects) you want to list the azure management groups for. 
   */
  public async listAzureManagementGroups(projectId: string, _options?: Configuration): Promise<AzureManagementGroups> {
    const responseWithInfo = await this.listAzureManagementGroupsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of EKS clusters for the given project.
   * List Eks Clusters
   * @param projectId [ID of the project](/global-settings/projects) whose restore accounts you want to retrieve. 
   * @param pageToken The token for the next page
   * @param pageSize The numbers of items per page
   */
  public async listEksClustersWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListEksClustersResponse>> {
    let requestContextValue = await this.requestFactory.listEksClusters(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listEksClustersWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of EKS clusters for the given project.
   * List Eks Clusters
   * @param projectId [ID of the project](/global-settings/projects) whose restore accounts you want to retrieve. 
   * @param pageToken The token for the next page
   * @param pageSize The numbers of items per page
   */
  public async listEksClusters(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListEksClustersResponse> {
    const responseWithInfo = await this.listEksClustersWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieve a list of all customer gcp folders.
   * List Gcp Folders
   * @param projectId [ID of the project](/global-settings/projects) you want to list the gcp folders for. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listGcpFoldersWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListGcpFoldersResponse>> {
    let requestContextValue = await this.requestFactory.listGcpFolders(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpFoldersWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all customer gcp folders.
   * List Gcp Folders
   * @param projectId [ID of the project](/global-settings/projects) you want to list the gcp folders for. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listGcpFolders(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListGcpFoldersResponse> {
    const responseWithInfo = await this.listGcpFoldersWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieve a list of all customer gcp organizations.
   * List Gcp Organizations
   * @param projectId [ID of the project](/global-settings/projects) you want to list the gcp organizations for. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listGcpOrganizationsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListGcpOrganizationsResponse>> {
    let requestContextValue = await this.requestFactory.listGcpOrganizations(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpOrganizationsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all customer gcp organizations.
   * List Gcp Organizations
   * @param projectId [ID of the project](/global-settings/projects) you want to list the gcp organizations for. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listGcpOrganizations(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListGcpOrganizationsResponse> {
    const responseWithInfo = await this.listGcpOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of restore accounts for the given project.
   * List Restore Accounts
   * @param projectId ID of the project whose restore accounts you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listRestoreAccountsRequest Filter options. 
   */
  public async listRestoreAccountsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, listRestoreAccountsRequest?: ListRestoreAccountsRequest, _options?: Configuration): Promise<HttpInfo<ListRestoreAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listRestoreAccounts(projectId, pageToken, pageSize, listRestoreAccountsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRestoreAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of restore accounts for the given project.
   * List Restore Accounts
   * @param projectId ID of the project whose restore accounts you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listRestoreAccountsRequest Filter options. 
   */
  public async listRestoreAccounts(projectId: string, pageToken?: string, pageSize?: number, listRestoreAccountsRequest?: ListRestoreAccountsRequest, _options?: Configuration): Promise<ListRestoreAccountsResponse> {
    const responseWithInfo = await this.listRestoreAccountsWithHttpInfo(projectId, pageToken, pageSize, listRestoreAccountsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of source accounts for the given project.
   * List Source Accounts
   * @param projectId ID of the project whose source accounts you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listSourceAccountsRequest Filter options. 
   */
  public async listSourceAccountsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, listSourceAccountsRequest?: ListSourceAccountsRequest, _options?: Configuration): Promise<HttpInfo<ListSourceAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listSourceAccounts(projectId, pageToken, pageSize, listSourceAccountsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listSourceAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of source accounts for the given project.
   * List Source Accounts
   * @param projectId ID of the project whose source accounts you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listSourceAccountsRequest Filter options. 
   */
  public async listSourceAccounts(projectId: string, pageToken?: string, pageSize?: number, listSourceAccountsRequest?: ListSourceAccountsRequest, _options?: Configuration): Promise<ListSourceAccountsResponse> {
    const responseWithInfo = await this.listSourceAccountsWithHttpInfo(projectId, pageToken, pageSize, listSourceAccountsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Reconnects a customer vault account to the given project. 
   * Reconnect Customer Vault Account
   * @param accountId Eon-assigned ID of the vault account to reconnect.
   */
  public async reconnectCustomerVaultAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<ReconnectCustomerVaultAccountResponse>> {
    let requestContextValue = await this.requestFactory.reconnectCustomerVaultAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.reconnectCustomerVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Reconnects a customer vault account to the given project. 
   * Reconnect Customer Vault Account
   * @param accountId Eon-assigned ID of the vault account to reconnect.
   */
  public async reconnectCustomerVaultAccount(accountId: string, _options?: Configuration): Promise<ReconnectCustomerVaultAccountResponse> {
    const responseWithInfo = await this.reconnectCustomerVaultAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Reconnects a restore account to the given project. 
   * Reconnect Restore Account
   * @param projectId ID of the project whose restore account you want to reconnect. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the restore account to reconnect.
   */
  public async reconnectRestoreAccountWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<ReconnectRestoreAccountResponse>> {
    let requestContextValue = await this.requestFactory.reconnectRestoreAccount(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.reconnectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Reconnects a restore account to the given project. 
   * Reconnect Restore Account
   * @param projectId ID of the project whose restore account you want to reconnect. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the restore account to reconnect.
   */
  public async reconnectRestoreAccount(projectId: string, accountId: string, _options?: Configuration): Promise<ReconnectRestoreAccountResponse> {
    const responseWithInfo = await this.reconnectRestoreAccountWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Reconnects a source account to the given project. 
   * Reconnect Source Account
   * @param projectId ID of the project whose source account you want to reconnect. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the source account to reconnect.
   */
  public async reconnectSourceAccountWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<ReconnectSourceAccountResponse>> {
    let requestContextValue = await this.requestFactory.reconnectSourceAccount(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.reconnectSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Reconnects a source account to the given project. 
   * Reconnect Source Account
   * @param projectId ID of the project whose source account you want to reconnect. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param accountId Eon-assigned ID of the source account to reconnect.
   */
  public async reconnectSourceAccount(projectId: string, accountId: string, _options?: Configuration): Promise<ReconnectSourceAccountResponse> {
    const responseWithInfo = await this.reconnectSourceAccountWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Triggers a permissions sync for a restore account. 
   * Sync Restore Account Permissions
   * @param projectId ID of the project. 
   * @param accountId Eon-assigned ID of the restore account to check.
   */
  public async syncRestoreAccountPermissionsWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.syncRestoreAccountPermissions(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.syncRestoreAccountPermissionsWithHttpInfo(responseContext)
  }

  /**
   * Description: Triggers a permissions sync for a restore account. 
   * Sync Restore Account Permissions
   * @param projectId ID of the project. 
   * @param accountId Eon-assigned ID of the restore account to check.
   */
  public async syncRestoreAccountPermissions(projectId: string, accountId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.syncRestoreAccountPermissionsWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Triggers a permissions sync for a source account. 
   * Sync Source Account Permissions
   * @param projectId ID of the project. 
   * @param accountId Eon-assigned ID of the source account to check.
   */
  public async syncSourceAccountPermissionsWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.syncSourceAccountPermissions(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.syncSourceAccountPermissionsWithHttpInfo(responseContext)
  }

  /**
   * Description: Triggers a permissions sync for a source account. 
   * Sync Source Account Permissions
   * @param projectId ID of the project. 
   * @param accountId Eon-assigned ID of the source account to check.
   */
  public async syncSourceAccountPermissions(projectId: string, accountId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.syncSourceAccountPermissionsWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates the connectivity configuration of the specified restore account. 
   * Update Restore Account Connectivity Configuration
   * @param accountId ID of the restore account
   * @param projectId ID of the project whose restore account connectivity configuration you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateRestoreAccountConnectivityConfigRequest 
   */
  public async updateRestoreAccountConnectivityConfigWithHttpInfo(accountId: string, projectId: string, updateRestoreAccountConnectivityConfigRequest: UpdateRestoreAccountConnectivityConfigRequest, _options?: Configuration): Promise<HttpInfo<UpdateRestoreAccountConnectivityConfigResponse>> {
    let requestContextValue = await this.requestFactory.updateRestoreAccountConnectivityConfig(accountId, projectId, updateRestoreAccountConnectivityConfigRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateRestoreAccountConnectivityConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates the connectivity configuration of the specified restore account. 
   * Update Restore Account Connectivity Configuration
   * @param accountId ID of the restore account
   * @param projectId ID of the project whose restore account connectivity configuration you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateRestoreAccountConnectivityConfigRequest 
   */
  public async updateRestoreAccountConnectivityConfig(accountId: string, projectId: string, updateRestoreAccountConnectivityConfigRequest: UpdateRestoreAccountConnectivityConfigRequest, _options?: Configuration): Promise<UpdateRestoreAccountConnectivityConfigResponse> {
    const responseWithInfo = await this.updateRestoreAccountConnectivityConfigWithHttpInfo(accountId, projectId, updateRestoreAccountConnectivityConfigRequest, _options);
    return responseWithInfo.data;
  }


}


import { AiAgentApiRequestFactory, AiAgentApiResponseProcessor} from "../apis/AiAgentApi";

export class PromiseAiAgentApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AiAgentApiRequestFactory = new AiAgentApiRequestFactory(configuration),
    private responseProcessor: AiAgentApiResponseProcessor = new AiAgentApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Create a new session for the authenticated user in an AI agent application.
   * Create AI Agent session
   * @param projectId The project ID
   * @param createSessionRequest 
   */
  public async createAIAgentSessionWithHttpInfo(projectId: string, createSessionRequest?: CreateSessionRequest, _options?: Configuration): Promise<HttpInfo<Session>> {
    let requestContextValue = await this.requestFactory.createAIAgentSession(projectId, createSessionRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createAIAgentSessionWithHttpInfo(responseContext)
  }

  /**
   * Description: Create a new session for the authenticated user in an AI agent application.
   * Create AI Agent session
   * @param projectId The project ID
   * @param createSessionRequest 
   */
  public async createAIAgentSession(projectId: string, createSessionRequest?: CreateSessionRequest, _options?: Configuration): Promise<Session> {
    const responseWithInfo = await this.createAIAgentSessionWithHttpInfo(projectId, createSessionRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Delete a specific session by ID.
   * Delete AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   */
  public async deleteAIAgentSessionWithHttpInfo(projectId: string, sessionId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteAIAgentSession(projectId, sessionId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteAIAgentSessionWithHttpInfo(responseContext)
  }

  /**
   * Description: Delete a specific session by ID.
   * Delete AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   */
  public async deleteAIAgentSession(projectId: string, sessionId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteAIAgentSessionWithHttpInfo(projectId, sessionId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Get a specific session by ID.
   * Get AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   */
  public async getAIAgentSessionWithHttpInfo(projectId: string, sessionId: string, _options?: Configuration): Promise<HttpInfo<Session>> {
    let requestContextValue = await this.requestFactory.getAIAgentSession(projectId, sessionId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAIAgentSessionWithHttpInfo(responseContext)
  }

  /**
   * Description: Get a specific session by ID.
   * Get AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   */
  public async getAIAgentSession(projectId: string, sessionId: string, _options?: Configuration): Promise<Session> {
    const responseWithInfo = await this.getAIAgentSessionWithHttpInfo(projectId, sessionId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: List all sessions for the authenticated user in an AI agent application.
   * List AI Agent sessions
   * @param projectId The project ID
   */
  public async listAIAgentSessionsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<Array<Session>>> {
    let requestContextValue = await this.requestFactory.listAIAgentSessions(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAIAgentSessionsWithHttpInfo(responseContext)
  }

  /**
   * Description: List all sessions for the authenticated user in an AI agent application.
   * List AI Agent sessions
   * @param projectId The project ID
   */
  public async listAIAgentSessions(projectId: string, _options?: Configuration): Promise<Array<Session>> {
    const responseWithInfo = await this.listAIAgentSessionsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Search for relevant resources using semantic and keyword search without generating SQL.
   * Search resources using semantic and keyword search
   * @param projectId The project ID
   * @param searchResourcesRequest 
   */
  public async searchResourcesWithHttpInfo(projectId: string, searchResourcesRequest: SearchResourcesRequest, _options?: Configuration): Promise<HttpInfo<SearchResources200Response>> {
    let requestContextValue = await this.requestFactory.searchResources(projectId, searchResourcesRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.searchResourcesWithHttpInfo(responseContext)
  }

  /**
   * Description: Search for relevant resources using semantic and keyword search without generating SQL.
   * Search resources using semantic and keyword search
   * @param projectId The project ID
   * @param searchResourcesRequest 
   */
  public async searchResources(projectId: string, searchResourcesRequest: SearchResourcesRequest, _options?: Configuration): Promise<SearchResources200Response> {
    const responseWithInfo = await this.searchResourcesWithHttpInfo(projectId, searchResourcesRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Send a message to the AI agent service. Include chat_id to continue an existing conversation.
   * Send message to AI Agent
   * @param projectId The project ID
   * @param sendMessageToAIRequest 
   */
  public async sendMessageToAIWithHttpInfo(projectId: string, sendMessageToAIRequest: SendMessageToAIRequest, _options?: Configuration): Promise<HttpInfo<string>> {
    let requestContextValue = await this.requestFactory.sendMessageToAI(projectId, sendMessageToAIRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.sendMessageToAIWithHttpInfo(responseContext)
  }

  /**
   * Description: Send a message to the AI agent service. Include chat_id to continue an existing conversation.
   * Send message to AI Agent
   * @param projectId The project ID
   * @param sendMessageToAIRequest 
   */
  public async sendMessageToAI(projectId: string, sendMessageToAIRequest: SendMessageToAIRequest, _options?: Configuration): Promise<string> {
    const responseWithInfo = await this.sendMessageToAIWithHttpInfo(projectId, sendMessageToAIRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Update a session\'s state without running the agent.
   * Update AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   * @param updateSessionRequest 
   */
  public async updateAIAgentSessionWithHttpInfo(projectId: string, sessionId: string, updateSessionRequest: UpdateSessionRequest, _options?: Configuration): Promise<HttpInfo<Session>> {
    let requestContextValue = await this.requestFactory.updateAIAgentSession(projectId, sessionId, updateSessionRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateAIAgentSessionWithHttpInfo(responseContext)
  }

  /**
   * Description: Update a session\'s state without running the agent.
   * Update AI Agent session
   * @param projectId The project ID
   * @param sessionId The session ID
   * @param updateSessionRequest 
   */
  public async updateAIAgentSession(projectId: string, sessionId: string, updateSessionRequest: UpdateSessionRequest, _options?: Configuration): Promise<Session> {
    const responseWithInfo = await this.updateAIAgentSessionWithHttpInfo(projectId, sessionId, updateSessionRequest, _options);
    return responseWithInfo.data;
  }


}


import { AmazonRegisterApiRequestFactory, AmazonRegisterApiResponseProcessor} from "../apis/AmazonRegisterApi";

export class PromiseAmazonRegisterApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AmazonRegisterApiRequestFactory = new AmazonRegisterApiRequestFactory(configuration),
    private responseProcessor: AmazonRegisterApiResponseProcessor = new AmazonRegisterApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information.
   * Callback endpoint for Amazon marketplace
   * @param xAmznMarketplaceToken Amazon marketplace token
   */
  public async amazonRegisterWithHttpInfo(xAmznMarketplaceToken?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.amazonRegister(xAmznMarketplaceToken, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.amazonRegisterWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information.
   * Callback endpoint for Amazon marketplace
   * @param xAmznMarketplaceToken Amazon marketplace token
   */
  public async amazonRegister(xAmznMarketplaceToken?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.amazonRegisterWithHttpInfo(xAmznMarketplaceToken, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information via GET request.
   * Callback endpoint for Amazon marketplace (GET)
   * @param xAmznMarketplaceToken Amazon marketplace token
   */
  public async amazonRegisterGetWithHttpInfo(xAmznMarketplaceToken: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.amazonRegisterGet(xAmznMarketplaceToken, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.amazonRegisterGetWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information via GET request.
   * Callback endpoint for Amazon marketplace (GET)
   * @param xAmznMarketplaceToken Amazon marketplace token
   */
  public async amazonRegisterGet(xAmznMarketplaceToken: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.amazonRegisterGetWithHttpInfo(xAmznMarketplaceToken, _options);
    return responseWithInfo.data;
  }


}


import { AuditLogsApiRequestFactory, AuditLogsApiResponseProcessor} from "../apis/AuditLogsApi";

export class PromiseAuditLogsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AuditLogsApiRequestFactory = new AuditLogsApiRequestFactory(configuration),
    private responseProcessor: AuditLogsApiResponseProcessor = new AuditLogsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Downloads audit logs for a specific project as a CSV file.
   * Download audit logs as csv file
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async auditLogsDownloadWithHttpInfo(projectId: string, filter?: string, _options?: Configuration): Promise<HttpInfo<HttpFile>> {
    let requestContextValue = await this.requestFactory.auditLogsDownload(projectId, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.auditLogsDownloadWithHttpInfo(responseContext)
  }

  /**
   * Description: Downloads audit logs for a specific project as a CSV file.
   * Download audit logs as csv file
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async auditLogsDownload(projectId: string, filter?: string, _options?: Configuration): Promise<HttpFile> {
    const responseWithInfo = await this.auditLogsDownloadWithHttpInfo(projectId, filter, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of audit logs. 
   * List Audit Logs
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listAuditLogRequest The request body for filtering audit logs
   */
  public async listAuditLogsWithHttpInfo(pageToken?: string, pageSize?: number, listAuditLogRequest?: ListAuditLogRequest, _options?: Configuration): Promise<HttpInfo<ListAuditLogResponse>> {
    let requestContextValue = await this.requestFactory.listAuditLogs(pageToken, pageSize, listAuditLogRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAuditLogsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of audit logs. 
   * List Audit Logs
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listAuditLogRequest The request body for filtering audit logs
   */
  public async listAuditLogs(pageToken?: string, pageSize?: number, listAuditLogRequest?: ListAuditLogRequest, _options?: Configuration): Promise<ListAuditLogResponse> {
    const responseWithInfo = await this.listAuditLogsWithHttpInfo(pageToken, pageSize, listAuditLogRequest, _options);
    return responseWithInfo.data;
  }


}


import { AuditLogsPopulateApiRequestFactory, AuditLogsPopulateApiResponseProcessor} from "../apis/AuditLogsPopulateApi";

export class PromiseAuditLogsPopulateApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AuditLogsPopulateApiRequestFactory = new AuditLogsPopulateApiRequestFactory(configuration),
    private responseProcessor: AuditLogsPopulateApiResponseProcessor = new AuditLogsPopulateApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Populates test data for a specific project with a given version number.
   * Populate test data
   * @param projectId The project ID
   * @param version the version number of the data to populate
   */
  public async auditLogsPopulateWithHttpInfo(projectId: string, version: number, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.auditLogsPopulate(projectId, version, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.auditLogsPopulateWithHttpInfo(responseContext)
  }

  /**
   * Description: Populates test data for a specific project with a given version number.
   * Populate test data
   * @param projectId The project ID
   * @param version the version number of the data to populate
   */
  public async auditLogsPopulate(projectId: string, version: number, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.auditLogsPopulateWithHttpInfo(projectId, version, _options);
    return responseWithInfo.data;
  }


}


import { AuthApiRequestFactory, AuthApiResponseProcessor} from "../apis/AuthApi";

export class PromiseAuthApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AuthApiRequestFactory = new AuthApiRequestFactory(configuration),
    private responseProcessor: AuthApiResponseProcessor = new AuthApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves an access token, which you then pass in the `Authorization` header when calling API methods.  The client ID and secret you provide when calling this method must be from a set of API credentials from your Eon account. To learn more about creating API credentials and completing the authentication flow, see [Authentication](/api/using-the-api/authentication).  The returned access token has the permissions included in the role you assigned to the API credentials. Always make sure the API credentials have the permissions needed for the API methods you want to use it for.  The access token is valid for 12 hours. After that, you must call this method again to get a new token. 
   * Get Access Token
   * @param apiCredentials 
   */
  public async getAccessTokenWithHttpInfo(apiCredentials: ApiCredentials, _options?: Configuration): Promise<HttpInfo<TokenResponse>> {
    let requestContextValue = await this.requestFactory.getAccessToken(apiCredentials, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAccessTokenWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves an access token, which you then pass in the `Authorization` header when calling API methods.  The client ID and secret you provide when calling this method must be from a set of API credentials from your Eon account. To learn more about creating API credentials and completing the authentication flow, see [Authentication](/api/using-the-api/authentication).  The returned access token has the permissions included in the role you assigned to the API credentials. Always make sure the API credentials have the permissions needed for the API methods you want to use it for.  The access token is valid for 12 hours. After that, you must call this method again to get a new token. 
   * Get Access Token
   * @param apiCredentials 
   */
  public async getAccessToken(apiCredentials: ApiCredentials, _options?: Configuration): Promise<TokenResponse> {
    const responseWithInfo = await this.getAccessTokenWithHttpInfo(apiCredentials, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves an access token using an OAuth-2.0-compliant flow. Accepts form-encoded credentials using standard `client_id` and `client_secret` field names.  This method is specifically designed for OAuth 2.0 clients like Grafana that send credentials as form-encoded data. For JSON-based API calls, use [Get Access Token].  The client ID and secret you provide when calling this method must be from a set of API credentials from your Eon account. To learn more about creating API credentials and completing the authentication flow, see [Authentication](/api/using-the-api/authentication).  The returned access token has the permissions included in the role you assigned to the API credentials. Always make sure the API credentials have the permissions needed for the API methods you want to use it for.  The access token is valid for 12 hours. After that, you must call this method again to get a new token.  [Get Access Token]: ./get-access-token 
   * Get Access Token (OAuth 2)
   * @param clientId API client ID.
   * @param clientSecret API client secret.
   * @param grantType The OAuth 2.0 grant type you\\\&#39;re using. Must be &#x60;client_credentials&#x60;. 
   */
  public async getAccessTokenOAuth2WithHttpInfo(clientId: string, clientSecret: string, grantType: string, _options?: Configuration): Promise<HttpInfo<Oauth2TokenResponse>> {
    let requestContextValue = await this.requestFactory.getAccessTokenOAuth2(clientId, clientSecret, grantType, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAccessTokenOAuth2WithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves an access token using an OAuth-2.0-compliant flow. Accepts form-encoded credentials using standard `client_id` and `client_secret` field names.  This method is specifically designed for OAuth 2.0 clients like Grafana that send credentials as form-encoded data. For JSON-based API calls, use [Get Access Token].  The client ID and secret you provide when calling this method must be from a set of API credentials from your Eon account. To learn more about creating API credentials and completing the authentication flow, see [Authentication](/api/using-the-api/authentication).  The returned access token has the permissions included in the role you assigned to the API credentials. Always make sure the API credentials have the permissions needed for the API methods you want to use it for.  The access token is valid for 12 hours. After that, you must call this method again to get a new token.  [Get Access Token]: ./get-access-token 
   * Get Access Token (OAuth 2)
   * @param clientId API client ID.
   * @param clientSecret API client secret.
   * @param grantType The OAuth 2.0 grant type you\\\&#39;re using. Must be &#x60;client_credentials&#x60;. 
   */
  public async getAccessTokenOAuth2(clientId: string, clientSecret: string, grantType: string, _options?: Configuration): Promise<Oauth2TokenResponse> {
    const responseWithInfo = await this.getAccessTokenOAuth2WithHttpInfo(clientId, clientSecret, grantType, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API allows users to retrieve a list of identity providers available for OAuth2 authentication.
   * List Identity Providers Names
   */
  public async listIdentityProvidersNamesWithHttpInfo(_options?: Configuration): Promise<HttpInfo<IdentityProvidersNames>> {
    let requestContextValue = await this.requestFactory.listIdentityProvidersNames(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listIdentityProvidersNamesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows users to retrieve a list of identity providers available for OAuth2 authentication.
   * List Identity Providers Names
   */
  public async listIdentityProvidersNames(_options?: Configuration): Promise<IdentityProvidersNames> {
    const responseWithInfo = await this.listIdentityProvidersNamesWithHttpInfo(_options);
    return responseWithInfo.data;
  }

  /**
   * Description: Replaces the client secret for the specified API client credentials.  This method can be used to rotate a secret for the API credentials specified in the request body. To replace the client secret for the access key passed in the `Authorization` header, you can also use [Rotate Current API Client Secret].  Once replaced, the old client secret can\'t be used to authenticate or obtain new tokens. However, access tokens that were issued before the rotation remain valid until they expire. This means there is an effective grace period of up to 12 hours where previously issued access tokens will continue to work, even though the client secret used to obtain them has been rotated.  [Rotate Current API Client Secret]: ./rotate-current-api-client-secret 
   * Rotate API Client Secret
   * @param clientId API client ID.
   */
  public async rotateApiClientSecretWithHttpInfo(clientId: string, _options?: Configuration): Promise<HttpInfo<ApiCredentials>> {
    let requestContextValue = await this.requestFactory.rotateApiClientSecret(clientId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.rotateApiClientSecretWithHttpInfo(responseContext)
  }

  /**
   * Description: Replaces the client secret for the specified API client credentials.  This method can be used to rotate a secret for the API credentials specified in the request body. To replace the client secret for the access key passed in the `Authorization` header, you can also use [Rotate Current API Client Secret].  Once replaced, the old client secret can\'t be used to authenticate or obtain new tokens. However, access tokens that were issued before the rotation remain valid until they expire. This means there is an effective grace period of up to 12 hours where previously issued access tokens will continue to work, even though the client secret used to obtain them has been rotated.  [Rotate Current API Client Secret]: ./rotate-current-api-client-secret 
   * Rotate API Client Secret
   * @param clientId API client ID.
   */
  public async rotateApiClientSecret(clientId: string, _options?: Configuration): Promise<ApiCredentials> {
    const responseWithInfo = await this.rotateApiClientSecretWithHttpInfo(clientId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Replaces the client secret for the currently used set of API credentials.  This method can be used to rotate a secret only for the access key passed in the `Authorization` header. To replace client secrets for other sets of API credentials, use [Rotate API Client Secret].  Once replaced, the old client secret can\'t be used to authenticate or obtain new tokens. However, access tokens that were issued before the rotation remain valid until they expire. This means there is an effective grace period of up to 12 hours where previously issued access tokens will continue to work, even though the client secret used to obtain them has been rotated.  [Rotate API Client Secret]: ./rotate-api-client-secret 
   * Rotate Current API Client Secret
   */
  public async rotateCurrentApiClientSecretWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ApiCredentials>> {
    let requestContextValue = await this.requestFactory.rotateCurrentApiClientSecret(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.rotateCurrentApiClientSecretWithHttpInfo(responseContext)
  }

  /**
   * Description: Replaces the client secret for the currently used set of API credentials.  This method can be used to rotate a secret only for the access key passed in the `Authorization` header. To replace client secrets for other sets of API credentials, use [Rotate API Client Secret].  Once replaced, the old client secret can\'t be used to authenticate or obtain new tokens. However, access tokens that were issued before the rotation remain valid until they expire. This means there is an effective grace period of up to 12 hours where previously issued access tokens will continue to work, even though the client secret used to obtain them has been rotated.  [Rotate API Client Secret]: ./rotate-api-client-secret 
   * Rotate Current API Client Secret
   */
  public async rotateCurrentApiClientSecret(_options?: Configuration): Promise<ApiCredentials> {
    const responseWithInfo = await this.rotateCurrentApiClientSecretWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { AzureMarketplaceApiRequestFactory, AzureMarketplaceApiResponseProcessor} from "../apis/AzureMarketplaceApi";

export class PromiseAzureMarketplaceApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AzureMarketplaceApiRequestFactory = new AzureMarketplaceApiRequestFactory(configuration),
    private responseProcessor: AzureMarketplaceApiResponseProcessor = new AzureMarketplaceApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Endpoint to handle webhook events sent by Microsoft for subscription changes, such as ChangePlan, ChangeQuantity, Renew, Suspend, Unsubscribe, or Reinstate. The input schema is defined in the AzureMarketplaceWebhookInput.yaml file.
   * Handle Azure Marketplace webhook events.
   * @param azureMarketplaceWebhookInput Payload for the Azure Marketplace webhook event.
   */
  public async handleAzureMarketplaceWebhookWithHttpInfo(azureMarketplaceWebhookInput: AzureMarketplaceWebhookInput, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.handleAzureMarketplaceWebhook(azureMarketplaceWebhookInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.handleAzureMarketplaceWebhookWithHttpInfo(responseContext)
  }

  /**
   * Description: Endpoint to handle webhook events sent by Microsoft for subscription changes, such as ChangePlan, ChangeQuantity, Renew, Suspend, Unsubscribe, or Reinstate. The input schema is defined in the AzureMarketplaceWebhookInput.yaml file.
   * Handle Azure Marketplace webhook events.
   * @param azureMarketplaceWebhookInput Payload for the Azure Marketplace webhook event.
   */
  public async handleAzureMarketplaceWebhook(azureMarketplaceWebhookInput: AzureMarketplaceWebhookInput, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.handleAzureMarketplaceWebhookWithHttpInfo(azureMarketplaceWebhookInput, _options);
    return responseWithInfo.data;
  }


}


import { AzureOAuthCallbackApiRequestFactory, AzureOAuthCallbackApiResponseProcessor} from "../apis/AzureOAuthCallbackApi";

export class PromiseAzureOAuthCallbackApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AzureOAuthCallbackApiRequestFactory = new AzureOAuthCallbackApiRequestFactory(configuration),
    private responseProcessor: AzureOAuthCallbackApiResponseProcessor = new AzureOAuthCallbackApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: API for onboarding Azure subscriptions to Eon
   * Callback endpoint for Azure OAuth
   * @param code Azure OAuth code
   * @param state Request id state
   */
  public async azureOAuthCallbackWithHttpInfo(code: string, state: string, _options?: Configuration): Promise<HttpInfo<string>> {
    let requestContextValue = await this.requestFactory.azureOAuthCallback(code, state, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.azureOAuthCallbackWithHttpInfo(responseContext)
  }

  /**
   * Description: API for onboarding Azure subscriptions to Eon
   * Callback endpoint for Azure OAuth
   * @param code Azure OAuth code
   * @param state Request id state
   */
  public async azureOAuthCallback(code: string, state: string, _options?: Configuration): Promise<string> {
    const responseWithInfo = await this.azureOAuthCallbackWithHttpInfo(code, state, _options);
    return responseWithInfo.data;
  }


}


import { AzureRegisterApiRequestFactory, AzureRegisterApiResponseProcessor} from "../apis/AzureRegisterApi";

export class PromiseAzureRegisterApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AzureRegisterApiRequestFactory = new AzureRegisterApiRequestFactory(configuration),
    private responseProcessor: AzureRegisterApiResponseProcessor = new AzureRegisterApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information.
   * Callback endpoint for Microsoft Azure marketplace
   * @param token Azure marketplace token
   */
  public async azureRegisterWithHttpInfo(token: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.azureRegister(token, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.azureRegisterWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information.
   * Callback endpoint for Microsoft Azure marketplace
   * @param token Azure marketplace token
   */
  public async azureRegister(token: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.azureRegisterWithHttpInfo(token, _options);
    return responseWithInfo.data;
  }


}


import { AzureScanningAccountSetupStatusApiRequestFactory, AzureScanningAccountSetupStatusApiResponseProcessor} from "../apis/AzureScanningAccountSetupStatusApi";

export class PromiseAzureScanningAccountSetupStatusApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: AzureScanningAccountSetupStatusApiRequestFactory = new AzureScanningAccountSetupStatusApiRequestFactory(configuration),
    private responseProcessor: AzureScanningAccountSetupStatusApiResponseProcessor = new AzureScanningAccountSetupStatusApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get azure scanning account setup status
   * @param projectId The project ID
   */
  public async azureScanningAccountSetupStatusWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<AzureScanningAccountSetupStatus200Response>> {
    let requestContextValue = await this.requestFactory.azureScanningAccountSetupStatus(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.azureScanningAccountSetupStatusWithHttpInfo(responseContext)
  }

  /**
   * Get azure scanning account setup status
   * @param projectId The project ID
   */
  public async azureScanningAccountSetupStatus(projectId: string, _options?: Configuration): Promise<AzureScanningAccountSetupStatus200Response> {
    const responseWithInfo = await this.azureScanningAccountSetupStatusWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { BackupJobsPopulateApiRequestFactory, BackupJobsPopulateApiResponseProcessor} from "../apis/BackupJobsPopulateApi";

export class PromiseBackupJobsPopulateApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: BackupJobsPopulateApiRequestFactory = new BackupJobsPopulateApiRequestFactory(configuration),
    private responseProcessor: BackupJobsPopulateApiResponseProcessor = new BackupJobsPopulateApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API populates test data for a given project based on the specified version number.
   * Populate test data
   * @param version the version number of the data to populate
   * @param projectId The project ID
   */
  public async backupJobsPopulateWithHttpInfo(version: number, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.backupJobsPopulate(version, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.backupJobsPopulateWithHttpInfo(responseContext)
  }

  /**
   * Description: This API populates test data for a given project based on the specified version number.
   * Populate test data
   * @param version the version number of the data to populate
   * @param projectId The project ID
   */
  public async backupJobsPopulate(version: number, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.backupJobsPopulateWithHttpInfo(version, projectId, _options);
    return responseWithInfo.data;
  }


}


import { BackupPoliciesApiRequestFactory, BackupPoliciesApiResponseProcessor} from "../apis/BackupPoliciesApi";

export class PromiseBackupPoliciesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: BackupPoliciesApiRequestFactory = new BackupPoliciesApiRequestFactory(configuration),
    private responseProcessor: BackupPoliciesApiResponseProcessor = new BackupPoliciesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a backup policy.
   * Create Backup Policy
   * @param projectId ID of the project you want to create a backup policy in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createBackupPolicyRequest 
   */
  public async createBackupPolicyWithHttpInfo(projectId: string, createBackupPolicyRequest: CreateBackupPolicyRequest, _options?: Configuration): Promise<HttpInfo<CreateBackupPolicyResponse>> {
    let requestContextValue = await this.requestFactory.createBackupPolicy(projectId, createBackupPolicyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createBackupPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a backup policy.
   * Create Backup Policy
   * @param projectId ID of the project you want to create a backup policy in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createBackupPolicyRequest 
   */
  public async createBackupPolicy(projectId: string, createBackupPolicyRequest: CreateBackupPolicyRequest, _options?: Configuration): Promise<CreateBackupPolicyResponse> {
    const responseWithInfo = await this.createBackupPolicyWithHttpInfo(projectId, createBackupPolicyRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Deletes a backup policy.
   * Delete Backup Policy
   * @param backupPolicyId ID of the backup policy to delete
   * @param projectId ID of the project whose backup policy you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteBackupPolicyWithHttpInfo(backupPolicyId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteBackupPolicy(backupPolicyId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteBackupPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes a backup policy.
   * Delete Backup Policy
   * @param backupPolicyId ID of the backup policy to delete
   * @param projectId ID of the project whose backup policy you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteBackupPolicy(backupPolicyId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteBackupPolicyWithHttpInfo(backupPolicyId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a backup policy.
   * Get Backup Policy
   * @param backupPolicyId ID of the backup policy to retrieve
   * @param projectId ID of the project whose backup policy you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getBackupPolicyWithHttpInfo(backupPolicyId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetBackupPolicyResponse>> {
    let requestContextValue = await this.requestFactory.getBackupPolicy(backupPolicyId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackupPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a backup policy.
   * Get Backup Policy
   * @param backupPolicyId ID of the backup policy to retrieve
   * @param projectId ID of the project whose backup policy you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getBackupPolicy(backupPolicyId: string, projectId: string, _options?: Configuration): Promise<GetBackupPolicyResponse> {
    const responseWithInfo = await this.getBackupPolicyWithHttpInfo(backupPolicyId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of backup policies in the specified project. 
   * List Backup Policies
   * @param projectId ID of the project whose backup policies you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async listBackupPoliciesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListBackupPoliciesResponse>> {
    let requestContextValue = await this.requestFactory.listBackupPolicies(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listBackupPoliciesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of backup policies in the specified project. 
   * List Backup Policies
   * @param projectId ID of the project whose backup policies you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async listBackupPolicies(projectId: string, _options?: Configuration): Promise<ListBackupPoliciesResponse> {
    const responseWithInfo = await this.listBackupPoliciesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates a backup policy.
   * Update Backup Policy
   * @param backupPolicyId ID of the backup policy to update
   * @param projectId ID of the project whose backup policy you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateBackupPolicyRequest The request body for updating a policy
   */
  public async updateBackupPolicyWithHttpInfo(backupPolicyId: string, projectId: string, updateBackupPolicyRequest: UpdateBackupPolicyRequest, _options?: Configuration): Promise<HttpInfo<UpdateBackupPolicyResponse>> {
    let requestContextValue = await this.requestFactory.updateBackupPolicy(backupPolicyId, projectId, updateBackupPolicyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateBackupPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates a backup policy.
   * Update Backup Policy
   * @param backupPolicyId ID of the backup policy to update
   * @param projectId ID of the project whose backup policy you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateBackupPolicyRequest The request body for updating a policy
   */
  public async updateBackupPolicy(backupPolicyId: string, projectId: string, updateBackupPolicyRequest: UpdateBackupPolicyRequest, _options?: Configuration): Promise<UpdateBackupPolicyResponse> {
    const responseWithInfo = await this.updateBackupPolicyWithHttpInfo(backupPolicyId, projectId, updateBackupPolicyRequest, _options);
    return responseWithInfo.data;
  }


}


import { BackupsApiRequestFactory, BackupsApiResponseProcessor} from "../apis/BackupsApi";

export class PromiseBackupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: BackupsApiRequestFactory = new BackupsApiRequestFactory(configuration),
    private responseProcessor: BackupsApiResponseProcessor = new BackupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Takes an on-demand snapshot of a llg resource.  This operation is asynchronous. It triggers a backup job and returns a job ID, which can be used to track the progress of the backup job. You can follow the progress of the backup job by calling [Get Backup Job](/api/reference/get-backup-job) with the job ID. 
   * Take LLG Snapshot
   * @param id Eon-assigned ID of the llg resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeSnapshotRequest 
   */
  public async takeLlgSnapshotWithHttpInfo(id: string, projectId: string, takeSnapshotRequest: TakeSnapshotRequest, _options?: Configuration): Promise<HttpInfo<TakeSnapshotResponse>> {
    let requestContextValue = await this.requestFactory.takeLlgSnapshot(id, projectId, takeSnapshotRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.takeLlgSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: Takes an on-demand snapshot of a llg resource.  This operation is asynchronous. It triggers a backup job and returns a job ID, which can be used to track the progress of the backup job. You can follow the progress of the backup job by calling [Get Backup Job](/api/reference/get-backup-job) with the job ID. 
   * Take LLG Snapshot
   * @param id Eon-assigned ID of the llg resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeSnapshotRequest 
   */
  public async takeLlgSnapshot(id: string, projectId: string, takeSnapshotRequest: TakeSnapshotRequest, _options?: Configuration): Promise<TakeSnapshotResponse> {
    const responseWithInfo = await this.takeLlgSnapshotWithHttpInfo(id, projectId, takeSnapshotRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Takes an on-demand native snapshot of the specified resource. Native snapshots are stored in the cloud provider\'s native backup service (e.g., FSx backup) rather than being copied to an Eon vault.  This operation is asynchronous. It triggers a backup job and returns a job ID, which can be used to track the progress of the backup job. You can follow the progress of the backup job by calling [Get Backup Job](/api/reference/get-backup-job) with the job ID. 
   * Take Native Snapshot
   * @param id Eon-assigned ID of the resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeNativeSnapshotRequest 
   */
  public async takeNativeSnapshotWithHttpInfo(id: string, projectId: string, takeNativeSnapshotRequest: TakeNativeSnapshotRequest, _options?: Configuration): Promise<HttpInfo<TakeSnapshotResponse>> {
    let requestContextValue = await this.requestFactory.takeNativeSnapshot(id, projectId, takeNativeSnapshotRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.takeNativeSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: Takes an on-demand native snapshot of the specified resource. Native snapshots are stored in the cloud provider\'s native backup service (e.g., FSx backup) rather than being copied to an Eon vault.  This operation is asynchronous. It triggers a backup job and returns a job ID, which can be used to track the progress of the backup job. You can follow the progress of the backup job by calling [Get Backup Job](/api/reference/get-backup-job) with the job ID. 
   * Take Native Snapshot
   * @param id Eon-assigned ID of the resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeNativeSnapshotRequest 
   */
  public async takeNativeSnapshot(id: string, projectId: string, takeNativeSnapshotRequest: TakeNativeSnapshotRequest, _options?: Configuration): Promise<TakeSnapshotResponse> {
    const responseWithInfo = await this.takeNativeSnapshotWithHttpInfo(id, projectId, takeNativeSnapshotRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Takes an on-demand snapshot of the specified resource.  This operation is asynchronous. It triggers a backup job and returns a job ID, which can be used to track the progress of the backup job. You can follow the progress of the backup job by calling [Get Backup Job](/api/api-reference/jobs/backup-jobs/get-backup-job) with the job ID. 
   * Take Snapshot
   * @param id Eon-assigned ID of the resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeSnapshotRequest 
   */
  public async takeSnapshotWithHttpInfo(id: string, projectId: string, takeSnapshotRequest: TakeSnapshotRequest, _options?: Configuration): Promise<HttpInfo<TakeSnapshotResponse>> {
    let requestContextValue = await this.requestFactory.takeSnapshot(id, projectId, takeSnapshotRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.takeSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: Takes an on-demand snapshot of the specified resource.  This operation is asynchronous. It triggers a backup job and returns a job ID, which can be used to track the progress of the backup job. You can follow the progress of the backup job by calling [Get Backup Job](/api/api-reference/jobs/backup-jobs/get-backup-job) with the job ID. 
   * Take Snapshot
   * @param id Eon-assigned ID of the resource to back up.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param takeSnapshotRequest 
   */
  public async takeSnapshot(id: string, projectId: string, takeSnapshotRequest: TakeSnapshotRequest, _options?: Configuration): Promise<TakeSnapshotResponse> {
    const responseWithInfo = await this.takeSnapshotWithHttpInfo(id, projectId, takeSnapshotRequest, _options);
    return responseWithInfo.data;
  }


}


import { BatchValidateResourceConfigurationApiRequestFactory, BatchValidateResourceConfigurationApiResponseProcessor} from "../apis/BatchValidateResourceConfigurationApi";

export class PromiseBatchValidateResourceConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: BatchValidateResourceConfigurationApiRequestFactory = new BatchValidateResourceConfigurationApiRequestFactory(configuration),
    private responseProcessor: BatchValidateResourceConfigurationApiResponseProcessor = new BatchValidateResourceConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Validate if multiple resources are properly configured for backup with Eon.
   * Batch validate resource configurations
   * @param projectId The project ID
   * @param batchValidateResourceConfigurationRequest 
   */
  public async batchValidateResourceConfigurationWithHttpInfo(projectId: string, batchValidateResourceConfigurationRequest: BatchValidateResourceConfigurationRequest, _options?: Configuration): Promise<HttpInfo<BatchValidateResourceConfigurationResponse>> {
    let requestContextValue = await this.requestFactory.batchValidateResourceConfiguration(projectId, batchValidateResourceConfigurationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.batchValidateResourceConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: Validate if multiple resources are properly configured for backup with Eon.
   * Batch validate resource configurations
   * @param projectId The project ID
   * @param batchValidateResourceConfigurationRequest 
   */
  public async batchValidateResourceConfiguration(projectId: string, batchValidateResourceConfigurationRequest: BatchValidateResourceConfigurationRequest, _options?: Configuration): Promise<BatchValidateResourceConfigurationResponse> {
    const responseWithInfo = await this.batchValidateResourceConfigurationWithHttpInfo(projectId, batchValidateResourceConfigurationRequest, _options);
    return responseWithInfo.data;
  }


}


import { BillingApiRequestFactory, BillingApiResponseProcessor} from "../apis/BillingApi";

export class PromiseBillingApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: BillingApiRequestFactory = new BillingApiRequestFactory(configuration),
    private responseProcessor: BillingApiResponseProcessor = new BillingApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Export cost data with support for filtering, grouping, and time period selection. Supports various granularities and dimensions for cost analysis and chargeback reporting.
   * Export cost data
   * @param exportCostDataRequest 
   */
  public async exportCostDataWithHttpInfo(exportCostDataRequest: ExportCostDataRequest, _options?: Configuration): Promise<HttpInfo<HttpFile>> {
    let requestContextValue = await this.requestFactory.exportCostData(exportCostDataRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.exportCostDataWithHttpInfo(responseContext)
  }

  /**
   * Description: Export cost data with support for filtering, grouping, and time period selection. Supports various granularities and dimensions for cost analysis and chargeback reporting.
   * Export cost data
   * @param exportCostDataRequest 
   */
  public async exportCostData(exportCostDataRequest: ExportCostDataRequest, _options?: Configuration): Promise<HttpFile> {
    const responseWithInfo = await this.exportCostDataWithHttpInfo(exportCostDataRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves billing transaction history for a specific cloud provider within a given time period
   * Get billing transaction history by provider
   * @param getBillingTransactionHistoryRequest 
   */
  public async getBillingTransactionsWithHttpInfo(getBillingTransactionHistoryRequest: GetBillingTransactionHistoryRequest, _options?: Configuration): Promise<HttpInfo<GetBillingTransactionHistoryResponse>> {
    let requestContextValue = await this.requestFactory.getBillingTransactions(getBillingTransactionHistoryRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBillingTransactionsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves billing transaction history for a specific cloud provider within a given time period
   * Get billing transaction history by provider
   * @param getBillingTransactionHistoryRequest 
   */
  public async getBillingTransactions(getBillingTransactionHistoryRequest: GetBillingTransactionHistoryRequest, _options?: Configuration): Promise<GetBillingTransactionHistoryResponse> {
    const responseWithInfo = await this.getBillingTransactionsWithHttpInfo(getBillingTransactionHistoryRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves cost and usage data for the given time frame.  Use Query Cost Data to analyze backup costs by cloud provider, resource type, and resource. For details on exploring your costs visually in the Eon console, see [About Cost Explorer](/user-guide/administration/cost-explorer/about-the-cost-explorer). 
   * Query Cost Data
   * @param queryCostDataRequest 
   * @param pageSize Maximum number of cost records to return in a single response.
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same time frame, grouping, and filters in the first request as all subsequent requests. 
   */
  public async queryCostDataWithHttpInfo(queryCostDataRequest: QueryCostDataRequest, pageSize?: number, pageToken?: string, _options?: Configuration): Promise<HttpInfo<QueryCostDataResponse>> {
    let requestContextValue = await this.requestFactory.queryCostData(queryCostDataRequest, pageSize, pageToken, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.queryCostDataWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves cost and usage data for the given time frame.  Use Query Cost Data to analyze backup costs by cloud provider, resource type, and resource. For details on exploring your costs visually in the Eon console, see [About Cost Explorer](/user-guide/administration/cost-explorer/about-the-cost-explorer). 
   * Query Cost Data
   * @param queryCostDataRequest 
   * @param pageSize Maximum number of cost records to return in a single response.
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same time frame, grouping, and filters in the first request as all subsequent requests. 
   */
  public async queryCostData(queryCostDataRequest: QueryCostDataRequest, pageSize?: number, pageToken?: string, _options?: Configuration): Promise<QueryCostDataResponse> {
    const responseWithInfo = await this.queryCostDataWithHttpInfo(queryCostDataRequest, pageSize, pageToken, _options);
    return responseWithInfo.data;
  }


}


import { CallbackApiRequestFactory, CallbackApiResponseProcessor} from "../apis/CallbackApi";

export class PromiseCallbackApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CallbackApiRequestFactory = new CallbackApiRequestFactory(configuration),
    private responseProcessor: CallbackApiResponseProcessor = new CallbackApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Endpoint to handle OAuth callback and exchange authorization code for access token.
   * Handle OAuth callback.
   * @param code Authorization code received from the OAuth provider.
   */
  public async callbackWithHttpInfo(code: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.callback(code, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.callbackWithHttpInfo(responseContext)
  }

  /**
   * Description: Endpoint to handle OAuth callback and exchange authorization code for access token.
   * Handle OAuth callback.
   * @param code Authorization code received from the OAuth provider.
   */
  public async callback(code: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.callbackWithHttpInfo(code, _options);
    return responseWithInfo.data;
  }


}


import { CheckAzureVMNameExistsApiRequestFactory, CheckAzureVMNameExistsApiResponseProcessor} from "../apis/CheckAzureVMNameExistsApi";

export class PromiseCheckAzureVMNameExistsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CheckAzureVMNameExistsApiRequestFactory = new CheckAzureVMNameExistsApiRequestFactory(configuration),
    private responseProcessor: CheckAzureVMNameExistsApiResponseProcessor = new CheckAzureVMNameExistsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Check if an Azure VM name already exists in a resource group
   * @param projectId The project ID
   * @param checkAzureVMNameExistsRequest The request body for checking Azure VM name existence
   */
  public async checkAzureVMNameExistsWithHttpInfo(projectId: string, checkAzureVMNameExistsRequest: CheckAzureVMNameExistsRequest, _options?: Configuration): Promise<HttpInfo<CheckAzureVMNameExists200Response>> {
    let requestContextValue = await this.requestFactory.checkAzureVMNameExists(projectId, checkAzureVMNameExistsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.checkAzureVMNameExistsWithHttpInfo(responseContext)
  }

  /**
   * Check if an Azure VM name already exists in a resource group
   * @param projectId The project ID
   * @param checkAzureVMNameExistsRequest The request body for checking Azure VM name existence
   */
  public async checkAzureVMNameExists(projectId: string, checkAzureVMNameExistsRequest: CheckAzureVMNameExistsRequest, _options?: Configuration): Promise<CheckAzureVMNameExists200Response> {
    const responseWithInfo = await this.checkAzureVMNameExistsWithHttpInfo(projectId, checkAzureVMNameExistsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConnectAzureBackupAccountApiRequestFactory, ConnectAzureBackupAccountApiResponseProcessor} from "../apis/ConnectAzureBackupAccountApi";

export class PromiseConnectAzureBackupAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConnectAzureBackupAccountApiRequestFactory = new ConnectAzureBackupAccountApiRequestFactory(configuration),
    private responseProcessor: ConnectAzureBackupAccountApiResponseProcessor = new ConnectAzureBackupAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Onboard Azure backup account to Eon
   * @param projectId The project ID
   * @param connectAzureBackupAccountRequest 
   */
  public async connectAzureBackupAccountWithHttpInfo(projectId: string, connectAzureBackupAccountRequest: ConnectAzureBackupAccountRequest, _options?: Configuration): Promise<HttpInfo<AzureOnboardingStatus>> {
    let requestContextValue = await this.requestFactory.connectAzureBackupAccount(projectId, connectAzureBackupAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectAzureBackupAccountWithHttpInfo(responseContext)
  }

  /**
   * Onboard Azure backup account to Eon
   * @param projectId The project ID
   * @param connectAzureBackupAccountRequest 
   */
  public async connectAzureBackupAccount(projectId: string, connectAzureBackupAccountRequest: ConnectAzureBackupAccountRequest, _options?: Configuration): Promise<AzureOnboardingStatus> {
    const responseWithInfo = await this.connectAzureBackupAccountWithHttpInfo(projectId, connectAzureBackupAccountRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConnectAzureBackupApplicationsApiRequestFactory, ConnectAzureBackupApplicationsApiResponseProcessor} from "../apis/ConnectAzureBackupApplicationsApi";

export class PromiseConnectAzureBackupApplicationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConnectAzureBackupApplicationsApiRequestFactory = new ConnectAzureBackupApplicationsApiRequestFactory(configuration),
    private responseProcessor: ConnectAzureBackupApplicationsApiResponseProcessor = new ConnectAzureBackupApplicationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Register Azure backup applications for cross-tenant backup operations
   * @param projectId The project ID
   * @param connectAzureBackupApplicationsRequest 
   */
  public async connectAzureBackupApplicationsWithHttpInfo(projectId: string, connectAzureBackupApplicationsRequest: ConnectAzureBackupApplicationsRequest, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.connectAzureBackupApplications(projectId, connectAzureBackupApplicationsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectAzureBackupApplicationsWithHttpInfo(responseContext)
  }

  /**
   * Register Azure backup applications for cross-tenant backup operations
   * @param projectId The project ID
   * @param connectAzureBackupApplicationsRequest 
   */
  public async connectAzureBackupApplications(projectId: string, connectAzureBackupApplicationsRequest: ConnectAzureBackupApplicationsRequest, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.connectAzureBackupApplicationsWithHttpInfo(projectId, connectAzureBackupApplicationsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConnectAzureRestoreAccountApiRequestFactory, ConnectAzureRestoreAccountApiResponseProcessor} from "../apis/ConnectAzureRestoreAccountApi";

export class PromiseConnectAzureRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConnectAzureRestoreAccountApiRequestFactory = new ConnectAzureRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: ConnectAzureRestoreAccountApiResponseProcessor = new ConnectAzureRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Onboard Azure restore account to Eon
   * @param projectId The project ID
   * @param connectAzureRestoreAccountRequest 
   */
  public async connectAzureRestoreAccountWithHttpInfo(projectId: string, connectAzureRestoreAccountRequest: ConnectAzureRestoreAccountRequest, _options?: Configuration): Promise<HttpInfo<AzureOnboardingStatus>> {
    let requestContextValue = await this.requestFactory.connectAzureRestoreAccount(projectId, connectAzureRestoreAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectAzureRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Onboard Azure restore account to Eon
   * @param projectId The project ID
   * @param connectAzureRestoreAccountRequest 
   */
  public async connectAzureRestoreAccount(projectId: string, connectAzureRestoreAccountRequest: ConnectAzureRestoreAccountRequest, _options?: Configuration): Promise<AzureOnboardingStatus> {
    const responseWithInfo = await this.connectAzureRestoreAccountWithHttpInfo(projectId, connectAzureRestoreAccountRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConnectAzureSourceAccountApiRequestFactory, ConnectAzureSourceAccountApiResponseProcessor} from "../apis/ConnectAzureSourceAccountApi";

export class PromiseConnectAzureSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConnectAzureSourceAccountApiRequestFactory = new ConnectAzureSourceAccountApiRequestFactory(configuration),
    private responseProcessor: ConnectAzureSourceAccountApiResponseProcessor = new ConnectAzureSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Onboard Azure source account to Eon
   * @param projectId The project ID
   * @param connectAzureSourceAccountRequest 
   */
  public async connectAzureSourceAccountWithHttpInfo(projectId: string, connectAzureSourceAccountRequest: ConnectAzureSourceAccountRequest, _options?: Configuration): Promise<HttpInfo<AzureOnboardingStatus>> {
    let requestContextValue = await this.requestFactory.connectAzureSourceAccount(projectId, connectAzureSourceAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectAzureSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Onboard Azure source account to Eon
   * @param projectId The project ID
   * @param connectAzureSourceAccountRequest 
   */
  public async connectAzureSourceAccount(projectId: string, connectAzureSourceAccountRequest: ConnectAzureSourceAccountRequest, _options?: Configuration): Promise<AzureOnboardingStatus> {
    const responseWithInfo = await this.connectAzureSourceAccountWithHttpInfo(projectId, connectAzureSourceAccountRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConnectAzureVaultAccountApiRequestFactory, ConnectAzureVaultAccountApiResponseProcessor} from "../apis/ConnectAzureVaultAccountApi";

export class PromiseConnectAzureVaultAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConnectAzureVaultAccountApiRequestFactory = new ConnectAzureVaultAccountApiRequestFactory(configuration),
    private responseProcessor: ConnectAzureVaultAccountApiResponseProcessor = new ConnectAzureVaultAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Onboard Azure vault account to Eon
   * @param projectId The project ID
   * @param connectAzureVaultAccountRequest 
   */
  public async connectAzureVaultAccountWithHttpInfo(projectId: string, connectAzureVaultAccountRequest: ConnectAzureVaultAccountRequest, _options?: Configuration): Promise<HttpInfo<AzureOnboardingStatus>> {
    let requestContextValue = await this.requestFactory.connectAzureVaultAccount(projectId, connectAzureVaultAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectAzureVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Onboard Azure vault account to Eon
   * @param projectId The project ID
   * @param connectAzureVaultAccountRequest 
   */
  public async connectAzureVaultAccount(projectId: string, connectAzureVaultAccountRequest: ConnectAzureVaultAccountRequest, _options?: Configuration): Promise<AzureOnboardingStatus> {
    const responseWithInfo = await this.connectAzureVaultAccountWithHttpInfo(projectId, connectAzureVaultAccountRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConnectBYOAzureBackupApplicationsApiRequestFactory, ConnectBYOAzureBackupApplicationsApiResponseProcessor} from "../apis/ConnectBYOAzureBackupApplicationsApi";

export class PromiseConnectBYOAzureBackupApplicationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConnectBYOAzureBackupApplicationsApiRequestFactory = new ConnectBYOAzureBackupApplicationsApiRequestFactory(configuration),
    private responseProcessor: ConnectBYOAzureBackupApplicationsApiResponseProcessor = new ConnectBYOAzureBackupApplicationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Register BYO (Bring Your Own) Azure backup applications from Terraform module
   * @param projectId The project ID
   * @param connectBYOAzureBackupApplicationsRequest 
   */
  public async connectBYOAzureBackupApplicationsWithHttpInfo(projectId: string, connectBYOAzureBackupApplicationsRequest: ConnectBYOAzureBackupApplicationsRequest, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.connectBYOAzureBackupApplications(projectId, connectBYOAzureBackupApplicationsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.connectBYOAzureBackupApplicationsWithHttpInfo(responseContext)
  }

  /**
   * Register BYO (Bring Your Own) Azure backup applications from Terraform module
   * @param projectId The project ID
   * @param connectBYOAzureBackupApplicationsRequest 
   */
  public async connectBYOAzureBackupApplications(projectId: string, connectBYOAzureBackupApplicationsRequest: ConnectBYOAzureBackupApplicationsRequest, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.connectBYOAzureBackupApplicationsWithHttpInfo(projectId, connectBYOAzureBackupApplicationsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConvertOtherSnapshotsOnDemandApiRequestFactory, ConvertOtherSnapshotsOnDemandApiResponseProcessor} from "../apis/ConvertOtherSnapshotsOnDemandApi";

export class PromiseConvertOtherSnapshotsOnDemandApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConvertOtherSnapshotsOnDemandApiRequestFactory = new ConvertOtherSnapshotsOnDemandApiRequestFactory(configuration),
    private responseProcessor: ConvertOtherSnapshotsOnDemandApiResponseProcessor = new ConvertOtherSnapshotsOnDemandApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a new backup job for a given project by specifying the project ID and providing necessary details in the request body, and a list of snapshots to backup.
   * Convert other snapshots to eon snapshots on demand
   * @param projectId The project ID
   * @param id ID of the inventory item
   * @param convertOtherSnapshotsOnDemandRequest The request body for creating a new backup job
   */
  public async convertOtherSnapshotsOnDemandWithHttpInfo(projectId: string, id: string, convertOtherSnapshotsOnDemandRequest: ConvertOtherSnapshotsOnDemandRequest, _options?: Configuration): Promise<HttpInfo<BackupJobResponse>> {
    let requestContextValue = await this.requestFactory.convertOtherSnapshotsOnDemand(projectId, id, convertOtherSnapshotsOnDemandRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.convertOtherSnapshotsOnDemandWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a new backup job for a given project by specifying the project ID and providing necessary details in the request body, and a list of snapshots to backup.
   * Convert other snapshots to eon snapshots on demand
   * @param projectId The project ID
   * @param id ID of the inventory item
   * @param convertOtherSnapshotsOnDemandRequest The request body for creating a new backup job
   */
  public async convertOtherSnapshotsOnDemand(projectId: string, id: string, convertOtherSnapshotsOnDemandRequest: ConvertOtherSnapshotsOnDemandRequest, _options?: Configuration): Promise<BackupJobResponse> {
    const responseWithInfo = await this.convertOtherSnapshotsOnDemandWithHttpInfo(projectId, id, convertOtherSnapshotsOnDemandRequest, _options);
    return responseWithInfo.data;
  }


}


import { ConvertResourceSnapshotsApiRequestFactory, ConvertResourceSnapshotsApiResponseProcessor} from "../apis/ConvertResourceSnapshotsApi";

export class PromiseConvertResourceSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ConvertResourceSnapshotsApiRequestFactory = new ConvertResourceSnapshotsApiRequestFactory(configuration),
    private responseProcessor: ConvertResourceSnapshotsApiResponseProcessor = new ConvertResourceSnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: For a given date, adding to the backup backlog
   * Convert resource snapshots to eon snapshots
   * @param projectId The project ID
   * @param id ID of the inventory item
   * @param convertResourceSnapshotsRequest The request body for backing up a resource from dates
   */
  public async convertResourceSnapshotsWithHttpInfo(projectId: string, id: string, convertResourceSnapshotsRequest: ConvertResourceSnapshotsRequest, _options?: Configuration): Promise<HttpInfo<ConvertResourceSnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.convertResourceSnapshots(projectId, id, convertResourceSnapshotsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.convertResourceSnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: For a given date, adding to the backup backlog
   * Convert resource snapshots to eon snapshots
   * @param projectId The project ID
   * @param id ID of the inventory item
   * @param convertResourceSnapshotsRequest The request body for backing up a resource from dates
   */
  public async convertResourceSnapshots(projectId: string, id: string, convertResourceSnapshotsRequest: ConvertResourceSnapshotsRequest, _options?: Configuration): Promise<ConvertResourceSnapshotsResponse> {
    const responseWithInfo = await this.convertResourceSnapshotsWithHttpInfo(projectId, id, convertResourceSnapshotsRequest, _options);
    return responseWithInfo.data;
  }


}


import { CreateApiCredentialsApiRequestFactory, CreateApiCredentialsApiResponseProcessor} from "../apis/CreateApiCredentialsApi";

export class PromiseCreateApiCredentialsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateApiCredentialsApiRequestFactory = new CreateApiCredentialsApiRequestFactory(configuration),
    private responseProcessor: CreateApiCredentialsApiResponseProcessor = new CreateApiCredentialsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows the creation of new api credentials for machine to machine communication. It requires administrative permissions.
   * Create a new api credentials
   * @param createApiCredentialsRequest The request body for creating api credentials
   */
  public async createApiCredentialsWithHttpInfo(createApiCredentialsRequest: CreateApiCredentialsRequest, _options?: Configuration): Promise<HttpInfo<CreateApiCredentialsResponse>> {
    let requestContextValue = await this.requestFactory.createApiCredentials(createApiCredentialsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createApiCredentialsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows the creation of new api credentials for machine to machine communication. It requires administrative permissions.
   * Create a new api credentials
   * @param createApiCredentialsRequest The request body for creating api credentials
   */
  public async createApiCredentials(createApiCredentialsRequest: CreateApiCredentialsRequest, _options?: Configuration): Promise<CreateApiCredentialsResponse> {
    const responseWithInfo = await this.createApiCredentialsWithHttpInfo(createApiCredentialsRequest, _options);
    return responseWithInfo.data;
  }


}


import { CreateBackupPolicyApiRequestFactory, CreateBackupPolicyApiResponseProcessor} from "../apis/CreateBackupPolicyApi";

export class PromiseCreateBackupPolicyApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateBackupPolicyApiRequestFactory = new CreateBackupPolicyApiRequestFactory(configuration),
    private responseProcessor: CreateBackupPolicyApiResponseProcessor = new CreateBackupPolicyApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows the creation of a backup policy for a specified project.
   * Create backup policy
   * @param projectId The project ID
   * @param createPolicyInput The request body for creating a policy
   */
  public async createPolicyWithHttpInfo(projectId: string, createPolicyInput: CreatePolicyInput, _options?: Configuration): Promise<HttpInfo<BackupPolicyConfig>> {
    let requestContextValue = await this.requestFactory.createPolicy(projectId, createPolicyInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows the creation of a backup policy for a specified project.
   * Create backup policy
   * @param projectId The project ID
   * @param createPolicyInput The request body for creating a policy
   */
  public async createPolicy(projectId: string, createPolicyInput: CreatePolicyInput, _options?: Configuration): Promise<BackupPolicyConfig> {
    const responseWithInfo = await this.createPolicyWithHttpInfo(projectId, createPolicyInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateControlApiRequestFactory, CreateControlApiResponseProcessor} from "../apis/CreateControlApi";

export class PromiseCreateControlApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateControlApiRequestFactory = new CreateControlApiRequestFactory(configuration),
    private responseProcessor: CreateControlApiResponseProcessor = new CreateControlApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation enables the creation of a new control within a specified project.
   * Create control
   * @param projectId The project ID
   * @param control The request body for creating a control
   */
  public async createControlWithHttpInfo(projectId: string, control: Control, _options?: Configuration): Promise<HttpInfo<Control>> {
    let requestContextValue = await this.requestFactory.createControl(projectId, control, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createControlWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation enables the creation of a new control within a specified project.
   * Create control
   * @param projectId The project ID
   * @param control The request body for creating a control
   */
  public async createControl(projectId: string, control: Control, _options?: Configuration): Promise<Control> {
    const responseWithInfo = await this.createControlWithHttpInfo(projectId, control, _options);
    return responseWithInfo.data;
  }


}


import { CreateInfectedExcludePathApiRequestFactory, CreateInfectedExcludePathApiResponseProcessor} from "../apis/CreateInfectedExcludePathApi";

export class PromiseCreateInfectedExcludePathApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateInfectedExcludePathApiRequestFactory = new CreateInfectedExcludePathApiRequestFactory(configuration),
    private responseProcessor: CreateInfectedExcludePathApiResponseProcessor = new CreateInfectedExcludePathApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates the infected exclude path for the Eon account or resource.
   * Create infected exclude path
   * @param projectId The project ID
   * @param createInfectedExcludePathInput The request body for creating the infected exclude path
   */
  public async createInfectedExcludePathWithHttpInfo(projectId: string, createInfectedExcludePathInput: CreateInfectedExcludePathInput, _options?: Configuration): Promise<HttpInfo<InfectedExcludePath>> {
    let requestContextValue = await this.requestFactory.createInfectedExcludePath(projectId, createInfectedExcludePathInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createInfectedExcludePathWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates the infected exclude path for the Eon account or resource.
   * Create infected exclude path
   * @param projectId The project ID
   * @param createInfectedExcludePathInput The request body for creating the infected exclude path
   */
  public async createInfectedExcludePath(projectId: string, createInfectedExcludePathInput: CreateInfectedExcludePathInput, _options?: Configuration): Promise<InfectedExcludePath> {
    const responseWithInfo = await this.createInfectedExcludePathWithHttpInfo(projectId, createInfectedExcludePathInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateObjectStoreExclusionRuleApiRequestFactory, CreateObjectStoreExclusionRuleApiResponseProcessor} from "../apis/CreateObjectStoreExclusionRuleApi";

export class PromiseCreateObjectStoreExclusionRuleApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateObjectStoreExclusionRuleApiRequestFactory = new CreateObjectStoreExclusionRuleApiRequestFactory(configuration),
    private responseProcessor: CreateObjectStoreExclusionRuleApiResponseProcessor = new CreateObjectStoreExclusionRuleApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation enables the creation of a new object store exclusion rule within a specified project.
   * Create object store exclusion rule
   * @param projectId The project ID
   * @param objectStoreExclusionRule The request body for creating a exclusion rule
   */
  public async createObjectStoreExclusionRuleWithHttpInfo(projectId: string, objectStoreExclusionRule: ObjectStoreExclusionRule, _options?: Configuration): Promise<HttpInfo<ObjectStoreExclusionRule>> {
    let requestContextValue = await this.requestFactory.createObjectStoreExclusionRule(projectId, objectStoreExclusionRule, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createObjectStoreExclusionRuleWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation enables the creation of a new object store exclusion rule within a specified project.
   * Create object store exclusion rule
   * @param projectId The project ID
   * @param objectStoreExclusionRule The request body for creating a exclusion rule
   */
  public async createObjectStoreExclusionRule(projectId: string, objectStoreExclusionRule: ObjectStoreExclusionRule, _options?: Configuration): Promise<ObjectStoreExclusionRule> {
    const responseWithInfo = await this.createObjectStoreExclusionRuleWithHttpInfo(projectId, objectStoreExclusionRule, _options);
    return responseWithInfo.data;
  }


}


import { CreateProjectApiRequestFactory, CreateProjectApiResponseProcessor} from "../apis/CreateProjectApi";

export class PromiseCreateProjectApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateProjectApiRequestFactory = new CreateProjectApiRequestFactory(configuration),
    private responseProcessor: CreateProjectApiResponseProcessor = new CreateProjectApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows an administrator to create a new project in the system. The request body must be in JSON format containing the project details.
   * Create a new project
   * @param createProjectInput The request body for creating a project
   */
  public async createProjectWithHttpInfo(createProjectInput: CreateProjectInput, _options?: Configuration): Promise<HttpInfo<Project>> {
    let requestContextValue = await this.requestFactory.createProject(createProjectInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createProjectWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows an administrator to create a new project in the system. The request body must be in JSON format containing the project details.
   * Create a new project
   * @param createProjectInput The request body for creating a project
   */
  public async createProject(createProjectInput: CreateProjectInput, _options?: Configuration): Promise<Project> {
    const responseWithInfo = await this.createProjectWithHttpInfo(createProjectInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateProjectRestoreAccountApiRequestFactory, CreateProjectRestoreAccountApiResponseProcessor} from "../apis/CreateProjectRestoreAccountApi";

export class PromiseCreateProjectRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateProjectRestoreAccountApiRequestFactory = new CreateProjectRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: CreateProjectRestoreAccountApiResponseProcessor = new CreateProjectRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a new restore account for the specified project by providing the required account information.
   * Create a new restore account
   * @param projectId The project ID
   * @param createAccountInput The request body for creating a restore account
   */
  public async createProjectRestoreAccountWithHttpInfo(projectId: string, createAccountInput: CreateAccountInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.createProjectRestoreAccount(projectId, createAccountInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createProjectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a new restore account for the specified project by providing the required account information.
   * Create a new restore account
   * @param projectId The project ID
   * @param createAccountInput The request body for creating a restore account
   */
  public async createProjectRestoreAccount(projectId: string, createAccountInput: CreateAccountInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.createProjectRestoreAccountWithHttpInfo(projectId, createAccountInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateProjectSourceAccountApiRequestFactory, CreateProjectSourceAccountApiResponseProcessor} from "../apis/CreateProjectSourceAccountApi";

export class PromiseCreateProjectSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateProjectSourceAccountApiRequestFactory = new CreateProjectSourceAccountApiRequestFactory(configuration),
    private responseProcessor: CreateProjectSourceAccountApiResponseProcessor = new CreateProjectSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to create a new source account for a specified project identified by the projectId.
   * Create a new source account
   * @param projectId The project ID
   * @param createAccountInput The request body for creating a source account
   */
  public async createProjectSourceAccountWithHttpInfo(projectId: string, createAccountInput: CreateAccountInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.createProjectSourceAccount(projectId, createAccountInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createProjectSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to create a new source account for a specified project identified by the projectId.
   * Create a new source account
   * @param projectId The project ID
   * @param createAccountInput The request body for creating a source account
   */
  public async createProjectSourceAccount(projectId: string, createAccountInput: CreateAccountInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.createProjectSourceAccountWithHttpInfo(projectId, createAccountInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateProjectUserApiRequestFactory, CreateProjectUserApiResponseProcessor} from "../apis/CreateProjectUserApi";

export class PromiseCreateProjectUserApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateProjectUserApiRequestFactory = new CreateProjectUserApiRequestFactory(configuration),
    private responseProcessor: CreateProjectUserApiResponseProcessor = new CreateProjectUserApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: API to create a new user in a specified project.
   * Create a new user
   * @param projectId The project ID
   * @param createUserInput The request body for creating a user
   */
  public async createProjectUserWithHttpInfo(projectId: string, createUserInput: CreateUserInput, _options?: Configuration): Promise<HttpInfo<User>> {
    let requestContextValue = await this.requestFactory.createProjectUser(projectId, createUserInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createProjectUserWithHttpInfo(responseContext)
  }

  /**
   * Description: API to create a new user in a specified project.
   * Create a new user
   * @param projectId The project ID
   * @param createUserInput The request body for creating a user
   */
  public async createProjectUser(projectId: string, createUserInput: CreateUserInput, _options?: Configuration): Promise<User> {
    const responseWithInfo = await this.createProjectUserWithHttpInfo(projectId, createUserInput, _options);
    return responseWithInfo.data;
  }


}


import { CreatePulumiConfigurationApiRequestFactory, CreatePulumiConfigurationApiResponseProcessor} from "../apis/CreatePulumiConfigurationApi";

export class PromiseCreatePulumiConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreatePulumiConfigurationApiRequestFactory = new CreatePulumiConfigurationApiRequestFactory(configuration),
    private responseProcessor: CreatePulumiConfigurationApiResponseProcessor = new CreatePulumiConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Create Pulumi configuration
   * @param createPulumiConfigurationRequest 
   */
  public async createPulumiConfigurationWithHttpInfo(createPulumiConfigurationRequest: CreatePulumiConfigurationRequest, _options?: Configuration): Promise<HttpInfo<PulumiConfiguration>> {
    let requestContextValue = await this.requestFactory.createPulumiConfiguration(createPulumiConfigurationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createPulumiConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Create Pulumi configuration
   * @param createPulumiConfigurationRequest 
   */
  public async createPulumiConfiguration(createPulumiConfigurationRequest: CreatePulumiConfigurationRequest, _options?: Configuration): Promise<PulumiConfiguration> {
    const responseWithInfo = await this.createPulumiConfigurationWithHttpInfo(createPulumiConfigurationRequest, _options);
    return responseWithInfo.data;
  }


}


import { CreateRestoreAccountApiRequestFactory, CreateRestoreAccountApiResponseProcessor} from "../apis/CreateRestoreAccountApi";

export class PromiseCreateRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateRestoreAccountApiRequestFactory = new CreateRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: CreateRestoreAccountApiResponseProcessor = new CreateRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This operation allows administrators to create a new restore account. The request body should include necessary account details in JSON format.
   * Create a new restore account
   * @param createAccountInput The request body for creating a restore account
   */
  public async createRestoreAccountWithHttpInfo(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.createRestoreAccount(createAccountInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This operation allows administrators to create a new restore account. The request body should include necessary account details in JSON format.
   * Create a new restore account
   * @param createAccountInput The request body for creating a restore account
   */
  public async createRestoreAccount(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.createRestoreAccountWithHttpInfo(createAccountInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateSamlIdentityProviderConfigApiRequestFactory, CreateSamlIdentityProviderConfigApiResponseProcessor} from "../apis/CreateSamlIdentityProviderConfigApi";

export class PromiseCreateSamlIdentityProviderConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateSamlIdentityProviderConfigApiRequestFactory = new CreateSamlIdentityProviderConfigApiRequestFactory(configuration),
    private responseProcessor: CreateSamlIdentityProviderConfigApiResponseProcessor = new CreateSamlIdentityProviderConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows administrators to create a new SAML identity provider configuration for customer authentication. It requires admin permissions (admin:idp_configs) and includes validation to prevent the creation of duplicate configurations for the same domain.
   * Create a new saml identity provider config
   * @param createSamlIdentityProviderConfigInput The request body for creating saml identity provider config
   */
  public async createSamlIdentityProviderConfigWithHttpInfo(createSamlIdentityProviderConfigInput: CreateSamlIdentityProviderConfigInput, _options?: Configuration): Promise<HttpInfo<SamlIdentityProvider>> {
    let requestContextValue = await this.requestFactory.createSamlIdentityProviderConfig(createSamlIdentityProviderConfigInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createSamlIdentityProviderConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows administrators to create a new SAML identity provider configuration for customer authentication. It requires admin permissions (admin:idp_configs) and includes validation to prevent the creation of duplicate configurations for the same domain.
   * Create a new saml identity provider config
   * @param createSamlIdentityProviderConfigInput The request body for creating saml identity provider config
   */
  public async createSamlIdentityProviderConfig(createSamlIdentityProviderConfigInput: CreateSamlIdentityProviderConfigInput, _options?: Configuration): Promise<SamlIdentityProvider> {
    const responseWithInfo = await this.createSamlIdentityProviderConfigWithHttpInfo(createSamlIdentityProviderConfigInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateScanJobApiRequestFactory, CreateScanJobApiResponseProcessor} from "../apis/CreateScanJobApi";

export class PromiseCreateScanJobApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateScanJobApiRequestFactory = new CreateScanJobApiRequestFactory(configuration),
    private responseProcessor: CreateScanJobApiResponseProcessor = new CreateScanJobApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a new scan job for a specified project ID, invoking a scan process and returning a message indicating successful initiation.
   * Create a new scan job
   * @param projectId The project ID
   * @param createScanJobInput The request body for creating a new scan job
   */
  public async createScanJobWithHttpInfo(projectId: string, createScanJobInput: CreateScanJobInput, _options?: Configuration): Promise<HttpInfo<CreateScanJobResponse>> {
    let requestContextValue = await this.requestFactory.createScanJob(projectId, createScanJobInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createScanJobWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a new scan job for a specified project ID, invoking a scan process and returning a message indicating successful initiation.
   * Create a new scan job
   * @param projectId The project ID
   * @param createScanJobInput The request body for creating a new scan job
   */
  public async createScanJob(projectId: string, createScanJobInput: CreateScanJobInput, _options?: Configuration): Promise<CreateScanJobResponse> {
    const responseWithInfo = await this.createScanJobWithHttpInfo(projectId, createScanJobInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateScanningAccountApiRequestFactory, CreateScanningAccountApiResponseProcessor} from "../apis/CreateScanningAccountApi";

export class PromiseCreateScanningAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateScanningAccountApiRequestFactory = new CreateScanningAccountApiRequestFactory(configuration),
    private responseProcessor: CreateScanningAccountApiResponseProcessor = new CreateScanningAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation allows an admin to create a new scanning account. The request body required includes details needed for the account creation. Upon successful creation, it returns the account details, or a 409 status if the account already exists.
   * Create a new scanning account
   * @param createAccountInput The request body for creating a scanning account
   */
  public async createScanningAccountWithHttpInfo(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.createScanningAccount(createAccountInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createScanningAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation allows an admin to create a new scanning account. The request body required includes details needed for the account creation. Upon successful creation, it returns the account details, or a 409 status if the account already exists.
   * Create a new scanning account
   * @param createAccountInput The request body for creating a scanning account
   */
  public async createScanningAccount(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.createScanningAccountWithHttpInfo(createAccountInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateSnapshotsCleanupJobApiRequestFactory, CreateSnapshotsCleanupJobApiResponseProcessor} from "../apis/CreateSnapshotsCleanupJobApi";

export class PromiseCreateSnapshotsCleanupJobApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateSnapshotsCleanupJobApiRequestFactory = new CreateSnapshotsCleanupJobApiRequestFactory(configuration),
    private responseProcessor: CreateSnapshotsCleanupJobApiResponseProcessor = new CreateSnapshotsCleanupJobApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a new job to cleanup snapshots in PENDING_DELETION status for a specified project ID, invoking a workflow and returning a message indicating successful initiation.
   * Create a new snapshots cleanup job
   * @param projectId The project ID
   */
  public async createSnapshotsCleanupJobWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.createSnapshotsCleanupJob(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createSnapshotsCleanupJobWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a new job to cleanup snapshots in PENDING_DELETION status for a specified project ID, invoking a workflow and returning a message indicating successful initiation.
   * Create a new snapshots cleanup job
   * @param projectId The project ID
   */
  public async createSnapshotsCleanupJob(projectId: string, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.createSnapshotsCleanupJobWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { CreateSourceAccountApiRequestFactory, CreateSourceAccountApiResponseProcessor} from "../apis/CreateSourceAccountApi";

export class PromiseCreateSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateSourceAccountApiRequestFactory = new CreateSourceAccountApiRequestFactory(configuration),
    private responseProcessor: CreateSourceAccountApiResponseProcessor = new CreateSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API creates a new source account. It requires admin permissions and expects a JSON request body conforming to the CreateAccountInput schema.
   * Create a new source account
   * @param createAccountInput The request body for creating a source account
   */
  public async createSourceAccountWithHttpInfo(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.createSourceAccount(createAccountInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API creates a new source account. It requires admin permissions and expects a JSON request body conforming to the CreateAccountInput schema.
   * Create a new source account
   * @param createAccountInput The request body for creating a source account
   */
  public async createSourceAccount(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.createSourceAccountWithHttpInfo(createAccountInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateUserApiRequestFactory, CreateUserApiResponseProcessor} from "../apis/CreateUserApi";

export class PromiseCreateUserApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateUserApiRequestFactory = new CreateUserApiRequestFactory(configuration),
    private responseProcessor: CreateUserApiResponseProcessor = new CreateUserApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows the creation of a new user. It requires administrative permissions and includes error handling for cases where the user already exists.
   * Create a new user
   * @param createUserInput The request body for creating a user
   */
  public async createUserWithHttpInfo(createUserInput: CreateUserInput, _options?: Configuration): Promise<HttpInfo<User>> {
    let requestContextValue = await this.requestFactory.createUser(createUserInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createUserWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows the creation of a new user. It requires administrative permissions and includes error handling for cases where the user already exists.
   * Create a new user
   * @param createUserInput The request body for creating a user
   */
  public async createUser(createUserInput: CreateUserInput, _options?: Configuration): Promise<User> {
    const responseWithInfo = await this.createUserWithHttpInfo(createUserInput, _options);
    return responseWithInfo.data;
  }


}


import { CreateVaultAccountApiRequestFactory, CreateVaultAccountApiResponseProcessor} from "../apis/CreateVaultAccountApi";

export class PromiseCreateVaultAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: CreateVaultAccountApiRequestFactory = new CreateVaultAccountApiRequestFactory(configuration),
    private responseProcessor: CreateVaultAccountApiResponseProcessor = new CreateVaultAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint is used to create a new vault account. It requires admin permissions and expects a JSON request body with the necessary account creation details.
   * Create a new eon-managed vault account
   * @param createAccountInput The request body for creating a vault account
   */
  public async createVaultAccountWithHttpInfo(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.createVaultAccount(createAccountInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint is used to create a new vault account. It requires admin permissions and expects a JSON request body with the necessary account creation details.
   * Create a new eon-managed vault account
   * @param createAccountInput The request body for creating a vault account
   */
  public async createVaultAccount(createAccountInput: CreateAccountInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.createVaultAccountWithHttpInfo(createAccountInput, _options);
    return responseWithInfo.data;
  }


}


import { DashboardApiRequestFactory, DashboardApiResponseProcessor} from "../apis/DashboardApi";

export class PromiseDashboardApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DashboardApiRequestFactory = new DashboardApiRequestFactory(configuration),
    private responseProcessor: DashboardApiResponseProcessor = new DashboardApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of snapshot storage sizes per day for the given date range.
   * Get Daily Storage Summaries
   * @param projectId ID of the project whose daily storage summaries you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param startDate First day of the date range in &#x60;YYYY-MM-DD&#x60; format.
   * @param endDate Last day of the date range in &#x60;YYYY-MM-DD&#x60; format.
   */
  public async getDailyStorageSummariesWithHttpInfo(projectId: string, startDate: string, endDate: string, _options?: Configuration): Promise<HttpInfo<DailyStorageSummaries>> {
    let requestContextValue = await this.requestFactory.getDailyStorageSummaries(projectId, startDate, endDate, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDailyStorageSummariesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of snapshot storage sizes per day for the given date range.
   * Get Daily Storage Summaries
   * @param projectId ID of the project whose daily storage summaries you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param startDate First day of the date range in &#x60;YYYY-MM-DD&#x60; format.
   * @param endDate Last day of the date range in &#x60;YYYY-MM-DD&#x60; format.
   */
  public async getDailyStorageSummaries(projectId: string, startDate: string, endDate: string, _options?: Configuration): Promise<DailyStorageSummaries> {
    const responseWithInfo = await this.getDailyStorageSummariesWithHttpInfo(projectId, startDate, endDate, _options);
    return responseWithInfo.data;
  }


}


import { DatabaseSnapshotsApiRequestFactory, DatabaseSnapshotsApiResponseProcessor} from "../apis/DatabaseSnapshotsApi";

export class PromiseDatabaseSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DatabaseSnapshotsApiRequestFactory = new DatabaseSnapshotsApiRequestFactory(configuration),
    private responseProcessor: DatabaseSnapshotsApiResponseProcessor = new DatabaseSnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the results of a query.  This method requires a query ID, which you can get only from the [Run Query](./run-query) response.  This method can be used only when a query has completed. You can check a query\'s status by calling [Get Query Status](./get-query-status). 
   * Get Query Result
   * @param projectId ID of the project that contains the query you want to retrieve. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings.  
   * @param queryId Query ID returned by [Run Query](./run-query).
   * @param pageSize Maximum number of items to return in the response.
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   */
  public async getQueryResultWithHttpInfo(projectId: string, queryId: string, pageSize?: number, pageToken?: string, _options?: Configuration): Promise<HttpInfo<QueryDBResultResponse>> {
    let requestContextValue = await this.requestFactory.getQueryResult(projectId, queryId, pageSize, pageToken, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getQueryResultWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the results of a query.  This method requires a query ID, which you can get only from the [Run Query](./run-query) response.  This method can be used only when a query has completed. You can check a query\'s status by calling [Get Query Status](./get-query-status). 
   * Get Query Result
   * @param projectId ID of the project that contains the query you want to retrieve. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings.  
   * @param queryId Query ID returned by [Run Query](./run-query).
   * @param pageSize Maximum number of items to return in the response.
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. 
   */
  public async getQueryResult(projectId: string, queryId: string, pageSize?: number, pageToken?: string, _options?: Configuration): Promise<QueryDBResultResponse> {
    const responseWithInfo = await this.getQueryResultWithHttpInfo(projectId, queryId, pageSize, pageToken, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a query\'s status.  This method requires a query ID, which you can get only from the [Run Query](./run-query) response.  When a query is completed, you can retrieve the query results in one of these ways:  - Following the `outputLocation` URI in this method\'s response object.   The URI points to where the results are stored as a CSV file. - Calling [Get Query Result](./get-query-result).   Get Query Result returns a paginated array of results as JSON for additional processing or handling.  If the query failed, see [Troubleshooting Queries] for possible errors and how to resolve them.  [Troubleshooting Queries]: /user-guide/searching-exploring-and-querying/query/troubleshooting-queries 
   * Get Query Status
   * @param projectId ID of the project that contains the query status you want to retrieve. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings.  
   * @param queryId Query ID returned by [Run Query](./run-query).
   */
  public async getQueryStatusWithHttpInfo(projectId: string, queryId: string, _options?: Configuration): Promise<HttpInfo<QueryDBStatusResponse>> {
    let requestContextValue = await this.requestFactory.getQueryStatus(projectId, queryId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getQueryStatusWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a query\'s status.  This method requires a query ID, which you can get only from the [Run Query](./run-query) response.  When a query is completed, you can retrieve the query results in one of these ways:  - Following the `outputLocation` URI in this method\'s response object.   The URI points to where the results are stored as a CSV file. - Calling [Get Query Result](./get-query-result).   Get Query Result returns a paginated array of results as JSON for additional processing or handling.  If the query failed, see [Troubleshooting Queries] for possible errors and how to resolve them.  [Troubleshooting Queries]: /user-guide/searching-exploring-and-querying/query/troubleshooting-queries 
   * Get Query Status
   * @param projectId ID of the project that contains the query status you want to retrieve. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings.  
   * @param queryId Query ID returned by [Run Query](./run-query).
   */
  public async getQueryStatus(projectId: string, queryId: string, _options?: Configuration): Promise<QueryDBStatusResponse> {
    const responseWithInfo = await this.getQueryStatusWithHttpInfo(projectId, queryId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Run a SQL query that joins across multiple database snapshots.  Provide a list of resources with aliases, then reference tables as `alias.schema.table` in your SQL query. This allows you to join tables from different databases or compare data across different snapshots of the same database.  **Important**: All resources must be stored in the same vault. Cross-vault queries are not supported.  This method is asynchronous. It returns a query ID that you can use to check the status of the query. You can then use the query ID to call [Get Query Status] and [Get Query Result].  [Get Query Status]: ./get-query-status [Get Query Result]: ./get-query-result 
   * Run Cross-Resource Query
   * @param projectId ID of the project that contains the database snapshots you want to query. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param crossResourceQueryRequest 
   */
  public async runCrossResourceQueryWithHttpInfo(projectId: string, crossResourceQueryRequest?: CrossResourceQueryRequest, _options?: Configuration): Promise<HttpInfo<QueryDBResponse>> {
    let requestContextValue = await this.requestFactory.runCrossResourceQuery(projectId, crossResourceQueryRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.runCrossResourceQueryWithHttpInfo(responseContext)
  }

  /**
   * Description: Run a SQL query that joins across multiple database snapshots.  Provide a list of resources with aliases, then reference tables as `alias.schema.table` in your SQL query. This allows you to join tables from different databases or compare data across different snapshots of the same database.  **Important**: All resources must be stored in the same vault. Cross-vault queries are not supported.  This method is asynchronous. It returns a query ID that you can use to check the status of the query. You can then use the query ID to call [Get Query Status] and [Get Query Result].  [Get Query Status]: ./get-query-status [Get Query Result]: ./get-query-result 
   * Run Cross-Resource Query
   * @param projectId ID of the project that contains the database snapshots you want to query. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param crossResourceQueryRequest 
   */
  public async runCrossResourceQuery(projectId: string, crossResourceQueryRequest?: CrossResourceQueryRequest, _options?: Configuration): Promise<QueryDBResponse> {
    const responseWithInfo = await this.runCrossResourceQueryWithHttpInfo(projectId, crossResourceQueryRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Runs a SQL query against a database snapshot.  This method is asynchronous. It returns a query ID that you can use to check the status of the query. You can then use the query ID to call [Get Query Status] and [Get Query Result].  When the query is complete, the results are saved as a CSV to the location you specify in your restore account.  If the request fails, see [Troubleshooting Queries] for possible errors and how to resolve them.  [Troubleshooting Queries]: /user-guide/searching-exploring-and-querying/query/troubleshooting-queries [Get Query Status]: ./get-query-status [Get Query Result]: ./get-query-result 
   * Run Query
   * @param projectId ID of the project that contains the database snapshot you want to retrieve. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings.  
   * @param snapshotId ID of the database [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) you want to query. Snapshot must belong to an RDS resource. 
   * @param queryDBRequest 
   */
  public async runQueryWithHttpInfo(projectId: string, snapshotId: string, queryDBRequest?: QueryDBRequest, _options?: Configuration): Promise<HttpInfo<QueryDBResponse>> {
    let requestContextValue = await this.requestFactory.runQuery(projectId, snapshotId, queryDBRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.runQueryWithHttpInfo(responseContext)
  }

  /**
   * Description: Runs a SQL query against a database snapshot.  This method is asynchronous. It returns a query ID that you can use to check the status of the query. You can then use the query ID to call [Get Query Status] and [Get Query Result].  When the query is complete, the results are saved as a CSV to the location you specify in your restore account.  If the request fails, see [Troubleshooting Queries] for possible errors and how to resolve them.  [Troubleshooting Queries]: /user-guide/searching-exploring-and-querying/query/troubleshooting-queries [Get Query Status]: ./get-query-status [Get Query Result]: ./get-query-result 
   * Run Query
   * @param projectId ID of the project that contains the database snapshot you want to retrieve. You can see your project ID in the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings.  
   * @param snapshotId ID of the database [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) you want to query. Snapshot must belong to an RDS resource. 
   * @param queryDBRequest 
   */
  public async runQuery(projectId: string, snapshotId: string, queryDBRequest?: QueryDBRequest, _options?: Configuration): Promise<QueryDBResponse> {
    const responseWithInfo = await this.runQueryWithHttpInfo(projectId, snapshotId, queryDBRequest, _options);
    return responseWithInfo.data;
  }


}


import { DeleteApiCredentialsApiRequestFactory, DeleteApiCredentialsApiResponseProcessor} from "../apis/DeleteApiCredentialsApi";

export class PromiseDeleteApiCredentialsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteApiCredentialsApiRequestFactory = new DeleteApiCredentialsApiRequestFactory(configuration),
    private responseProcessor: DeleteApiCredentialsApiResponseProcessor = new DeleteApiCredentialsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows an admin to delete api credentials by their ID.
   * Delete api credentials
   * @param clientId Client ID of the api credentials to delete
   */
  public async deleteApiCredentialsWithHttpInfo(clientId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteApiCredentials(clientId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteApiCredentialsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows an admin to delete api credentials by their ID.
   * Delete api credentials
   * @param clientId Client ID of the api credentials to delete
   */
  public async deleteApiCredentials(clientId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteApiCredentialsWithHttpInfo(clientId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteApplicationOverrideApiRequestFactory, DeleteApplicationOverrideApiResponseProcessor} from "../apis/DeleteApplicationOverrideApi";

export class PromiseDeleteApplicationOverrideApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteApplicationOverrideApiRequestFactory = new DeleteApplicationOverrideApiRequestFactory(configuration),
    private responseProcessor: DeleteApplicationOverrideApiResponseProcessor = new DeleteApplicationOverrideApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation allows the deletion of application overrides for specified inventory items within a project.
   * Delete application override for an inventory item
   * @param projectId The project ID
   * @param applicationOverride The request body for deleting application override
   */
  public async deleteApplicationOverrideWithHttpInfo(projectId: string, applicationOverride: Array<ApplicationOverride>, _options?: Configuration): Promise<HttpInfo<BatchOperationResponse>> {
    let requestContextValue = await this.requestFactory.deleteApplicationOverride(projectId, applicationOverride, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteApplicationOverrideWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation allows the deletion of application overrides for specified inventory items within a project.
   * Delete application override for an inventory item
   * @param projectId The project ID
   * @param applicationOverride The request body for deleting application override
   */
  public async deleteApplicationOverride(projectId: string, applicationOverride: Array<ApplicationOverride>, _options?: Configuration): Promise<BatchOperationResponse> {
    const responseWithInfo = await this.deleteApplicationOverrideWithHttpInfo(projectId, applicationOverride, _options);
    return responseWithInfo.data;
  }


}


import { DeleteBackupPolicyApiRequestFactory, DeleteBackupPolicyApiResponseProcessor} from "../apis/DeleteBackupPolicyApi";

export class PromiseDeleteBackupPolicyApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteBackupPolicyApiRequestFactory = new DeleteBackupPolicyApiRequestFactory(configuration),
    private responseProcessor: DeleteBackupPolicyApiResponseProcessor = new DeleteBackupPolicyApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Deletes a specified policy by its ID within a given project.
   * Delete a policy
   * @param id ID of the policy to delete
   * @param projectId The project ID
   */
  public async deletePolicyWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deletePolicy(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deletePolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes a specified policy by its ID within a given project.
   * Delete a policy
   * @param id ID of the policy to delete
   * @param projectId The project ID
   */
  public async deletePolicy(id: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deletePolicyWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteBackupVaultApiRequestFactory, DeleteBackupVaultApiResponseProcessor} from "../apis/DeleteBackupVaultApi";

export class PromiseDeleteBackupVaultApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteBackupVaultApiRequestFactory = new DeleteBackupVaultApiRequestFactory(configuration),
    private responseProcessor: DeleteBackupVaultApiResponseProcessor = new DeleteBackupVaultApiResponseProcessor() 
  ) {
    
  }

  /**
   * Delete a vault
   * @param vaultId ID of the backup vault to delete
   * @param projectId The project ID
   */
  public async deleteVaultWithHttpInfo(vaultId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteVault(vaultId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteVaultWithHttpInfo(responseContext)
  }

  /**
   * Delete a vault
   * @param vaultId ID of the backup vault to delete
   * @param projectId The project ID
   */
  public async deleteVault(vaultId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteVaultWithHttpInfo(vaultId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteControlApiRequestFactory, DeleteControlApiResponseProcessor} from "../apis/DeleteControlApi";

export class PromiseDeleteControlApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteControlApiRequestFactory = new DeleteControlApiRequestFactory(configuration),
    private responseProcessor: DeleteControlApiResponseProcessor = new DeleteControlApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API deletes a specific control identified by its ID within a given project.
   * Delete a control
   * @param id ID of the control to delete
   * @param projectId The project ID
   */
  public async deleteControlWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteControl(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteControlWithHttpInfo(responseContext)
  }

  /**
   * Description: This API deletes a specific control identified by its ID within a given project.
   * Delete a control
   * @param id ID of the control to delete
   * @param projectId The project ID
   */
  public async deleteControl(id: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteControlWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteDynamoDbBackupConfigApiRequestFactory, DeleteDynamoDbBackupConfigApiResponseProcessor} from "../apis/DeleteDynamoDbBackupConfigApi";

export class PromiseDeleteDynamoDbBackupConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteDynamoDbBackupConfigApiRequestFactory = new DeleteDynamoDbBackupConfigApiRequestFactory(configuration),
    private responseProcessor: DeleteDynamoDbBackupConfigApiResponseProcessor = new DeleteDynamoDbBackupConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Deletes the DynamoDB backup configuration at the resource, account, or project level.
   * Delete DynamoDB backup configuration
   * @param projectId The project ID
   * @param accountId The cloud account ID for account-level configuration
   * @param resourceId The resource ID for resource-level configuration
   */
  public async deleteDynamoDbBackupConfigWithHttpInfo(projectId: string, accountId?: string, resourceId?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteDynamoDbBackupConfig(projectId, accountId, resourceId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteDynamoDbBackupConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes the DynamoDB backup configuration at the resource, account, or project level.
   * Delete DynamoDB backup configuration
   * @param projectId The project ID
   * @param accountId The cloud account ID for account-level configuration
   * @param resourceId The resource ID for resource-level configuration
   */
  public async deleteDynamoDbBackupConfig(projectId: string, accountId?: string, resourceId?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteDynamoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteInfectedExcludePathApiRequestFactory, DeleteInfectedExcludePathApiResponseProcessor} from "../apis/DeleteInfectedExcludePathApi";

export class PromiseDeleteInfectedExcludePathApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteInfectedExcludePathApiRequestFactory = new DeleteInfectedExcludePathApiRequestFactory(configuration),
    private responseProcessor: DeleteInfectedExcludePathApiResponseProcessor = new DeleteInfectedExcludePathApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Deletes the infected exclude path for the Eon account or resource.
   * Delete infected exclude path
   * @param projectId The project ID
   * @param id The ID of the infected exclude path to delete
   */
  public async deleteInfectedExcludePathWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteInfectedExcludePath(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteInfectedExcludePathWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes the infected exclude path for the Eon account or resource.
   * Delete infected exclude path
   * @param projectId The project ID
   * @param id The ID of the infected exclude path to delete
   */
  public async deleteInfectedExcludePath(projectId: string, id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteInfectedExcludePathWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { DeleteObjectStoreExclusionRuleApiRequestFactory, DeleteObjectStoreExclusionRuleApiResponseProcessor} from "../apis/DeleteObjectStoreExclusionRuleApi";

export class PromiseDeleteObjectStoreExclusionRuleApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteObjectStoreExclusionRuleApiRequestFactory = new DeleteObjectStoreExclusionRuleApiRequestFactory(configuration),
    private responseProcessor: DeleteObjectStoreExclusionRuleApiResponseProcessor = new DeleteObjectStoreExclusionRuleApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API deletes a specific object store exclusion rule identified by its ID within a given project.
   * Delete a object store exclusion rule
   * @param id ID of the rule to delete
   * @param projectId The project ID
   */
  public async deleteObjectStoreExclusionRuleWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteObjectStoreExclusionRule(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteObjectStoreExclusionRuleWithHttpInfo(responseContext)
  }

  /**
   * Description: This API deletes a specific object store exclusion rule identified by its ID within a given project.
   * Delete a object store exclusion rule
   * @param id ID of the rule to delete
   * @param projectId The project ID
   */
  public async deleteObjectStoreExclusionRule(id: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteObjectStoreExclusionRuleWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteProjectApiRequestFactory, DeleteProjectApiResponseProcessor} from "../apis/DeleteProjectApi";

export class PromiseDeleteProjectApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteProjectApiRequestFactory = new DeleteProjectApiRequestFactory(configuration),
    private responseProcessor: DeleteProjectApiResponseProcessor = new DeleteProjectApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows the deletion of a project identified by its unique projectId. It requires admin permissions and returns appropriate status codes based on the result of the operation.
   * Delete a project
   * @param projectId ID of the project to delete
   */
  public async deleteProjectWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteProject(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteProjectWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows the deletion of a project identified by its unique projectId. It requires admin permissions and returns appropriate status codes based on the result of the operation.
   * Delete a project
   * @param projectId ID of the project to delete
   */
  public async deleteProject(projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteProjectWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteProjectRestoreAccountApiRequestFactory, DeleteProjectRestoreAccountApiResponseProcessor} from "../apis/DeleteProjectRestoreAccountApi";

export class PromiseDeleteProjectRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteProjectRestoreAccountApiRequestFactory = new DeleteProjectRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: DeleteProjectRestoreAccountApiResponseProcessor = new DeleteProjectRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This operation deletes a specified restore account associated with a given project.
   * Delete a restore account
   * @param accountId ID of the restore account to delete
   * @param projectId The project ID
   */
  public async deleteProjectRestoreAccountWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteProjectRestoreAccount(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteProjectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This operation deletes a specified restore account associated with a given project.
   * Delete a restore account
   * @param accountId ID of the restore account to delete
   * @param projectId The project ID
   */
  public async deleteProjectRestoreAccount(accountId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteProjectRestoreAccountWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteProjectUserApiRequestFactory, DeleteProjectUserApiResponseProcessor} from "../apis/DeleteProjectUserApi";

export class PromiseDeleteProjectUserApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteProjectUserApiRequestFactory = new DeleteProjectUserApiRequestFactory(configuration),
    private responseProcessor: DeleteProjectUserApiResponseProcessor = new DeleteProjectUserApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API call deletes a user from a specified project. The user ID and project ID must be provided.
   * Delete a user
   * @param id ID of the user to delete
   * @param projectId The project ID
   */
  public async deleteProjectUserWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteProjectUser(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteProjectUserWithHttpInfo(responseContext)
  }

  /**
   * Description: This API call deletes a user from a specified project. The user ID and project ID must be provided.
   * Delete a user
   * @param id ID of the user to delete
   * @param projectId The project ID
   */
  public async deleteProjectUser(id: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteProjectUserWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteRestoreAccountApiRequestFactory, DeleteRestoreAccountApiResponseProcessor} from "../apis/DeleteRestoreAccountApi";

export class PromiseDeleteRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteRestoreAccountApiRequestFactory = new DeleteRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: DeleteRestoreAccountApiResponseProcessor = new DeleteRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API deletes a specified restore account identified by its accountId.
   * Delete a restore account
   * @param accountId ID of the restore account to delete
   */
  public async deleteRestoreAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteRestoreAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API deletes a specified restore account identified by its accountId.
   * Delete a restore account
   * @param accountId ID of the restore account to delete
   */
  public async deleteRestoreAccount(accountId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteRestoreAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteSamlIdentityProviderConfigApiRequestFactory, DeleteSamlIdentityProviderConfigApiResponseProcessor} from "../apis/DeleteSamlIdentityProviderConfigApi";

export class PromiseDeleteSamlIdentityProviderConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteSamlIdentityProviderConfigApiRequestFactory = new DeleteSamlIdentityProviderConfigApiRequestFactory(configuration),
    private responseProcessor: DeleteSamlIdentityProviderConfigApiResponseProcessor = new DeleteSamlIdentityProviderConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Deletes an existing SAML identity provider configuration by its ID.
   * Delete saml identity provider config
   * @param id The ID of the saml identity provider
   */
  public async deleteSamlIdentityProviderConfigWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteSamlIdentityProviderConfig(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteSamlIdentityProviderConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes an existing SAML identity provider configuration by its ID.
   * Delete saml identity provider config
   * @param id The ID of the saml identity provider
   */
  public async deleteSamlIdentityProviderConfig(id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteSamlIdentityProviderConfigWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }


}


import { DeleteScanningAccountApiRequestFactory, DeleteScanningAccountApiResponseProcessor} from "../apis/DeleteScanningAccountApi";

export class PromiseDeleteScanningAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteScanningAccountApiRequestFactory = new DeleteScanningAccountApiRequestFactory(configuration),
    private responseProcessor: DeleteScanningAccountApiResponseProcessor = new DeleteScanningAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows the deletion of a scanning account identified by its UUID. Authorization with \'admin:scanning_accounts\' permission is required.
   * Delete a scanning account
   * @param accountId ID of the scanning account to delete
   */
  public async deleteScanningAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteScanningAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteScanningAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows the deletion of a scanning account identified by its UUID. Authorization with \'admin:scanning_accounts\' permission is required.
   * Delete a scanning account
   * @param accountId ID of the scanning account to delete
   */
  public async deleteScanningAccount(accountId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteScanningAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteSourceAccountApiRequestFactory, DeleteSourceAccountApiResponseProcessor} from "../apis/DeleteSourceAccountApi";

export class PromiseDeleteSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteSourceAccountApiRequestFactory = new DeleteSourceAccountApiRequestFactory(configuration),
    private responseProcessor: DeleteSourceAccountApiResponseProcessor = new DeleteSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: API endpoint to delete a source account by its unique accountId. Requires admin permissions.
   * Delete a source account
   * @param accountId ID of the source account to delete
   */
  public async deleteSourceAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteSourceAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: API endpoint to delete a source account by its unique accountId. Requires admin permissions.
   * Delete a source account
   * @param accountId ID of the source account to delete
   */
  public async deleteSourceAccount(accountId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteSourceAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { DeleteUserApiRequestFactory, DeleteUserApiResponseProcessor} from "../apis/DeleteUserApi";

export class PromiseDeleteUserApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteUserApiRequestFactory = new DeleteUserApiRequestFactory(configuration),
    private responseProcessor: DeleteUserApiResponseProcessor = new DeleteUserApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows an admin to delete a user by their ID.
   * Delete a user
   * @param id ID of the user to delete
   */
  public async deleteUserWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteUser(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteUserWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows an admin to delete a user by their ID.
   * Delete a user
   * @param id ID of the user to delete
   */
  public async deleteUser(id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteUserWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }


}


import { DeleteVaultAccountApiRequestFactory, DeleteVaultAccountApiResponseProcessor} from "../apis/DeleteVaultAccountApi";

export class PromiseDeleteVaultAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DeleteVaultAccountApiRequestFactory = new DeleteVaultAccountApiRequestFactory(configuration),
    private responseProcessor: DeleteVaultAccountApiResponseProcessor = new DeleteVaultAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation deletes a vault account identified by its accountId. Requires admin permissions on vault accounts.
   * Delete a vault account
   * @param accountId ID of the vault account to delete
   */
  public async deleteVaultAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteVaultAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation deletes a vault account identified by its accountId. Requires admin permissions on vault accounts.
   * Delete a vault account
   * @param accountId ID of the vault account to delete
   */
  public async deleteVaultAccount(accountId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteVaultAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { DiscoveryApiRequestFactory, DiscoveryApiResponseProcessor} from "../apis/DiscoveryApi";

export class PromiseDiscoveryApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DiscoveryApiRequestFactory = new DiscoveryApiRequestFactory(configuration),
    private responseProcessor: DiscoveryApiResponseProcessor = new DiscoveryApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation initiates a discovery process for a specific account identified by projectId and accountId.
   * Invokes a discovery process for a specific account
   * @param projectId The project ID
   * @param accountId The account ID
   * @param discoveryInvokeParams The request body for invoking discovery
   */
  public async invokeDiscoverWithHttpInfo(projectId: string, accountId: string, discoveryInvokeParams: DiscoveryInvokeParams, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.invokeDiscover(projectId, accountId, discoveryInvokeParams, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.invokeDiscoverWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation initiates a discovery process for a specific account identified by projectId and accountId.
   * Invokes a discovery process for a specific account
   * @param projectId The project ID
   * @param accountId The account ID
   * @param discoveryInvokeParams The request body for invoking discovery
   */
  public async invokeDiscover(projectId: string, accountId: string, discoveryInvokeParams: DiscoveryInvokeParams, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.invokeDiscoverWithHttpInfo(projectId, accountId, discoveryInvokeParams, _options);
    return responseWithInfo.data;
  }


}


import { DiscoveryStatusApiRequestFactory, DiscoveryStatusApiResponseProcessor} from "../apis/DiscoveryStatusApi";

export class PromiseDiscoveryStatusApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DiscoveryStatusApiRequestFactory = new DiscoveryStatusApiRequestFactory(configuration),
    private responseProcessor: DiscoveryStatusApiResponseProcessor = new DiscoveryStatusApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the current status of the discovery process for a specified project and account.
   * Gets status of discovery process for a project
   * @param projectId The project ID
   * @param accountId The account ID
   */
  public async discoveryStatusWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<DiscoveryStatusResponse>> {
    let requestContextValue = await this.requestFactory.discoveryStatus(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.discoveryStatusWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the current status of the discovery process for a specified project and account.
   * Gets status of discovery process for a project
   * @param projectId The project ID
   * @param accountId The account ID
   */
  public async discoveryStatus(projectId: string, accountId: string, _options?: Configuration): Promise<DiscoveryStatusResponse> {
    const responseWithInfo = await this.discoveryStatusWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }


}


import { DlsgPoliciesApiRequestFactory, DlsgPoliciesApiResponseProcessor} from "../apis/DlsgPoliciesApi";

export class PromiseDlsgPoliciesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DlsgPoliciesApiRequestFactory = new DlsgPoliciesApiRequestFactory(configuration),
    private responseProcessor: DlsgPoliciesApiResponseProcessor = new DlsgPoliciesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a dlsg policy.
   * Create DLSG Policy
   * @param projectId ID of the project you want to create a dlsg policy in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createDlsgPolicyRequest 
   */
  public async createDlsgPolicyWithHttpInfo(projectId: string, createDlsgPolicyRequest: CreateDlsgPolicyRequest, _options?: Configuration): Promise<HttpInfo<CreateDlsgPolicyResponse>> {
    let requestContextValue = await this.requestFactory.createDlsgPolicy(projectId, createDlsgPolicyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createDlsgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a dlsg policy.
   * Create DLSG Policy
   * @param projectId ID of the project you want to create a dlsg policy in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createDlsgPolicyRequest 
   */
  public async createDlsgPolicy(projectId: string, createDlsgPolicyRequest: CreateDlsgPolicyRequest, _options?: Configuration): Promise<CreateDlsgPolicyResponse> {
    const responseWithInfo = await this.createDlsgPolicyWithHttpInfo(projectId, createDlsgPolicyRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Deletes a dlsg policy.
   * Delete Dlsg Policy
   * @param dlsgPolicyId ID of the dlsg policy to delete
   * @param projectId ID of the project whose dlsg policy you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteDlsgPolicyWithHttpInfo(dlsgPolicyId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteDlsgPolicy(dlsgPolicyId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteDlsgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes a dlsg policy.
   * Delete Dlsg Policy
   * @param dlsgPolicyId ID of the dlsg policy to delete
   * @param projectId ID of the project whose dlsg policy you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteDlsgPolicy(dlsgPolicyId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteDlsgPolicyWithHttpInfo(dlsgPolicyId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a dlsg policy.
   * Get Dlsg Policy
   * @param dlsgPolicyId ID of the dlsg policy to retrieve
   * @param projectId ID of the project whose dlsg policy you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getDlsgPolicyWithHttpInfo(dlsgPolicyId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetDlsgPolicyResponse>> {
    let requestContextValue = await this.requestFactory.getDlsgPolicy(dlsgPolicyId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDlsgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a dlsg policy.
   * Get Dlsg Policy
   * @param dlsgPolicyId ID of the dlsg policy to retrieve
   * @param projectId ID of the project whose dlsg policy you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getDlsgPolicy(dlsgPolicyId: string, projectId: string, _options?: Configuration): Promise<GetDlsgPolicyResponse> {
    const responseWithInfo = await this.getDlsgPolicyWithHttpInfo(dlsgPolicyId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of dlsg policies in the specified project. 
   * List Dlsg Policies
   * @param projectId ID of the project whose dlsg policies you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listDlsgPoliciesWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListDlsgPoliciesResponse>> {
    let requestContextValue = await this.requestFactory.listDlsgPolicies(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listDlsgPoliciesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of dlsg policies in the specified project. 
   * List Dlsg Policies
   * @param projectId ID of the project whose dlsg policies you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listDlsgPolicies(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListDlsgPoliciesResponse> {
    const responseWithInfo = await this.listDlsgPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates a dlsg policy.
   * Update Dlsg Policy
   * @param dlsgPolicyId ID of the dlsg policy to update
   * @param projectId ID of the project whose dlsg policy you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateDlsgPolicyRequest The request body for updating a policy
   */
  public async updateDlsgPolicyWithHttpInfo(dlsgPolicyId: string, projectId: string, updateDlsgPolicyRequest: UpdateDlsgPolicyRequest, _options?: Configuration): Promise<HttpInfo<UpdateDlsgPolicyResponse>> {
    let requestContextValue = await this.requestFactory.updateDlsgPolicy(dlsgPolicyId, projectId, updateDlsgPolicyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateDlsgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates a dlsg policy.
   * Update Dlsg Policy
   * @param dlsgPolicyId ID of the dlsg policy to update
   * @param projectId ID of the project whose dlsg policy you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateDlsgPolicyRequest The request body for updating a policy
   */
  public async updateDlsgPolicy(dlsgPolicyId: string, projectId: string, updateDlsgPolicyRequest: UpdateDlsgPolicyRequest, _options?: Configuration): Promise<UpdateDlsgPolicyResponse> {
    const responseWithInfo = await this.updateDlsgPolicyWithHttpInfo(dlsgPolicyId, projectId, updateDlsgPolicyRequest, _options);
    return responseWithInfo.data;
  }


}


import { DlsgSettingsApiRequestFactory, DlsgSettingsApiResponseProcessor} from "../apis/DlsgSettingsApi";

export class PromiseDlsgSettingsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: DlsgSettingsApiRequestFactory = new DlsgSettingsApiRequestFactory(configuration),
    private responseProcessor: DlsgSettingsApiResponseProcessor = new DlsgSettingsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the DLSG settings for the specified project.
   * Get DLSG Settings
   * @param projectId ID of the project whose DLSG settings you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getDlsgSettingsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<DlsgSettings>> {
    let requestContextValue = await this.requestFactory.getDlsgSettings(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDlsgSettingsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the DLSG settings for the specified project.
   * Get DLSG Settings
   * @param projectId ID of the project whose DLSG settings you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getDlsgSettings(projectId: string, _options?: Configuration): Promise<DlsgSettings> {
    const responseWithInfo = await this.getDlsgSettingsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Saves the DLSG settings for the specified project.
   * Save DLSG Settings
   * @param projectId ID of the project whose DLSG settings you want to save. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param dlsgSettings The DLSG settings to save.
   */
  public async saveDlsgSettingsWithHttpInfo(projectId: string, dlsgSettings: DlsgSettings, _options?: Configuration): Promise<HttpInfo<DlsgSettings>> {
    let requestContextValue = await this.requestFactory.saveDlsgSettings(projectId, dlsgSettings, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.saveDlsgSettingsWithHttpInfo(responseContext)
  }

  /**
   * Description: Saves the DLSG settings for the specified project.
   * Save DLSG Settings
   * @param projectId ID of the project whose DLSG settings you want to save. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param dlsgSettings The DLSG settings to save.
   */
  public async saveDlsgSettings(projectId: string, dlsgSettings: DlsgSettings, _options?: Configuration): Promise<DlsgSettings> {
    const responseWithInfo = await this.saveDlsgSettingsWithHttpInfo(projectId, dlsgSettings, _options);
    return responseWithInfo.data;
  }


}


import { EonAccountsApiRequestFactory, EonAccountsApiResponseProcessor} from "../apis/EonAccountsApi";

export class PromiseEonAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: EonAccountsApiRequestFactory = new EonAccountsApiRequestFactory(configuration),
    private responseProcessor: EonAccountsApiResponseProcessor = new EonAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Deactivates an EON account. This is an internal-only API endpoint. 
   * Deactivate an EON account
   * @param accountId ID of the EON account to inactivate
   * @param deactivateAccountRequest 
   */
  public async deactivateAccountWithHttpInfo(accountId: string, deactivateAccountRequest: DeactivateAccountRequest, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deactivateAccount(accountId, deactivateAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deactivateAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Deactivates an EON account. This is an internal-only API endpoint. 
   * Deactivate an EON account
   * @param accountId ID of the EON account to inactivate
   * @param deactivateAccountRequest 
   */
  public async deactivateAccount(accountId: string, deactivateAccountRequest: DeactivateAccountRequest, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deactivateAccountWithHttpInfo(accountId, deactivateAccountRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Reactivates an EON account by changing its status to ACTIVE.  This is an internal-only API endpoint that should only be used by authorized internal services. 
   * Reactivate an EON account
   * @param accountId ID of the EON account to reactivate
   * @param reactivateAccountRequest 
   */
  public async reactivateAccountWithHttpInfo(accountId: string, reactivateAccountRequest: ReactivateAccountRequest, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.reactivateAccount(accountId, reactivateAccountRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.reactivateAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Reactivates an EON account by changing its status to ACTIVE.  This is an internal-only API endpoint that should only be used by authorized internal services. 
   * Reactivate an EON account
   * @param accountId ID of the EON account to reactivate
   * @param reactivateAccountRequest 
   */
  public async reactivateAccount(accountId: string, reactivateAccountRequest: ReactivateAccountRequest, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.reactivateAccountWithHttpInfo(accountId, reactivateAccountRequest, _options);
    return responseWithInfo.data;
  }


}


import { FabricApiRequestFactory, FabricApiResponseProcessor} from "../apis/FabricApi";

export class PromiseFabricApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: FabricApiRequestFactory = new FabricApiRequestFactory(configuration),
    private responseProcessor: FabricApiResponseProcessor = new FabricApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates multiple Microsoft Fabric table shortcuts for database snapshots (Demo version).  This operation creates table shortcuts in Microsoft Fabric under the /Tables path that point to the backed-up database tables in S3 storage. Supports both Parquet (with automatic Delta conversion) and Iceberg formats. Note: This is specifically for table shortcuts. File shortcuts are not supported by this endpoint.  This API processes each table shortcut independently and returns partial success results. Even if some shortcuts fail to create, the operation will continue processing the remaining shortcuts and return success/failure status for each individual shortcut.  Demo API: This is a demo/POC implementation for testing Microsoft Fabric integration. 
   * Create Fabric Table Shortcuts Demo (Batch)
   * @param projectId ID of the project.
   * @param createFabricTableShortcutDemoRequest 
   */
  public async createFabricTableShortcutDemoWithHttpInfo(projectId: string, createFabricTableShortcutDemoRequest: CreateFabricTableShortcutDemoRequest, _options?: Configuration): Promise<HttpInfo<CreateFabricTableShortcutDemoResponse>> {
    let requestContextValue = await this.requestFactory.createFabricTableShortcutDemo(projectId, createFabricTableShortcutDemoRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createFabricTableShortcutDemoWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates multiple Microsoft Fabric table shortcuts for database snapshots (Demo version).  This operation creates table shortcuts in Microsoft Fabric under the /Tables path that point to the backed-up database tables in S3 storage. Supports both Parquet (with automatic Delta conversion) and Iceberg formats. Note: This is specifically for table shortcuts. File shortcuts are not supported by this endpoint.  This API processes each table shortcut independently and returns partial success results. Even if some shortcuts fail to create, the operation will continue processing the remaining shortcuts and return success/failure status for each individual shortcut.  Demo API: This is a demo/POC implementation for testing Microsoft Fabric integration. 
   * Create Fabric Table Shortcuts Demo (Batch)
   * @param projectId ID of the project.
   * @param createFabricTableShortcutDemoRequest 
   */
  public async createFabricTableShortcutDemo(projectId: string, createFabricTableShortcutDemoRequest: CreateFabricTableShortcutDemoRequest, _options?: Configuration): Promise<CreateFabricTableShortcutDemoResponse> {
    const responseWithInfo = await this.createFabricTableShortcutDemoWithHttpInfo(projectId, createFabricTableShortcutDemoRequest, _options);
    return responseWithInfo.data;
  }


}


import { FileExplorerCountSecurityAlertsInResourceApiRequestFactory, FileExplorerCountSecurityAlertsInResourceApiResponseProcessor} from "../apis/FileExplorerCountSecurityAlertsInResourceApi";

export class PromiseFileExplorerCountSecurityAlertsInResourceApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: FileExplorerCountSecurityAlertsInResourceApiRequestFactory = new FileExplorerCountSecurityAlertsInResourceApiRequestFactory(configuration),
    private responseProcessor: FileExplorerCountSecurityAlertsInResourceApiResponseProcessor = new FileExplorerCountSecurityAlertsInResourceApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Count security alerts in a resource for File Explorer
   * Count security alerts in a resource for File Explorer
   * @param projectId The project ID
   * @param resourceId The resource ID
   */
  public async fileExplorerCountSecurityAlertsInResourceWithHttpInfo(projectId: string, resourceId: string, _options?: Configuration): Promise<HttpInfo<FileExplorerCountSecurityAlertsInResourceResponse>> {
    let requestContextValue = await this.requestFactory.fileExplorerCountSecurityAlertsInResource(projectId, resourceId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.fileExplorerCountSecurityAlertsInResourceWithHttpInfo(responseContext)
  }

  /**
   * Description: Count security alerts in a resource for File Explorer
   * Count security alerts in a resource for File Explorer
   * @param projectId The project ID
   * @param resourceId The resource ID
   */
  public async fileExplorerCountSecurityAlertsInResource(projectId: string, resourceId: string, _options?: Configuration): Promise<FileExplorerCountSecurityAlertsInResourceResponse> {
    const responseWithInfo = await this.fileExplorerCountSecurityAlertsInResourceWithHttpInfo(projectId, resourceId, _options);
    return responseWithInfo.data;
  }


}


import { FileExplorerListPathApiRequestFactory, FileExplorerListPathApiResponseProcessor} from "../apis/FileExplorerListPathApi";

export class PromiseFileExplorerListPathApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: FileExplorerListPathApiRequestFactory = new FileExplorerListPathApiRequestFactory(configuration),
    private responseProcessor: FileExplorerListPathApiResponseProcessor = new FileExplorerListPathApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a list of all files within a specified directory in the given project and cloud resource, supporting pagination and optional filters.
   * Get all items in a given path
   * @param projectId The project ID
   * @param resourceId The resource ID
   * @param prefix The path prefix of the directory
   * @param pageSize The number of items to return per page
   * @param pageToken The page token for pagination
   * @param securityAlertedOnly Whether to include only security alerted files in the response
   * @param snapshotId The Eon snapshot id to list files from
   */
  public async fileExplorerListPathWithHttpInfo(projectId: string, resourceId: string, prefix?: string, pageSize?: number, pageToken?: string, securityAlertedOnly?: boolean, snapshotId?: string, _options?: Configuration): Promise<HttpInfo<FileExplorerResponse>> {
    let requestContextValue = await this.requestFactory.fileExplorerListPath(projectId, resourceId, prefix, pageSize, pageToken, securityAlertedOnly, snapshotId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.fileExplorerListPathWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all files within a specified directory in the given project and cloud resource, supporting pagination and optional filters.
   * Get all items in a given path
   * @param projectId The project ID
   * @param resourceId The resource ID
   * @param prefix The path prefix of the directory
   * @param pageSize The number of items to return per page
   * @param pageToken The page token for pagination
   * @param securityAlertedOnly Whether to include only security alerted files in the response
   * @param snapshotId The Eon snapshot id to list files from
   */
  public async fileExplorerListPath(projectId: string, resourceId: string, prefix?: string, pageSize?: number, pageToken?: string, securityAlertedOnly?: boolean, snapshotId?: string, _options?: Configuration): Promise<FileExplorerResponse> {
    const responseWithInfo = await this.fileExplorerListPathWithHttpInfo(projectId, resourceId, prefix, pageSize, pageToken, securityAlertedOnly, snapshotId, _options);
    return responseWithInfo.data;
  }


}


import { ForceDeleteSnapshotsApiRequestFactory, ForceDeleteSnapshotsApiResponseProcessor} from "../apis/ForceDeleteSnapshotsApi";

export class PromiseForceDeleteSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ForceDeleteSnapshotsApiRequestFactory = new ForceDeleteSnapshotsApiRequestFactory(configuration),
    private responseProcessor: ForceDeleteSnapshotsApiResponseProcessor = new ForceDeleteSnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Force delete test snapshots by marking them as pending deletion.
   * Force delete snapshots (test only)
   * @param projectId The project ID
   * @param forceDeleteSnapshotsRequest 
   */
  public async forceDeleteSnapshotsWithHttpInfo(projectId: string, forceDeleteSnapshotsRequest: ForceDeleteSnapshotsRequest, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.forceDeleteSnapshots(projectId, forceDeleteSnapshotsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.forceDeleteSnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: Force delete test snapshots by marking them as pending deletion.
   * Force delete snapshots (test only)
   * @param projectId The project ID
   * @param forceDeleteSnapshotsRequest 
   */
  public async forceDeleteSnapshots(projectId: string, forceDeleteSnapshotsRequest: ForceDeleteSnapshotsRequest, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.forceDeleteSnapshotsWithHttpInfo(projectId, forceDeleteSnapshotsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ForgotPasswordApiRequestFactory, ForgotPasswordApiResponseProcessor} from "../apis/ForgotPasswordApi";

export class PromiseForgotPasswordApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ForgotPasswordApiRequestFactory = new ForgotPasswordApiRequestFactory(configuration),
    private responseProcessor: ForgotPasswordApiResponseProcessor = new ForgotPasswordApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Endpoint to initiate forgot password process for users. It can redirect to the Cognito forgot password endpoint.
   * Forgot Password
   */
  public async forgotPasswordWithHttpInfo(_options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.forgotPassword(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.forgotPasswordWithHttpInfo(responseContext)
  }

  /**
   * Description: Endpoint to initiate forgot password process for users. It can redirect to the Cognito forgot password endpoint.
   * Forgot Password
   */
  public async forgotPassword(_options?: Configuration): Promise<void> {
    const responseWithInfo = await this.forgotPasswordWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { GcpRegisterApiRequestFactory, GcpRegisterApiResponseProcessor} from "../apis/GcpRegisterApi";

export class PromiseGcpRegisterApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GcpRegisterApiRequestFactory = new GcpRegisterApiRequestFactory(configuration),
    private responseProcessor: GcpRegisterApiResponseProcessor = new GcpRegisterApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing a payment token and initial login information through Google Cloud Marketplace.
   * Callback endpoint for Google Cloud marketplace
   * @param xGcpMarketplaceToken GCP marketplace token
   */
  public async gcpRegisterWithHttpInfo(xGcpMarketplaceToken?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.gcpRegister(xGcpMarketplaceToken, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.gcpRegisterWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing a payment token and initial login information through Google Cloud Marketplace.
   * Callback endpoint for Google Cloud marketplace
   * @param xGcpMarketplaceToken GCP marketplace token
   */
  public async gcpRegister(xGcpMarketplaceToken?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.gcpRegisterWithHttpInfo(xGcpMarketplaceToken, _options);
    return responseWithInfo.data;
  }


}


import { GetAWSStackDetailsApiRequestFactory, GetAWSStackDetailsApiResponseProcessor} from "../apis/GetAWSStackDetailsApi";

export class PromiseGetAWSStackDetailsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAWSStackDetailsApiRequestFactory = new GetAWSStackDetailsApiRequestFactory(configuration),
    private responseProcessor: GetAWSStackDetailsApiResponseProcessor = new GetAWSStackDetailsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Returns AWS stack details for a specific cloud account
   * Get AWS stack details by cloud account ID
   * @param projectId The ID of the project
   * @param accountId The cloud account ID
   */
  public async getAWSStackDetailsWithHttpInfo(projectId: string, accountId: string, _options?: Configuration): Promise<HttpInfo<GetAWSStackDetailsResponse>> {
    let requestContextValue = await this.requestFactory.getAWSStackDetails(projectId, accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAWSStackDetailsWithHttpInfo(responseContext)
  }

  /**
   * Description: Returns AWS stack details for a specific cloud account
   * Get AWS stack details by cloud account ID
   * @param projectId The ID of the project
   * @param accountId The cloud account ID
   */
  public async getAWSStackDetails(projectId: string, accountId: string, _options?: Configuration): Promise<GetAWSStackDetailsResponse> {
    const responseWithInfo = await this.getAWSStackDetailsWithHttpInfo(projectId, accountId, _options);
    return responseWithInfo.data;
  }


}


import { GetAccountUIPreferencesApiRequestFactory, GetAccountUIPreferencesApiResponseProcessor} from "../apis/GetAccountUIPreferencesApi";

export class PromiseGetAccountUIPreferencesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAccountUIPreferencesApiRequestFactory = new GetAccountUIPreferencesApiRequestFactory(configuration),
    private responseProcessor: GetAccountUIPreferencesApiResponseProcessor = new GetAccountUIPreferencesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the UI preferences for a specific account based on the provided key.
   * Get account UI preferences
   * @param key Key of the preference
   */
  public async getAccountUIPreferencesWithHttpInfo(key: string, _options?: Configuration): Promise<HttpInfo<Preferences>> {
    let requestContextValue = await this.requestFactory.getAccountUIPreferences(key, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAccountUIPreferencesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the UI preferences for a specific account based on the provided key.
   * Get account UI preferences
   * @param key Key of the preference
   */
  public async getAccountUIPreferences(key: string, _options?: Configuration): Promise<Preferences> {
    const responseWithInfo = await this.getAccountUIPreferencesWithHttpInfo(key, _options);
    return responseWithInfo.data;
  }


}


import { GetAccountsApiRequestFactory, GetAccountsApiResponseProcessor} from "../apis/GetAccountsApi";

export class PromiseGetAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAccountsApiRequestFactory = new GetAccountsApiRequestFactory(configuration),
    private responseProcessor: GetAccountsApiResponseProcessor = new GetAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve detailed information of a specified account using its ID.
   * Get details of an account
   * @param id ID of the account
   */
  public async getAccountWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<EonAccount>> {
    let requestContextValue = await this.requestFactory.getAccount(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve detailed information of a specified account using its ID.
   * Get details of an account
   * @param id ID of the account
   */
  public async getAccount(id: string, _options?: Configuration): Promise<EonAccount> {
    const responseWithInfo = await this.getAccountWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }


}


import { GetAmiApiRequestFactory, GetAmiApiResponseProcessor} from "../apis/GetAmiApi";

export class PromiseGetAmiApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAmiApiRequestFactory = new GetAmiApiRequestFactory(configuration),
    private responseProcessor: GetAmiApiResponseProcessor = new GetAmiApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve an AMI information for the restore account in a given project and region.
   * Gets AMI information
   * @param accountId ID of the restore account
   * @param region The region to get the AMI from
   * @param projectId The project ID
   * @param id ID of the AMI
   */
  public async getAmiWithHttpInfo(accountId: string, region: string, projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<Ami>> {
    let requestContextValue = await this.requestFactory.getAmi(accountId, region, projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAmiWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve an AMI information for the restore account in a given project and region.
   * Gets AMI information
   * @param accountId ID of the restore account
   * @param region The region to get the AMI from
   * @param projectId The project ID
   * @param id ID of the AMI
   */
  public async getAmi(accountId: string, region: string, projectId: string, id: string, _options?: Configuration): Promise<Ami> {
    const responseWithInfo = await this.getAmiWithHttpInfo(accountId, region, projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { GetApiCredentialsApiRequestFactory, GetApiCredentialsApiResponseProcessor} from "../apis/GetApiCredentialsApi";

export class PromiseGetApiCredentialsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetApiCredentialsApiRequestFactory = new GetApiCredentialsApiRequestFactory(configuration),
    private responseProcessor: GetApiCredentialsApiResponseProcessor = new GetApiCredentialsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches detailed information about an api credential identified by their ID.
   * Get details of api credentials
   * @param clientId Client ID of the api credentials to fetch
   */
  public async getApiCredentialsWithHttpInfo(clientId: string, _options?: Configuration): Promise<HttpInfo<ApiCredentialsProperties>> {
    let requestContextValue = await this.requestFactory.getApiCredentials(clientId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getApiCredentialsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches detailed information about an api credential identified by their ID.
   * Get details of api credentials
   * @param clientId Client ID of the api credentials to fetch
   */
  public async getApiCredentials(clientId: string, _options?: Configuration): Promise<ApiCredentialsProperties> {
    const responseWithInfo = await this.getApiCredentialsWithHttpInfo(clientId, _options);
    return responseWithInfo.data;
  }


}


import { GetAzureBackupApplicationsApiRequestFactory, GetAzureBackupApplicationsApiResponseProcessor} from "../apis/GetAzureBackupApplicationsApi";

export class PromiseGetAzureBackupApplicationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAzureBackupApplicationsApiRequestFactory = new GetAzureBackupApplicationsApiRequestFactory(configuration),
    private responseProcessor: GetAzureBackupApplicationsApiResponseProcessor = new GetAzureBackupApplicationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get Azure backup applications for cross-tenant backup operations
   * @param projectId The project ID
   * @param accountType The type of cloud account to get applications for (optional - if not provided, returns only key vault access and management apps)
   * @param tenantId Optional tenant ID to filter data access apps for restore accounts. When provided for Restore account type, returns the data access app matching this tenant ID instead of using the default \&quot;not Eon tenant\&quot; logic.
   */
  public async getAzureBackupApplicationsWithHttpInfo(projectId: string, accountType?: 'Source' | 'Scanning' | 'Vault' | 'Restore', tenantId?: string, _options?: Configuration): Promise<HttpInfo<AzureBackupApplicationsResponse>> {
    let requestContextValue = await this.requestFactory.getAzureBackupApplications(projectId, accountType, tenantId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAzureBackupApplicationsWithHttpInfo(responseContext)
  }

  /**
   * Get Azure backup applications for cross-tenant backup operations
   * @param projectId The project ID
   * @param accountType The type of cloud account to get applications for (optional - if not provided, returns only key vault access and management apps)
   * @param tenantId Optional tenant ID to filter data access apps for restore accounts. When provided for Restore account type, returns the data access app matching this tenant ID instead of using the default \&quot;not Eon tenant\&quot; logic.
   */
  public async getAzureBackupApplications(projectId: string, accountType?: 'Source' | 'Scanning' | 'Vault' | 'Restore', tenantId?: string, _options?: Configuration): Promise<AzureBackupApplicationsResponse> {
    const responseWithInfo = await this.getAzureBackupApplicationsWithHttpInfo(projectId, accountType, tenantId, _options);
    return responseWithInfo.data;
  }


}


import { GetAzureConsentUrlApiRequestFactory, GetAzureConsentUrlApiResponseProcessor} from "../apis/GetAzureConsentUrlApi";

export class PromiseGetAzureConsentUrlApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAzureConsentUrlApiRequestFactory = new GetAzureConsentUrlApiRequestFactory(configuration),
    private responseProcessor: GetAzureConsentUrlApiResponseProcessor = new GetAzureConsentUrlApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get Azure consent URL
   * @param projectId The project ID
   * @param requestId 
   * @param tenantId The redirect URI
   */
  public async getAzureConsentUrlWithHttpInfo(projectId: string, requestId: string, tenantId?: string, _options?: Configuration): Promise<HttpInfo<AzureConsentUrlResponse>> {
    let requestContextValue = await this.requestFactory.getAzureConsentUrl(projectId, requestId, tenantId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAzureConsentUrlWithHttpInfo(responseContext)
  }

  /**
   * Get Azure consent URL
   * @param projectId The project ID
   * @param requestId 
   * @param tenantId The redirect URI
   */
  public async getAzureConsentUrl(projectId: string, requestId: string, tenantId?: string, _options?: Configuration): Promise<AzureConsentUrlResponse> {
    const responseWithInfo = await this.getAzureConsentUrlWithHttpInfo(projectId, requestId, tenantId, _options);
    return responseWithInfo.data;
  }


}


import { GetAzureEonApplicationApiRequestFactory, GetAzureEonApplicationApiResponseProcessor} from "../apis/GetAzureEonApplicationApi";

export class PromiseGetAzureEonApplicationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAzureEonApplicationApiRequestFactory = new GetAzureEonApplicationApiRequestFactory(configuration),
    private responseProcessor: GetAzureEonApplicationApiResponseProcessor = new GetAzureEonApplicationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get Eon Azure application ID for the project
   * @param projectId The project ID
   */
  public async getAzureEonApplicationWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<AzureEonApplication>> {
    let requestContextValue = await this.requestFactory.getAzureEonApplication(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAzureEonApplicationWithHttpInfo(responseContext)
  }

  /**
   * Get Eon Azure application ID for the project
   * @param projectId The project ID
   */
  public async getAzureEonApplication(projectId: string, _options?: Configuration): Promise<AzureEonApplication> {
    const responseWithInfo = await this.getAzureEonApplicationWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetAzureSubscriptionsApiRequestFactory, GetAzureSubscriptionsApiResponseProcessor} from "../apis/GetAzureSubscriptionsApi";

export class PromiseGetAzureSubscriptionsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetAzureSubscriptionsApiRequestFactory = new GetAzureSubscriptionsApiRequestFactory(configuration),
    private responseProcessor: GetAzureSubscriptionsApiResponseProcessor = new GetAzureSubscriptionsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get available Azure subscriptions
   * @param projectId The project ID
   * @param requestId 
   */
  public async getAzureSubscriptionsWithHttpInfo(projectId: string, requestId: string, _options?: Configuration): Promise<HttpInfo<AzureListSubscriptionsResponse>> {
    let requestContextValue = await this.requestFactory.getAzureSubscriptions(projectId, requestId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getAzureSubscriptionsWithHttpInfo(responseContext)
  }

  /**
   * Get available Azure subscriptions
   * @param projectId The project ID
   * @param requestId 
   */
  public async getAzureSubscriptions(projectId: string, requestId: string, _options?: Configuration): Promise<AzureListSubscriptionsResponse> {
    const responseWithInfo = await this.getAzureSubscriptionsWithHttpInfo(projectId, requestId, _options);
    return responseWithInfo.data;
  }


}


import { GetBackedUpResourcesApiRequestFactory, GetBackedUpResourcesApiResponseProcessor} from "../apis/GetBackedUpResourcesApi";

export class PromiseGetBackedUpResourcesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBackedUpResourcesApiRequestFactory = new GetBackedUpResourcesApiRequestFactory(configuration),
    private responseProcessor: GetBackedUpResourcesApiResponseProcessor = new GetBackedUpResourcesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch details of resources that have been backed up for a specified project in the given cloud provider.
   * Get backed up resources
   * @param cloudProvider The cloudProvider
   * @param projectId The project ID
   */
  public async getBackedUpResourcesWithHttpInfo(cloudProvider: string, projectId: string, _options?: Configuration): Promise<HttpInfo<BackedUpByResourceType>> {
    let requestContextValue = await this.requestFactory.getBackedUpResources(cloudProvider, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackedUpResourcesWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch details of resources that have been backed up for a specified project in the given cloud provider.
   * Get backed up resources
   * @param cloudProvider The cloudProvider
   * @param projectId The project ID
   */
  public async getBackedUpResources(cloudProvider: string, projectId: string, _options?: Configuration): Promise<BackedUpByResourceType> {
    const responseWithInfo = await this.getBackedUpResourcesWithHttpInfo(cloudProvider, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetBackedUpStorageDateHistogramApiRequestFactory, GetBackedUpStorageDateHistogramApiResponseProcessor} from "../apis/GetBackedUpStorageDateHistogramApi";

export class PromiseGetBackedUpStorageDateHistogramApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBackedUpStorageDateHistogramApiRequestFactory = new GetBackedUpStorageDateHistogramApiRequestFactory(configuration),
    private responseProcessor: GetBackedUpStorageDateHistogramApiResponseProcessor = new GetBackedUpStorageDateHistogramApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a histogram of backed-up storage data for a specified project.
   * Get backed up storage date histogram
   * @param projectId The project ID
   */
  public async getBackedUpStorageDateHistogramWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<BackedUpStorageDateHistogram>> {
    let requestContextValue = await this.requestFactory.getBackedUpStorageDateHistogram(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackedUpStorageDateHistogramWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a histogram of backed-up storage data for a specified project.
   * Get backed up storage date histogram
   * @param projectId The project ID
   */
  public async getBackedUpStorageDateHistogram(projectId: string, _options?: Configuration): Promise<BackedUpStorageDateHistogram> {
    const responseWithInfo = await this.getBackedUpStorageDateHistogramWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetBackupControlViolationsApiRequestFactory, GetBackupControlViolationsApiResponseProcessor} from "../apis/GetBackupControlViolationsApi";

export class PromiseGetBackupControlViolationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBackupControlViolationsApiRequestFactory = new GetBackupControlViolationsApiRequestFactory(configuration),
    private responseProcessor: GetBackupControlViolationsApiResponseProcessor = new GetBackupControlViolationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches violations of backup control policies for a specific project.
   * Get backup control violations
   * @param projectId The project ID
   */
  public async getBackupControlViolationsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<BackupControlViolations>> {
    let requestContextValue = await this.requestFactory.getBackupControlViolations(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackupControlViolationsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches violations of backup control policies for a specific project.
   * Get backup control violations
   * @param projectId The project ID
   */
  public async getBackupControlViolations(projectId: string, _options?: Configuration): Promise<BackupControlViolations> {
    const responseWithInfo = await this.getBackupControlViolationsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetBackupPolicyApiRequestFactory, GetBackupPolicyApiResponseProcessor} from "../apis/GetBackupPolicyApi";

export class PromiseGetBackupPolicyApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBackupPolicyApiRequestFactory = new GetBackupPolicyApiRequestFactory(configuration),
    private responseProcessor: GetBackupPolicyApiResponseProcessor = new GetBackupPolicyApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the details of a specific policy identified by its ID and project ID.
   * Get policy details
   * @param id ID of the policy to retrieve
   * @param projectId The project ID
   */
  public async getPolicyWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<BackupPolicyConfig>> {
    let requestContextValue = await this.requestFactory.getPolicy(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the details of a specific policy identified by its ID and project ID.
   * Get policy details
   * @param id ID of the policy to retrieve
   * @param projectId The project ID
   */
  public async getPolicy(id: string, projectId: string, _options?: Configuration): Promise<BackupPolicyConfig> {
    const responseWithInfo = await this.getPolicyWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetBackupScopeApiRequestFactory, GetBackupScopeApiResponseProcessor} from "../apis/GetBackupScopeApi";

export class PromiseGetBackupScopeApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBackupScopeApiRequestFactory = new GetBackupScopeApiRequestFactory(configuration),
    private responseProcessor: GetBackupScopeApiResponseProcessor = new GetBackupScopeApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the backup scope for the project ID.
   * Get the backup scope
   * @param projectId The project ID
   */
  public async getBackupScopeWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<BackupScope>> {
    let requestContextValue = await this.requestFactory.getBackupScope(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackupScopeWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the backup scope for the project ID.
   * Get the backup scope
   * @param projectId The project ID
   */
  public async getBackupScope(projectId: string, _options?: Configuration): Promise<BackupScope> {
    const responseWithInfo = await this.getBackupScopeWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetBackupScopeSummaryApiRequestFactory, GetBackupScopeSummaryApiResponseProcessor} from "../apis/GetBackupScopeSummaryApi";

export class PromiseGetBackupScopeSummaryApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBackupScopeSummaryApiRequestFactory = new GetBackupScopeSummaryApiRequestFactory(configuration),
    private responseProcessor: GetBackupScopeSummaryApiResponseProcessor = new GetBackupScopeSummaryApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the backup scope results summary for the project ID.
   * Get the backup scope results summary
   * @param projectId The project ID
   */
  public async getBackupScopeSummaryWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<BackupScopeSummary>> {
    let requestContextValue = await this.requestFactory.getBackupScopeSummary(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackupScopeSummaryWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the backup scope results summary for the project ID.
   * Get the backup scope results summary
   * @param projectId The project ID
   */
  public async getBackupScopeSummary(projectId: string, _options?: Configuration): Promise<BackupScopeSummary> {
    const responseWithInfo = await this.getBackupScopeSummaryWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetBillingPeriodsApiRequestFactory, GetBillingPeriodsApiResponseProcessor} from "../apis/GetBillingPeriodsApi";

export class PromiseGetBillingPeriodsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetBillingPeriodsApiRequestFactory = new GetBillingPeriodsApiRequestFactory(configuration),
    private responseProcessor: GetBillingPeriodsApiResponseProcessor = new GetBillingPeriodsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a list of billing periods sorted from newest to oldest, covering the past year.
   * Get billing periods
   */
  public async getBillingPeriodsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<GetBillingPeriodsResponse>> {
    let requestContextValue = await this.requestFactory.getBillingPeriods(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBillingPeriodsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of billing periods sorted from newest to oldest, covering the past year.
   * Get billing periods
   */
  public async getBillingPeriods(_options?: Configuration): Promise<GetBillingPeriodsResponse> {
    const responseWithInfo = await this.getBillingPeriodsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { GetControlApiRequestFactory, GetControlApiResponseProcessor} from "../apis/GetControlApi";

export class PromiseGetControlApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetControlApiRequestFactory = new GetControlApiRequestFactory(configuration),
    private responseProcessor: GetControlApiResponseProcessor = new GetControlApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve details of a specific control by its ID within a given project.
   * Get control details
   * @param id ID of the control to retrieve
   * @param projectId The project ID
   */
  public async getControlWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<Control>> {
    let requestContextValue = await this.requestFactory.getControl(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getControlWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve details of a specific control by its ID within a given project.
   * Get control details
   * @param id ID of the control to retrieve
   * @param projectId The project ID
   */
  public async getControl(id: string, projectId: string, _options?: Configuration): Promise<Control> {
    const responseWithInfo = await this.getControlWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetCreditsApiRequestFactory, GetCreditsApiResponseProcessor} from "../apis/GetCreditsApi";

export class PromiseGetCreditsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetCreditsApiRequestFactory = new GetCreditsApiRequestFactory(configuration),
    private responseProcessor: GetCreditsApiResponseProcessor = new GetCreditsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the current credits available for the customer.
   * Get current credits
   */
  public async getCreditsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<GetCreditsResponse>> {
    let requestContextValue = await this.requestFactory.getCredits(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getCreditsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the current credits available for the customer.
   * Get current credits
   */
  public async getCredits(_options?: Configuration): Promise<GetCreditsResponse> {
    const responseWithInfo = await this.getCreditsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { GetCreditsByProviderApiRequestFactory, GetCreditsByProviderApiResponseProcessor} from "../apis/GetCreditsByProviderApi";

export class PromiseGetCreditsByProviderApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetCreditsByProviderApiRequestFactory = new GetCreditsByProviderApiRequestFactory(configuration),
    private responseProcessor: GetCreditsByProviderApiResponseProcessor = new GetCreditsByProviderApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the current credits available for the customer by provider name.
   * Get current credits by provider
   * @param provider The cloud provider name (AWS, AZURE, or GCP)
   */
  public async getCreditsByProviderWithHttpInfo(provider: 'AWS' | 'AZURE' | 'GCP', _options?: Configuration): Promise<HttpInfo<GetCreditsResponse>> {
    let requestContextValue = await this.requestFactory.getCreditsByProvider(provider, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getCreditsByProviderWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the current credits available for the customer by provider name.
   * Get current credits by provider
   * @param provider The cloud provider name (AWS, AZURE, or GCP)
   */
  public async getCreditsByProvider(provider: 'AWS' | 'AZURE' | 'GCP', _options?: Configuration): Promise<GetCreditsResponse> {
    const responseWithInfo = await this.getCreditsByProviderWithHttpInfo(provider, _options);
    return responseWithInfo.data;
  }


}


import { GetDashboardHomeDriftProtectionApiRequestFactory, GetDashboardHomeDriftProtectionApiResponseProcessor} from "../apis/GetDashboardHomeDriftProtectionApi";

export class PromiseGetDashboardHomeDriftProtectionApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetDashboardHomeDriftProtectionApiRequestFactory = new GetDashboardHomeDriftProtectionApiRequestFactory(configuration),
    private responseProcessor: GetDashboardHomeDriftProtectionApiResponseProcessor = new GetDashboardHomeDriftProtectionApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches drift protection summary data for the specified project\'s dashboard home page, providing insights into drift status and protections.
   * Get the Drift protection data for the dashboard home page
   * @param projectId The project ID
   */
  public async getDashboardHomeDriftProtectionWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<DriftProtectionSummary>> {
    let requestContextValue = await this.requestFactory.getDashboardHomeDriftProtection(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDashboardHomeDriftProtectionWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches drift protection summary data for the specified project\'s dashboard home page, providing insights into drift status and protections.
   * Get the Drift protection data for the dashboard home page
   * @param projectId The project ID
   */
  public async getDashboardHomeDriftProtection(projectId: string, _options?: Configuration): Promise<DriftProtectionSummary> {
    const responseWithInfo = await this.getDashboardHomeDriftProtectionWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetDatabasesByServerApiRequestFactory, GetDatabasesByServerApiResponseProcessor} from "../apis/GetDatabasesByServerApi";

export class PromiseGetDatabasesByServerApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetDatabasesByServerApiRequestFactory = new GetDatabasesByServerApiRequestFactory(configuration),
    private responseProcessor: GetDatabasesByServerApiResponseProcessor = new GetDatabasesByServerApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API fetches all databases associated with a specified server and snapshot within a project.
   * Get all databases for a given server and snapshot
   * @param serverId ID of the database server
   * @param snapshotId ID of the snapshot
   * @param projectId The project ID
   * @param dbName 
   */
  public async getDatabasesByServerWithHttpInfo(serverId: string, snapshotId: string, projectId: string, dbName?: string, _options?: Configuration): Promise<HttpInfo<DBServerRecord>> {
    let requestContextValue = await this.requestFactory.getDatabasesByServer(serverId, snapshotId, projectId, dbName, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDatabasesByServerWithHttpInfo(responseContext)
  }

  /**
   * Description: This API fetches all databases associated with a specified server and snapshot within a project.
   * Get all databases for a given server and snapshot
   * @param serverId ID of the database server
   * @param snapshotId ID of the snapshot
   * @param projectId The project ID
   * @param dbName 
   */
  public async getDatabasesByServer(serverId: string, snapshotId: string, projectId: string, dbName?: string, _options?: Configuration): Promise<DBServerRecord> {
    const responseWithInfo = await this.getDatabasesByServerWithHttpInfo(serverId, snapshotId, projectId, dbName, _options);
    return responseWithInfo.data;
  }


}


import { GetDynamoDbBackupConfigApiRequestFactory, GetDynamoDbBackupConfigApiResponseProcessor} from "../apis/GetDynamoDbBackupConfigApi";

export class PromiseGetDynamoDbBackupConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetDynamoDbBackupConfigApiRequestFactory = new GetDynamoDbBackupConfigApiRequestFactory(configuration),
    private responseProcessor: GetDynamoDbBackupConfigApiResponseProcessor = new GetDynamoDbBackupConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the DynamoDB backup configuration at the resource, account, or project level. Returns hierarchical fallback configuration if specific level not found.
   * Get DynamoDB backup configuration
   * @param projectId The project ID
   * @param accountId The cloud account ID for account-level configuration
   * @param resourceId The resource ID for resource-level configuration
   */
  public async getDynamoDbBackupConfigWithHttpInfo(projectId: string, accountId?: string, resourceId?: string, _options?: Configuration): Promise<HttpInfo<DynamoDbBackupConfig>> {
    let requestContextValue = await this.requestFactory.getDynamoDbBackupConfig(projectId, accountId, resourceId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDynamoDbBackupConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the DynamoDB backup configuration at the resource, account, or project level. Returns hierarchical fallback configuration if specific level not found.
   * Get DynamoDB backup configuration
   * @param projectId The project ID
   * @param accountId The cloud account ID for account-level configuration
   * @param resourceId The resource ID for resource-level configuration
   */
  public async getDynamoDbBackupConfig(projectId: string, accountId?: string, resourceId?: string, _options?: Configuration): Promise<DynamoDbBackupConfig> {
    const responseWithInfo = await this.getDynamoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
    return responseWithInfo.data;
  }


}


import { GetDynamoDbBackupConfigApplicationApiRequestFactory, GetDynamoDbBackupConfigApplicationApiResponseProcessor} from "../apis/GetDynamoDbBackupConfigApplicationApi";

export class PromiseGetDynamoDbBackupConfigApplicationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetDynamoDbBackupConfigApplicationApiRequestFactory = new GetDynamoDbBackupConfigApplicationApiRequestFactory(configuration),
    private responseProcessor: GetDynamoDbBackupConfigApplicationApiResponseProcessor = new GetDynamoDbBackupConfigApplicationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the DynamoDB backup configuration application details for a specific resource, including calculated read capacity requirements and autoscale settings.
   * Get DynamoDB backup configuration application details
   * @param projectId The project ID
   * @param id The resource ID
   */
  public async getDynamoDbBackupConfigApplicationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<DynamoDbBackupConfigApplication>> {
    let requestContextValue = await this.requestFactory.getDynamoDbBackupConfigApplication(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getDynamoDbBackupConfigApplicationWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the DynamoDB backup configuration application details for a specific resource, including calculated read capacity requirements and autoscale settings.
   * Get DynamoDB backup configuration application details
   * @param projectId The project ID
   * @param id The resource ID
   */
  public async getDynamoDbBackupConfigApplication(projectId: string, id: string, _options?: Configuration): Promise<DynamoDbBackupConfigApplication> {
    const responseWithInfo = await this.getDynamoDbBackupConfigApplicationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { GetInfectedDataByClassificationApiRequestFactory, GetInfectedDataByClassificationApiResponseProcessor} from "../apis/GetInfectedDataByClassificationApi";

export class PromiseGetInfectedDataByClassificationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetInfectedDataByClassificationApiRequestFactory = new GetInfectedDataByClassificationApiRequestFactory(configuration),
    private responseProcessor: GetInfectedDataByClassificationApiResponseProcessor = new GetInfectedDataByClassificationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch the infected resources size totals per data class for a specified project.
   * Get the infected resources size by data class
   * @param projectId The project ID
   * @param prod Filter data on production resources only, default is false
   * @param providers Filter data on specified cloud providers only, default is all
   */
  public async getInfectedDataByClassificationWithHttpInfo(projectId: string, prod?: boolean, providers?: Array<Provider>, _options?: Configuration): Promise<HttpInfo<InfectedDataByClassification>> {
    let requestContextValue = await this.requestFactory.getInfectedDataByClassification(projectId, prod, providers, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getInfectedDataByClassificationWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch the infected resources size totals per data class for a specified project.
   * Get the infected resources size by data class
   * @param projectId The project ID
   * @param prod Filter data on production resources only, default is false
   * @param providers Filter data on specified cloud providers only, default is all
   */
  public async getInfectedDataByClassification(projectId: string, prod?: boolean, providers?: Array<Provider>, _options?: Configuration): Promise<InfectedDataByClassification> {
    const responseWithInfo = await this.getInfectedDataByClassificationWithHttpInfo(projectId, prod, providers, _options);
    return responseWithInfo.data;
  }


}


import { GetInfectedDataOverTimeApiRequestFactory, GetInfectedDataOverTimeApiResponseProcessor} from "../apis/GetInfectedDataOverTimeApi";

export class PromiseGetInfectedDataOverTimeApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetInfectedDataOverTimeApiRequestFactory = new GetInfectedDataOverTimeApiRequestFactory(configuration),
    private responseProcessor: GetInfectedDataOverTimeApiResponseProcessor = new GetInfectedDataOverTimeApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch the infected resources size totals over time for a specified project.
   * Get the infected resources size history
   * @param projectId The project ID
   * @param days Number of days to fetch data for
   * @param prod Filter data on production resources only, default is false
   * @param providers Filter data on specified cloud providers only, default is all
   */
  public async getInfectedDataOverTimeWithHttpInfo(projectId: string, days: number, prod?: boolean, providers?: Array<Provider>, _options?: Configuration): Promise<HttpInfo<InfectedDataOverTime>> {
    let requestContextValue = await this.requestFactory.getInfectedDataOverTime(projectId, days, prod, providers, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getInfectedDataOverTimeWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch the infected resources size totals over time for a specified project.
   * Get the infected resources size history
   * @param projectId The project ID
   * @param days Number of days to fetch data for
   * @param prod Filter data on production resources only, default is false
   * @param providers Filter data on specified cloud providers only, default is all
   */
  public async getInfectedDataOverTime(projectId: string, days: number, prod?: boolean, providers?: Array<Provider>, _options?: Configuration): Promise<InfectedDataOverTime> {
    const responseWithInfo = await this.getInfectedDataOverTimeWithHttpInfo(projectId, days, prod, providers, _options);
    return responseWithInfo.data;
  }


}


import { GetObjectStoreExclusionRuleApiRequestFactory, GetObjectStoreExclusionRuleApiResponseProcessor} from "../apis/GetObjectStoreExclusionRuleApi";

export class PromiseGetObjectStoreExclusionRuleApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetObjectStoreExclusionRuleApiRequestFactory = new GetObjectStoreExclusionRuleApiRequestFactory(configuration),
    private responseProcessor: GetObjectStoreExclusionRuleApiResponseProcessor = new GetObjectStoreExclusionRuleApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve details of a specific object store exclusion rule by its ID within a given project.
   * Get object store exclusion rule details
   * @param id ID of the rule to retrieve
   * @param projectId The project ID
   */
  public async getObjectStoreExclusionRuleWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ObjectStoreExclusionRule>> {
    let requestContextValue = await this.requestFactory.getObjectStoreExclusionRule(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getObjectStoreExclusionRuleWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve details of a specific object store exclusion rule by its ID within a given project.
   * Get object store exclusion rule details
   * @param id ID of the rule to retrieve
   * @param projectId The project ID
   */
  public async getObjectStoreExclusionRule(id: string, projectId: string, _options?: Configuration): Promise<ObjectStoreExclusionRule> {
    const responseWithInfo = await this.getObjectStoreExclusionRuleWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetProjectApiRequestFactory, GetProjectApiResponseProcessor} from "../apis/GetProjectApi";

export class PromiseGetProjectApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetProjectApiRequestFactory = new GetProjectApiRequestFactory(configuration),
    private responseProcessor: GetProjectApiResponseProcessor = new GetProjectApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve detailed information about a specific project using its unique project ID.
   * Get details of a project
   * @param projectId ID of the project
   */
  public async getProjectWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<Project>> {
    let requestContextValue = await this.requestFactory.getProject(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve detailed information about a specific project using its unique project ID.
   * Get details of a project
   * @param projectId ID of the project
   */
  public async getProject(projectId: string, _options?: Configuration): Promise<Project> {
    const responseWithInfo = await this.getProjectWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetProjectRestoreAccountApiRequestFactory, GetProjectRestoreAccountApiResponseProcessor} from "../apis/GetProjectRestoreAccountApi";

export class PromiseGetProjectRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetProjectRestoreAccountApiRequestFactory = new GetProjectRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: GetProjectRestoreAccountApiResponseProcessor = new GetProjectRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the details of a specific restore account using its account ID and project ID.
   * Get details of a restore account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async getProjectRestoreAccountWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getProjectRestoreAccount(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the details of a specific restore account using its account ID and project ID.
   * Get details of a restore account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async getProjectRestoreAccount(accountId: string, projectId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getProjectRestoreAccountWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetProjectScanningAccountApiRequestFactory, GetProjectScanningAccountApiResponseProcessor} from "../apis/GetProjectScanningAccountApi";

export class PromiseGetProjectScanningAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetProjectScanningAccountApiRequestFactory = new GetProjectScanningAccountApiRequestFactory(configuration),
    private responseProcessor: GetProjectScanningAccountApiResponseProcessor = new GetProjectScanningAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch detailed information about a specified scanning account within a particular project.
   * Get details of a scanning account
   * @param accountId ID of the scanning account
   * @param projectId The project ID
   */
  public async getProjectScanningAccountWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getProjectScanningAccount(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectScanningAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch detailed information about a specified scanning account within a particular project.
   * Get details of a scanning account
   * @param accountId ID of the scanning account
   * @param projectId The project ID
   */
  public async getProjectScanningAccount(accountId: string, projectId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getProjectScanningAccountWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetProjectSourceAccountApiRequestFactory, GetProjectSourceAccountApiResponseProcessor} from "../apis/GetProjectSourceAccountApi";

export class PromiseGetProjectSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetProjectSourceAccountApiRequestFactory = new GetProjectSourceAccountApiRequestFactory(configuration),
    private responseProcessor: GetProjectSourceAccountApiResponseProcessor = new GetProjectSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the specified source account details for a given project using accountId and projectId.
   * Get details of a source account
   * @param accountId ID of the source account
   * @param projectId The project ID
   */
  public async getProjectSourceAccountWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getProjectSourceAccount(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the specified source account details for a given project using accountId and projectId.
   * Get details of a source account
   * @param accountId ID of the source account
   * @param projectId The project ID
   */
  public async getProjectSourceAccount(accountId: string, projectId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getProjectSourceAccountWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetProjectSourceAccountConfigurationApiRequestFactory, GetProjectSourceAccountConfigurationApiResponseProcessor} from "../apis/GetProjectSourceAccountConfigurationApi";

export class PromiseGetProjectSourceAccountConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetProjectSourceAccountConfigurationApiRequestFactory = new GetProjectSourceAccountConfigurationApiRequestFactory(configuration),
    private responseProcessor: GetProjectSourceAccountConfigurationApiResponseProcessor = new GetProjectSourceAccountConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the specified source account configuration for a given project using accountId and projectId.
   * Get configuration of a source account
   * @param accountId ID of the source account
   * @param projectId The project ID
   */
  public async getProjectSourceAccountConfigurationWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<any>> {
    let requestContextValue = await this.requestFactory.getProjectSourceAccountConfiguration(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectSourceAccountConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the specified source account configuration for a given project using accountId and projectId.
   * Get configuration of a source account
   * @param accountId ID of the source account
   * @param projectId The project ID
   */
  public async getProjectSourceAccountConfiguration(accountId: string, projectId: string, _options?: Configuration): Promise<any> {
    const responseWithInfo = await this.getProjectSourceAccountConfigurationWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetProjectUserApiRequestFactory, GetProjectUserApiResponseProcessor} from "../apis/GetProjectUserApi";

export class PromiseGetProjectUserApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetProjectUserApiRequestFactory = new GetProjectUserApiRequestFactory(configuration),
    private responseProcessor: GetProjectUserApiResponseProcessor = new GetProjectUserApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch specific user details within a given project using user ID and project ID.
   * Get details of a user
   * @param id ID of the user
   * @param projectId The project ID
   */
  public async getProjectUserWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<User>> {
    let requestContextValue = await this.requestFactory.getProjectUser(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectUserWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch specific user details within a given project using user ID and project ID.
   * Get details of a user
   * @param id ID of the user
   * @param projectId The project ID
   */
  public async getProjectUser(id: string, projectId: string, _options?: Configuration): Promise<User> {
    const responseWithInfo = await this.getProjectUserWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetPulumiConfigurationApiRequestFactory, GetPulumiConfigurationApiResponseProcessor} from "../apis/GetPulumiConfigurationApi";

export class PromiseGetPulumiConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetPulumiConfigurationApiRequestFactory = new GetPulumiConfigurationApiRequestFactory(configuration),
    private responseProcessor: GetPulumiConfigurationApiResponseProcessor = new GetPulumiConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get Pulumi configuration by id
   * @param id 
   */
  public async getPulumiConfigurationWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<PulumiConfiguration>> {
    let requestContextValue = await this.requestFactory.getPulumiConfiguration(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getPulumiConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Get Pulumi configuration by id
   * @param id 
   */
  public async getPulumiConfiguration(id: string, _options?: Configuration): Promise<PulumiConfiguration> {
    const responseWithInfo = await this.getPulumiConfigurationWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }


}


import { GetResourceConfigurationApiRequestFactory, GetResourceConfigurationApiResponseProcessor} from "../apis/GetResourceConfigurationApi";

export class PromiseGetResourceConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetResourceConfigurationApiRequestFactory = new GetResourceConfigurationApiRequestFactory(configuration),
    private responseProcessor: GetResourceConfigurationApiResponseProcessor = new GetResourceConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the configuration for a specified inventory resource within a given project.
   * Get configuration for an inventory resource
   * @param id ID of the inventory resource
   * @param projectId The project ID
   */
  public async getResourceConfigurationWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<CloudResourceConfiguration>> {
    let requestContextValue = await this.requestFactory.getResourceConfiguration(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getResourceConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the configuration for a specified inventory resource within a given project.
   * Get configuration for an inventory resource
   * @param id ID of the inventory resource
   * @param projectId The project ID
   */
  public async getResourceConfiguration(id: string, projectId: string, _options?: Configuration): Promise<CloudResourceConfiguration> {
    const responseWithInfo = await this.getResourceConfigurationWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetRestoreAccountApiRequestFactory, GetRestoreAccountApiResponseProcessor} from "../apis/GetRestoreAccountApi";

export class PromiseGetRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetRestoreAccountApiRequestFactory = new GetRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: GetRestoreAccountApiResponseProcessor = new GetRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the information of a specific restore account using its accountId.
   * Get details of a restore account
   * @param accountId ID of the restore account
   */
  public async getRestoreAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getRestoreAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the information of a specific restore account using its accountId.
   * Get details of a restore account
   * @param accountId ID of the restore account
   */
  public async getRestoreAccount(accountId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getRestoreAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { GetRestoreCloudAccountTagsConfigurationApiRequestFactory, GetRestoreCloudAccountTagsConfigurationApiResponseProcessor} from "../apis/GetRestoreCloudAccountTagsConfigurationApi";

export class PromiseGetRestoreCloudAccountTagsConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetRestoreCloudAccountTagsConfigurationApiRequestFactory = new GetRestoreCloudAccountTagsConfigurationApiRequestFactory(configuration),
    private responseProcessor: GetRestoreCloudAccountTagsConfigurationApiResponseProcessor = new GetRestoreCloudAccountTagsConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the cloud account custom tags configuration of a specific restore account using its account ID and project ID.
   * Get the restore cloud account custom tags configuration
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async getRestoreCloudAccountTagsConfigurationWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<Array<CustomTagsInner>>> {
    let requestContextValue = await this.requestFactory.getRestoreCloudAccountTagsConfiguration(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRestoreCloudAccountTagsConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the cloud account custom tags configuration of a specific restore account using its account ID and project ID.
   * Get the restore cloud account custom tags configuration
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async getRestoreCloudAccountTagsConfiguration(accountId: string, projectId: string, _options?: Configuration): Promise<Array<CustomTagsInner>> {
    const responseWithInfo = await this.getRestoreCloudAccountTagsConfigurationWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetSamlIdentityProviderConfigApiRequestFactory, GetSamlIdentityProviderConfigApiResponseProcessor} from "../apis/GetSamlIdentityProviderConfigApi";

export class PromiseGetSamlIdentityProviderConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSamlIdentityProviderConfigApiRequestFactory = new GetSamlIdentityProviderConfigApiRequestFactory(configuration),
    private responseProcessor: GetSamlIdentityProviderConfigApiResponseProcessor = new GetSamlIdentityProviderConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches the configuration details for a specified SAML identity provider by its ID.
   * Get saml identity provider config
   * @param id The ID of the saml identity provider
   */
  public async getSamlIdentityProviderConfigWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<SamlIdentityProvider>> {
    let requestContextValue = await this.requestFactory.getSamlIdentityProviderConfig(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSamlIdentityProviderConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches the configuration details for a specified SAML identity provider by its ID.
   * Get saml identity provider config
   * @param id The ID of the saml identity provider
   */
  public async getSamlIdentityProviderConfig(id: string, _options?: Configuration): Promise<SamlIdentityProvider> {
    const responseWithInfo = await this.getSamlIdentityProviderConfigWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }


}


import { GetScanningAccountApiRequestFactory, GetScanningAccountApiResponseProcessor} from "../apis/GetScanningAccountApi";

export class PromiseGetScanningAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetScanningAccountApiRequestFactory = new GetScanningAccountApiRequestFactory(configuration),
    private responseProcessor: GetScanningAccountApiResponseProcessor = new GetScanningAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve detailed information about a specific scanning account using its account ID.
   * Get details of a scanning account
   * @param accountId ID of the scanning account
   */
  public async getScanningAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getScanningAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getScanningAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve detailed information about a specific scanning account using its account ID.
   * Get details of a scanning account
   * @param accountId ID of the scanning account
   */
  public async getScanningAccount(accountId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getScanningAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { GetSecurityPlanApiRequestFactory, GetSecurityPlanApiResponseProcessor} from "../apis/GetSecurityPlanApi";

export class PromiseGetSecurityPlanApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSecurityPlanApiRequestFactory = new GetSecurityPlanApiRequestFactory(configuration),
    private responseProcessor: GetSecurityPlanApiResponseProcessor = new GetSecurityPlanApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the security plan for the project ID.
   * Get security plan condition
   * @param projectId The project ID
   */
  public async getSecurityPlanWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<SecurityPlan>> {
    let requestContextValue = await this.requestFactory.getSecurityPlan(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSecurityPlanWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the security plan for the project ID.
   * Get security plan condition
   * @param projectId The project ID
   */
  public async getSecurityPlan(projectId: string, _options?: Configuration): Promise<SecurityPlan> {
    const responseWithInfo = await this.getSecurityPlanWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetSecuritySummaryApiRequestFactory, GetSecuritySummaryApiResponseProcessor} from "../apis/GetSecuritySummaryApi";

export class PromiseGetSecuritySummaryApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSecuritySummaryApiRequestFactory = new GetSecuritySummaryApiRequestFactory(configuration),
    private responseProcessor: GetSecuritySummaryApiResponseProcessor = new GetSecuritySummaryApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch the ransomware conclusions aggregation for a specified project.
   * Get the ransomware conclusions aggregation
   * @param projectId The project ID
   * @param prod Filter data on production resources only, default is false
   * @param providers Filter data on specified cloud providers only, default is all
   */
  public async getSecuritySummaryWithHttpInfo(projectId: string, prod?: boolean, providers?: Array<Provider>, _options?: Configuration): Promise<HttpInfo<SecuritySummary>> {
    let requestContextValue = await this.requestFactory.getSecuritySummary(projectId, prod, providers, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSecuritySummaryWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch the ransomware conclusions aggregation for a specified project.
   * Get the ransomware conclusions aggregation
   * @param projectId The project ID
   * @param prod Filter data on production resources only, default is false
   * @param providers Filter data on specified cloud providers only, default is all
   */
  public async getSecuritySummary(projectId: string, prod?: boolean, providers?: Array<Provider>, _options?: Configuration): Promise<SecuritySummary> {
    const responseWithInfo = await this.getSecuritySummaryWithHttpInfo(projectId, prod, providers, _options);
    return responseWithInfo.data;
  }


}


import { GetServiceProviderDetailsApiRequestFactory, GetServiceProviderDetailsApiResponseProcessor} from "../apis/GetServiceProviderDetailsApi";

export class PromiseGetServiceProviderDetailsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetServiceProviderDetailsApiRequestFactory = new GetServiceProviderDetailsApiRequestFactory(configuration),
    private responseProcessor: GetServiceProviderDetailsApiResponseProcessor = new GetServiceProviderDetailsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches details of a specified service provider, including options to filter by provider name.
   * Get service provider details
   * @param providerName The name of the identity provider
   */
  public async getServiceProviderDetailsWithHttpInfo(providerName?: string, _options?: Configuration): Promise<HttpInfo<ServiceProviderDetails>> {
    let requestContextValue = await this.requestFactory.getServiceProviderDetails(providerName, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getServiceProviderDetailsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches details of a specified service provider, including options to filter by provider name.
   * Get service provider details
   * @param providerName The name of the identity provider
   */
  public async getServiceProviderDetails(providerName?: string, _options?: Configuration): Promise<ServiceProviderDetails> {
    const responseWithInfo = await this.getServiceProviderDetailsWithHttpInfo(providerName, _options);
    return responseWithInfo.data;
  }


}


import { GetSnapshotsByServerApiRequestFactory, GetSnapshotsByServerApiResponseProcessor} from "../apis/GetSnapshotsByServerApi";

export class PromiseGetSnapshotsByServerApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSnapshotsByServerApiRequestFactory = new GetSnapshotsByServerApiRequestFactory(configuration),
    private responseProcessor: GetSnapshotsByServerApiResponseProcessor = new GetSnapshotsByServerApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation retrieves all snapshots associated with a specific database server within a given project. It requires the server ID, project ID, start date, and end date.
   * Get all snapshots for a database server
   * @param serverId ID of the database server
   * @param start The start date for the query
   * @param end The end date for the query
   * @param projectId The project ID
   */
  public async getSnapshotsByServerWithHttpInfo(serverId: string, start: Date, end: Date, projectId: string, _options?: Configuration): Promise<HttpInfo<ListDatabaseSnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.getSnapshotsByServer(serverId, start, end, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSnapshotsByServerWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation retrieves all snapshots associated with a specific database server within a given project. It requires the server ID, project ID, start date, and end date.
   * Get all snapshots for a database server
   * @param serverId ID of the database server
   * @param start The start date for the query
   * @param end The end date for the query
   * @param projectId The project ID
   */
  public async getSnapshotsByServer(serverId: string, start: Date, end: Date, projectId: string, _options?: Configuration): Promise<ListDatabaseSnapshotsResponse> {
    const responseWithInfo = await this.getSnapshotsByServerWithHttpInfo(serverId, start, end, projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetSnapshotsSummaryApiRequestFactory, GetSnapshotsSummaryApiResponseProcessor} from "../apis/GetSnapshotsSummaryApi";

export class PromiseGetSnapshotsSummaryApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSnapshotsSummaryApiRequestFactory = new GetSnapshotsSummaryApiRequestFactory(configuration),
    private responseProcessor: GetSnapshotsSummaryApiResponseProcessor = new GetSnapshotsSummaryApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a summary of snapshots for a specific resource, including total count, oldest/latest snapshot info, and counts grouped by vault.
   * Get snapshots summary for a resource
   * @param projectId The project ID
   * @param id ID of the inventory item (resource)
   */
  public async getSnapshotsSummaryWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<SnapshotsSummaryResponse>> {
    let requestContextValue = await this.requestFactory.getSnapshotsSummary(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSnapshotsSummaryWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a summary of snapshots for a specific resource, including total count, oldest/latest snapshot info, and counts grouped by vault.
   * Get snapshots summary for a resource
   * @param projectId The project ID
   * @param id ID of the inventory item (resource)
   */
  public async getSnapshotsSummary(projectId: string, id: string, _options?: Configuration): Promise<SnapshotsSummaryResponse> {
    const responseWithInfo = await this.getSnapshotsSummaryWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { GetSourceAccountApiRequestFactory, GetSourceAccountApiResponseProcessor} from "../apis/GetSourceAccountApi";

export class PromiseGetSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSourceAccountApiRequestFactory = new GetSourceAccountApiRequestFactory(configuration),
    private responseProcessor: GetSourceAccountApiResponseProcessor = new GetSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches detailed information for a specific source account identified by its account ID.
   * Get details of a source account
   * @param accountId ID of the source account
   */
  public async getSourceAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getSourceAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches detailed information for a specific source account identified by its account ID.
   * Get details of a source account
   * @param accountId ID of the source account
   */
  public async getSourceAccount(accountId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getSourceAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { GetSourceStorageBackupStatusApiRequestFactory, GetSourceStorageBackupStatusApiResponseProcessor} from "../apis/GetSourceStorageBackupStatusApi";

export class PromiseGetSourceStorageBackupStatusApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSourceStorageBackupStatusApiRequestFactory = new GetSourceStorageBackupStatusApiRequestFactory(configuration),
    private responseProcessor: GetSourceStorageBackupStatusApiResponseProcessor = new GetSourceStorageBackupStatusApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch the backup status of source storage for a specified project.
   * Get source storage backup status
   * @param projectId The project ID
   */
  public async getSourceStorageBackupStatusWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<SourceStorageBackupStatus>> {
    let requestContextValue = await this.requestFactory.getSourceStorageBackupStatus(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSourceStorageBackupStatusWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch the backup status of source storage for a specified project.
   * Get source storage backup status
   * @param projectId The project ID
   */
  public async getSourceStorageBackupStatus(projectId: string, _options?: Configuration): Promise<SourceStorageBackupStatus> {
    const responseWithInfo = await this.getSourceStorageBackupStatusWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { GetSqlVmNetworkInfoApiRequestFactory, GetSqlVmNetworkInfoApiResponseProcessor} from "../apis/GetSqlVmNetworkInfoApi";

export class PromiseGetSqlVmNetworkInfoApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetSqlVmNetworkInfoApiRequestFactory = new GetSqlVmNetworkInfoApiRequestFactory(configuration),
    private responseProcessor: GetSqlVmNetworkInfoApiResponseProcessor = new GetSqlVmNetworkInfoApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves network topology information for an Azure SQL VM, including network interface, subnet, and virtual network details. This information is used for configuring storage account network security rules for SQL VM Point-in-Time Restore (PiTR) backups.
   * Get network information for an Azure SQL VM
   * @param projectId The project ID
   * @param getSqlVmNetworkInfoRequest The request body for getting SQL VM network information
   */
  public async getSqlVmNetworkInfoWithHttpInfo(projectId: string, getSqlVmNetworkInfoRequest: GetSqlVmNetworkInfoRequest, _options?: Configuration): Promise<HttpInfo<GetSqlVmNetworkInfoResponse>> {
    let requestContextValue = await this.requestFactory.getSqlVmNetworkInfo(projectId, getSqlVmNetworkInfoRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSqlVmNetworkInfoWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves network topology information for an Azure SQL VM, including network interface, subnet, and virtual network details. This information is used for configuring storage account network security rules for SQL VM Point-in-Time Restore (PiTR) backups.
   * Get network information for an Azure SQL VM
   * @param projectId The project ID
   * @param getSqlVmNetworkInfoRequest The request body for getting SQL VM network information
   */
  public async getSqlVmNetworkInfo(projectId: string, getSqlVmNetworkInfoRequest: GetSqlVmNetworkInfoRequest, _options?: Configuration): Promise<GetSqlVmNetworkInfoResponse> {
    const responseWithInfo = await this.getSqlVmNetworkInfoWithHttpInfo(projectId, getSqlVmNetworkInfoRequest, _options);
    return responseWithInfo.data;
  }


}


import { GetUserApiRequestFactory, GetUserApiResponseProcessor} from "../apis/GetUserApi";

export class PromiseGetUserApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetUserApiRequestFactory = new GetUserApiRequestFactory(configuration),
    private responseProcessor: GetUserApiResponseProcessor = new GetUserApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches detailed information about a user given their unique ID.
   * Get details of a user
   * @param id ID of the user
   */
  public async getUserWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<User>> {
    let requestContextValue = await this.requestFactory.getUser(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getUserWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches detailed information about a user given their unique ID.
   * Get details of a user
   * @param id ID of the user
   */
  public async getUser(id: string, _options?: Configuration): Promise<User> {
    const responseWithInfo = await this.getUserWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }


}


import { GetVaultAccountApiRequestFactory, GetVaultAccountApiResponseProcessor} from "../apis/GetVaultAccountApi";

export class PromiseGetVaultAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetVaultAccountApiRequestFactory = new GetVaultAccountApiRequestFactory(configuration),
    private responseProcessor: GetVaultAccountApiResponseProcessor = new GetVaultAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch details of a vault account by providing the account ID. This operation requires administrative permissions and returns account information if the account exists.
   * Get details of a vault account
   * @param accountId ID of the vault account
   */
  public async getVaultAccountWithHttpInfo(accountId: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.getVaultAccount(accountId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch details of a vault account by providing the account ID. This operation requires administrative permissions and returns account information if the account exists.
   * Get details of a vault account
   * @param accountId ID of the vault account
   */
  public async getVaultAccount(accountId: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.getVaultAccountWithHttpInfo(accountId, _options);
    return responseWithInfo.data;
  }


}


import { GetViewerApiRequestFactory, GetViewerApiResponseProcessor} from "../apis/GetViewerApi";

export class PromiseGetViewerApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetViewerApiRequestFactory = new GetViewerApiRequestFactory(configuration),
    private responseProcessor: GetViewerApiResponseProcessor = new GetViewerApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve detailed information about the viewer.
   * Get viewer info
   */
  public async getViewerWithHttpInfo(_options?: Configuration): Promise<HttpInfo<Viewer>> {
    let requestContextValue = await this.requestFactory.getViewer(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getViewerWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve detailed information about the viewer.
   * Get viewer info
   */
  public async getViewer(_options?: Configuration): Promise<Viewer> {
    const responseWithInfo = await this.getViewerWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { GetViewerUIPreferencesApiRequestFactory, GetViewerUIPreferencesApiResponseProcessor} from "../apis/GetViewerUIPreferencesApi";

export class PromiseGetViewerUIPreferencesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: GetViewerUIPreferencesApiRequestFactory = new GetViewerUIPreferencesApiRequestFactory(configuration),
    private responseProcessor: GetViewerUIPreferencesApiResponseProcessor = new GetViewerUIPreferencesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the user\'s viewer interface preferences based on the provided key.
   * Get viewer UI preferences.
   * @param key Key of the preference
   */
  public async getViewerUIPreferencesWithHttpInfo(key: string, _options?: Configuration): Promise<HttpInfo<Preferences>> {
    let requestContextValue = await this.requestFactory.getViewerUIPreferences(key, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getViewerUIPreferencesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the user\'s viewer interface preferences based on the provided key.
   * Get viewer UI preferences.
   * @param key Key of the preference
   */
  public async getViewerUIPreferences(key: string, _options?: Configuration): Promise<Preferences> {
    const responseWithInfo = await this.getViewerUIPreferencesWithHttpInfo(key, _options);
    return responseWithInfo.data;
  }


}


import { IamApiRequestFactory, IamApiResponseProcessor} from "../apis/IamApi";

export class PromiseIamApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: IamApiRequestFactory = new IamApiRequestFactory(configuration),
    private responseProcessor: IamApiResponseProcessor = new IamApiResponseProcessor() 
  ) {
    
  }

  /**
   * Create a new role
   * @param createRoleRequest 
   */
  public async createRoleWithHttpInfo(createRoleRequest?: CreateRoleRequest, _options?: Configuration): Promise<HttpInfo<CreateRoleResponse>> {
    let requestContextValue = await this.requestFactory.createRole(createRoleRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createRoleWithHttpInfo(responseContext)
  }

  /**
   * Create a new role
   * @param createRoleRequest 
   */
  public async createRole(createRoleRequest?: CreateRoleRequest, _options?: Configuration): Promise<CreateRoleResponse> {
    const responseWithInfo = await this.createRoleWithHttpInfo(createRoleRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API operation deletes a role using its unique ID. 
   * Delete a role
   * @param roleId ID of the role
   */
  public async deleteRoleWithHttpInfo(roleId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteRole(roleId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteRoleWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation deletes a role using its unique ID. 
   * Delete a role
   * @param roleId ID of the role
   */
  public async deleteRole(roleId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteRoleWithHttpInfo(roleId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Fetches specific details of a role using its unique ID.
   * Get details of a role
   * @param roleId ID of the role
   */
  public async getRoleWithHttpInfo(roleId: string, _options?: Configuration): Promise<HttpInfo<GetRoleResponse>> {
    let requestContextValue = await this.requestFactory.getRole(roleId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRoleWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches specific details of a role using its unique ID.
   * Get details of a role
   * @param roleId ID of the role
   */
  public async getRole(roleId: string, _options?: Configuration): Promise<GetRoleResponse> {
    const responseWithInfo = await this.getRoleWithHttpInfo(roleId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Fetches and retrieves a list of all user roles.
   * Summary of all permissions
   */
  public async listPermissionsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListPermissionsResponse>> {
    let requestContextValue = await this.requestFactory.listPermissions(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listPermissionsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches and retrieves a list of all user roles.
   * Summary of all permissions
   */
  public async listPermissions(_options?: Configuration): Promise<ListPermissionsResponse> {
    const responseWithInfo = await this.listPermissionsWithHttpInfo(_options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of all user roles.
   * List Roles
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listRolesWithHttpInfo(pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListRolesResponse>> {
    let requestContextValue = await this.requestFactory.listRoles(pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRolesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all user roles.
   * List Roles
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listRoles(pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListRolesResponse> {
    const responseWithInfo = await this.listRolesWithHttpInfo(pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API operation updates the details of a role using its unique ID. The request body must contain the updated role details.
   * Update a role
   * @param roleId ID of the role
   * @param updateRoleRequest The request body for updating a user
   */
  public async updateRoleWithHttpInfo(roleId: string, updateRoleRequest: UpdateRoleRequest, _options?: Configuration): Promise<HttpInfo<UpdateRoleResponse>> {
    let requestContextValue = await this.requestFactory.updateRole(roleId, updateRoleRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateRoleWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the details of a role using its unique ID. The request body must contain the updated role details.
   * Update a role
   * @param roleId ID of the role
   * @param updateRoleRequest The request body for updating a user
   */
  public async updateRole(roleId: string, updateRoleRequest: UpdateRoleRequest, _options?: Configuration): Promise<UpdateRoleResponse> {
    const responseWithInfo = await this.updateRoleWithHttpInfo(roleId, updateRoleRequest, _options);
    return responseWithInfo.data;
  }


}


import { InitiateAuthApiRequestFactory, InitiateAuthApiResponseProcessor} from "../apis/InitiateAuthApi";

export class PromiseInitiateAuthApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: InitiateAuthApiRequestFactory = new InitiateAuthApiRequestFactory(configuration),
    private responseProcessor: InitiateAuthApiResponseProcessor = new InitiateAuthApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation initiates the authentication process by accepting authentication parameters and returning an authentication response.
   * InitiateAuth
   * @param initAuthInput Auth parameters
   */
  public async initiateAuthWithHttpInfo(initAuthInput: InitAuthInput, _options?: Configuration): Promise<HttpInfo<AuthResponse>> {
    let requestContextValue = await this.requestFactory.initiateAuth(initAuthInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.initiateAuthWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation initiates the authentication process by accepting authentication parameters and returning an authentication response.
   * InitiateAuth
   * @param initAuthInput Auth parameters
   */
  public async initiateAuth(initAuthInput: InitAuthInput, _options?: Configuration): Promise<AuthResponse> {
    const responseWithInfo = await this.initiateAuthWithHttpInfo(initAuthInput, _options);
    return responseWithInfo.data;
  }


}


import { IntegrationsApiRequestFactory, IntegrationsApiResponseProcessor} from "../apis/IntegrationsApi";

export class PromiseIntegrationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: IntegrationsApiRequestFactory = new IntegrationsApiRequestFactory(configuration),
    private responseProcessor: IntegrationsApiResponseProcessor = new IntegrationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a new integration in the specified project.
   * Create Integration
   * @param projectId The project ID
   * @param createIntegrationRequest 
   */
  public async createIntegrationWithHttpInfo(projectId: string, createIntegrationRequest: CreateIntegrationRequest, _options?: Configuration): Promise<HttpInfo<CreateIntegrationResponse>> {
    let requestContextValue = await this.requestFactory.createIntegration(projectId, createIntegrationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createIntegrationWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a new integration in the specified project.
   * Create Integration
   * @param projectId The project ID
   * @param createIntegrationRequest 
   */
  public async createIntegration(projectId: string, createIntegrationRequest: CreateIntegrationRequest, _options?: Configuration): Promise<CreateIntegrationResponse> {
    const responseWithInfo = await this.createIntegrationWithHttpInfo(projectId, createIntegrationRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Removes the integration and cleans up stored procedures and views.
   * Remove Integration
   * @param projectId The project ID
   * @param id The integration ID
   */
  public async deleteIntegrationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteIntegration(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteIntegrationWithHttpInfo(responseContext)
  }

  /**
   * Description: Removes the integration and cleans up stored procedures and views.
   * Remove Integration
   * @param projectId The project ID
   * @param id The integration ID
   */
  public async deleteIntegration(projectId: string, id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteIntegrationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a single integration details by ID.
   * Get Integration
   * @param projectId The project ID
   * @param id The integration ID
   */
  public async getIntegrationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<GetIntegrationResponse>> {
    let requestContextValue = await this.requestFactory.getIntegration(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getIntegrationWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a single integration details by ID.
   * Get Integration
   * @param projectId The project ID
   * @param id The integration ID
   */
  public async getIntegration(projectId: string, id: string, _options?: Configuration): Promise<GetIntegrationResponse> {
    const responseWithInfo = await this.getIntegrationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves all integrations for the specified project.
   * List Integrations
   * @param projectId The project ID
   */
  public async listIntegrationsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListIntegrationsResponse>> {
    let requestContextValue = await this.requestFactory.listIntegrations(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listIntegrationsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves all integrations for the specified project.
   * List Integrations
   * @param projectId The project ID
   */
  public async listIntegrations(projectId: string, _options?: Configuration): Promise<ListIntegrationsResponse> {
    const responseWithInfo = await this.listIntegrationsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Manually triggers the stored procedure execution for all resources in scope.
   * Force Sync
   * @param projectId The project ID
   * @param id The integration ID
   */
  public async syncIntegrationWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.syncIntegration(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.syncIntegrationWithHttpInfo(responseContext)
  }

  /**
   * Description: Manually triggers the stored procedure execution for all resources in scope.
   * Force Sync
   * @param projectId The project ID
   * @param id The integration ID
   */
  public async syncIntegration(projectId: string, id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.syncIntegrationWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates the integration\'s condition and manual IDs. Triggers a sync for new resources and a cleanup for removed ones. 
   * Update Integration
   * @param projectId The project ID
   * @param id The integration ID
   * @param updateIntegrationRequest 
   */
  public async updateIntegrationWithHttpInfo(projectId: string, id: string, updateIntegrationRequest: UpdateIntegrationRequest, _options?: Configuration): Promise<HttpInfo<UpdateIntegrationResponse>> {
    let requestContextValue = await this.requestFactory.updateIntegration(projectId, id, updateIntegrationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateIntegrationWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates the integration\'s condition and manual IDs. Triggers a sync for new resources and a cleanup for removed ones. 
   * Update Integration
   * @param projectId The project ID
   * @param id The integration ID
   * @param updateIntegrationRequest 
   */
  public async updateIntegration(projectId: string, id: string, updateIntegrationRequest: UpdateIntegrationRequest, _options?: Configuration): Promise<UpdateIntegrationResponse> {
    const responseWithInfo = await this.updateIntegrationWithHttpInfo(projectId, id, updateIntegrationRequest, _options);
    return responseWithInfo.data;
  }


}


import { InventoryPopulateApiRequestFactory, InventoryPopulateApiResponseProcessor} from "../apis/InventoryPopulateApi";

export class PromiseInventoryPopulateApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: InventoryPopulateApiRequestFactory = new InventoryPopulateApiRequestFactory(configuration),
    private responseProcessor: InventoryPopulateApiResponseProcessor = new InventoryPopulateApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API populates test data for a given project based on the specified version number.
   * Populate test data
   * @param version the version number of the data to populate
   * @param projectId The project ID
   */
  public async inventoryPopulateWithHttpInfo(version: number, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.inventoryPopulate(version, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.inventoryPopulateWithHttpInfo(responseContext)
  }

  /**
   * Description: This API populates test data for a given project based on the specified version number.
   * Populate test data
   * @param version the version number of the data to populate
   * @param projectId The project ID
   */
  public async inventoryPopulate(version: number, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.inventoryPopulateWithHttpInfo(version, projectId, _options);
    return responseWithInfo.data;
  }


}


import { IsSecurityPlanEnabledApiRequestFactory, IsSecurityPlanEnabledApiResponseProcessor} from "../apis/IsSecurityPlanEnabledApi";

export class PromiseIsSecurityPlanEnabledApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: IsSecurityPlanEnabledApiRequestFactory = new IsSecurityPlanEnabledApiRequestFactory(configuration),
    private responseProcessor: IsSecurityPlanEnabledApiResponseProcessor = new IsSecurityPlanEnabledApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the security plan enabled status for the project ID.
   * Get security plan enabled status
   * @param projectId The project ID
   */
  public async isSecurityPlanEnabledWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<SecurityPlanEnabledResponse>> {
    let requestContextValue = await this.requestFactory.isSecurityPlanEnabled(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.isSecurityPlanEnabledWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the security plan enabled status for the project ID.
   * Get security plan enabled status
   * @param projectId The project ID
   */
  public async isSecurityPlanEnabled(projectId: string, _options?: Configuration): Promise<SecurityPlanEnabledResponse> {
    const responseWithInfo = await this.isSecurityPlanEnabledWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { JobsApiRequestFactory, JobsApiResponseProcessor} from "../apis/JobsApi";

export class PromiseJobsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: JobsApiRequestFactory = new JobsApiRequestFactory(configuration),
    private responseProcessor: JobsApiResponseProcessor = new JobsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a restore job by ID.
   * Cancel Restore Job Internal
   * @param jobId ID of the job to cancel.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async cancelRestoreJobWithHttpInfo(jobId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.cancelRestoreJob(jobId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.cancelRestoreJobWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a restore job by ID.
   * Cancel Restore Job Internal
   * @param jobId ID of the job to cancel.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async cancelRestoreJob(jobId: string, projectId: string, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.cancelRestoreJobWithHttpInfo(jobId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a backup job by ID.
   * Get Backup Job
   * @param jobId ID of the job to retrieve.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getBackupJobWithHttpInfo(jobId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetBackupJobResponse>> {
    let requestContextValue = await this.requestFactory.getBackupJob(jobId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getBackupJobWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a backup job by ID.
   * Get Backup Job
   * @param jobId ID of the job to retrieve.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getBackupJob(jobId: string, projectId: string, _options?: Configuration): Promise<GetBackupJobResponse> {
    const responseWithInfo = await this.getBackupJobWithHttpInfo(jobId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a restore job by ID.
   * Get Restore Job
   * @param jobId ID of the job to retrieve.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getRestoreJobWithHttpInfo(jobId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetRestoreJobResponse>> {
    let requestContextValue = await this.requestFactory.getRestoreJob(jobId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRestoreJobWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a restore job by ID.
   * Get Restore Job
   * @param jobId ID of the job to retrieve.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getRestoreJob(jobId: string, projectId: string, _options?: Configuration): Promise<GetRestoreJobResponse> {
    const responseWithInfo = await this.getRestoreJobWithHttpInfo(jobId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a scan job by ID.
   * Get Scan Job
   * @param jobId ID of the job to retrieve.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getScanJobWithHttpInfo(jobId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetScanJobResponse>> {
    let requestContextValue = await this.requestFactory.getScanJob(jobId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getScanJobWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a scan job by ID.
   * Get Scan Job
   * @param jobId ID of the job to retrieve.
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getScanJob(jobId: string, projectId: string, _options?: Configuration): Promise<GetScanJobResponse> {
    const responseWithInfo = await this.getScanJobWithHttpInfo(jobId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of backup jobs for the given project.
   * List Backup Jobs
   * @param projectId ID of the project whose restore jobs you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listBackupJobsRequest Filter and sort options.
   */
  public async listBackupJobsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, listBackupJobsRequest?: ListBackupJobsRequest, _options?: Configuration): Promise<HttpInfo<ListBackupJobsResponse>> {
    let requestContextValue = await this.requestFactory.listBackupJobs(projectId, pageToken, pageSize, listBackupJobsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listBackupJobsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of backup jobs for the given project.
   * List Backup Jobs
   * @param projectId ID of the project whose restore jobs you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listBackupJobsRequest Filter and sort options.
   */
  public async listBackupJobs(projectId: string, pageToken?: string, pageSize?: number, listBackupJobsRequest?: ListBackupJobsRequest, _options?: Configuration): Promise<ListBackupJobsResponse> {
    const responseWithInfo = await this.listBackupJobsWithHttpInfo(projectId, pageToken, pageSize, listBackupJobsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Fetches backup jobs for a given project ID, with pagination and sorting options.
   * List Backup Jobs Internal
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listBackupJobsInternalWithHttpInfo(projectId: string, pageIndex: number, pageSize: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListBackupJobsInternal200Response>> {
    let requestContextValue = await this.requestFactory.listBackupJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listBackupJobsInternalWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches backup jobs for a given project ID, with pagination and sorting options.
   * List Backup Jobs Internal
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listBackupJobsInternal(projectId: string, pageIndex: number, pageSize: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListBackupJobsInternal200Response> {
    const responseWithInfo = await this.listBackupJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of restore jobs for the given project. 
   * List Restore Jobs
   * @param projectId ID of the project whose restore jobs you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listRestoreJobsRequest Filter and sort options. 
   */
  public async listRestoreJobsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, listRestoreJobsRequest?: ListRestoreJobsRequest, _options?: Configuration): Promise<HttpInfo<ListRestoreJobsResponse>> {
    let requestContextValue = await this.requestFactory.listRestoreJobs(projectId, pageToken, pageSize, listRestoreJobsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRestoreJobsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of restore jobs for the given project. 
   * List Restore Jobs
   * @param projectId ID of the project whose restore jobs you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listRestoreJobsRequest Filter and sort options. 
   */
  public async listRestoreJobs(projectId: string, pageToken?: string, pageSize?: number, listRestoreJobsRequest?: ListRestoreJobsRequest, _options?: Configuration): Promise<ListRestoreJobsResponse> {
    const responseWithInfo = await this.listRestoreJobsWithHttpInfo(projectId, pageToken, pageSize, listRestoreJobsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Fetches restore jobs for a given project ID, with pagination and sorting options.
   * List Restore Jobs Internal
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listRestoreJobsInternalWithHttpInfo(projectId: string, pageIndex: number, pageSize: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListRestoreJobsInternal200Response>> {
    let requestContextValue = await this.requestFactory.listRestoreJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRestoreJobsInternalWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches restore jobs for a given project ID, with pagination and sorting options.
   * List Restore Jobs Internal
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listRestoreJobsInternal(projectId: string, pageIndex: number, pageSize: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListRestoreJobsInternal200Response> {
    const responseWithInfo = await this.listRestoreJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Fetches backup jobs for a given project ID, with pagination and sorting options.
   * List Scan Jobs
   * @param projectId The project ID
   * @param pageToken The token for the next page
   * @param pageSize The numbers of items per page
   * @param listScanJobsRequest The request body for filtering jobs
   */
  public async listScanJobsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, listScanJobsRequest?: ListScanJobsRequest, _options?: Configuration): Promise<HttpInfo<ListScanJobsResponse>> {
    let requestContextValue = await this.requestFactory.listScanJobs(projectId, pageToken, pageSize, listScanJobsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listScanJobsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches backup jobs for a given project ID, with pagination and sorting options.
   * List Scan Jobs
   * @param projectId The project ID
   * @param pageToken The token for the next page
   * @param pageSize The numbers of items per page
   * @param listScanJobsRequest The request body for filtering jobs
   */
  public async listScanJobs(projectId: string, pageToken?: string, pageSize?: number, listScanJobsRequest?: ListScanJobsRequest, _options?: Configuration): Promise<ListScanJobsResponse> {
    const responseWithInfo = await this.listScanJobsWithHttpInfo(projectId, pageToken, pageSize, listScanJobsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Fetches scan jobs for a given project ID, with pagination and sorting options.
   * List Scan Jobs Internal
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listScanJobsInternalWithHttpInfo(projectId: string, pageIndex: number, pageSize: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListScanJobsInternal200Response>> {
    let requestContextValue = await this.requestFactory.listScanJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listScanJobsInternalWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches scan jobs for a given project ID, with pagination and sorting options.
   * List Scan Jobs Internal
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listScanJobsInternal(projectId: string, pageIndex: number, pageSize: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListScanJobsInternal200Response> {
    const responseWithInfo = await this.listScanJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }


}


import { LimitsApiRequestFactory, LimitsApiResponseProcessor} from "../apis/LimitsApi";

export class PromiseLimitsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: LimitsApiRequestFactory = new LimitsApiRequestFactory(configuration),
    private responseProcessor: LimitsApiResponseProcessor = new LimitsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Disables the override status for a resource 
   * Disable Limits Override for a Resource
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to enable from limit exceeded status for.
   */
  public async disableResourceOverrideLimitsWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<DisableResourceOverrideLimitsResponse>> {
    let requestContextValue = await this.requestFactory.disableResourceOverrideLimits(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.disableResourceOverrideLimitsWithHttpInfo(responseContext)
  }

  /**
   * Description: Disables the override status for a resource 
   * Disable Limits Override for a Resource
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to enable from limit exceeded status for.
   */
  public async disableResourceOverrideLimits(projectId: string, id: string, _options?: Configuration): Promise<DisableResourceOverrideLimitsResponse> {
    const responseWithInfo = await this.disableResourceOverrideLimitsWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Enables the override status for a resource 
   * Enable Limits Override for a Resource
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to enable from limit exceeded status for.
   */
  public async enableResourceOverrideLimitsWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<EnableResourceOverrideLimitsResponse>> {
    let requestContextValue = await this.requestFactory.enableResourceOverrideLimits(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.enableResourceOverrideLimitsWithHttpInfo(responseContext)
  }

  /**
   * Description: Enables the override status for a resource 
   * Enable Limits Override for a Resource
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to enable from limit exceeded status for.
   */
  public async enableResourceOverrideLimits(projectId: string, id: string, _options?: Configuration): Promise<EnableResourceOverrideLimitsResponse> {
    const responseWithInfo = await this.enableResourceOverrideLimitsWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves the limits of a project by ID.
   * Get Project Limits
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getProjectLimitsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<{ [key: string]: GetLimitsResponseValue; }>> {
    let requestContextValue = await this.requestFactory.getProjectLimits(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectLimitsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the limits of a project by ID.
   * Get Project Limits
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getProjectLimits(projectId: string, _options?: Configuration): Promise<{ [key: string]: GetLimitsResponseValue; }> {
    const responseWithInfo = await this.getProjectLimitsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Sets the limits of a project by ID.
   * Set Project Limits
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param requestBody 
   */
  public async setProjectLimitsWithHttpInfo(projectId: string, requestBody: { [key: string]: LimitData; }, _options?: Configuration): Promise<HttpInfo<{ [key: string]: GetLimitsResponseValue; }>> {
    let requestContextValue = await this.requestFactory.setProjectLimits(projectId, requestBody, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.setProjectLimitsWithHttpInfo(responseContext)
  }

  /**
   * Description: Sets the limits of a project by ID.
   * Set Project Limits
   * @param projectId ID of the project the job is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param requestBody 
   */
  public async setProjectLimits(projectId: string, requestBody: { [key: string]: LimitData; }, _options?: Configuration): Promise<{ [key: string]: GetLimitsResponseValue; }> {
    const responseWithInfo = await this.setProjectLimitsWithHttpInfo(projectId, requestBody, _options);
    return responseWithInfo.data;
  }


}


import { ListAllPulumiConfigurationsApiRequestFactory, ListAllPulumiConfigurationsApiResponseProcessor} from "../apis/ListAllPulumiConfigurationsApi";

export class PromiseListAllPulumiConfigurationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAllPulumiConfigurationsApiRequestFactory = new ListAllPulumiConfigurationsApiRequestFactory(configuration),
    private responseProcessor: ListAllPulumiConfigurationsApiResponseProcessor = new ListAllPulumiConfigurationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * List All Pulumi configurations
   * @param pageIndex 
   * @param pageSize 
   * @param sort 
   * @param pulumiConfigurationsRequest Filter options. 
   */
  public async listAllPulumiConfigurationsWithHttpInfo(pageIndex?: number, pageSize?: number, sort?: string, pulumiConfigurationsRequest?: PulumiConfigurationsRequest, _options?: Configuration): Promise<HttpInfo<PulumiConfigurations>> {
    let requestContextValue = await this.requestFactory.listAllPulumiConfigurations(pageIndex, pageSize, sort, pulumiConfigurationsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAllPulumiConfigurationsWithHttpInfo(responseContext)
  }

  /**
   * List All Pulumi configurations
   * @param pageIndex 
   * @param pageSize 
   * @param sort 
   * @param pulumiConfigurationsRequest Filter options. 
   */
  public async listAllPulumiConfigurations(pageIndex?: number, pageSize?: number, sort?: string, pulumiConfigurationsRequest?: PulumiConfigurationsRequest, _options?: Configuration): Promise<PulumiConfigurations> {
    const responseWithInfo = await this.listAllPulumiConfigurationsWithHttpInfo(pageIndex, pageSize, sort, pulumiConfigurationsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ListAllRestoreAccountsApiRequestFactory, ListAllRestoreAccountsApiResponseProcessor} from "../apis/ListAllRestoreAccountsApi";

export class PromiseListAllRestoreAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAllRestoreAccountsApiRequestFactory = new ListAllRestoreAccountsApiRequestFactory(configuration),
    private responseProcessor: ListAllRestoreAccountsApiResponseProcessor = new ListAllRestoreAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows administrators to retrieve a list of all restore accounts.
   * Get all restore accounts
   */
  public async listAllRestoreAccountsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listAllRestoreAccounts(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAllRestoreAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows administrators to retrieve a list of all restore accounts.
   * Get all restore accounts
   */
  public async listAllRestoreAccounts(_options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listAllRestoreAccountsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { ListApiCredentialsApiRequestFactory, ListApiCredentialsApiResponseProcessor} from "../apis/ListApiCredentialsApi";

export class PromiseListApiCredentialsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListApiCredentialsApiRequestFactory = new ListApiCredentialsApiRequestFactory(configuration),
    private responseProcessor: ListApiCredentialsApiResponseProcessor = new ListApiCredentialsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation retrieves a list of all api credentials associated with a specific account.
   * Get all api credentials of an account
   */
  public async listApiCredentialsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListApiCredentialsResponse>> {
    let requestContextValue = await this.requestFactory.listApiCredentials(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listApiCredentialsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation retrieves a list of all api credentials associated with a specific account.
   * Get all api credentials of an account
   */
  public async listApiCredentials(_options?: Configuration): Promise<ListApiCredentialsResponse> {
    const responseWithInfo = await this.listApiCredentialsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { ListApplicationOverridesApiRequestFactory, ListApplicationOverridesApiResponseProcessor} from "../apis/ListApplicationOverridesApi";

export class PromiseListApplicationOverridesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListApplicationOverridesApiRequestFactory = new ListApplicationOverridesApiRequestFactory(configuration),
    private responseProcessor: ListApplicationOverridesApiResponseProcessor = new ListApplicationOverridesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the application-specific overrides configured for a specified inventory item within a given project.
   * Get application overrides for an inventory item
   * @param id ID of the inventory item
   * @param projectId The project ID
   */
  public async listApplicationOverridesWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListApplicationOverrideResponse>> {
    let requestContextValue = await this.requestFactory.listApplicationOverrides(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listApplicationOverridesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the application-specific overrides configured for a specified inventory item within a given project.
   * Get application overrides for an inventory item
   * @param id ID of the inventory item
   * @param projectId The project ID
   */
  public async listApplicationOverrides(id: string, projectId: string, _options?: Configuration): Promise<ListApplicationOverrideResponse> {
    const responseWithInfo = await this.listApplicationOverridesWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAtlasProjectsApiRequestFactory, ListAtlasProjectsApiResponseProcessor} from "../apis/ListAtlasProjectsApi";

export class PromiseListAtlasProjectsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAtlasProjectsApiRequestFactory = new ListAtlasProjectsApiRequestFactory(configuration),
    private responseProcessor: ListAtlasProjectsApiResponseProcessor = new ListAtlasProjectsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all Atlas projects associated with a specific restore account and project.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listAtlasProjectsWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<AtlasProjects>> {
    let requestContextValue = await this.requestFactory.listAtlasProjects(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAtlasProjectsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all Atlas projects associated with a specific restore account and project.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listAtlasProjects(accountId: string, projectId: string, _options?: Configuration): Promise<AtlasProjects> {
    const responseWithInfo = await this.listAtlasProjectsWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAuditLogsApiRequestFactory, ListAuditLogsApiResponseProcessor} from "../apis/ListAuditLogsApi";

export class PromiseListAuditLogsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAuditLogsApiRequestFactory = new ListAuditLogsApiRequestFactory(configuration),
    private responseProcessor: ListAuditLogsApiResponseProcessor = new ListAuditLogsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a paginated list of audit logs with optional sorting and filtering capabilities.
   * Get all audit logs
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listAuditLogsOldWithHttpInfo(pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListAuditLogsResponse>> {
    let requestContextValue = await this.requestFactory.listAuditLogsOld(pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAuditLogsOldWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a paginated list of audit logs with optional sorting and filtering capabilities.
   * Get all audit logs
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listAuditLogsOld(pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListAuditLogsResponse> {
    const responseWithInfo = await this.listAuditLogsOldWithHttpInfo(pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListAvailabilityZonesApiRequestFactory, ListAvailabilityZonesApiResponseProcessor} from "../apis/ListAvailabilityZonesApi";

export class PromiseListAvailabilityZonesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAvailabilityZonesApiRequestFactory = new ListAvailabilityZonesApiRequestFactory(configuration),
    private responseProcessor: ListAvailabilityZonesApiResponseProcessor = new ListAvailabilityZonesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves the available restore zones for a specified account in a particular region and project.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   */
  public async listAvailabilityZonesWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<RestoreAvailabilityZones>> {
    let requestContextValue = await this.requestFactory.listAvailabilityZones(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAvailabilityZonesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves the available restore zones for a specified account in a particular region and project.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   */
  public async listAvailabilityZones(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<RestoreAvailabilityZones> {
    const responseWithInfo = await this.listAvailabilityZonesWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsInstanceProfilesApiRequestFactory, ListAwsInstanceProfilesApiResponseProcessor} from "../apis/ListAwsInstanceProfilesApi";

export class PromiseListAwsInstanceProfilesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsInstanceProfilesApiRequestFactory = new ListAwsInstanceProfilesApiRequestFactory(configuration),
    private responseProcessor: ListAwsInstanceProfilesApiResponseProcessor = new ListAwsInstanceProfilesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve instance profiles information for a specific restore account.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to connect to
   * @param projectId The project ID
   */
  public async listAwsInstanceProfilesWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListInstanceProfilesResponse>> {
    let requestContextValue = await this.requestFactory.listAwsInstanceProfiles(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsInstanceProfilesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve instance profiles information for a specific restore account.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to connect to
   * @param projectId The project ID
   */
  public async listAwsInstanceProfiles(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<ListInstanceProfilesResponse> {
    const responseWithInfo = await this.listAwsInstanceProfilesWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsInstanceTypesApiRequestFactory, ListAwsInstanceTypesApiResponseProcessor} from "../apis/ListAwsInstanceTypesApi";

export class PromiseListAwsInstanceTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsInstanceTypesApiRequestFactory = new ListAwsInstanceTypesApiRequestFactory(configuration),
    private responseProcessor: ListAwsInstanceTypesApiResponseProcessor = new ListAwsInstanceTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve instance types information for a specific restore account in a given project and subnet.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get instance types for
   * @param projectId The project ID
   * @param subnetId The subnet ID to get instance types for
   */
  public async listAwsInstanceTypesWithHttpInfo(accountId: string, region: string, projectId: string, subnetId?: string, _options?: Configuration): Promise<HttpInfo<ListInstanceTypesResponse>> {
    let requestContextValue = await this.requestFactory.listAwsInstanceTypes(accountId, region, projectId, subnetId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsInstanceTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve instance types information for a specific restore account in a given project and subnet.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get instance types for
   * @param projectId The project ID
   * @param subnetId The subnet ID to get instance types for
   */
  public async listAwsInstanceTypes(accountId: string, region: string, projectId: string, subnetId?: string, _options?: Configuration): Promise<ListInstanceTypesResponse> {
    const responseWithInfo = await this.listAwsInstanceTypesWithHttpInfo(accountId, region, projectId, subnetId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsRdsInstanceClassesApiRequestFactory, ListAwsRdsInstanceClassesApiResponseProcessor} from "../apis/ListAwsRdsInstanceClassesApi";

export class PromiseListAwsRdsInstanceClassesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsRdsInstanceClassesApiRequestFactory = new ListAwsRdsInstanceClassesApiRequestFactory(configuration),
    private responseProcessor: ListAwsRdsInstanceClassesApiResponseProcessor = new ListAwsRdsInstanceClassesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve possible RDS instance classes for a specific engine and version for the restore account in a given project and region.
   * Gets available RDS instance classes for the engine in the region
   * @param engine 
   * @param engineVersion 
   * @param accountId ID of the restore account
   * @param region The region to get instance classes for
   * @param projectId The project ID
   */
  public async listAwsRdsInstanceClassesWithHttpInfo(engine: AwsRdsEngine, engineVersion: string, accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<RestoreRdsInstanceClasses>> {
    let requestContextValue = await this.requestFactory.listAwsRdsInstanceClasses(engine, engineVersion, accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsRdsInstanceClassesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve possible RDS instance classes for a specific engine and version for the restore account in a given project and region.
   * Gets available RDS instance classes for the engine in the region
   * @param engine 
   * @param engineVersion 
   * @param accountId ID of the restore account
   * @param region The region to get instance classes for
   * @param projectId The project ID
   */
  public async listAwsRdsInstanceClasses(engine: AwsRdsEngine, engineVersion: string, accountId: string, region: string, projectId: string, _options?: Configuration): Promise<RestoreRdsInstanceClasses> {
    const responseWithInfo = await this.listAwsRdsInstanceClassesWithHttpInfo(engine, engineVersion, accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsRdsSubnetGroupsApiRequestFactory, ListAwsRdsSubnetGroupsApiResponseProcessor} from "../apis/ListAwsRdsSubnetGroupsApi";

export class PromiseListAwsRdsSubnetGroupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsRdsSubnetGroupsApiRequestFactory = new ListAwsRdsSubnetGroupsApiRequestFactory(configuration),
    private responseProcessor: ListAwsRdsSubnetGroupsApiResponseProcessor = new ListAwsRdsSubnetGroupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve RDS subnet groups information for a specific restore account in a given project and region.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   */
  public async listAwsRdsSubnetGroupsWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<RestoreRdsSubnetGroups>> {
    let requestContextValue = await this.requestFactory.listAwsRdsSubnetGroups(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsRdsSubnetGroupsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve RDS subnet groups information for a specific restore account in a given project and region.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   */
  public async listAwsRdsSubnetGroups(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<RestoreRdsSubnetGroups> {
    const responseWithInfo = await this.listAwsRdsSubnetGroupsWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsSecurityGroupsApiRequestFactory, ListAwsSecurityGroupsApiResponseProcessor} from "../apis/ListAwsSecurityGroupsApi";

export class PromiseListAwsSecurityGroupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsSecurityGroupsApiRequestFactory = new ListAwsSecurityGroupsApiRequestFactory(configuration),
    private responseProcessor: ListAwsSecurityGroupsApiResponseProcessor = new ListAwsSecurityGroupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve security group information for a specific restore account in a given project and region.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   * @param vpcId Optional VPC ID to filter security groups by
   * @param subnetId Optional subnet ID to filter security groups by
   */
  public async listAwsSecurityGroupsWithHttpInfo(accountId: string, region: string, projectId: string, vpcId?: string, subnetId?: string, _options?: Configuration): Promise<HttpInfo<RestoreSecurityGroups>> {
    let requestContextValue = await this.requestFactory.listAwsSecurityGroups(accountId, region, projectId, vpcId, subnetId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsSecurityGroupsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve security group information for a specific restore account in a given project and region.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   * @param vpcId Optional VPC ID to filter security groups by
   * @param subnetId Optional subnet ID to filter security groups by
   */
  public async listAwsSecurityGroups(accountId: string, region: string, projectId: string, vpcId?: string, subnetId?: string, _options?: Configuration): Promise<RestoreSecurityGroups> {
    const responseWithInfo = await this.listAwsSecurityGroupsWithHttpInfo(accountId, region, projectId, vpcId, subnetId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsSubnetsApiRequestFactory, ListAwsSubnetsApiResponseProcessor} from "../apis/ListAwsSubnetsApi";

export class PromiseListAwsSubnetsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsSubnetsApiRequestFactory = new ListAwsSubnetsApiRequestFactory(configuration),
    private responseProcessor: ListAwsSubnetsApiResponseProcessor = new ListAwsSubnetsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve subnets information for a specific restore account in a given project and region.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get subnets for
   * @param projectId The project ID
   */
  public async listAwsSubnetsWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListSubnetsResponse>> {
    let requestContextValue = await this.requestFactory.listAwsSubnets(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsSubnetsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve subnets information for a specific restore account in a given project and region.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get subnets for
   * @param projectId The project ID
   */
  public async listAwsSubnets(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<ListSubnetsResponse> {
    const responseWithInfo = await this.listAwsSubnetsWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAwsSubnetsV2ApiRequestFactory, ListAwsSubnetsV2ApiResponseProcessor} from "../apis/ListAwsSubnetsV2Api";

export class PromiseListAwsSubnetsV2Api {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAwsSubnetsV2ApiRequestFactory = new ListAwsSubnetsV2ApiRequestFactory(configuration),
    private responseProcessor: ListAwsSubnetsV2ApiResponseProcessor = new ListAwsSubnetsV2ApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve subnets information for a specific restore account in a given project and region.
   * Gets subnets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get subnets for
   * @param projectId The project ID
   */
  public async listAwsSubnetsV2WithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListSubnetsResponseV2>> {
    let requestContextValue = await this.requestFactory.listAwsSubnetsV2(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAwsSubnetsV2WithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve subnets information for a specific restore account in a given project and region.
   * Gets subnets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get subnets for
   * @param projectId The project ID
   */
  public async listAwsSubnetsV2(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<ListSubnetsResponseV2> {
    const responseWithInfo = await this.listAwsSubnetsV2WithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureRegionsApiRequestFactory, ListAzureRegionsApiResponseProcessor} from "../apis/ListAzureRegionsApi";

export class PromiseListAzureRegionsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureRegionsApiRequestFactory = new ListAzureRegionsApiRequestFactory(configuration),
    private responseProcessor: ListAzureRegionsApiResponseProcessor = new ListAzureRegionsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Get all available Azure regions
   * @param projectId The project ID
   */
  public async listAzureRegionsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListAzureRegions200Response>> {
    let requestContextValue = await this.requestFactory.listAzureRegions(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureRegionsWithHttpInfo(responseContext)
  }

  /**
   * Get all available Azure regions
   * @param projectId The project ID
   */
  public async listAzureRegions(projectId: string, _options?: Configuration): Promise<ListAzureRegions200Response> {
    const responseWithInfo = await this.listAzureRegionsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureResourceGroupsApiRequestFactory, ListAzureResourceGroupsApiResponseProcessor} from "../apis/ListAzureResourceGroupsApi";

export class PromiseListAzureResourceGroupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureResourceGroupsApiRequestFactory = new ListAzureResourceGroupsApiRequestFactory(configuration),
    private responseProcessor: ListAzureResourceGroupsApiResponseProcessor = new ListAzureResourceGroupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all Storage accounts associated with a specific azure restore account and project.
   * Get all Storage Accounts in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listAzureResourceGroupsWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<AzureResourceGroups>> {
    let requestContextValue = await this.requestFactory.listAzureResourceGroups(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureResourceGroupsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all Storage accounts associated with a specific azure restore account and project.
   * Get all Storage Accounts in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listAzureResourceGroups(accountId: string, projectId: string, _options?: Configuration): Promise<AzureResourceGroups> {
    const responseWithInfo = await this.listAzureResourceGroupsWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureSKUsApiRequestFactory, ListAzureSKUsApiResponseProcessor} from "../apis/ListAzureSKUsApi";

export class PromiseListAzureSKUsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureSKUsApiRequestFactory = new ListAzureSKUsApiRequestFactory(configuration),
    private responseProcessor: ListAzureSKUsApiResponseProcessor = new ListAzureSKUsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all available SKUs in subscription
   * Get all available SKUs in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param listAzureSKUsRequest The request body for listing available SKUs
   */
  public async listAzureSKUsWithHttpInfo(accountId: string, projectId: string, listAzureSKUsRequest: ListAzureSKUsRequest, _options?: Configuration): Promise<HttpInfo<ListAzureSKUs200Response>> {
    let requestContextValue = await this.requestFactory.listAzureSKUs(accountId, projectId, listAzureSKUsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureSKUsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all available SKUs in subscription
   * Get all available SKUs in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param listAzureSKUsRequest The request body for listing available SKUs
   */
  public async listAzureSKUs(accountId: string, projectId: string, listAzureSKUsRequest: ListAzureSKUsRequest, _options?: Configuration): Promise<ListAzureSKUs200Response> {
    const responseWithInfo = await this.listAzureSKUsWithHttpInfo(accountId, projectId, listAzureSKUsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureStorageAccountContainersApiRequestFactory, ListAzureStorageAccountContainersApiResponseProcessor} from "../apis/ListAzureStorageAccountContainersApi";

export class PromiseListAzureStorageAccountContainersApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureStorageAccountContainersApiRequestFactory = new ListAzureStorageAccountContainersApiRequestFactory(configuration),
    private responseProcessor: ListAzureStorageAccountContainersApiResponseProcessor = new ListAzureStorageAccountContainersApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all Storage accounts associated with a specific azure restore account and project.
   * Get all Storage Account Containers in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param listAzureStorageAccountContainersRequest The request body for creating a new backup job
   */
  public async listAzureStorageAccountContainersWithHttpInfo(accountId: string, projectId: string, listAzureStorageAccountContainersRequest: ListAzureStorageAccountContainersRequest, _options?: Configuration): Promise<HttpInfo<ListAzureStorageAccountContainers200Response>> {
    let requestContextValue = await this.requestFactory.listAzureStorageAccountContainers(accountId, projectId, listAzureStorageAccountContainersRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureStorageAccountContainersWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all Storage accounts associated with a specific azure restore account and project.
   * Get all Storage Account Containers in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param listAzureStorageAccountContainersRequest The request body for creating a new backup job
   */
  public async listAzureStorageAccountContainers(accountId: string, projectId: string, listAzureStorageAccountContainersRequest: ListAzureStorageAccountContainersRequest, _options?: Configuration): Promise<ListAzureStorageAccountContainers200Response> {
    const responseWithInfo = await this.listAzureStorageAccountContainersWithHttpInfo(accountId, projectId, listAzureStorageAccountContainersRequest, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureStorageAccountsApiRequestFactory, ListAzureStorageAccountsApiResponseProcessor} from "../apis/ListAzureStorageAccountsApi";

export class PromiseListAzureStorageAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureStorageAccountsApiRequestFactory = new ListAzureStorageAccountsApiRequestFactory(configuration),
    private responseProcessor: ListAzureStorageAccountsApiResponseProcessor = new ListAzureStorageAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all Storage accounts associated with a specific azure restore account and project.
   * Get all Storage Accounts in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listAzureStorageAccountsWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<AzureStorageAccounts>> {
    let requestContextValue = await this.requestFactory.listAzureStorageAccounts(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureStorageAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all Storage accounts associated with a specific azure restore account and project.
   * Get all Storage Accounts in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listAzureStorageAccounts(accountId: string, projectId: string, _options?: Configuration): Promise<AzureStorageAccounts> {
    const responseWithInfo = await this.listAzureStorageAccountsWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureSubscriptionResourceGroupsApiRequestFactory, ListAzureSubscriptionResourceGroupsApiResponseProcessor} from "../apis/ListAzureSubscriptionResourceGroupsApi";

export class PromiseListAzureSubscriptionResourceGroupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureSubscriptionResourceGroupsApiRequestFactory = new ListAzureSubscriptionResourceGroupsApiRequestFactory(configuration),
    private responseProcessor: ListAzureSubscriptionResourceGroupsApiResponseProcessor = new ListAzureSubscriptionResourceGroupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * List Azure resource groups in a subscription
   * @param projectId The project ID
   * @param subscriptionId The subscription ID
   * @param requestId 
   */
  public async listAzureSubscriptionResourceGroupsWithHttpInfo(projectId: string, subscriptionId: string, requestId: string, _options?: Configuration): Promise<HttpInfo<AzureListResourceGroupsResponse>> {
    let requestContextValue = await this.requestFactory.listAzureSubscriptionResourceGroups(projectId, subscriptionId, requestId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureSubscriptionResourceGroupsWithHttpInfo(responseContext)
  }

  /**
   * List Azure resource groups in a subscription
   * @param projectId The project ID
   * @param subscriptionId The subscription ID
   * @param requestId 
   */
  public async listAzureSubscriptionResourceGroups(projectId: string, subscriptionId: string, requestId: string, _options?: Configuration): Promise<AzureListResourceGroupsResponse> {
    const responseWithInfo = await this.listAzureSubscriptionResourceGroupsWithHttpInfo(projectId, subscriptionId, requestId, _options);
    return responseWithInfo.data;
  }


}


import { ListAzureUnassignedNetworkInterfacesApiRequestFactory, ListAzureUnassignedNetworkInterfacesApiResponseProcessor} from "../apis/ListAzureUnassignedNetworkInterfacesApi";

export class PromiseListAzureUnassignedNetworkInterfacesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListAzureUnassignedNetworkInterfacesApiRequestFactory = new ListAzureUnassignedNetworkInterfacesApiRequestFactory(configuration),
    private responseProcessor: ListAzureUnassignedNetworkInterfacesApiResponseProcessor = new ListAzureUnassignedNetworkInterfacesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all unassigned network interfaces in the subscription
   * Get all Unassigned network interfaces in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param listAzureUnassignedNetworkInterfacesRequest The request body for listing unassigned network interfaces
   */
  public async listAzureUnassignedNetworkInterfacesWithHttpInfo(accountId: string, projectId: string, listAzureUnassignedNetworkInterfacesRequest: ListAzureUnassignedNetworkInterfacesRequest, _options?: Configuration): Promise<HttpInfo<ListAzureUnassignedNetworkInterfaces200Response>> {
    let requestContextValue = await this.requestFactory.listAzureUnassignedNetworkInterfaces(accountId, projectId, listAzureUnassignedNetworkInterfacesRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listAzureUnassignedNetworkInterfacesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all unassigned network interfaces in the subscription
   * Get all Unassigned network interfaces in subscription
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param listAzureUnassignedNetworkInterfacesRequest The request body for listing unassigned network interfaces
   */
  public async listAzureUnassignedNetworkInterfaces(accountId: string, projectId: string, listAzureUnassignedNetworkInterfacesRequest: ListAzureUnassignedNetworkInterfacesRequest, _options?: Configuration): Promise<ListAzureUnassignedNetworkInterfaces200Response> {
    const responseWithInfo = await this.listAzureUnassignedNetworkInterfacesWithHttpInfo(accountId, projectId, listAzureUnassignedNetworkInterfacesRequest, _options);
    return responseWithInfo.data;
  }


}


import { ListBackupJobErrorCodesApiRequestFactory, ListBackupJobErrorCodesApiResponseProcessor} from "../apis/ListBackupJobErrorCodesApi";

export class PromiseListBackupJobErrorCodesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListBackupJobErrorCodesApiRequestFactory = new ListBackupJobErrorCodesApiRequestFactory(configuration),
    private responseProcessor: ListBackupJobErrorCodesApiResponseProcessor = new ListBackupJobErrorCodesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all error codes that have occurred in backup jobs for a specified project.
   * List all backup job error codes
   * @param projectId The project ID
   */
  public async listBackupJobErrorCodesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListJobErrorCodesResponse>> {
    let requestContextValue = await this.requestFactory.listBackupJobErrorCodes(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listBackupJobErrorCodesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all error codes that have occurred in backup jobs for a specified project.
   * List all backup job error codes
   * @param projectId The project ID
   */
  public async listBackupJobErrorCodes(projectId: string, _options?: Configuration): Promise<ListJobErrorCodesResponse> {
    const responseWithInfo = await this.listBackupJobErrorCodesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListBackupJobsTypesApiRequestFactory, ListBackupJobsTypesApiResponseProcessor} from "../apis/ListBackupJobsTypesApi";

export class PromiseListBackupJobsTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListBackupJobsTypesApiRequestFactory = new ListBackupJobsTypesApiRequestFactory(configuration),
    private responseProcessor: ListBackupJobsTypesApiResponseProcessor = new ListBackupJobsTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all backup job types within the job inventory for a specified project.
   * List all backup jobs types
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listBackupJobsTypesWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListBackupJobTypesResponse>> {
    let requestContextValue = await this.requestFactory.listBackupJobsTypes(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listBackupJobsTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all backup job types within the job inventory for a specified project.
   * List all backup jobs types
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listBackupJobsTypes(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListBackupJobTypesResponse> {
    const responseWithInfo = await this.listBackupJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListBackupPoliciesApiRequestFactory, ListBackupPoliciesApiResponseProcessor} from "../apis/ListBackupPoliciesApi";

export class PromiseListBackupPoliciesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListBackupPoliciesApiRequestFactory = new ListBackupPoliciesApiRequestFactory(configuration),
    private responseProcessor: ListBackupPoliciesApiResponseProcessor = new ListBackupPoliciesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all backup policies associated with a specified project ID.
   * Get all policies
   * @param projectId The project ID
   */
  public async listPoliciesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListBackupPolicyResponse>> {
    let requestContextValue = await this.requestFactory.listPolicies(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listPoliciesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all backup policies associated with a specified project ID.
   * Get all policies
   * @param projectId The project ID
   */
  public async listPolicies(projectId: string, _options?: Configuration): Promise<ListBackupPolicyResponse> {
    const responseWithInfo = await this.listPoliciesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListCloudSnapshotsApiRequestFactory, ListCloudSnapshotsApiResponseProcessor} from "../apis/ListCloudSnapshotsApi";

export class PromiseListCloudSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListCloudSnapshotsApiRequestFactory = new ListCloudSnapshotsApiRequestFactory(configuration),
    private responseProcessor: ListCloudSnapshotsApiResponseProcessor = new ListCloudSnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a list of all snapshots for a specific project, with pagination and optional sorting and filtering.
   * Get cloud snapshots
   * @param projectId The project ID
   * @param id ID of the inventory item
   * @param start Start date of the time range, inclusive
   * @param end End date of the time range, inclusive
   */
  public async listCloudSnapshotsWithHttpInfo(projectId: string, id: string, start: Date, end: Date, _options?: Configuration): Promise<HttpInfo<ListCloudSnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.listCloudSnapshots(projectId, id, start, end, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listCloudSnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all snapshots for a specific project, with pagination and optional sorting and filtering.
   * Get cloud snapshots
   * @param projectId The project ID
   * @param id ID of the inventory item
   * @param start Start date of the time range, inclusive
   * @param end End date of the time range, inclusive
   */
  public async listCloudSnapshots(projectId: string, id: string, start: Date, end: Date, _options?: Configuration): Promise<ListCloudSnapshotsResponse> {
    const responseWithInfo = await this.listCloudSnapshotsWithHttpInfo(projectId, id, start, end, _options);
    return responseWithInfo.data;
  }


}


import { ListControlViolationsApiRequestFactory, ListControlViolationsApiResponseProcessor} from "../apis/ListControlViolationsApi";

export class PromiseListControlViolationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListControlViolationsApiRequestFactory = new ListControlViolationsApiRequestFactory(configuration),
    private responseProcessor: ListControlViolationsApiResponseProcessor = new ListControlViolationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a list of all control violations for a specific resource identified by project ID and resource ID.
   * List all control violations
   * @param id ID of the resource
   * @param projectId The project ID
   */
  public async listControlViolationsWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListControlViolationsResponse>> {
    let requestContextValue = await this.requestFactory.listControlViolations(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listControlViolationsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a list of all control violations for a specific resource identified by project ID and resource ID.
   * List all control violations
   * @param id ID of the resource
   * @param projectId The project ID
   */
  public async listControlViolations(id: string, projectId: string, _options?: Configuration): Promise<ListControlViolationsResponse> {
    const responseWithInfo = await this.listControlViolationsWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListControlsApiRequestFactory, ListControlsApiResponseProcessor} from "../apis/ListControlsApi";

export class PromiseListControlsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListControlsApiRequestFactory = new ListControlsApiRequestFactory(configuration),
    private responseProcessor: ListControlsApiResponseProcessor = new ListControlsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a list of controls for a specified project, with options to paginate, sort, and filter the results.
   * List all controls
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listControlsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListControlsResponse>> {
    let requestContextValue = await this.requestFactory.listControls(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listControlsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a list of controls for a specified project, with options to paginate, sort, and filter the results.
   * List all controls
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listControls(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListControlsResponse> {
    const responseWithInfo = await this.listControlsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListDailySnapshotsApiRequestFactory, ListDailySnapshotsApiResponseProcessor} from "../apis/ListDailySnapshotsApi";

export class PromiseListDailySnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListDailySnapshotsApiRequestFactory = new ListDailySnapshotsApiRequestFactory(configuration),
    private responseProcessor: ListDailySnapshotsApiResponseProcessor = new ListDailySnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a list of the daily snapshots for a specific inventory item.
   * Get daily snapshots
   * @param projectId The project ID
   * @param id ID of the inventory item
   */
  public async listDailySnapshotsWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<ListDailySnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.listDailySnapshots(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listDailySnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of the daily snapshots for a specific inventory item.
   * Get daily snapshots
   * @param projectId The project ID
   * @param id ID of the inventory item
   */
  public async listDailySnapshots(projectId: string, id: string, _options?: Configuration): Promise<ListDailySnapshotsResponse> {
    const responseWithInfo = await this.listDailySnapshotsWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { ListDataClassificationEntitiesApiRequestFactory, ListDataClassificationEntitiesApiResponseProcessor} from "../apis/ListDataClassificationEntitiesApi";

export class PromiseListDataClassificationEntitiesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListDataClassificationEntitiesApiRequestFactory = new ListDataClassificationEntitiesApiRequestFactory(configuration),
    private responseProcessor: ListDataClassificationEntitiesApiResponseProcessor = new ListDataClassificationEntitiesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all data classification entities for a specified project and inventory item.
   * Get all data classification entities
   * @param projectId The project ID
   * @param id ID of the inventory item
   */
  public async listDataClassificationEntitiesWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<ListDataClassificationEntitiesResponse>> {
    let requestContextValue = await this.requestFactory.listDataClassificationEntities(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listDataClassificationEntitiesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all data classification entities for a specified project and inventory item.
   * Get all data classification entities
   * @param projectId The project ID
   * @param id ID of the inventory item
   */
  public async listDataClassificationEntities(projectId: string, id: string, _options?: Configuration): Promise<ListDataClassificationEntitiesResponse> {
    const responseWithInfo = await this.listDataClassificationEntitiesWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { ListDynamoDbBackupConfigsApiRequestFactory, ListDynamoDbBackupConfigsApiResponseProcessor} from "../apis/ListDynamoDbBackupConfigsApi";

export class PromiseListDynamoDbBackupConfigsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListDynamoDbBackupConfigsApiRequestFactory = new ListDynamoDbBackupConfigsApiRequestFactory(configuration),
    private responseProcessor: ListDynamoDbBackupConfigsApiResponseProcessor = new ListDynamoDbBackupConfigsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of DynamoDB backup configurations for the project.
   * List DynamoDB backup configurations
   * @param projectId The project ID
   */
  public async listDynamoDbBackupConfigsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListDynamoDbBackupConfigsResponse>> {
    let requestContextValue = await this.requestFactory.listDynamoDbBackupConfigs(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listDynamoDbBackupConfigsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of DynamoDB backup configurations for the project.
   * List DynamoDB backup configurations
   * @param projectId The project ID
   */
  public async listDynamoDbBackupConfigs(projectId: string, _options?: Configuration): Promise<ListDynamoDbBackupConfigsResponse> {
    const responseWithInfo = await this.listDynamoDbBackupConfigsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListEncryptionKeysApiRequestFactory, ListEncryptionKeysApiResponseProcessor} from "../apis/ListEncryptionKeysApi";

export class PromiseListEncryptionKeysApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListEncryptionKeysApiRequestFactory = new ListEncryptionKeysApiRequestFactory(configuration),
    private responseProcessor: ListEncryptionKeysApiResponseProcessor = new ListEncryptionKeysApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a list of all encryption keys associated with a specific restore account, region, and project. It requires specifying the account ID, region, and project ID as path parameters.
   * Get all encryption keys
   * @param accountId ID of the restore account
   * @param region Region
   * @param projectId The project ID
   * @param includeAwsManagedKeys Whether to include AWS managed keys in the response
   */
  public async listEncryptionKeysWithHttpInfo(accountId: string, region: string, projectId: string, includeAwsManagedKeys?: boolean, _options?: Configuration): Promise<HttpInfo<EncryptionKeys>> {
    let requestContextValue = await this.requestFactory.listEncryptionKeys(accountId, region, projectId, includeAwsManagedKeys, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listEncryptionKeysWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a list of all encryption keys associated with a specific restore account, region, and project. It requires specifying the account ID, region, and project ID as path parameters.
   * Get all encryption keys
   * @param accountId ID of the restore account
   * @param region Region
   * @param projectId The project ID
   * @param includeAwsManagedKeys Whether to include AWS managed keys in the response
   */
  public async listEncryptionKeys(accountId: string, region: string, projectId: string, includeAwsManagedKeys?: boolean, _options?: Configuration): Promise<EncryptionKeys> {
    const responseWithInfo = await this.listEncryptionKeysWithHttpInfo(accountId, region, projectId, includeAwsManagedKeys, _options);
    return responseWithInfo.data;
  }


}


import { ListGCSBucketsApiRequestFactory, ListGCSBucketsApiResponseProcessor} from "../apis/ListGCSBucketsApi";

export class PromiseListGCSBucketsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGCSBucketsApiRequestFactory = new ListGCSBucketsApiRequestFactory(configuration),
    private responseProcessor: ListGCSBucketsApiResponseProcessor = new ListGCSBucketsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all GCS buckets associated with a specific restore account and project.
   * Get all buckets in account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listGCSBucketsWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GCSBuckets>> {
    let requestContextValue = await this.requestFactory.listGCSBuckets(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGCSBucketsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all GCS buckets associated with a specific restore account and project.
   * Get all buckets in account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listGCSBuckets(accountId: string, projectId: string, _options?: Configuration): Promise<GCSBuckets> {
    const responseWithInfo = await this.listGCSBucketsWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpDiskTypesApiRequestFactory, ListGcpDiskTypesApiResponseProcessor} from "../apis/ListGcpDiskTypesApi";

export class PromiseListGcpDiskTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpDiskTypesApiRequestFactory = new ListGcpDiskTypesApiRequestFactory(configuration),
    private responseProcessor: ListGcpDiskTypesApiResponseProcessor = new ListGcpDiskTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve disk types information for a specific restore account in a given project, zone and machine type.
   * Gets restore information for gcp project
   * @param accountId ID of the restore account
   * @param zone The zone to get disk types for
   * @param projectId The project ID
   * @param machineType The machine type to get disk types for
   */
  public async listGcpDiskTypesWithHttpInfo(accountId: string, zone: string, projectId: string, machineType?: string, _options?: Configuration): Promise<HttpInfo<GcpDiskTypesResponse>> {
    let requestContextValue = await this.requestFactory.listGcpDiskTypes(accountId, zone, projectId, machineType, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpDiskTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve disk types information for a specific restore account in a given project, zone and machine type.
   * Gets restore information for gcp project
   * @param accountId ID of the restore account
   * @param zone The zone to get disk types for
   * @param projectId The project ID
   * @param machineType The machine type to get disk types for
   */
  public async listGcpDiskTypes(accountId: string, zone: string, projectId: string, machineType?: string, _options?: Configuration): Promise<GcpDiskTypesResponse> {
    const responseWithInfo = await this.listGcpDiskTypesWithHttpInfo(accountId, zone, projectId, machineType, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpEncryptionKeysApiRequestFactory, ListGcpEncryptionKeysApiResponseProcessor} from "../apis/ListGcpEncryptionKeysApi";

export class PromiseListGcpEncryptionKeysApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpEncryptionKeysApiRequestFactory = new ListGcpEncryptionKeysApiRequestFactory(configuration),
    private responseProcessor: ListGcpEncryptionKeysApiResponseProcessor = new ListGcpEncryptionKeysApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a list of all encryption keys associated with a specific restore account, region, and project. It requires specifying the account ID, region, and project ID as path parameters.
   * Get all encryption keys
   * @param accountId ID of the restore account
   * @param region Region
   * @param projectId The project ID
   */
  public async listGcpEncryptionKeysWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GcpEncryptionKeys>> {
    let requestContextValue = await this.requestFactory.listGcpEncryptionKeys(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpEncryptionKeysWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a list of all encryption keys associated with a specific restore account, region, and project. It requires specifying the account ID, region, and project ID as path parameters.
   * Get all encryption keys
   * @param accountId ID of the restore account
   * @param region Region
   * @param projectId The project ID
   */
  public async listGcpEncryptionKeys(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<GcpEncryptionKeys> {
    const responseWithInfo = await this.listGcpEncryptionKeysWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpMachineTypesApiRequestFactory, ListGcpMachineTypesApiResponseProcessor} from "../apis/ListGcpMachineTypesApi";

export class PromiseListGcpMachineTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpMachineTypesApiRequestFactory = new ListGcpMachineTypesApiRequestFactory(configuration),
    private responseProcessor: ListGcpMachineTypesApiResponseProcessor = new ListGcpMachineTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve machine types information for a specific restore account in a given project and zone.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param zone The zone to get machine types for
   * @param projectId The project ID
   */
  public async listGcpMachineTypesWithHttpInfo(accountId: string, zone: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GcpMachineTypesResponse>> {
    let requestContextValue = await this.requestFactory.listGcpMachineTypes(accountId, zone, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpMachineTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve machine types information for a specific restore account in a given project and zone.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param zone The zone to get machine types for
   * @param projectId The project ID
   */
  public async listGcpMachineTypes(accountId: string, zone: string, projectId: string, _options?: Configuration): Promise<GcpMachineTypesResponse> {
    const responseWithInfo = await this.listGcpMachineTypesWithHttpInfo(accountId, zone, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpNetworksApiRequestFactory, ListGcpNetworksApiResponseProcessor} from "../apis/ListGcpNetworksApi";

export class PromiseListGcpNetworksApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpNetworksApiRequestFactory = new ListGcpNetworksApiRequestFactory(configuration),
    private responseProcessor: ListGcpNetworksApiResponseProcessor = new ListGcpNetworksApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve networks information for a specific restore gcp project in a given project.
   * Gets restore networks for gcp project
   * @param accountId ID of the restore gcp project
   * @param projectId The project ID
   */
  public async listGcpNetworksWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListGcpNetworksResponse>> {
    let requestContextValue = await this.requestFactory.listGcpNetworks(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpNetworksWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve networks information for a specific restore gcp project in a given project.
   * Gets restore networks for gcp project
   * @param accountId ID of the restore gcp project
   * @param projectId The project ID
   */
  public async listGcpNetworks(accountId: string, projectId: string, _options?: Configuration): Promise<ListGcpNetworksResponse> {
    const responseWithInfo = await this.listGcpNetworksWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpSharedNetworksApiRequestFactory, ListGcpSharedNetworksApiResponseProcessor} from "../apis/ListGcpSharedNetworksApi";

export class PromiseListGcpSharedNetworksApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpSharedNetworksApiRequestFactory = new ListGcpSharedNetworksApiRequestFactory(configuration),
    private responseProcessor: ListGcpSharedNetworksApiResponseProcessor = new ListGcpSharedNetworksApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve shared networks information for a specific restore gcp project in a given project.
   * Gets restore shared networks for gcp project
   * @param accountId ID of the restore gcp project
   * @param projectId The project ID
   */
  public async listGcpSharedNetworksWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListGcpNetworksResponse>> {
    let requestContextValue = await this.requestFactory.listGcpSharedNetworks(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpSharedNetworksWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve shared networks information for a specific restore gcp project in a given project.
   * Gets restore shared networks for gcp project
   * @param accountId ID of the restore gcp project
   * @param projectId The project ID
   */
  public async listGcpSharedNetworks(accountId: string, projectId: string, _options?: Configuration): Promise<ListGcpNetworksResponse> {
    const responseWithInfo = await this.listGcpSharedNetworksWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpSharedSubnetsApiRequestFactory, ListGcpSharedSubnetsApiResponseProcessor} from "../apis/ListGcpSharedSubnetsApi";

export class PromiseListGcpSharedSubnetsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpSharedSubnetsApiRequestFactory = new ListGcpSharedSubnetsApiRequestFactory(configuration),
    private responseProcessor: ListGcpSharedSubnetsApiResponseProcessor = new ListGcpSharedSubnetsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve shared subnets from other GCP projects that are usable in this project for a given region.
   * Gets shared subnets from other GCP projects that are usable in this project
   * @param accountId ID of the restore gcp project
   * @param region The region to get shared subnets for
   * @param projectId The project ID
   * @param network Optional network name to filter subnets
   */
  public async listGcpSharedSubnetsWithHttpInfo(accountId: string, region: string, projectId: string, network?: string, _options?: Configuration): Promise<HttpInfo<ListGcpSubnetsResponse>> {
    let requestContextValue = await this.requestFactory.listGcpSharedSubnets(accountId, region, projectId, network, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpSharedSubnetsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve shared subnets from other GCP projects that are usable in this project for a given region.
   * Gets shared subnets from other GCP projects that are usable in this project
   * @param accountId ID of the restore gcp project
   * @param region The region to get shared subnets for
   * @param projectId The project ID
   * @param network Optional network name to filter subnets
   */
  public async listGcpSharedSubnets(accountId: string, region: string, projectId: string, network?: string, _options?: Configuration): Promise<ListGcpSubnetsResponse> {
    const responseWithInfo = await this.listGcpSharedSubnetsWithHttpInfo(accountId, region, projectId, network, _options);
    return responseWithInfo.data;
  }


}


import { ListGcpSubnetsApiRequestFactory, ListGcpSubnetsApiResponseProcessor} from "../apis/ListGcpSubnetsApi";

export class PromiseListGcpSubnetsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListGcpSubnetsApiRequestFactory = new ListGcpSubnetsApiRequestFactory(configuration),
    private responseProcessor: ListGcpSubnetsApiResponseProcessor = new ListGcpSubnetsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve subnets information for a specific restore gcp project in a given project and region.
   * Gets restore subnets for gcp project
   * @param accountId ID of the restore gcp project
   * @param region The region to get subnets for
   * @param projectId The project ID
   * @param network Optional network name to filter subnets
   */
  public async listGcpSubnetsWithHttpInfo(accountId: string, region: string, projectId: string, network?: string, _options?: Configuration): Promise<HttpInfo<ListGcpSubnetsResponse>> {
    let requestContextValue = await this.requestFactory.listGcpSubnets(accountId, region, projectId, network, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listGcpSubnetsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve subnets information for a specific restore gcp project in a given project and region.
   * Gets restore subnets for gcp project
   * @param accountId ID of the restore gcp project
   * @param region The region to get subnets for
   * @param projectId The project ID
   * @param network Optional network name to filter subnets
   */
  public async listGcpSubnets(accountId: string, region: string, projectId: string, network?: string, _options?: Configuration): Promise<ListGcpSubnetsResponse> {
    const responseWithInfo = await this.listGcpSubnetsWithHttpInfo(accountId, region, projectId, network, _options);
    return responseWithInfo.data;
  }


}


import { ListInfectedExcludePathsApiRequestFactory, ListInfectedExcludePathsApiResponseProcessor} from "../apis/ListInfectedExcludePathsApi";

export class PromiseListInfectedExcludePathsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInfectedExcludePathsApiRequestFactory = new ListInfectedExcludePathsApiRequestFactory(configuration),
    private responseProcessor: ListInfectedExcludePathsApiResponseProcessor = new ListInfectedExcludePathsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the infected exclude paths for the Eon account, and optionally for a resource ID.
   * List infected exclude paths for the account
   * @param projectId The project ID
   * @param resourceId The resource ID for which to get the path exclusions on resource level
   */
  public async listInfectedExcludePathsWithHttpInfo(projectId: string, resourceId?: string, _options?: Configuration): Promise<HttpInfo<ListInfectedExcludePathsResponse>> {
    let requestContextValue = await this.requestFactory.listInfectedExcludePaths(projectId, resourceId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInfectedExcludePathsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the infected exclude paths for the Eon account, and optionally for a resource ID.
   * List infected exclude paths for the account
   * @param projectId The project ID
   * @param resourceId The resource ID for which to get the path exclusions on resource level
   */
  public async listInfectedExcludePaths(projectId: string, resourceId?: string, _options?: Configuration): Promise<ListInfectedExcludePathsResponse> {
    const responseWithInfo = await this.listInfectedExcludePathsWithHttpInfo(projectId, resourceId, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryApiRequestFactory, ListInventoryApiResponseProcessor} from "../apis/ListInventoryApi";

export class PromiseListInventoryApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryApiRequestFactory = new ListInventoryApiRequestFactory(configuration),
    private responseProcessor: ListInventoryApiResponseProcessor = new ListInventoryApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch a paginated list of all inventory items for a specified project with optional sorting and filtering.
   * Get all inventory items
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async listInventoryWithHttpInfo(pageIndex: number, pageSize: number, sort: string, projectId: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryResourcesResponse>> {
    let requestContextValue = await this.requestFactory.listInventory(pageIndex, pageSize, sort, projectId, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch a paginated list of all inventory items for a specified project with optional sorting and filtering.
   * Get all inventory items
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async listInventory(pageIndex: number, pageSize: number, sort: string, projectId: string, filter?: string, _options?: Configuration): Promise<ListInventoryResourcesResponse> {
    const responseWithInfo = await this.listInventoryWithHttpInfo(pageIndex, pageSize, sort, projectId, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryAppsApiRequestFactory, ListInventoryAppsApiResponseProcessor} from "../apis/ListInventoryAppsApi";

export class PromiseListInventoryAppsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryAppsApiRequestFactory = new ListInventoryAppsApiRequestFactory(configuration),
    private responseProcessor: ListInventoryAppsApiResponseProcessor = new ListInventoryAppsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a paginated list of all resource applications associated with a specified project.
   * List all resources applications
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryAppsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryAppsResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryApps(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryAppsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a paginated list of all resource applications associated with a specified project.
   * List all resources applications
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryApps(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventoryAppsResponse> {
    const responseWithInfo = await this.listInventoryAppsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryBackupRegionsApiRequestFactory, ListInventoryBackupRegionsApiResponseProcessor} from "../apis/ListInventoryBackupRegionsApi";

export class PromiseListInventoryBackupRegionsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryBackupRegionsApiRequestFactory = new ListInventoryBackupRegionsApiRequestFactory(configuration),
    private responseProcessor: ListInventoryBackupRegionsApiResponseProcessor = new ListInventoryBackupRegionsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Provides a list of all available backup regions for a given project ID.
   * List all backup regions
   * @param projectId The project ID
   */
  public async listInventoryBackupRegionsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListInventoryBackupRegionsResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryBackupRegions(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryBackupRegionsWithHttpInfo(responseContext)
  }

  /**
   * Description: Provides a list of all available backup regions for a given project ID.
   * List all backup regions
   * @param projectId The project ID
   */
  public async listInventoryBackupRegions(projectId: string, _options?: Configuration): Promise<ListInventoryBackupRegionsResponse> {
    const responseWithInfo = await this.listInventoryBackupRegionsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryEnvironmentsApiRequestFactory, ListInventoryEnvironmentsApiResponseProcessor} from "../apis/ListInventoryEnvironmentsApi";

export class PromiseListInventoryEnvironmentsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryEnvironmentsApiRequestFactory = new ListInventoryEnvironmentsApiRequestFactory(configuration),
    private responseProcessor: ListInventoryEnvironmentsApiResponseProcessor = new ListInventoryEnvironmentsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a list of all inventory environments associated with a specific project ID.
   * List all environments
   * @param projectId The project ID
   */
  public async listInventoryEnvironmentsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListInventoryEnvironmentsResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryEnvironments(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryEnvironmentsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a list of all inventory environments associated with a specific project ID.
   * List all environments
   * @param projectId The project ID
   */
  public async listInventoryEnvironments(projectId: string, _options?: Configuration): Promise<ListInventoryEnvironmentsResponse> {
    const responseWithInfo = await this.listInventoryEnvironmentsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryGlobalDatabasesApiRequestFactory, ListInventoryGlobalDatabasesApiResponseProcessor} from "../apis/ListInventoryGlobalDatabasesApi";

export class PromiseListInventoryGlobalDatabasesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryGlobalDatabasesApiRequestFactory = new ListInventoryGlobalDatabasesApiRequestFactory(configuration),
    private responseProcessor: ListInventoryGlobalDatabasesApiResponseProcessor = new ListInventoryGlobalDatabasesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch a paginated list of Aurora Global Database identifiers for a specified project ID with optional filtering capabilities.
   * List all Aurora Global Database identifiers
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryGlobalDatabasesWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryGlobalDatabasesResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryGlobalDatabases(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryGlobalDatabasesWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch a paginated list of Aurora Global Database identifiers for a specified project ID with optional filtering capabilities.
   * List all Aurora Global Database identifiers
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryGlobalDatabases(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventoryGlobalDatabasesResponse> {
    const responseWithInfo = await this.listInventoryGlobalDatabasesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryItemSnapshotsApiRequestFactory, ListInventoryItemSnapshotsApiResponseProcessor} from "../apis/ListInventoryItemSnapshotsApi";

export class PromiseListInventoryItemSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryItemSnapshotsApiRequestFactory = new ListInventoryItemSnapshotsApiRequestFactory(configuration),
    private responseProcessor: ListInventoryItemSnapshotsApiResponseProcessor = new ListInventoryItemSnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all snapshots for a specified inventory item within a project by providing the inventory item ID, project ID, start time, end time, page index, page size, and snapshot type.
   * Get all snapshots for a specific inventory item
   * @param id ID of the inventory item
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param snapshotType snapshot types eon or gp
   * @param projectId The project ID
   * @param start The start date for the query
   * @param end The end date for the query
   */
  public async listInventoryItemSnapshotsWithHttpInfo(id: string, pageIndex: number, pageSize: number, snapshotType: string, projectId: string, start?: Date, end?: Date, _options?: Configuration): Promise<HttpInfo<ListInventoryItemSnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryItemSnapshots(id, pageIndex, pageSize, snapshotType, projectId, start, end, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryItemSnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all snapshots for a specified inventory item within a project by providing the inventory item ID, project ID, start time, end time, page index, page size, and snapshot type.
   * Get all snapshots for a specific inventory item
   * @param id ID of the inventory item
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param snapshotType snapshot types eon or gp
   * @param projectId The project ID
   * @param start The start date for the query
   * @param end The end date for the query
   */
  public async listInventoryItemSnapshots(id: string, pageIndex: number, pageSize: number, snapshotType: string, projectId: string, start?: Date, end?: Date, _options?: Configuration): Promise<ListInventoryItemSnapshotsResponse> {
    const responseWithInfo = await this.listInventoryItemSnapshotsWithHttpInfo(id, pageIndex, pageSize, snapshotType, projectId, start, end, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryNetworksApiRequestFactory, ListInventoryNetworksApiResponseProcessor} from "../apis/ListInventoryNetworksApi";

export class PromiseListInventoryNetworksApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryNetworksApiRequestFactory = new ListInventoryNetworksApiRequestFactory(configuration),
    private responseProcessor: ListInventoryNetworksApiResponseProcessor = new ListInventoryNetworksApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch a paginated list of inventory networks for a specified project ID with optional filtering capabilities.
   * List all resources networks
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryNetworksWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryNetworksResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryNetworks(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryNetworksWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch a paginated list of inventory networks for a specified project ID with optional filtering capabilities.
   * List all resources networks
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryNetworks(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventoryNetworksResponse> {
    const responseWithInfo = await this.listInventoryNetworksWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryResourceGroupsApiRequestFactory, ListInventoryResourceGroupsApiResponseProcessor} from "../apis/ListInventoryResourceGroupsApi";

export class PromiseListInventoryResourceGroupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryResourceGroupsApiRequestFactory = new ListInventoryResourceGroupsApiRequestFactory(configuration),
    private responseProcessor: ListInventoryResourceGroupsApiResponseProcessor = new ListInventoryResourceGroupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a paginated list of all subnets associated with a specific project.
   * List all resource groups
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryResourceGroupsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryResourceGroupsResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryResourceGroups(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryResourceGroupsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a paginated list of all subnets associated with a specific project.
   * List all resource groups
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryResourceGroups(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventoryResourceGroupsResponse> {
    const responseWithInfo = await this.listInventoryResourceGroupsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryResourceIdentifiersApiRequestFactory, ListInventoryResourceIdentifiersApiResponseProcessor} from "../apis/ListInventoryResourceIdentifiersApi";

export class PromiseListInventoryResourceIdentifiersApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryResourceIdentifiersApiRequestFactory = new ListInventoryResourceIdentifiersApiRequestFactory(configuration),
    private responseProcessor: ListInventoryResourceIdentifiersApiResponseProcessor = new ListInventoryResourceIdentifiersApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetch a paginated list of inventory networks for a specified project ID with optional filtering capabilities.
   * List all resources ids and names
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryResourceIdentifiersWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryResourceIdentifiersResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryResourceIdentifiers(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryResourceIdentifiersWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetch a paginated list of inventory networks for a specified project ID with optional filtering capabilities.
   * List all resources ids and names
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryResourceIdentifiers(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventoryResourceIdentifiersResponse> {
    const responseWithInfo = await this.listInventoryResourceIdentifiersWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryResourceTypesApiRequestFactory, ListInventoryResourceTypesApiResponseProcessor} from "../apis/ListInventoryResourceTypesApi";

export class PromiseListInventoryResourceTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryResourceTypesApiRequestFactory = new ListInventoryResourceTypesApiRequestFactory(configuration),
    private responseProcessor: ListInventoryResourceTypesApiResponseProcessor = new ListInventoryResourceTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches and lists all available resource types for a given project.
   * List all resource types
   * @param projectId The project ID
   */
  public async listInventoryResourceTypesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListInventoryResourceTypesResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryResourceTypes(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryResourceTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches and lists all available resource types for a given project.
   * List all resource types
   * @param projectId The project ID
   */
  public async listInventoryResourceTypes(projectId: string, _options?: Configuration): Promise<ListInventoryResourceTypesResponse> {
    const responseWithInfo = await this.listInventoryResourceTypesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListInventorySourceRegionsApiRequestFactory, ListInventorySourceRegionsApiResponseProcessor} from "../apis/ListInventorySourceRegionsApi";

export class PromiseListInventorySourceRegionsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventorySourceRegionsApiRequestFactory = new ListInventorySourceRegionsApiRequestFactory(configuration),
    private responseProcessor: ListInventorySourceRegionsApiResponseProcessor = new ListInventorySourceRegionsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all source regions within the inventory for a specified project.
   * List all resources source regions
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventorySourceRegionsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventorySourceRegionsResponse>> {
    let requestContextValue = await this.requestFactory.listInventorySourceRegions(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventorySourceRegionsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all source regions within the inventory for a specified project.
   * List all resources source regions
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventorySourceRegions(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventorySourceRegionsResponse> {
    const responseWithInfo = await this.listInventorySourceRegionsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventorySubnetsApiRequestFactory, ListInventorySubnetsApiResponseProcessor} from "../apis/ListInventorySubnetsApi";

export class PromiseListInventorySubnetsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventorySubnetsApiRequestFactory = new ListInventorySubnetsApiRequestFactory(configuration),
    private responseProcessor: ListInventorySubnetsApiResponseProcessor = new ListInventorySubnetsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a paginated list of all subnets associated with a specific project.
   * List all resources subnets
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventorySubnetsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventorySubnetsResponse>> {
    let requestContextValue = await this.requestFactory.listInventorySubnets(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventorySubnetsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a paginated list of all subnets associated with a specific project.
   * List all resources subnets
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventorySubnets(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventorySubnetsResponse> {
    const responseWithInfo = await this.listInventorySubnetsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListInventoryTagsApiRequestFactory, ListInventoryTagsApiResponseProcessor} from "../apis/ListInventoryTagsApi";

export class PromiseListInventoryTagsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListInventoryTagsApiRequestFactory = new ListInventoryTagsApiRequestFactory(configuration),
    private responseProcessor: ListInventoryTagsApiResponseProcessor = new ListInventoryTagsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a paginated list of all tags associated with a specific project.
   * List all resources tags
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryTagsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListInventoryTagsResponse>> {
    let requestContextValue = await this.requestFactory.listInventoryTags(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listInventoryTagsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a paginated list of all tags associated with a specific project.
   * List all resources tags
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listInventoryTags(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListInventoryTagsResponse> {
    const responseWithInfo = await this.listInventoryTagsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListObjectStoreExclusionRulesApiRequestFactory, ListObjectStoreExclusionRulesApiResponseProcessor} from "../apis/ListObjectStoreExclusionRulesApi";

export class PromiseListObjectStoreExclusionRulesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListObjectStoreExclusionRulesApiRequestFactory = new ListObjectStoreExclusionRulesApiRequestFactory(configuration),
    private responseProcessor: ListObjectStoreExclusionRulesApiResponseProcessor = new ListObjectStoreExclusionRulesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves a list of object store exclusion rules for a specified project, with options to paginate, sort, and filter the results.
   * List all rules
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listObjectStoreExclusionRulesWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListObjectStoreExclusionRulesResponse>> {
    let requestContextValue = await this.requestFactory.listObjectStoreExclusionRules(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listObjectStoreExclusionRulesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves a list of object store exclusion rules for a specified project, with options to paginate, sort, and filter the results.
   * List all rules
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply
   */
  public async listObjectStoreExclusionRules(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListObjectStoreExclusionRulesResponse> {
    const responseWithInfo = await this.listObjectStoreExclusionRulesWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListPathSnapshotsApiRequestFactory, ListPathSnapshotsApiResponseProcessor} from "../apis/ListPathSnapshotsApi";

export class PromiseListPathSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListPathSnapshotsApiRequestFactory = new ListPathSnapshotsApiRequestFactory(configuration),
    private responseProcessor: ListPathSnapshotsApiResponseProcessor = new ListPathSnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all snapshots for a specified file on a resource within a project
   * Get all snapshots for a specific file on resource
   * @param resourceId ID of the inventory item
   * @param start The start date for the query
   * @param end The end date for the query
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param projectId The project ID
   * @param pathSnapshotsRequest The request body for searching records
   */
  public async listPathSnapshotsWithHttpInfo(resourceId: string, start: Date, end: Date, pageIndex: number, pageSize: number, projectId: string, pathSnapshotsRequest: PathSnapshotsRequest, _options?: Configuration): Promise<HttpInfo<PathSnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.listPathSnapshots(resourceId, start, end, pageIndex, pageSize, projectId, pathSnapshotsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listPathSnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all snapshots for a specified file on a resource within a project
   * Get all snapshots for a specific file on resource
   * @param resourceId ID of the inventory item
   * @param start The start date for the query
   * @param end The end date for the query
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param projectId The project ID
   * @param pathSnapshotsRequest The request body for searching records
   */
  public async listPathSnapshots(resourceId: string, start: Date, end: Date, pageIndex: number, pageSize: number, projectId: string, pathSnapshotsRequest: PathSnapshotsRequest, _options?: Configuration): Promise<PathSnapshotsResponse> {
    const responseWithInfo = await this.listPathSnapshotsWithHttpInfo(resourceId, start, end, pageIndex, pageSize, projectId, pathSnapshotsRequest, _options);
    return responseWithInfo.data;
  }


}


import { ListProjectRestoreAccountsApiRequestFactory, ListProjectRestoreAccountsApiResponseProcessor} from "../apis/ListProjectRestoreAccountsApi";

export class PromiseListProjectRestoreAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListProjectRestoreAccountsApiRequestFactory = new ListProjectRestoreAccountsApiRequestFactory(configuration),
    private responseProcessor: ListProjectRestoreAccountsApiResponseProcessor = new ListProjectRestoreAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a list of all restore accounts associated with a specific project using its project ID.
   * Get all restore accounts
   * @param projectId The project ID
   * @param pageIndex The page index (0-based)
   * @param pageSize The number of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply (JSON format)
   */
  public async listProjectRestoreAccountsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listProjectRestoreAccounts(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listProjectRestoreAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all restore accounts associated with a specific project using its project ID.
   * Get all restore accounts
   * @param projectId The project ID
   * @param pageIndex The page index (0-based)
   * @param pageSize The number of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply (JSON format)
   */
  public async listProjectRestoreAccounts(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listProjectRestoreAccountsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListProjectScanningAccountsApiRequestFactory, ListProjectScanningAccountsApiResponseProcessor} from "../apis/ListProjectScanningAccountsApi";

export class PromiseListProjectScanningAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListProjectScanningAccountsApiRequestFactory = new ListProjectScanningAccountsApiRequestFactory(configuration),
    private responseProcessor: ListProjectScanningAccountsApiResponseProcessor = new ListProjectScanningAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves all scanning accounts associated with a specified project.
   * Get all scanning accounts
   * @param projectId The project ID
   */
  public async listProjectScanningAccountsWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listProjectScanningAccounts(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listProjectScanningAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves all scanning accounts associated with a specified project.
   * Get all scanning accounts
   * @param projectId The project ID
   */
  public async listProjectScanningAccounts(projectId: string, _options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listProjectScanningAccountsWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListProjectSourceAccountsApiRequestFactory, ListProjectSourceAccountsApiResponseProcessor} from "../apis/ListProjectSourceAccountsApi";

export class PromiseListProjectSourceAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListProjectSourceAccountsApiRequestFactory = new ListProjectSourceAccountsApiRequestFactory(configuration),
    private responseProcessor: ListProjectSourceAccountsApiResponseProcessor = new ListProjectSourceAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API retrieves all source accounts associated with a specific project identified by the project ID.
   * Get all source accounts
   * @param projectId The project ID
   * @param pageIndex The page index (0-based)
   * @param pageSize The number of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply (JSON format)
   */
  public async listProjectSourceAccountsWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listProjectSourceAccounts(projectId, pageIndex, pageSize, sort, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listProjectSourceAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API retrieves all source accounts associated with a specific project identified by the project ID.
   * Get all source accounts
   * @param projectId The project ID
   * @param pageIndex The page index (0-based)
   * @param pageSize The number of items per page
   * @param sort The sort order, field:direction
   * @param filter The filter to apply (JSON format)
   */
  public async listProjectSourceAccounts(projectId: string, pageIndex?: number, pageSize?: number, sort?: string, filter?: string, _options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listProjectSourceAccountsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListProjectUsersApiRequestFactory, ListProjectUsersApiResponseProcessor} from "../apis/ListProjectUsersApi";

export class PromiseListProjectUsersApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListProjectUsersApiRequestFactory = new ListProjectUsersApiRequestFactory(configuration),
    private responseProcessor: ListProjectUsersApiResponseProcessor = new ListProjectUsersApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a paginated list of users associated with a specific project.
   * Get all users of a project
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async listProjectUsersWithHttpInfo(pageIndex: number, pageSize: number, sort: string, projectId: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListUsersResponse>> {
    let requestContextValue = await this.requestFactory.listProjectUsers(pageIndex, pageSize, sort, projectId, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listProjectUsersWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a paginated list of users associated with a specific project.
   * Get all users of a project
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async listProjectUsers(pageIndex: number, pageSize: number, sort: string, projectId: string, filter?: string, _options?: Configuration): Promise<ListUsersResponse> {
    const responseWithInfo = await this.listProjectUsersWithHttpInfo(pageIndex, pageSize, sort, projectId, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListRedshiftClustersApiRequestFactory, ListRedshiftClustersApiResponseProcessor} from "../apis/ListRedshiftClustersApi";

export class PromiseListRedshiftClustersApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListRedshiftClustersApiRequestFactory = new ListRedshiftClustersApiRequestFactory(configuration),
    private responseProcessor: ListRedshiftClustersApiResponseProcessor = new ListRedshiftClustersApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all redshift clusters associated with a specific restore account and project in a given region.
   * Get all redshift clusters in a region for an account
   * @param accountId ID of the restore account
   * @param region The region to get redshift clusters for
   * @param projectId The project ID
   */
  public async listRedshiftClustersWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<RedshiftClusters>> {
    let requestContextValue = await this.requestFactory.listRedshiftClusters(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRedshiftClustersWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all redshift clusters associated with a specific restore account and project in a given region.
   * Get all redshift clusters in a region for an account
   * @param accountId ID of the restore account
   * @param region The region to get redshift clusters for
   * @param projectId The project ID
   */
  public async listRedshiftClusters(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<RedshiftClusters> {
    const responseWithInfo = await this.listRedshiftClustersWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListRestoreJobErrorCodesApiRequestFactory, ListRestoreJobErrorCodesApiResponseProcessor} from "../apis/ListRestoreJobErrorCodesApi";

export class PromiseListRestoreJobErrorCodesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListRestoreJobErrorCodesApiRequestFactory = new ListRestoreJobErrorCodesApiRequestFactory(configuration),
    private responseProcessor: ListRestoreJobErrorCodesApiResponseProcessor = new ListRestoreJobErrorCodesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all error codes that have occurred in restore jobs for a specified project.
   * List all restore job error codes
   * @param projectId The project ID
   */
  public async listRestoreJobErrorCodesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListJobErrorCodesResponse>> {
    let requestContextValue = await this.requestFactory.listRestoreJobErrorCodes(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRestoreJobErrorCodesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all error codes that have occurred in restore jobs for a specified project.
   * List all restore job error codes
   * @param projectId The project ID
   */
  public async listRestoreJobErrorCodes(projectId: string, _options?: Configuration): Promise<ListJobErrorCodesResponse> {
    const responseWithInfo = await this.listRestoreJobErrorCodesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListRestoreJobsTypesApiRequestFactory, ListRestoreJobsTypesApiResponseProcessor} from "../apis/ListRestoreJobsTypesApi";

export class PromiseListRestoreJobsTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListRestoreJobsTypesApiRequestFactory = new ListRestoreJobsTypesApiRequestFactory(configuration),
    private responseProcessor: ListRestoreJobsTypesApiResponseProcessor = new ListRestoreJobsTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all restore job types within the job inventory for a specified project.
   * List all restore jobs types
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listRestoreJobsTypesWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListRestoreJobTypesResponse>> {
    let requestContextValue = await this.requestFactory.listRestoreJobsTypes(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRestoreJobsTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all restore job types within the job inventory for a specified project.
   * List all restore jobs types
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listRestoreJobsTypes(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListRestoreJobTypesResponse> {
    const responseWithInfo = await this.listRestoreJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListRestoreRegionsApiRequestFactory, ListRestoreRegionsApiResponseProcessor} from "../apis/ListRestoreRegionsApi";

export class PromiseListRestoreRegionsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListRestoreRegionsApiRequestFactory = new ListRestoreRegionsApiRequestFactory(configuration),
    private responseProcessor: ListRestoreRegionsApiResponseProcessor = new ListRestoreRegionsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint retrieves the restore information for a specified account. It returns details about the restore regions associated with an account in a given project.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listRestoreRegionsWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<RestoreAccountRegions>> {
    let requestContextValue = await this.requestFactory.listRestoreRegions(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listRestoreRegionsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint retrieves the restore information for a specified account. It returns details about the restore regions associated with an account in a given project.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listRestoreRegions(accountId: string, projectId: string, _options?: Configuration): Promise<RestoreAccountRegions> {
    const responseWithInfo = await this.listRestoreRegionsWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListS3BucketsApiRequestFactory, ListS3BucketsApiResponseProcessor} from "../apis/ListS3BucketsApi";

export class PromiseListS3BucketsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListS3BucketsApiRequestFactory = new ListS3BucketsApiRequestFactory(configuration),
    private responseProcessor: ListS3BucketsApiResponseProcessor = new ListS3BucketsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve all S3 buckets associated with a specific restore account and project.
   * Get all buckets in account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listS3BucketsWithHttpInfo(accountId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<S3Buckets>> {
    let requestContextValue = await this.requestFactory.listS3Buckets(accountId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listS3BucketsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve all S3 buckets associated with a specific restore account and project.
   * Get all buckets in account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   */
  public async listS3Buckets(accountId: string, projectId: string, _options?: Configuration): Promise<S3Buckets> {
    const responseWithInfo = await this.listS3BucketsWithHttpInfo(accountId, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListSamlIdentityProviderConfigsApiRequestFactory, ListSamlIdentityProviderConfigsApiResponseProcessor} from "../apis/ListSamlIdentityProviderConfigsApi";

export class PromiseListSamlIdentityProviderConfigsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListSamlIdentityProviderConfigsApiRequestFactory = new ListSamlIdentityProviderConfigsApiRequestFactory(configuration),
    private responseProcessor: ListSamlIdentityProviderConfigsApiResponseProcessor = new ListSamlIdentityProviderConfigsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches all SAML identity provider configurations associated with a given account, requiring \'admin:idp_configs\' permissions.
   * Get all saml identity provider configs of an account
   */
  public async listSamlIdentityProviderConfigsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<SamlIdentityProviderConfigsResponse>> {
    let requestContextValue = await this.requestFactory.listSamlIdentityProviderConfigs(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listSamlIdentityProviderConfigsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches all SAML identity provider configurations associated with a given account, requiring \'admin:idp_configs\' permissions.
   * Get all saml identity provider configs of an account
   */
  public async listSamlIdentityProviderConfigs(_options?: Configuration): Promise<SamlIdentityProviderConfigsResponse> {
    const responseWithInfo = await this.listSamlIdentityProviderConfigsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { ListScanJobErrorCodesApiRequestFactory, ListScanJobErrorCodesApiResponseProcessor} from "../apis/ListScanJobErrorCodesApi";

export class PromiseListScanJobErrorCodesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListScanJobErrorCodesApiRequestFactory = new ListScanJobErrorCodesApiRequestFactory(configuration),
    private responseProcessor: ListScanJobErrorCodesApiResponseProcessor = new ListScanJobErrorCodesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all error codes that have occurred in scan jobs for a specified project.
   * List all scan job error codes
   * @param projectId The project ID
   */
  public async listScanJobErrorCodesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListJobErrorCodesResponse>> {
    let requestContextValue = await this.requestFactory.listScanJobErrorCodes(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listScanJobErrorCodesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all error codes that have occurred in scan jobs for a specified project.
   * List all scan job error codes
   * @param projectId The project ID
   */
  public async listScanJobErrorCodes(projectId: string, _options?: Configuration): Promise<ListJobErrorCodesResponse> {
    const responseWithInfo = await this.listScanJobErrorCodesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListScanJobsTypesApiRequestFactory, ListScanJobsTypesApiResponseProcessor} from "../apis/ListScanJobsTypesApi";

export class PromiseListScanJobsTypesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListScanJobsTypesApiRequestFactory = new ListScanJobsTypesApiRequestFactory(configuration),
    private responseProcessor: ListScanJobsTypesApiResponseProcessor = new ListScanJobsTypesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of all scan job types within the job inventory for a specified project.
   * List all scan jobs types
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listScanJobsTypesWithHttpInfo(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<HttpInfo<ListScanJobTypesResponse>> {
    let requestContextValue = await this.requestFactory.listScanJobsTypes(projectId, pageIndex, pageSize, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listScanJobsTypesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all scan job types within the job inventory for a specified project.
   * List all scan jobs types
   * @param projectId The project ID
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param filter The filter to apply
   */
  public async listScanJobsTypes(projectId: string, pageIndex?: number, pageSize?: number, filter?: string, _options?: Configuration): Promise<ListScanJobTypesResponse> {
    const responseWithInfo = await this.listScanJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
    return responseWithInfo.data;
  }


}


import { ListScanningAccountsApiRequestFactory, ListScanningAccountsApiResponseProcessor} from "../apis/ListScanningAccountsApi";

export class PromiseListScanningAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListScanningAccountsApiRequestFactory = new ListScanningAccountsApiRequestFactory(configuration),
    private responseProcessor: ListScanningAccountsApiResponseProcessor = new ListScanningAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Fetches a list of all scanning accounts available in the system.
   * Get all scanning accounts
   */
  public async listScanningAccountsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listScanningAccounts(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listScanningAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: Fetches a list of all scanning accounts available in the system.
   * Get all scanning accounts
   */
  public async listScanningAccounts(_options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listScanningAccountsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { ListSecurityGroupsApiRequestFactory, ListSecurityGroupsApiResponseProcessor} from "../apis/ListSecurityGroupsApi";

export class PromiseListSecurityGroupsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListSecurityGroupsApiRequestFactory = new ListSecurityGroupsApiRequestFactory(configuration),
    private responseProcessor: ListSecurityGroupsApiResponseProcessor = new ListSecurityGroupsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve security group information for a specific restore account in a given project and region - that have a default RDS subnet group.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   */
  public async listSecurityGroupsWithHttpInfo(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<HttpInfo<RestoreSecurityGroups>> {
    let requestContextValue = await this.requestFactory.listSecurityGroups(accountId, region, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listSecurityGroupsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve security group information for a specific restore account in a given project and region - that have a default RDS subnet group.
   * Gets restore information for account
   * @param accountId ID of the restore account
   * @param region The region to get availability zones for
   * @param projectId The project ID
   */
  public async listSecurityGroups(accountId: string, region: string, projectId: string, _options?: Configuration): Promise<RestoreSecurityGroups> {
    const responseWithInfo = await this.listSecurityGroupsWithHttpInfo(accountId, region, projectId, _options);
    return responseWithInfo.data;
  }


}


import { ListSourceAccountsApiRequestFactory, ListSourceAccountsApiResponseProcessor} from "../apis/ListSourceAccountsApi";

export class PromiseListSourceAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListSourceAccountsApiRequestFactory = new ListSourceAccountsApiRequestFactory(configuration),
    private responseProcessor: ListSourceAccountsApiResponseProcessor = new ListSourceAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows users to retrieve all source accounts associated with their project. It requires admin-level permissions.
   * Get all source accounts
   */
  public async listSourceAccountsGlobalWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listSourceAccountsGlobal(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listSourceAccountsGlobalWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows users to retrieve all source accounts associated with their project. It requires admin-level permissions.
   * Get all source accounts
   */
  public async listSourceAccountsGlobal(_options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listSourceAccountsGlobalWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { ListUsersApiRequestFactory, ListUsersApiResponseProcessor} from "../apis/ListUsersApi";

export class PromiseListUsersApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListUsersApiRequestFactory = new ListUsersApiRequestFactory(configuration),
    private responseProcessor: ListUsersApiResponseProcessor = new ListUsersApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation retrieves a list of all users associated with a specific account, supporting pagination, sorting, and filtering.
   * Get all users of an account
   */
  public async listUsersWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListUsersResponse>> {
    let requestContextValue = await this.requestFactory.listUsers(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listUsersWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation retrieves a list of all users associated with a specific account, supporting pagination, sorting, and filtering.
   * Get all users of an account
   */
  public async listUsers(_options?: Configuration): Promise<ListUsersResponse> {
    const responseWithInfo = await this.listUsersWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { ListVaultAccountsApiRequestFactory, ListVaultAccountsApiResponseProcessor} from "../apis/ListVaultAccountsApi";

export class PromiseListVaultAccountsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ListVaultAccountsApiRequestFactory = new ListVaultAccountsApiRequestFactory(configuration),
    private responseProcessor: ListVaultAccountsApiResponseProcessor = new ListVaultAccountsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve a list of all vault accounts. Requires admin permissions.
   * Get all vault accounts
   */
  public async listVaultAccountsWithHttpInfo(_options?: Configuration): Promise<HttpInfo<ListAccountsResponse>> {
    let requestContextValue = await this.requestFactory.listVaultAccounts(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listVaultAccountsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve a list of all vault accounts. Requires admin permissions.
   * Get all vault accounts
   */
  public async listVaultAccounts(_options?: Configuration): Promise<ListAccountsResponse> {
    const responseWithInfo = await this.listVaultAccountsWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { LlgPoliciesApiRequestFactory, LlgPoliciesApiResponseProcessor} from "../apis/LlgPoliciesApi";

export class PromiseLlgPoliciesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: LlgPoliciesApiRequestFactory = new LlgPoliciesApiRequestFactory(configuration),
    private responseProcessor: LlgPoliciesApiResponseProcessor = new LlgPoliciesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates an LLG policy.
   * Create LLG Policy
   * @param projectId ID of the project you want to create an LLG policy in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createLlgPolicyRequest 
   */
  public async createLlgPolicyWithHttpInfo(projectId: string, createLlgPolicyRequest: CreateLlgPolicyRequest, _options?: Configuration): Promise<HttpInfo<CreateLlgPolicyResponse>> {
    let requestContextValue = await this.requestFactory.createLlgPolicy(projectId, createLlgPolicyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createLlgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates an LLG policy.
   * Create LLG Policy
   * @param projectId ID of the project you want to create an LLG policy in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createLlgPolicyRequest 
   */
  public async createLlgPolicy(projectId: string, createLlgPolicyRequest: CreateLlgPolicyRequest, _options?: Configuration): Promise<CreateLlgPolicyResponse> {
    const responseWithInfo = await this.createLlgPolicyWithHttpInfo(projectId, createLlgPolicyRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Deletes an LLG policy.
   * Delete LLG Policy
   * @param llgPolicyId ID of the LLG policy to delete
   * @param projectId ID of the project whose LLG policy you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteLlgPolicyWithHttpInfo(llgPolicyId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteLlgPolicy(llgPolicyId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteLlgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes an LLG policy.
   * Delete LLG Policy
   * @param llgPolicyId ID of the LLG policy to delete
   * @param projectId ID of the project whose LLG policy you want to delete. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async deleteLlgPolicy(llgPolicyId: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteLlgPolicyWithHttpInfo(llgPolicyId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves an LLG policy.
   * Get LLG Policy
   * @param llgPolicyId ID of the LLG policy to retrieve
   * @param projectId ID of the project whose LLG policy you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getLlgPolicyWithHttpInfo(llgPolicyId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetLlgPolicyResponse>> {
    let requestContextValue = await this.requestFactory.getLlgPolicy(llgPolicyId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getLlgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves an LLG policy.
   * Get LLG Policy
   * @param llgPolicyId ID of the LLG policy to retrieve
   * @param projectId ID of the project whose LLG policy you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getLlgPolicy(llgPolicyId: string, projectId: string, _options?: Configuration): Promise<GetLlgPolicyResponse> {
    const responseWithInfo = await this.getLlgPolicyWithHttpInfo(llgPolicyId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of LLG policies in the specified project. 
   * List LLG Policies
   * @param projectId ID of the project whose LLG policies you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listLlgPoliciesWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListLlgPoliciesResponse>> {
    let requestContextValue = await this.requestFactory.listLlgPolicies(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listLlgPoliciesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of LLG policies in the specified project. 
   * List LLG Policies
   * @param projectId ID of the project whose LLG policies you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listLlgPolicies(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListLlgPoliciesResponse> {
    const responseWithInfo = await this.listLlgPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates an LLG policy.
   * Update LLG Policy
   * @param llgPolicyId ID of the LLG policy to update
   * @param projectId ID of the project whose LLG policy you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateLlgPolicyRequest The request body for updating a policy
   */
  public async updateLlgPolicyWithHttpInfo(llgPolicyId: string, projectId: string, updateLlgPolicyRequest: UpdateLlgPolicyRequest, _options?: Configuration): Promise<HttpInfo<UpdateLlgPolicyResponse>> {
    let requestContextValue = await this.requestFactory.updateLlgPolicy(llgPolicyId, projectId, updateLlgPolicyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateLlgPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates an LLG policy.
   * Update LLG Policy
   * @param llgPolicyId ID of the LLG policy to update
   * @param projectId ID of the project whose LLG policy you want to update. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateLlgPolicyRequest The request body for updating a policy
   */
  public async updateLlgPolicy(llgPolicyId: string, projectId: string, updateLlgPolicyRequest: UpdateLlgPolicyRequest, _options?: Configuration): Promise<UpdateLlgPolicyResponse> {
    const responseWithInfo = await this.updateLlgPolicyWithHttpInfo(llgPolicyId, projectId, updateLlgPolicyRequest, _options);
    return responseWithInfo.data;
  }


}


import { LogSourcesApiRequestFactory, LogSourcesApiResponseProcessor} from "../apis/LogSourcesApi";

export class PromiseLogSourcesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: LogSourcesApiRequestFactory = new LogSourcesApiRequestFactory(configuration),
    private responseProcessor: LogSourcesApiResponseProcessor = new LogSourcesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of log sources (LLG resources) in the specified project. 
   * List Log Sources
   * @param projectId ID of the project whose log sources you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param locationResourceId Filter log sources by parent resource ID (e.g., S3 bucket resource ID).
   * @param type Filter log sources by type.
   */
  public async listLogSourcesWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, locationResourceId?: string, type?: 'CLOUDTRAIL' | 'DATADOG' | 'CLOUDWATCH' | 'SPLUNK', _options?: Configuration): Promise<HttpInfo<ListLogSourcesResponse>> {
    let requestContextValue = await this.requestFactory.listLogSources(projectId, pageToken, pageSize, locationResourceId, type, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listLogSourcesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of log sources (LLG resources) in the specified project. 
   * List Log Sources
   * @param projectId ID of the project whose log sources you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param locationResourceId Filter log sources by parent resource ID (e.g., S3 bucket resource ID).
   * @param type Filter log sources by type.
   */
  public async listLogSources(projectId: string, pageToken?: string, pageSize?: number, locationResourceId?: string, type?: 'CLOUDTRAIL' | 'DATADOG' | 'CLOUDWATCH' | 'SPLUNK', _options?: Configuration): Promise<ListLogSourcesResponse> {
    const responseWithInfo = await this.listLogSourcesWithHttpInfo(projectId, pageToken, pageSize, locationResourceId, type, _options);
    return responseWithInfo.data;
  }


}


import { LoginApiRequestFactory, LoginApiResponseProcessor} from "../apis/LoginApi";

export class PromiseLoginApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: LoginApiRequestFactory = new LoginApiRequestFactory(configuration),
    private responseProcessor: LoginApiResponseProcessor = new LoginApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint handles user login by optionally accepting a domain and return URL as query parameters. Depending on the outcome, it will either return no content or redirect to Cognito callback.
   * Login
   * @param domain the domain of the account. If not provided, will be extracted from the subdomain of the request
   * @param returnUrl a url to redirect to after login. this only works when cognito is bypassed
   */
  public async loginWithHttpInfo(domain?: string, returnUrl?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.login(domain, returnUrl, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.loginWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint handles user login by optionally accepting a domain and return URL as query parameters. Depending on the outcome, it will either return no content or redirect to Cognito callback.
   * Login
   * @param domain the domain of the account. If not provided, will be extracted from the subdomain of the request
   * @param returnUrl a url to redirect to after login. this only works when cognito is bypassed
   */
  public async login(domain?: string, returnUrl?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.loginWithHttpInfo(domain, returnUrl, _options);
    return responseWithInfo.data;
  }


}


import { LoginDocsApiRequestFactory, LoginDocsApiResponseProcessor} from "../apis/LoginDocsApi";

export class PromiseLoginDocsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: LoginDocsApiRequestFactory = new LoginDocsApiRequestFactory(configuration),
    private responseProcessor: LoginDocsApiResponseProcessor = new LoginDocsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint handles user login by optionally accepting a domain and return URL as query parameters. Depending on the outcome, it will either return no content or redirect to Cognito callback.
   * Login Docs
   * @param returnUrl a url to redirect to after login. this only works when cognito is bypassed
   */
  public async loginDocsWithHttpInfo(returnUrl?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.loginDocs(returnUrl, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.loginDocsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint handles user login by optionally accepting a domain and return URL as query parameters. Depending on the outcome, it will either return no content or redirect to Cognito callback.
   * Login Docs
   * @param returnUrl a url to redirect to after login. this only works when cognito is bypassed
   */
  public async loginDocs(returnUrl?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.loginDocsWithHttpInfo(returnUrl, _options);
    return responseWithInfo.data;
  }


}


import { LogoutApiRequestFactory, LogoutApiResponseProcessor} from "../apis/LogoutApi";

export class PromiseLogoutApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: LogoutApiRequestFactory = new LogoutApiRequestFactory(configuration),
    private responseProcessor: LogoutApiResponseProcessor = new LogoutApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation logs out a user by generating a logout URL and redirecting to the cognito logout endpoint if necessary.
   * logout
   */
  public async logoutWithHttpInfo(_options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.logout(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.logoutWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation logs out a user by generating a logout URL and redirecting to the cognito logout endpoint if necessary.
   * logout
   */
  public async logout(_options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.logoutWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { MeteringPopulateApiRequestFactory, MeteringPopulateApiResponseProcessor} from "../apis/MeteringPopulateApi";

export class PromiseMeteringPopulateApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: MeteringPopulateApiRequestFactory = new MeteringPopulateApiRequestFactory(configuration),
    private responseProcessor: MeteringPopulateApiResponseProcessor = new MeteringPopulateApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Populates test data for a specific project with a given version number.
   * Populate test data
   * @param projectId The project ID
   * @param version the version number of the data to populate
   */
  public async meteringPopulateWithHttpInfo(projectId: string, version: number, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.meteringPopulate(projectId, version, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.meteringPopulateWithHttpInfo(responseContext)
  }

  /**
   * Description: Populates test data for a specific project with a given version number.
   * Populate test data
   * @param projectId The project ID
   * @param version the version number of the data to populate
   */
  public async meteringPopulate(projectId: string, version: number, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.meteringPopulateWithHttpInfo(projectId, version, _options);
    return responseWithInfo.data;
  }


}


import { NotificationsApiRequestFactory, NotificationsApiResponseProcessor} from "../apis/NotificationsApi";

export class PromiseNotificationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: NotificationsApiRequestFactory = new NotificationsApiRequestFactory(configuration),
    private responseProcessor: NotificationsApiResponseProcessor = new NotificationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates a new notification policy within a given project.
   * Create Notification Policy
   * @param projectId The project ID
   * @param notificationPolicy 
   */
  public async createNotificationPolicyWithHttpInfo(projectId: string, notificationPolicy: NotificationPolicy, _options?: Configuration): Promise<HttpInfo<NotificationPolicy>> {
    let requestContextValue = await this.requestFactory.createNotificationPolicy(projectId, notificationPolicy, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createNotificationPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a new notification policy within a given project.
   * Create Notification Policy
   * @param projectId The project ID
   * @param notificationPolicy 
   */
  public async createNotificationPolicy(projectId: string, notificationPolicy: NotificationPolicy, _options?: Configuration): Promise<NotificationPolicy> {
    const responseWithInfo = await this.createNotificationPolicyWithHttpInfo(projectId, notificationPolicy, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Deletes a notification policy by its ID within a given project.
   * Delete Notification Policy
   * @param id ID of the policy to delete
   * @param projectId The project ID
   */
  public async deleteNotificationPolicyWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.deleteNotificationPolicy(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.deleteNotificationPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Deletes a notification policy by its ID within a given project.
   * Delete Notification Policy
   * @param id ID of the policy to delete
   * @param projectId The project ID
   */
  public async deleteNotificationPolicy(id: string, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.deleteNotificationPolicyWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of all notification policies associated with a specified project.
   * List Notification Policies
   * @param projectId The project ID
   */
  public async listNotificationPoliciesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListNotificationPoliciesResponse>> {
    let requestContextValue = await this.requestFactory.listNotificationPolicies(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listNotificationPoliciesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of all notification policies associated with a specified project.
   * List Notification Policies
   * @param projectId The project ID
   */
  public async listNotificationPolicies(projectId: string, _options?: Configuration): Promise<ListNotificationPoliciesResponse> {
    const responseWithInfo = await this.listNotificationPoliciesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a project\'s notifications.
   * List Notifications
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async listNotificationsWithHttpInfo(pageIndex: number, pageSize: number, sort: string, projectId: string, filter?: string, _options?: Configuration): Promise<HttpInfo<ListNotificationsResponse>> {
    let requestContextValue = await this.requestFactory.listNotifications(pageIndex, pageSize, sort, projectId, filter, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listNotificationsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a project\'s notifications.
   * List Notifications
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   * @param sort The sort order, field:direction
   * @param projectId The project ID
   * @param filter The filter to apply
   */
  public async listNotifications(pageIndex: number, pageSize: number, sort: string, projectId: string, filter?: string, _options?: Configuration): Promise<ListNotificationsResponse> {
    const responseWithInfo = await this.listNotificationsWithHttpInfo(pageIndex, pageSize, sort, projectId, filter, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates a notification policy identified by its ID and project ID.
   * Update Notification Policy
   * @param id ID of the notification policy to update
   * @param projectId The project ID
   * @param notificationPolicy 
   */
  public async updateNotificationPolicyWithHttpInfo(id: string, projectId: string, notificationPolicy: NotificationPolicy, _options?: Configuration): Promise<HttpInfo<NotificationPolicy>> {
    let requestContextValue = await this.requestFactory.updateNotificationPolicy(id, projectId, notificationPolicy, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateNotificationPolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates a notification policy identified by its ID and project ID.
   * Update Notification Policy
   * @param id ID of the notification policy to update
   * @param projectId The project ID
   * @param notificationPolicy 
   */
  public async updateNotificationPolicy(id: string, projectId: string, notificationPolicy: NotificationPolicy, _options?: Configuration): Promise<NotificationPolicy> {
    const responseWithInfo = await this.updateNotificationPolicyWithHttpInfo(id, projectId, notificationPolicy, _options);
    return responseWithInfo.data;
  }


}


import { OnboardingApiRequestFactory, OnboardingApiResponseProcessor} from "../apis/OnboardingApi";

export class PromiseOnboardingApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: OnboardingApiRequestFactory = new OnboardingApiRequestFactory(configuration),
    private responseProcessor: OnboardingApiResponseProcessor = new OnboardingApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieve the cloud providers available for this customer\'s onboarding flow.
   * Get available providers for onboarding
   */
  public async getOnboardingAvailableProvidersWithHttpInfo(_options?: Configuration): Promise<HttpInfo<Array<Provider>>> {
    let requestContextValue = await this.requestFactory.getOnboardingAvailableProviders(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getOnboardingAvailableProvidersWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieve the cloud providers available for this customer\'s onboarding flow.
   * Get available providers for onboarding
   */
  public async getOnboardingAvailableProviders(_options?: Configuration): Promise<Array<Provider>> {
    const responseWithInfo = await this.getOnboardingAvailableProvidersWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { OverrideApplicationsApiRequestFactory, OverrideApplicationsApiResponseProcessor} from "../apis/OverrideApplicationsApi";

export class PromiseOverrideApplicationsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: OverrideApplicationsApiRequestFactory = new OverrideApplicationsApiRequestFactory(configuration),
    private responseProcessor: OverrideApplicationsApiResponseProcessor = new OverrideApplicationsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows you to update the application override settings for a specific inventory item identified by the project ID.
   * Update application override for an inventory item
   * @param projectId The project ID
   * @param batchApplicationOverrides The request body for updating application override
   */
  public async overrideApplicationsWithHttpInfo(projectId: string, batchApplicationOverrides: BatchApplicationOverrides, _options?: Configuration): Promise<HttpInfo<BatchOperationResponse>> {
    let requestContextValue = await this.requestFactory.overrideApplications(projectId, batchApplicationOverrides, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.overrideApplicationsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows you to update the application override settings for a specific inventory item identified by the project ID.
   * Update application override for an inventory item
   * @param projectId The project ID
   * @param batchApplicationOverrides The request body for updating application override
   */
  public async overrideApplications(projectId: string, batchApplicationOverrides: BatchApplicationOverrides, _options?: Configuration): Promise<BatchOperationResponse> {
    const responseWithInfo = await this.overrideApplicationsWithHttpInfo(projectId, batchApplicationOverrides, _options);
    return responseWithInfo.data;
  }


}


import { ProjectConfigurationKeysApiRequestFactory, ProjectConfigurationKeysApiResponseProcessor} from "../apis/ProjectConfigurationKeysApi";

export class PromiseProjectConfigurationKeysApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ProjectConfigurationKeysApiRequestFactory = new ProjectConfigurationKeysApiRequestFactory(configuration),
    private responseProcessor: ProjectConfigurationKeysApiResponseProcessor = new ProjectConfigurationKeysApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the configuration keys of a project by ID.
   * Get Project Configuration Keys
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getProjectConfigurationKeysWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<GetProjectConfigurationKeysResponse>> {
    let requestContextValue = await this.requestFactory.getProjectConfigurationKeys(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getProjectConfigurationKeysWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the configuration keys of a project by ID.
   * Get Project Configuration Keys
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getProjectConfigurationKeys(projectId: string, _options?: Configuration): Promise<GetProjectConfigurationKeysResponse> {
    const responseWithInfo = await this.getProjectConfigurationKeysWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates the configuration key of a project by ID.
   * Update Project Configuration Key
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateProjectConfigurationKeyRequest The request body for updating the configuration key.
   */
  public async updateProjectConfigurationKeyWithHttpInfo(projectId: string, updateProjectConfigurationKeyRequest: UpdateProjectConfigurationKeyRequest, _options?: Configuration): Promise<HttpInfo<GetProjectConfigurationKeyResponse>> {
    let requestContextValue = await this.requestFactory.updateProjectConfigurationKey(projectId, updateProjectConfigurationKeyRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateProjectConfigurationKeyWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates the configuration key of a project by ID.
   * Update Project Configuration Key
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateProjectConfigurationKeyRequest The request body for updating the configuration key.
   */
  public async updateProjectConfigurationKey(projectId: string, updateProjectConfigurationKeyRequest: UpdateProjectConfigurationKeyRequest, _options?: Configuration): Promise<GetProjectConfigurationKeyResponse> {
    const responseWithInfo = await this.updateProjectConfigurationKeyWithHttpInfo(projectId, updateProjectConfigurationKeyRequest, _options);
    return responseWithInfo.data;
  }


}


import { ProjectPopulateApiRequestFactory, ProjectPopulateApiResponseProcessor} from "../apis/ProjectPopulateApi";

export class PromiseProjectPopulateApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ProjectPopulateApiRequestFactory = new ProjectPopulateApiRequestFactory(configuration),
    private responseProcessor: ProjectPopulateApiResponseProcessor = new ProjectPopulateApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint populates test data for a specified project using the given project ID and version number.
   * Populate test data
   * @param projectId The project ID
   * @param version the version number of the data to populate
   */
  public async projectPopulateWithHttpInfo(projectId: string, version: number, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.projectPopulate(projectId, version, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.projectPopulateWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint populates test data for a specified project using the given project ID and version number.
   * Populate test data
   * @param projectId The project ID
   * @param version the version number of the data to populate
   */
  public async projectPopulate(projectId: string, version: number, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.projectPopulateWithHttpInfo(projectId, version, _options);
    return responseWithInfo.data;
  }


}


import { ProjectsApiRequestFactory, ProjectsApiResponseProcessor} from "../apis/ProjectsApi";

export class PromiseProjectsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ProjectsApiRequestFactory = new ProjectsApiRequestFactory(configuration),
    private responseProcessor: ProjectsApiResponseProcessor = new ProjectsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves a list of projects in your Eon account.  This method takes no parameters and returns a list of projects the access key has access to. 
   * List Projects
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listProjectsWithHttpInfo(pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListProjectsResponse>> {
    let requestContextValue = await this.requestFactory.listProjects(pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listProjectsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of projects in your Eon account.  This method takes no parameters and returns a list of projects the access key has access to. 
   * List Projects
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listProjects(pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListProjectsResponse> {
    const responseWithInfo = await this.listProjectsWithHttpInfo(pageToken, pageSize, _options);
    return responseWithInfo.data;
  }


}


import { PulumiApiRequestFactory, PulumiApiResponseProcessor} from "../apis/PulumiApi";

export class PromisePulumiApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: PulumiApiRequestFactory = new PulumiApiRequestFactory(configuration),
    private responseProcessor: PulumiApiResponseProcessor = new PulumiApiResponseProcessor() 
  ) {
    
  }

  /**
   * Run Pulumi up for configuration
   * @param id 
   */
  public async upPulumiStackWithHttpInfo(id: string, _options?: Configuration): Promise<HttpInfo<JobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.upPulumiStack(id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.upPulumiStackWithHttpInfo(responseContext)
  }

  /**
   * Run Pulumi up for configuration
   * @param id 
   */
  public async upPulumiStack(id: string, _options?: Configuration): Promise<JobInitiationResponse> {
    const responseWithInfo = await this.upPulumiStackWithHttpInfo(id, _options);
    return responseWithInfo.data;
  }

  /**
   * Run Pulumi up for multiple configurations
   * @param pulumiConfigurationsRequest Filter options. 
   */
  public async upPulumiStacksWithHttpInfo(pulumiConfigurationsRequest?: PulumiConfigurationsRequest, _options?: Configuration): Promise<HttpInfo<UpPulumiStacksResponse>> {
    let requestContextValue = await this.requestFactory.upPulumiStacks(pulumiConfigurationsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.upPulumiStacksWithHttpInfo(responseContext)
  }

  /**
   * Run Pulumi up for multiple configurations
   * @param pulumiConfigurationsRequest Filter options. 
   */
  public async upPulumiStacks(pulumiConfigurationsRequest?: PulumiConfigurationsRequest, _options?: Configuration): Promise<UpPulumiStacksResponse> {
    const responseWithInfo = await this.upPulumiStacksWithHttpInfo(pulumiConfigurationsRequest, _options);
    return responseWithInfo.data;
  }


}


import { RefreshApiRequestFactory, RefreshApiResponseProcessor} from "../apis/RefreshApi";

export class PromiseRefreshApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: RefreshApiRequestFactory = new RefreshApiRequestFactory(configuration),
    private responseProcessor: RefreshApiResponseProcessor = new RefreshApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Endpoint to refresh the OAuth token and potentially redirect to a specified URL or the homepage.
   * refresh
   * @param selectedRoleId Optional role ID to select after refreshing the token.
   */
  public async refreshWithHttpInfo(selectedRoleId?: string, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.refresh(selectedRoleId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.refreshWithHttpInfo(responseContext)
  }

  /**
   * Description: Endpoint to refresh the OAuth token and potentially redirect to a specified URL or the homepage.
   * refresh
   * @param selectedRoleId Optional role ID to select after refreshing the token.
   */
  public async refresh(selectedRoleId?: string, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.refreshWithHttpInfo(selectedRoleId, _options);
    return responseWithInfo.data;
  }


}


import { RegisterApiRequestFactory, RegisterApiResponseProcessor} from "../apis/RegisterApi";

export class PromiseRegisterApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: RegisterApiRequestFactory = new RegisterApiRequestFactory(configuration),
    private responseProcessor: RegisterApiResponseProcessor = new RegisterApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information.
   * Register a new account
   * @param registerInput Includes payment token, and initial login information
   */
  public async registerWithHttpInfo(registerInput: RegisterInput, _options?: Configuration): Promise<HttpInfo<RegisterResponse>> {
    let requestContextValue = await this.requestFactory.register(registerInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.registerWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to register a new account by providing payment token and initial login information.
   * Register a new account
   * @param registerInput Includes payment token, and initial login information
   */
  public async register(registerInput: RegisterInput, _options?: Configuration): Promise<RegisterResponse> {
    const responseWithInfo = await this.registerWithHttpInfo(registerInput, _options);
    return responseWithInfo.data;
  }


}


import { ResourcesApiRequestFactory, ResourcesApiResponseProcessor} from "../apis/ResourcesApi";

export class PromiseResourcesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ResourcesApiRequestFactory = new ResourcesApiRequestFactory(configuration),
    private responseProcessor: ResourcesApiResponseProcessor = new ResourcesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Allows a resource to be backed up by Eon, scanned, and checked for violations. 
   * Cancel Resource Backup Exclusion
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to cancel the backup exclusion for.
   */
  public async cancelResourceBackupExclusionWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<CancelExclusionFromBackupResponse>> {
    let requestContextValue = await this.requestFactory.cancelResourceBackupExclusion(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.cancelResourceBackupExclusionWithHttpInfo(responseContext)
  }

  /**
   * Description: Allows a resource to be backed up by Eon, scanned, and checked for violations. 
   * Cancel Resource Backup Exclusion
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to cancel the backup exclusion for.
   */
  public async cancelResourceBackupExclusion(projectId: string, id: string, _options?: Configuration): Promise<CancelExclusionFromBackupResponse> {
    const responseWithInfo = await this.cancelResourceBackupExclusionWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Enables a resource from limit exceeded status making it available for backup. 
   * Enable Resource from Limit Exceeded
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to enable from limit exceeded status for.
   */
  public async enableResourceFromLimitExceededWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<EnableFromLimitExceededResponse>> {
    let requestContextValue = await this.requestFactory.enableResourceFromLimitExceeded(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.enableResourceFromLimitExceededWithHttpInfo(responseContext)
  }

  /**
   * Description: Enables a resource from limit exceeded status making it available for backup. 
   * Enable Resource from Limit Exceeded
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to enable from limit exceeded status for.
   */
  public async enableResourceFromLimitExceeded(projectId: string, id: string, _options?: Configuration): Promise<EnableFromLimitExceededResponse> {
    const responseWithInfo = await this.enableResourceFromLimitExceededWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Prevents a resource from being backed up by Eon and suppresses scanning and violations.  You can cancel this action by calling [Cancel Resource Backup Exclusion](cancel-resource-backup-exclusion) for the same resource. 
   * Exclude Resource from Backup
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to exclude from backup.
   */
  public async excludeResourceFromBackupWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<ExcludeFromBackupResponse>> {
    let requestContextValue = await this.requestFactory.excludeResourceFromBackup(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.excludeResourceFromBackupWithHttpInfo(responseContext)
  }

  /**
   * Description: Prevents a resource from being backed up by Eon and suppresses scanning and violations.  You can cancel this action by calling [Cancel Resource Backup Exclusion](cancel-resource-backup-exclusion) for the same resource. 
   * Exclude Resource from Backup
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to exclude from backup.
   */
  public async excludeResourceFromBackup(projectId: string, id: string, _options?: Configuration): Promise<ExcludeFromBackupResponse> {
    const responseWithInfo = await this.excludeResourceFromBackupWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a resource by ID.
   * Get Resource
   * @param id Eon-assigned ID of the resource to retrieve.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getResourceWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetResourceResponse>> {
    let requestContextValue = await this.requestFactory.getResource(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getResourceWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a resource by ID.
   * Get Resource
   * @param id Eon-assigned ID of the resource to retrieve.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getResource(id: string, projectId: string, _options?: Configuration): Promise<GetResourceResponse> {
    const responseWithInfo = await this.getResourceWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Lists the history of backup policy attachment events for a resource.
   * List Resource Attachment Events
   * @param id Eon-assigned ID of the resource.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async listResourceAttachmentEventsWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<ListResourceAttachmentEventsResponse>> {
    let requestContextValue = await this.requestFactory.listResourceAttachmentEvents(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listResourceAttachmentEventsWithHttpInfo(responseContext)
  }

  /**
   * Description: Lists the history of backup policy attachment events for a resource.
   * List Resource Attachment Events
   * @param id Eon-assigned ID of the resource.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async listResourceAttachmentEvents(id: string, projectId: string, _options?: Configuration): Promise<ListResourceAttachmentEventsResponse> {
    const responseWithInfo = await this.listResourceAttachmentEventsWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of resources for the given project.
   * List Resources
   * @param projectId ID of the project whose resources you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listInventoryRequest 
   */
  public async listResourcesWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, listInventoryRequest?: ListInventoryRequest, _options?: Configuration): Promise<HttpInfo<ListResourcesResponse>> {
    let requestContextValue = await this.requestFactory.listResources(projectId, pageToken, pageSize, listInventoryRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listResourcesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of resources for the given project.
   * List Resources
   * @param projectId ID of the project whose resources you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listInventoryRequest 
   */
  public async listResources(projectId: string, pageToken?: string, pageSize?: number, listInventoryRequest?: ListInventoryRequest, _options?: Configuration): Promise<ListResourcesResponse> {
    const responseWithInfo = await this.listResourcesWithHttpInfo(projectId, pageToken, pageSize, listInventoryRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Manually sets a resource\'s data classes, overriding auto-classification.  You can revert to auto-classification of data classes by calling [Remove Data Classes Override](remove-data-classes-override) for the same resource. 
   * Override Data Classes
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to override.
   * @param overrideDataClassificationsRequest 
   */
  public async overrideDataClassesWithHttpInfo(projectId: string, id: string, overrideDataClassificationsRequest: OverrideDataClassificationsRequest, _options?: Configuration): Promise<HttpInfo<OverrideDataClassificationsResponse>> {
    let requestContextValue = await this.requestFactory.overrideDataClasses(projectId, id, overrideDataClassificationsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.overrideDataClassesWithHttpInfo(responseContext)
  }

  /**
   * Description: Manually sets a resource\'s data classes, overriding auto-classification.  You can revert to auto-classification of data classes by calling [Remove Data Classes Override](remove-data-classes-override) for the same resource. 
   * Override Data Classes
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to override.
   * @param overrideDataClassificationsRequest 
   */
  public async overrideDataClasses(projectId: string, id: string, overrideDataClassificationsRequest: OverrideDataClassificationsRequest, _options?: Configuration): Promise<OverrideDataClassificationsResponse> {
    const responseWithInfo = await this.overrideDataClassesWithHttpInfo(projectId, id, overrideDataClassificationsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Manually sets a resource\'s environment, overriding auto-classification.  You can revert to auto-classification of environment by calling [Remove Environment Override](remove-environment-override) for the same resource. 
   * Override Environment
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to override.
   * @param overrideEnvironmentRequest 
   */
  public async overrideEnvironmentWithHttpInfo(projectId: string, id: string, overrideEnvironmentRequest: OverrideEnvironmentRequest, _options?: Configuration): Promise<HttpInfo<OverrideEnvironmentResponse>> {
    let requestContextValue = await this.requestFactory.overrideEnvironment(projectId, id, overrideEnvironmentRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.overrideEnvironmentWithHttpInfo(responseContext)
  }

  /**
   * Description: Manually sets a resource\'s environment, overriding auto-classification.  You can revert to auto-classification of environment by calling [Remove Environment Override](remove-environment-override) for the same resource. 
   * Override Environment
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to override.
   * @param overrideEnvironmentRequest 
   */
  public async overrideEnvironment(projectId: string, id: string, overrideEnvironmentRequest: OverrideEnvironmentRequest, _options?: Configuration): Promise<OverrideEnvironmentResponse> {
    const responseWithInfo = await this.overrideEnvironmentWithHttpInfo(projectId, id, overrideEnvironmentRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Removes a resource\'s data classes override, which re-enables auto-classification for the resource. 
   * Remove Data Classes Override
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to remove the override from.
   */
  public async removeDataClassesOverrideWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.removeDataClassesOverride(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.removeDataClassesOverrideWithHttpInfo(responseContext)
  }

  /**
   * Description: Removes a resource\'s data classes override, which re-enables auto-classification for the resource. 
   * Remove Data Classes Override
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to remove the override from.
   */
  public async removeDataClassesOverride(projectId: string, id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.removeDataClassesOverrideWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Removes a resource\'s environment override, which re-enables auto-classification for the resource. 
   * Remove Environment Override
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to remove the override from.
   */
  public async removeEnvironmentOverrideWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.removeEnvironmentOverride(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.removeEnvironmentOverrideWithHttpInfo(responseContext)
  }

  /**
   * Description: Removes a resource\'s environment override, which re-enables auto-classification for the resource. 
   * Remove Environment Override
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned ID of the resource to remove the override from.
   */
  public async removeEnvironmentOverride(projectId: string, id: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.removeEnvironmentOverrideWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }


}


import { RestoreAtlasClusterApiRequestFactory, RestoreAtlasClusterApiResponseProcessor} from "../apis/RestoreAtlasClusterApi";

export class PromiseRestoreAtlasClusterApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: RestoreAtlasClusterApiRequestFactory = new RestoreAtlasClusterApiRequestFactory(configuration),
    private responseProcessor: RestoreAtlasClusterApiResponseProcessor = new RestoreAtlasClusterApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API initiates the process of restoring a database from a specified snapshot.
   * Start a database restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreAtlasClusterInput The request body for restoring files
   */
  public async restoreAtlasClusterWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreAtlasClusterInput: RestoreAtlasClusterInput, _options?: Configuration): Promise<HttpInfo<Identifier>> {
    let requestContextValue = await this.requestFactory.restoreAtlasCluster(projectId, id, snapshotId, restoreAtlasClusterInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreAtlasClusterWithHttpInfo(responseContext)
  }

  /**
   * Description: This API initiates the process of restoring a database from a specified snapshot.
   * Start a database restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreAtlasClusterInput The request body for restoring files
   */
  public async restoreAtlasCluster(projectId: string, id: string, snapshotId: string, restoreAtlasClusterInput: RestoreAtlasClusterInput, _options?: Configuration): Promise<Identifier> {
    const responseWithInfo = await this.restoreAtlasClusterWithHttpInfo(projectId, id, snapshotId, restoreAtlasClusterInput, _options);
    return responseWithInfo.data;
  }


}


import { RestoreEksNamespaceApiRequestFactory, RestoreEksNamespaceApiResponseProcessor} from "../apis/RestoreEksNamespaceApi";

export class PromiseRestoreEksNamespaceApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: RestoreEksNamespaceApiRequestFactory = new RestoreEksNamespaceApiRequestFactory(configuration),
    private responseProcessor: RestoreEksNamespaceApiResponseProcessor = new RestoreEksNamespaceApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API initiates the process of restoring a instance from a specified snapshot.
   * Start an Eks Namespace restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreEksNamespaceInput The request body for restoring the instance
   */
  public async restoreEksNamespaceWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreEksNamespaceInput: RestoreEksNamespaceInput, _options?: Configuration): Promise<HttpInfo<Identifier>> {
    let requestContextValue = await this.requestFactory.restoreEksNamespace(projectId, id, snapshotId, restoreEksNamespaceInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreEksNamespaceWithHttpInfo(responseContext)
  }

  /**
   * Description: This API initiates the process of restoring a instance from a specified snapshot.
   * Start an Eks Namespace restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreEksNamespaceInput The request body for restoring the instance
   */
  public async restoreEksNamespace(projectId: string, id: string, snapshotId: string, restoreEksNamespaceInput: RestoreEksNamespaceInput, _options?: Configuration): Promise<Identifier> {
    const responseWithInfo = await this.restoreEksNamespaceWithHttpInfo(projectId, id, snapshotId, restoreEksNamespaceInput, _options);
    return responseWithInfo.data;
  }


}


import { RestoreImageApiRequestFactory, RestoreImageApiResponseProcessor} from "../apis/RestoreImageApi";

export class PromiseRestoreImageApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: RestoreImageApiRequestFactory = new RestoreImageApiRequestFactory(configuration),
    private responseProcessor: RestoreImageApiResponseProcessor = new RestoreImageApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API initiates the process of restoring an image from a specified snapshot.
   * Start an image restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreImageInput The request body for restoring the image
   */
  public async restoreImageWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreImageInput: RestoreImageInput, _options?: Configuration): Promise<HttpInfo<Identifier>> {
    let requestContextValue = await this.requestFactory.restoreImage(projectId, id, snapshotId, restoreImageInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreImageWithHttpInfo(responseContext)
  }

  /**
   * Description: This API initiates the process of restoring an image from a specified snapshot.
   * Start an image restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreImageInput The request body for restoring the image
   */
  public async restoreImage(projectId: string, id: string, snapshotId: string, restoreImageInput: RestoreImageInput, _options?: Configuration): Promise<Identifier> {
    const responseWithInfo = await this.restoreImageWithHttpInfo(projectId, id, snapshotId, restoreImageInput, _options);
    return responseWithInfo.data;
  }


}


import { SamlCallbackApiRequestFactory, SamlCallbackApiResponseProcessor} from "../apis/SamlCallbackApi";

export class PromiseSamlCallbackApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SamlCallbackApiRequestFactory = new SamlCallbackApiRequestFactory(configuration),
    private responseProcessor: SamlCallbackApiResponseProcessor = new SamlCallbackApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Endpoint to handle SAML Assertion Consumer Service (ACS) callback. Receives a signed SAML response from the identity provider, validates it, and generates an Eon JWT token for the authenticated user.
   * Handle SAML callback.
   * @param sAMLResponse Base64-encoded SAML assertion response from the identity provider
   * @param relayState Optional return URL to redirect the user after successful authentication
   */
  public async sAMLCallbackWithHttpInfo(sAMLResponse: string, relayState?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.sAMLCallback(sAMLResponse, relayState, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.sAMLCallbackWithHttpInfo(responseContext)
  }

  /**
   * Description: Endpoint to handle SAML Assertion Consumer Service (ACS) callback. Receives a signed SAML response from the identity provider, validates it, and generates an Eon JWT token for the authenticated user.
   * Handle SAML callback.
   * @param sAMLResponse Base64-encoded SAML assertion response from the identity provider
   * @param relayState Optional return URL to redirect the user after successful authentication
   */
  public async sAMLCallback(sAMLResponse: string, relayState?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.sAMLCallbackWithHttpInfo(sAMLResponse, relayState, _options);
    return responseWithInfo.data;
  }


}


import { SamlMetadataApiRequestFactory, SamlMetadataApiResponseProcessor} from "../apis/SamlMetadataApi";

export class PromiseSamlMetadataApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SamlMetadataApiRequestFactory = new SamlMetadataApiRequestFactory(configuration),
    private responseProcessor: SamlMetadataApiResponseProcessor = new SamlMetadataApiResponseProcessor() 
  ) {
    
  }

  /**
   * SAML Metadata Endpoint
   */
  public async samlMetadataWithHttpInfo(_options?: Configuration): Promise<HttpInfo<string>> {
    let requestContextValue = await this.requestFactory.samlMetadata(_options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.samlMetadataWithHttpInfo(responseContext)
  }

  /**
   * SAML Metadata Endpoint
   */
  public async samlMetadata(_options?: Configuration): Promise<string> {
    const responseWithInfo = await this.samlMetadataWithHttpInfo(_options);
    return responseWithInfo.data;
  }


}


import { SamlSSOApiRequestFactory, SamlSSOApiResponseProcessor} from "../apis/SamlSSOApi";

export class PromiseSamlSSOApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SamlSSOApiRequestFactory = new SamlSSOApiRequestFactory(configuration),
    private responseProcessor: SamlSSOApiResponseProcessor = new SamlSSOApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Bypasses the Eon login screen and redirects the browser straight to the configured Identity Provider (IdP) with a **signed** SAML 2.0 `<AuthnRequest>`.   The signature uses the tenant-specific service-provider certificate generated by `GenerateSAMLServiceProviderCertificate`. 
   * Initiate SAML SSO with Signed AuthnRequest
   * @param provider Name of the SAML IdP configuration to use   (case-sensitive &#x60;provider_name&#x60;). 
   * @param returnUrl Absolute URL to redirect the user to **after** successful authentication.   Defaults to &#x60;https://&lt;domain&gt;.console.eon.io/home&#x60;. 
   */
  public async samlSsoWithHttpInfo(provider: string, returnUrl?: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.samlSso(provider, returnUrl, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.samlSsoWithHttpInfo(responseContext)
  }

  /**
   * Description: Bypasses the Eon login screen and redirects the browser straight to the configured Identity Provider (IdP) with a **signed** SAML 2.0 `<AuthnRequest>`.   The signature uses the tenant-specific service-provider certificate generated by `GenerateSAMLServiceProviderCertificate`. 
   * Initiate SAML SSO with Signed AuthnRequest
   * @param provider Name of the SAML IdP configuration to use   (case-sensitive &#x60;provider_name&#x60;). 
   * @param returnUrl Absolute URL to redirect the user to **after** successful authentication.   Defaults to &#x60;https://&lt;domain&gt;.console.eon.io/home&#x60;. 
   */
  public async samlSso(provider: string, returnUrl?: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.samlSsoWithHttpInfo(provider, returnUrl, _options);
    return responseWithInfo.data;
  }


}


import { SaveBackupScopeApiRequestFactory, SaveBackupScopeApiResponseProcessor} from "../apis/SaveBackupScopeApi";

export class PromiseSaveBackupScopeApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SaveBackupScopeApiRequestFactory = new SaveBackupScopeApiRequestFactory(configuration),
    private responseProcessor: SaveBackupScopeApiResponseProcessor = new SaveBackupScopeApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Saves the backup scope for the project ID.
   * Save the backup scope
   * @param projectId The project ID
   * @param backupScope The request body for updating the backup scope
   * @param triggerDiscovery Trigger resources discovery, default is false
   */
  public async saveBackupScopeWithHttpInfo(projectId: string, backupScope: BackupScope, triggerDiscovery?: boolean, _options?: Configuration): Promise<HttpInfo<BackupScope>> {
    let requestContextValue = await this.requestFactory.saveBackupScope(projectId, backupScope, triggerDiscovery, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.saveBackupScopeWithHttpInfo(responseContext)
  }

  /**
   * Description: Saves the backup scope for the project ID.
   * Save the backup scope
   * @param projectId The project ID
   * @param backupScope The request body for updating the backup scope
   * @param triggerDiscovery Trigger resources discovery, default is false
   */
  public async saveBackupScope(projectId: string, backupScope: BackupScope, triggerDiscovery?: boolean, _options?: Configuration): Promise<BackupScope> {
    const responseWithInfo = await this.saveBackupScopeWithHttpInfo(projectId, backupScope, triggerDiscovery, _options);
    return responseWithInfo.data;
  }


}


import { SaveDynamoDbBackupConfigApiRequestFactory, SaveDynamoDbBackupConfigApiResponseProcessor} from "../apis/SaveDynamoDbBackupConfigApi";

export class PromiseSaveDynamoDbBackupConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SaveDynamoDbBackupConfigApiRequestFactory = new SaveDynamoDbBackupConfigApiRequestFactory(configuration),
    private responseProcessor: SaveDynamoDbBackupConfigApiResponseProcessor = new SaveDynamoDbBackupConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Saves or updates the DynamoDB backup configuration at the resource, account, or project level.
   * Save DynamoDB backup configuration
   * @param projectId The project ID
   * @param dynamoDbBackupConfig The DynamoDB backup configuration to save
   * @param accountId The cloud account ID for account-level configuration
   * @param resourceId The resource ID for resource-level configuration
   */
  public async saveDynamoDbBackupConfigWithHttpInfo(projectId: string, dynamoDbBackupConfig: DynamoDbBackupConfig, accountId?: string, resourceId?: string, _options?: Configuration): Promise<HttpInfo<DynamoDbBackupConfig>> {
    let requestContextValue = await this.requestFactory.saveDynamoDbBackupConfig(projectId, dynamoDbBackupConfig, accountId, resourceId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.saveDynamoDbBackupConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: Saves or updates the DynamoDB backup configuration at the resource, account, or project level.
   * Save DynamoDB backup configuration
   * @param projectId The project ID
   * @param dynamoDbBackupConfig The DynamoDB backup configuration to save
   * @param accountId The cloud account ID for account-level configuration
   * @param resourceId The resource ID for resource-level configuration
   */
  public async saveDynamoDbBackupConfig(projectId: string, dynamoDbBackupConfig: DynamoDbBackupConfig, accountId?: string, resourceId?: string, _options?: Configuration): Promise<DynamoDbBackupConfig> {
    const responseWithInfo = await this.saveDynamoDbBackupConfigWithHttpInfo(projectId, dynamoDbBackupConfig, accountId, resourceId, _options);
    return responseWithInfo.data;
  }


}


import { SaveSecurityPlanApiRequestFactory, SaveSecurityPlanApiResponseProcessor} from "../apis/SaveSecurityPlanApi";

export class PromiseSaveSecurityPlanApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SaveSecurityPlanApiRequestFactory = new SaveSecurityPlanApiRequestFactory(configuration),
    private responseProcessor: SaveSecurityPlanApiResponseProcessor = new SaveSecurityPlanApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Saves the security plan for the project ID.
   * Save the security plan
   * @param projectId The project ID
   * @param securityPlan The request body for updating the security plan
   */
  public async saveSecurityPlanWithHttpInfo(projectId: string, securityPlan: SecurityPlan, _options?: Configuration): Promise<HttpInfo<SecurityPlan>> {
    let requestContextValue = await this.requestFactory.saveSecurityPlan(projectId, securityPlan, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.saveSecurityPlanWithHttpInfo(responseContext)
  }

  /**
   * Description: Saves the security plan for the project ID.
   * Save the security plan
   * @param projectId The project ID
   * @param securityPlan The request body for updating the security plan
   */
  public async saveSecurityPlan(projectId: string, securityPlan: SecurityPlan, _options?: Configuration): Promise<SecurityPlan> {
    const responseWithInfo = await this.saveSecurityPlanWithHttpInfo(projectId, securityPlan, _options);
    return responseWithInfo.data;
  }


}


import { SearchApiRequestFactory, SearchApiResponseProcessor} from "../apis/SearchApi";

export class PromiseSearchApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SearchApiRequestFactory = new SearchApiRequestFactory(configuration),
    private responseProcessor: SearchApiResponseProcessor = new SearchApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves schema information for all LLG log sources in the project.  Returns the available schemas for each LLG resource that has been backed up, including column names, types, and row counts. 
   * Get Logs Schemas
   * @param projectId The project ID.
   */
  public async getLogsSchemasWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<LogsSchemasResponse>> {
    let requestContextValue = await this.requestFactory.getLogsSchemas(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getLogsSchemasWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves schema information for all LLG log sources in the project.  Returns the available schemas for each LLG resource that has been backed up, including column names, types, and row counts. 
   * Get Logs Schemas
   * @param projectId The project ID.
   */
  public async getLogsSchemas(projectId: string, _options?: Configuration): Promise<LogsSchemasResponse> {
    const responseWithInfo = await this.getLogsSchemasWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API allows searching for records within a specified project with pagination support.
   * Search for files
   * @param projectId The project ID
   * @param searchInput The request body for searching records
   */
  public async searchFilesWithHttpInfo(projectId: string, searchInput: SearchInput, _options?: Configuration): Promise<HttpInfo<SearchFilesResponse>> {
    let requestContextValue = await this.requestFactory.searchFiles(projectId, searchInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.searchFilesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows searching for records within a specified project with pagination support.
   * Search for files
   * @param projectId The project ID
   * @param searchInput The request body for searching records
   */
  public async searchFiles(projectId: string, searchInput: SearchInput, _options?: Configuration): Promise<SearchFilesResponse> {
    const responseWithInfo = await this.searchFilesWithHttpInfo(projectId, searchInput, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Searches logs in the text index using Lucene syntax queries.  This API queries the text index generated by the LLG backup workflow for CloudTrail and other log sources.  Lucene query syntax is supported, including: - Field queries: `field:value` - Boolean operators: `AND`, `OR`, `NOT` - Wildcards: `*` (multiple chars), `?` (single char) - Range queries: `field:[min TO max]` - Grouping: `(query1 OR query2) AND query3` 
   * Search Logs
   * @param projectId The project ID.
   * @param searchLogsInput The request body for searching logs.
   */
  public async searchLogsWithHttpInfo(projectId: string, searchLogsInput: SearchLogsInput, _options?: Configuration): Promise<HttpInfo<SearchLogsResponse>> {
    let requestContextValue = await this.requestFactory.searchLogs(projectId, searchLogsInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.searchLogsWithHttpInfo(responseContext)
  }

  /**
   * Description: Searches logs in the text index using Lucene syntax queries.  This API queries the text index generated by the LLG backup workflow for CloudTrail and other log sources.  Lucene query syntax is supported, including: - Field queries: `field:value` - Boolean operators: `AND`, `OR`, `NOT` - Wildcards: `*` (multiple chars), `?` (single char) - Range queries: `field:[min TO max]` - Grouping: `(query1 OR query2) AND query3` 
   * Search Logs
   * @param projectId The project ID.
   * @param searchLogsInput The request body for searching logs.
   */
  public async searchLogs(projectId: string, searchLogsInput: SearchLogsInput, _options?: Configuration): Promise<SearchLogsResponse> {
    const responseWithInfo = await this.searchLogsWithHttpInfo(projectId, searchLogsInput, _options);
    return responseWithInfo.data;
  }


}


import { SearchPopulateApiRequestFactory, SearchPopulateApiResponseProcessor} from "../apis/SearchPopulateApi";

export class PromiseSearchPopulateApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SearchPopulateApiRequestFactory = new SearchPopulateApiRequestFactory(configuration),
    private responseProcessor: SearchPopulateApiResponseProcessor = new SearchPopulateApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint is used to populate test data for a specific project. It requires the project ID and the version number of the data to be populated.
   * Populate test data
   * @param version the version number of the data to populate
   * @param projectId The project ID
   */
  public async searchPopulateWithHttpInfo(version: number, projectId: string, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.searchPopulate(version, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.searchPopulateWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint is used to populate test data for a specific project. It requires the project ID and the version number of the data to be populated.
   * Populate test data
   * @param version the version number of the data to populate
   * @param projectId The project ID
   */
  public async searchPopulate(version: number, projectId: string, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.searchPopulateWithHttpInfo(version, projectId, _options);
    return responseWithInfo.data;
  }


}


import { SearchTablesApiRequestFactory, SearchTablesApiResponseProcessor} from "../apis/SearchTablesApi";

export class PromiseSearchTablesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SearchTablesApiRequestFactory = new SearchTablesApiRequestFactory(configuration),
    private responseProcessor: SearchTablesApiResponseProcessor = new SearchTablesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API searches for database records within a specified project. It allows pagination through the use of `pageIndex` and `pageSize` query parameters, and requires a JSON request body conforming to the SearchInput schema.
   * Search for database records
   * @param projectId The project ID
   * @param searchInput The request body for searching records
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   */
  public async searchTablesWithHttpInfo(projectId: string, searchInput: SearchInput, pageIndex?: number, pageSize?: number, _options?: Configuration): Promise<HttpInfo<SearchDBResponse>> {
    let requestContextValue = await this.requestFactory.searchTables(projectId, searchInput, pageIndex, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.searchTablesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API searches for database records within a specified project. It allows pagination through the use of `pageIndex` and `pageSize` query parameters, and requires a JSON request body conforming to the SearchInput schema.
   * Search for database records
   * @param projectId The project ID
   * @param searchInput The request body for searching records
   * @param pageIndex The page index
   * @param pageSize The numbers of items per page
   */
  public async searchTables(projectId: string, searchInput: SearchInput, pageIndex?: number, pageSize?: number, _options?: Configuration): Promise<SearchDBResponse> {
    const responseWithInfo = await this.searchTablesWithHttpInfo(projectId, searchInput, pageIndex, pageSize, _options);
    return responseWithInfo.data;
  }


}


import { SecurityScansApiRequestFactory, SecurityScansApiResponseProcessor} from "../apis/SecurityScansApi";

export class PromiseSecurityScansApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SecurityScansApiRequestFactory = new SecurityScansApiRequestFactory(configuration),
    private responseProcessor: SecurityScansApiResponseProcessor = new SecurityScansApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Retrieves the number of infected resources which is not mutted by resource ID.
   * Count the number of infected resources which is not mutted
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   */
  public async countInfectedResourcesWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<InfectedResourcesCountResponse>> {
    let requestContextValue = await this.requestFactory.countInfectedResources(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.countInfectedResourcesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves the number of infected resources which is not mutted by resource ID.
   * Count the number of infected resources which is not mutted
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   */
  public async countInfectedResources(projectId: string, _options?: Configuration): Promise<InfectedResourcesCountResponse> {
    const responseWithInfo = await this.countInfectedResourcesWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves Scans information by resource ID.
   * Get Security related properties on the resource
   * @param id ID of the resource to retrieve.
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   */
  public async getSecurityPropertiesWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetCloudResourceSecurityPropertiesResponse>> {
    let requestContextValue = await this.requestFactory.getSecurityProperties(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSecurityPropertiesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves Scans information by resource ID.
   * Get Security related properties on the resource
   * @param id ID of the resource to retrieve.
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   */
  public async getSecurityProperties(id: string, projectId: string, _options?: Configuration): Promise<GetCloudResourceSecurityPropertiesResponse> {
    const responseWithInfo = await this.getSecurityPropertiesWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves Scans information by resource ID.
   * Security Scans summary, first infected, last healthy, last scan by resource ID
   * @param id ID of the resource to retrieve.
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   */
  public async getSecurityScansSummaryWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<SecurityScansSummaryResponse>> {
    let requestContextValue = await this.requestFactory.getSecurityScansSummary(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSecurityScansSummaryWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves Scans information by resource ID.
   * Security Scans summary, first infected, last healthy, last scan by resource ID
   * @param id ID of the resource to retrieve.
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   */
  public async getSecurityScansSummary(id: string, projectId: string, _options?: Configuration): Promise<SecurityScansSummaryResponse> {
    const responseWithInfo = await this.getSecurityScansSummaryWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves Scans information by resource ID.
   * Save Security related properties on the resource
   * @param id ID of the resource to retrieve.
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   * @param saveCloudResourceSecurityPropertiesRequest The request body for creating a new scan job
   */
  public async saveSecurityPropertiesWithHttpInfo(id: string, projectId: string, saveCloudResourceSecurityPropertiesRequest: SaveCloudResourceSecurityPropertiesRequest, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.saveSecurityProperties(id, projectId, saveCloudResourceSecurityPropertiesRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.saveSecurityPropertiesWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves Scans information by resource ID.
   * Save Security related properties on the resource
   * @param id ID of the resource to retrieve.
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   * @param saveCloudResourceSecurityPropertiesRequest The request body for creating a new scan job
   */
  public async saveSecurityProperties(id: string, projectId: string, saveCloudResourceSecurityPropertiesRequest: SaveCloudResourceSecurityPropertiesRequest, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.saveSecurityPropertiesWithHttpInfo(id, projectId, saveCloudResourceSecurityPropertiesRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Exclude or cancel exclusion of a resource from security scans.
   * Update resource security scans exclusion
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   * @param id Eon-assigned ID of the resource to exclude from security scans.
   * @param updateResourceSecurityScansExclusionRequest The request body for updating a resource security scans exclusion
   */
  public async updateResourceSecurityScansExclusionWithHttpInfo(projectId: string, id: string, updateResourceSecurityScansExclusionRequest: UpdateResourceSecurityScansExclusionRequest, _options?: Configuration): Promise<HttpInfo<SecurityAssignmentStatus>> {
    let requestContextValue = await this.requestFactory.updateResourceSecurityScansExclusion(projectId, id, updateResourceSecurityScansExclusionRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateResourceSecurityScansExclusionWithHttpInfo(responseContext)
  }

  /**
   * Description: Exclude or cancel exclusion of a resource from security scans.
   * Update resource security scans exclusion
   * @param projectId [ID of the project](/global-settings/projects) the resource is in. 
   * @param id Eon-assigned ID of the resource to exclude from security scans.
   * @param updateResourceSecurityScansExclusionRequest The request body for updating a resource security scans exclusion
   */
  public async updateResourceSecurityScansExclusion(projectId: string, id: string, updateResourceSecurityScansExclusionRequest: UpdateResourceSecurityScansExclusionRequest, _options?: Configuration): Promise<SecurityAssignmentStatus> {
    const responseWithInfo = await this.updateResourceSecurityScansExclusionWithHttpInfo(projectId, id, updateResourceSecurityScansExclusionRequest, _options);
    return responseWithInfo.data;
  }


}


import { SnapshotsApiRequestFactory, SnapshotsApiResponseProcessor} from "../apis/SnapshotsApi";

export class PromiseSnapshotsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: SnapshotsApiRequestFactory = new SnapshotsApiRequestFactory(configuration),
    private responseProcessor: SnapshotsApiResponseProcessor = new SnapshotsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Checks if a specific restore method is available for a DynamoDB snapshot.  For CapacityBased method, this always returns true.  For ImportTable method, checks: - If the snapshot has Local Secondary Indexes (LSI), the method is not available unless the destination includes advancedIndexConfig with localSecondaryIndexMode set to discard. - If the snapshot table size exceeds a certain size, the method is not available. 
   * Checks DynamoDB Restore Method Availability for a snapshot
   * @param projectId ID of the project the snapshot is in.
   * @param id ID of the snapshot to check.
   * @param dynamoDBRestoreMethodAvailabilityRequest 
   */
  public async dynamoDBRestoreMethodAvailabilityWithHttpInfo(projectId: string, id: string, dynamoDBRestoreMethodAvailabilityRequest: DynamoDBRestoreMethodAvailabilityRequest, _options?: Configuration): Promise<HttpInfo<DynamoDBRestoreMethodAvailabilityResponse>> {
    let requestContextValue = await this.requestFactory.dynamoDBRestoreMethodAvailability(projectId, id, dynamoDBRestoreMethodAvailabilityRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.dynamoDBRestoreMethodAvailabilityWithHttpInfo(responseContext)
  }

  /**
   * Description: Checks if a specific restore method is available for a DynamoDB snapshot.  For CapacityBased method, this always returns true.  For ImportTable method, checks: - If the snapshot has Local Secondary Indexes (LSI), the method is not available unless the destination includes advancedIndexConfig with localSecondaryIndexMode set to discard. - If the snapshot table size exceeds a certain size, the method is not available. 
   * Checks DynamoDB Restore Method Availability for a snapshot
   * @param projectId ID of the project the snapshot is in.
   * @param id ID of the snapshot to check.
   * @param dynamoDBRestoreMethodAvailabilityRequest 
   */
  public async dynamoDBRestoreMethodAvailability(projectId: string, id: string, dynamoDBRestoreMethodAvailabilityRequest: DynamoDBRestoreMethodAvailabilityRequest, _options?: Configuration): Promise<DynamoDBRestoreMethodAvailabilityResponse> {
    const responseWithInfo = await this.dynamoDBRestoreMethodAvailabilityWithHttpInfo(projectId, id, dynamoDBRestoreMethodAvailabilityRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Returns the available point-in-time restore window for an RDS database (Aurora cluster or RDS instance).  This shows the earliest and latest times you can restore to, based on the database\'s automated backup retention period.  **Note:** The latest restorable time is typically a few minutes behind current time, as AWS maintains continuous backups. 
   * Get RDS AWS Native PITR Window
   * @param projectId ID of the project the RDS database is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned RDS database resource ID (Aurora cluster or RDS instance).
   */
  public async getRdsNativePitrWindowWithHttpInfo(projectId: string, id: string, _options?: Configuration): Promise<HttpInfo<RdsNativePitrWindow>> {
    let requestContextValue = await this.requestFactory.getRdsNativePitrWindow(projectId, id, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getRdsNativePitrWindowWithHttpInfo(responseContext)
  }

  /**
   * Description: Returns the available point-in-time restore window for an RDS database (Aurora cluster or RDS instance).  This shows the earliest and latest times you can restore to, based on the database\'s automated backup retention period.  **Note:** The latest restorable time is typically a few minutes behind current time, as AWS maintains continuous backups. 
   * Get RDS AWS Native PITR Window
   * @param projectId ID of the project the RDS database is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned RDS database resource ID (Aurora cluster or RDS instance).
   */
  public async getRdsNativePitrWindow(projectId: string, id: string, _options?: Configuration): Promise<RdsNativePitrWindow> {
    const responseWithInfo = await this.getRdsNativePitrWindowWithHttpInfo(projectId, id, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves an Eon snapshot.
   * Get Snapshot
   * @param id ID of the Eon snapshot to retrieve.
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getSnapshotWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetSnapshotResponse>> {
    let requestContextValue = await this.requestFactory.getSnapshot(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves an Eon snapshot.
   * Get Snapshot
   * @param id ID of the Eon snapshot to retrieve.
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getSnapshot(id: string, projectId: string, _options?: Configuration): Promise<GetSnapshotResponse> {
    const responseWithInfo = await this.getSnapshotWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Places a user hold on an Eon snapshot. Snapshots with user holds are protected from removal even if their expiration time passes.  <Note> Point-in-time recovery (PiTR) snapshots can\'t be placed on hold. </Note> 
   * Hold Snapshot
   * @param id ID of the Eon snapshot.
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async holdSnapshotWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<any>> {
    let requestContextValue = await this.requestFactory.holdSnapshot(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.holdSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: Places a user hold on an Eon snapshot. Snapshots with user holds are protected from removal even if their expiration time passes.  <Note> Point-in-time recovery (PiTR) snapshots can\'t be placed on hold. </Note> 
   * Hold Snapshot
   * @param id ID of the Eon snapshot.
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async holdSnapshot(id: string, projectId: string, _options?: Configuration): Promise<any> {
    const responseWithInfo = await this.holdSnapshotWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of Eon snapshots for the given resource. 
   * List Resource Snapshots
   * @param id Eon-assigned ID of the resource whose Eon snapshots you want to retrieve.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listInventorySnapshotsRequest Filter options.
   */
  public async listResourceSnapshotsWithHttpInfo(id: string, projectId: string, pageToken?: string, pageSize?: number, listInventorySnapshotsRequest?: ListInventorySnapshotsRequest, _options?: Configuration): Promise<HttpInfo<ListInventorySnapshotsResponse>> {
    let requestContextValue = await this.requestFactory.listResourceSnapshots(id, projectId, pageToken, pageSize, listInventorySnapshotsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listResourceSnapshotsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of Eon snapshots for the given resource. 
   * List Resource Snapshots
   * @param id Eon-assigned ID of the resource whose Eon snapshots you want to retrieve.
   * @param projectId ID of the project the resource is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param listInventorySnapshotsRequest Filter options.
   */
  public async listResourceSnapshots(id: string, projectId: string, pageToken?: string, pageSize?: number, listInventorySnapshotsRequest?: ListInventorySnapshotsRequest, _options?: Configuration): Promise<ListInventorySnapshotsResponse> {
    const responseWithInfo = await this.listResourceSnapshotsWithHttpInfo(id, projectId, pageToken, pageSize, listInventorySnapshotsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Removes user hold from an Eon snapshot, allowing it to be removed once it passes its expiration time.  <Warning> If the snapshot is past its expiration time, it\'s marked for removal as soon as the hold is removed. </Warning> 
   * Remove Snapshot Hold
   * @param id ID of the Eon snapshot.
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async removeSnapshotHoldWithHttpInfo(id: string, projectId: string, _options?: Configuration): Promise<HttpInfo<any>> {
    let requestContextValue = await this.requestFactory.removeSnapshotHold(id, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.removeSnapshotHoldWithHttpInfo(responseContext)
  }

  /**
   * Description: Removes user hold from an Eon snapshot, allowing it to be removed once it passes its expiration time.  <Warning> If the snapshot is past its expiration time, it\'s marked for removal as soon as the hold is removed. </Warning> 
   * Remove Snapshot Hold
   * @param id ID of the Eon snapshot.
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async removeSnapshotHold(id: string, projectId: string, _options?: Configuration): Promise<any> {
    const responseWithInfo = await this.removeSnapshotHoldWithHttpInfo(id, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an Azure disk from an Eon snapshot.  When restoring a disk, you\'ll specify the configurations of the disk you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Azure Disk
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAzureDiskRequest 
   */
  public async restoreAzureDiskWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreAzureDiskRequest: RestoreAzureDiskRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreAzureDisk(projectId, id, snapshotId, restoreAzureDiskRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreAzureDiskWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an Azure disk from an Eon snapshot.  When restoring a disk, you\'ll specify the configurations of the disk you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Azure Disk
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAzureDiskRequest 
   */
  public async restoreAzureDisk(projectId: string, id: string, snapshotId: string, restoreAzureDiskRequest: RestoreAzureDiskRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreAzureDiskWithHttpInfo(projectId, id, snapshotId, restoreAzureDiskRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an Azure SQL Database from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Azure SQL Database
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAzureSqlDatabaseRequest The request body for restoring the instance
   */
  public async restoreAzureSqlDatabaseWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreAzureSqlDatabaseRequest: RestoreAzureSqlDatabaseRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreAzureSqlDatabase(projectId, id, snapshotId, restoreAzureSqlDatabaseRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreAzureSqlDatabaseWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an Azure SQL Database from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Azure SQL Database
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAzureSqlDatabaseRequest The request body for restoring the instance
   */
  public async restoreAzureSqlDatabase(projectId: string, id: string, snapshotId: string, restoreAzureSqlDatabaseRequest: RestoreAzureSqlDatabaseRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreAzureSqlDatabaseWithHttpInfo(projectId, id, snapshotId, restoreAzureSqlDatabaseRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an Azure VM instance from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Azure VM Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAzureVmInstanceRequest 
   */
  public async restoreAzureVmInstanceWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreAzureVmInstanceRequest: RestoreAzureVmInstanceRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreAzureVmInstance(projectId, id, snapshotId, restoreAzureVmInstanceRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreAzureVmInstanceWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an Azure VM instance from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Azure VM Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAzureVmInstanceRequest 
   */
  public async restoreAzureVmInstance(projectId: string, id: string, snapshotId: string, restoreAzureVmInstanceRequest: RestoreAzureVmInstanceRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreAzureVmInstanceWithHttpInfo(projectId, id, snapshotId, restoreAzureVmInstanceRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores a BigQuery dataset from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore BigQuery Dataset
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreBigQueryDatasetRequest 
   */
  public async restoreBigQueryDatasetWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreBigQueryDatasetRequest: RestoreBigQueryDatasetRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreBigQueryDataset(projectId, id, snapshotId, restoreBigQueryDatasetRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreBigQueryDatasetWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores a BigQuery dataset from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore BigQuery Dataset
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreBigQueryDatasetRequest 
   */
  public async restoreBigQueryDataset(projectId: string, id: string, snapshotId: string, restoreBigQueryDatasetRequest: RestoreBigQueryDatasetRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreBigQueryDatasetWithHttpInfo(projectId, id, snapshotId, restoreBigQueryDatasetRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores a bucket from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Bucket
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreBucketRequest 
   */
  public async restoreBucketWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreBucketRequest: RestoreBucketRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreBucket(projectId, id, snapshotId, restoreBucketRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreBucketWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores a bucket from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Bucket
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreBucketRequest 
   */
  public async restoreBucket(projectId: string, id: string, snapshotId: string, restoreBucketRequest: RestoreBucketRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreBucketWithHttpInfo(projectId, id, snapshotId, restoreBucketRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an RDS instance from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore RDS Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreDbToRdsInstanceRequest 
   */
  public async restoreDatabaseWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreDbToRdsInstanceRequest: RestoreDbToRdsInstanceRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreDatabase(projectId, id, snapshotId, restoreDbToRdsInstanceRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreDatabaseWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an RDS instance from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore RDS Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreDbToRdsInstanceRequest 
   */
  public async restoreDatabase(projectId: string, id: string, snapshotId: string, restoreDbToRdsInstanceRequest: RestoreDbToRdsInstanceRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreDatabaseWithHttpInfo(projectId, id, snapshotId, restoreDbToRdsInstanceRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API initiates the process of restoring a database from a specified snapshot.
   * Start a database restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreDbToRdsSnapshotRequest The request body for restoring files
   */
  public async restoreDbToRdsSnapshotWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreDbToRdsSnapshotRequest: RestoreDbToRdsSnapshotRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreDbToRdsSnapshot(projectId, id, snapshotId, restoreDbToRdsSnapshotRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreDbToRdsSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: This API initiates the process of restoring a database from a specified snapshot.
   * Start a database restore from snapshot
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreDbToRdsSnapshotRequest The request body for restoring files
   */
  public async restoreDbToRdsSnapshot(projectId: string, id: string, snapshotId: string, restoreDbToRdsSnapshotRequest: RestoreDbToRdsSnapshotRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreDbToRdsSnapshotWithHttpInfo(projectId, id, snapshotId, restoreDbToRdsSnapshotRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores a DynamoDB table from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore DynamoDB Table
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreDynamoDBTableRequest 
   */
  public async restoreDynamoDBTableWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreDynamoDBTableRequest: RestoreDynamoDBTableRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreDynamoDBTable(projectId, id, snapshotId, restoreDynamoDBTableRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreDynamoDBTableWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores a DynamoDB table from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore DynamoDB Table
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreDynamoDBTableRequest 
   */
  public async restoreDynamoDBTable(projectId: string, id: string, snapshotId: string, restoreDynamoDBTableRequest: RestoreDynamoDBTableRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreDynamoDBTableWithHttpInfo(projectId, id, snapshotId, restoreDynamoDBTableRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: This API initiates the process of restoring a DynamoDB table snapshot to an existing table. The target table must be empty and have matching key schema.
   * Restore DynamoDB snapshot to an existing table
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreDynamoDBToExistingTableRequest The request body for restoring DynamoDB to an existing table
   */
  public async restoreDynamoDBToExistingTableWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreDynamoDBToExistingTableRequest: RestoreDynamoDBToExistingTableRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreDynamoDBToExistingTable(projectId, id, snapshotId, restoreDynamoDBToExistingTableRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreDynamoDBToExistingTableWithHttpInfo(responseContext)
  }

  /**
   * Description: This API initiates the process of restoring a DynamoDB table snapshot to an existing table. The target table must be empty and have matching key schema.
   * Restore DynamoDB snapshot to an existing table
   * @param projectId The project ID
   * @param id The inventory item ID
   * @param snapshotId The snapshot ID
   * @param restoreDynamoDBToExistingTableRequest The request body for restoring DynamoDB to an existing table
   */
  public async restoreDynamoDBToExistingTable(projectId: string, id: string, snapshotId: string, restoreDynamoDBToExistingTableRequest: RestoreDynamoDBToExistingTableRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreDynamoDBToExistingTableWithHttpInfo(projectId, id, snapshotId, restoreDynamoDBToExistingTableRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an EFS file system from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID.  The EFS file system will be restored to the source AWS account where the backup was created. Cross-region restore is not currently supported for EFS. 
   * Restore EFS File System
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreEFSRequest 
   */
  public async restoreEFSWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreEFSRequest: RestoreEFSRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreEFS(projectId, id, snapshotId, restoreEFSRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreEFSWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an EFS file system from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID.  The EFS file system will be restored to the source AWS account where the backup was created. Cross-region restore is not currently supported for EFS. 
   * Restore EFS File System
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreEFSRequest 
   */
  public async restoreEFS(projectId: string, id: string, snapshotId: string, restoreEFSRequest: RestoreEFSRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreEFSWithHttpInfo(projectId, id, snapshotId, restoreEFSRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an EBS volume from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore EBS Volume
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreVolumeToEbsRequest 
   */
  public async restoreEbsVolumeWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreVolumeToEbsRequest: RestoreVolumeToEbsRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreEbsVolume(projectId, id, snapshotId, restoreVolumeToEbsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreEbsVolumeWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an EBS volume from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore EBS Volume
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreVolumeToEbsRequest 
   */
  public async restoreEbsVolume(projectId: string, id: string, snapshotId: string, restoreVolumeToEbsRequest: RestoreVolumeToEbsRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreEbsVolumeWithHttpInfo(projectId, id, snapshotId, restoreVolumeToEbsRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an EC2 instance from an Eon snapshot.  When restoring an EC2 instance, you\'ll need to specify the configurations of the instance and each volume you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore EC2 Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAwsEc2InstanceRequest 
   */
  public async restoreEc2InstanceWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreAwsEc2InstanceRequest: RestoreAwsEc2InstanceRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreEc2Instance(projectId, id, snapshotId, restoreAwsEc2InstanceRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreEc2InstanceWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an EC2 instance from an Eon snapshot.  When restoring an EC2 instance, you\'ll need to specify the configurations of the instance and each volume you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore EC2 Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreAwsEc2InstanceRequest 
   */
  public async restoreEc2Instance(projectId: string, id: string, snapshotId: string, restoreAwsEc2InstanceRequest: RestoreAwsEc2InstanceRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreEc2InstanceWithHttpInfo(projectId, id, snapshotId, restoreAwsEc2InstanceRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an FSx file system from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID.  The FSx file system will be restored to the source AWS account where the backup was created. Cross-region restore is supported - if a different region is specified, the backup will be copied to the target region before restore. 
   * Restore FSx File System
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreFSxRequest 
   */
  public async restoreFSxWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreFSxRequest: RestoreFSxRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreFSx(projectId, id, snapshotId, restoreFSxRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreFSxWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an FSx file system from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID.  The FSx file system will be restored to the source AWS account where the backup was created. Cross-region restore is supported - if a different region is specified, the backup will be copied to the target region before restore. 
   * Restore FSx File System
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreFSxRequest 
   */
  public async restoreFSx(projectId: string, id: string, snapshotId: string, restoreFSxRequest: RestoreFSxRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreFSxWithHttpInfo(projectId, id, snapshotId, restoreFSxRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores files from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Files
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreFilesRequest 
   */
  public async restoreFilesWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreFilesRequest: RestoreFilesRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreFiles(projectId, id, snapshotId, restoreFilesRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreFilesWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores files from an Eon snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore Files
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreFilesRequest 
   */
  public async restoreFiles(projectId: string, id: string, snapshotId: string, restoreFilesRequest: RestoreFilesRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreFilesWithHttpInfo(projectId, id, snapshotId, restoreFilesRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores a GCP Cloud SQL instance from an Eon snapshot.  When restoring a GCP Cloud SQL instance, you\'ll need to specify the configurations of the database instance you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore GCP Cloud SQL Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreGcpCloudSqlRequest 
   */
  public async restoreGcpCloudSqlWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreGcpCloudSqlRequest: RestoreGcpCloudSqlRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreGcpCloudSql(projectId, id, snapshotId, restoreGcpCloudSqlRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreGcpCloudSqlWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores a GCP Cloud SQL instance from an Eon snapshot.  When restoring a GCP Cloud SQL instance, you\'ll need to specify the configurations of the database instance you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore GCP Cloud SQL Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreGcpCloudSqlRequest 
   */
  public async restoreGcpCloudSql(projectId: string, id: string, snapshotId: string, restoreGcpCloudSqlRequest: RestoreGcpCloudSqlRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreGcpCloudSqlWithHttpInfo(projectId, id, snapshotId, restoreGcpCloudSqlRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores a GCP disk from an Eon snapshot.  When restoring a disk, you\'ll specify the configurations of the disk you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore GCP Disk
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreGcpDiskRequest 
   */
  public async restoreGcpDiskWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreGcpDiskRequest: RestoreGcpDiskRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreGcpDisk(projectId, id, snapshotId, restoreGcpDiskRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreGcpDiskWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores a GCP disk from an Eon snapshot.  When restoring a disk, you\'ll specify the configurations of the disk you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore GCP Disk
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreGcpDiskRequest 
   */
  public async restoreGcpDisk(projectId: string, id: string, snapshotId: string, restoreGcpDiskRequest: RestoreGcpDiskRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreGcpDiskWithHttpInfo(projectId, id, snapshotId, restoreGcpDiskRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores a GCP VM instance from an Eon snapshot.  When restoring a GCP VM instance, you\'ll need to specify the configurations of the instance and each disk you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore GCP VM Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreGcpVmInstanceRequest 
   */
  public async restoreGcpVmInstanceWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreGcpVmInstanceRequest: RestoreGcpVmInstanceRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreGcpVmInstance(projectId, id, snapshotId, restoreGcpVmInstanceRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreGcpVmInstanceWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores a GCP VM instance from an Eon snapshot.  When restoring a GCP VM instance, you\'ll need to specify the configurations of the instance and each disk you want to restore. You can retrieve the configurations from the snapshot by calling [Get Snapshot](/api/api-reference/backups/snapshots/get-snapshot) or [List Resource Snapshots](/api/api-reference/backups/snapshots/list-resource-snapshots).  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore GCP VM Instance
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreGcpVmInstanceRequest 
   */
  public async restoreGcpVmInstance(projectId: string, id: string, snapshotId: string, restoreGcpVmInstanceRequest: RestoreGcpVmInstanceRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreGcpVmInstanceWithHttpInfo(projectId, id, snapshotId, restoreGcpVmInstanceRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an Aurora DB cluster or RDS instance to a specific point in time using AWS\'s native PITR feature.  This operation uses AWS\'s automated continuous backups to restore the database to any second within the backup retention period.  The API automatically detects whether the resource is an Aurora cluster or a regular RDS instance and performs the appropriate restore operation.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore RDS Database using AWS Native Point-in-Time Restore
   * @param projectId ID of the project the RDS database is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned RDS database resource ID (Aurora cluster or RDS instance).
   * @param restoreRdsNativePitrRequest 
   */
  public async restoreRdsNativePitrWithHttpInfo(projectId: string, id: string, restoreRdsNativePitrRequest: RestoreRdsNativePitrRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreRdsNativePitr(projectId, id, restoreRdsNativePitrRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreRdsNativePitrWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an Aurora DB cluster or RDS instance to a specific point in time using AWS\'s native PITR feature.  This operation uses AWS\'s automated continuous backups to restore the database to any second within the backup retention period.  The API automatically detects whether the resource is an Aurora cluster or a regular RDS instance and performs the appropriate restore operation.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore RDS Database using AWS Native Point-in-Time Restore
   * @param projectId ID of the project the RDS database is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned RDS database resource ID (Aurora cluster or RDS instance).
   * @param restoreRdsNativePitrRequest 
   */
  public async restoreRdsNativePitr(projectId: string, id: string, restoreRdsNativePitrRequest: RestoreRdsNativePitrRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreRdsNativePitrWithHttpInfo(projectId, id, restoreRdsNativePitrRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Restores an Eon volume snapshot to an EBS snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore to EBS Snapshot
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreVolumeToEbsSnapshotRequest 
   */
  public async restoreToEbsSnapshotWithHttpInfo(projectId: string, id: string, snapshotId: string, restoreVolumeToEbsSnapshotRequest: RestoreVolumeToEbsSnapshotRequest, _options?: Configuration): Promise<HttpInfo<RestoreJobInitiationResponse>> {
    let requestContextValue = await this.requestFactory.restoreToEbsSnapshot(projectId, id, snapshotId, restoreVolumeToEbsSnapshotRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.restoreToEbsSnapshotWithHttpInfo(responseContext)
  }

  /**
   * Description: Restores an Eon volume snapshot to an EBS snapshot.  This operation is asynchronous. It triggers a restore job and returns a job ID, which can be used to track the progress of the restore job. You can follow the progress of the restore job by calling [Get Restore Job](/api/api-reference/jobs/restore-jobs/get-restore-job) with the job ID. 
   * Restore to EBS Snapshot
   * @param projectId ID of the project the snapshot is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param id Eon-assigned resource ID.
   * @param snapshotId ID of the Eon [snapshot](/api/api-reference/backups/snapshots/list-resource-snapshots) to restore.
   * @param restoreVolumeToEbsSnapshotRequest 
   */
  public async restoreToEbsSnapshot(projectId: string, id: string, snapshotId: string, restoreVolumeToEbsSnapshotRequest: RestoreVolumeToEbsSnapshotRequest, _options?: Configuration): Promise<RestoreJobInitiationResponse> {
    const responseWithInfo = await this.restoreToEbsSnapshotWithHttpInfo(projectId, id, snapshotId, restoreVolumeToEbsSnapshotRequest, _options);
    return responseWithInfo.data;
  }


}


import { StorageGatewaysApiRequestFactory, StorageGatewaysApiResponseProcessor} from "../apis/StorageGatewaysApi";

export class PromiseStorageGatewaysApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: StorageGatewaysApiRequestFactory = new StorageGatewaysApiRequestFactory(configuration),
    private responseProcessor: StorageGatewaysApiResponseProcessor = new StorageGatewaysApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Imports an ACM certificate into the scanning account and swaps the DLSG gateway\'s TLS listener to use it. 
   * Import DLSG certificate and apply to gateway
   * @param projectId 
   * @param importDlsgCertificateRequest 
   */
  public async importDlsgCertificateWithHttpInfo(projectId: string, importDlsgCertificateRequest: ImportDlsgCertificateRequest, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.importDlsgCertificate(projectId, importDlsgCertificateRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.importDlsgCertificateWithHttpInfo(responseContext)
  }

  /**
   * Description: Imports an ACM certificate into the scanning account and swaps the DLSG gateway\'s TLS listener to use it. 
   * Import DLSG certificate and apply to gateway
   * @param projectId 
   * @param importDlsgCertificateRequest 
   */
  public async importDlsgCertificate(projectId: string, importDlsgCertificateRequest: ImportDlsgCertificateRequest, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.importDlsgCertificateWithHttpInfo(projectId, importDlsgCertificateRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of storage gateways in the specified project. 
   * List Storage Gateways
   * @param projectId ID of the project whose storage gateways you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async listStorageGatewaysWithHttpInfo(projectId: string, _options?: Configuration): Promise<HttpInfo<ListStorageGatewaysResponse>> {
    let requestContextValue = await this.requestFactory.listStorageGateways(projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listStorageGatewaysWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of storage gateways in the specified project. 
   * List Storage Gateways
   * @param projectId ID of the project whose storage gateways you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async listStorageGateways(projectId: string, _options?: Configuration): Promise<ListStorageGatewaysResponse> {
    const responseWithInfo = await this.listStorageGatewaysWithHttpInfo(projectId, _options);
    return responseWithInfo.data;
  }


}


import { UpdateAccountUIPreferencesApiRequestFactory, UpdateAccountUIPreferencesApiResponseProcessor} from "../apis/UpdateAccountUIPreferencesApi";

export class PromiseUpdateAccountUIPreferencesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateAccountUIPreferencesApiRequestFactory = new UpdateAccountUIPreferencesApiRequestFactory(configuration),
    private responseProcessor: UpdateAccountUIPreferencesApiResponseProcessor = new UpdateAccountUIPreferencesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows updating the UI preferences at the organization level. It requires the key of the preference to be updated and a request body containing the new preference settings.
   * Update UI preferences at organization level
   * @param key Key of the preference
   * @param requestBody The request body for updating account UI preferences
   */
  public async updateAccountUIPreferencesWithHttpInfo(key: string, requestBody: { [key: string]: any; }, _options?: Configuration): Promise<HttpInfo<Preferences>> {
    let requestContextValue = await this.requestFactory.updateAccountUIPreferences(key, requestBody, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateAccountUIPreferencesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows updating the UI preferences at the organization level. It requires the key of the preference to be updated and a request body containing the new preference settings.
   * Update UI preferences at organization level
   * @param key Key of the preference
   * @param requestBody The request body for updating account UI preferences
   */
  public async updateAccountUIPreferences(key: string, requestBody: { [key: string]: any; }, _options?: Configuration): Promise<Preferences> {
    const responseWithInfo = await this.updateAccountUIPreferencesWithHttpInfo(key, requestBody, _options);
    return responseWithInfo.data;
  }


}


import { UpdateApiCredentialsApiRequestFactory, UpdateApiCredentialsApiResponseProcessor} from "../apis/UpdateApiCredentialsApi";

export class PromiseUpdateApiCredentialsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateApiCredentialsApiRequestFactory = new UpdateApiCredentialsApiRequestFactory(configuration),
    private responseProcessor: UpdateApiCredentialsApiResponseProcessor = new UpdateApiCredentialsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API updates the details of existing api credentials identified by their ID. It overrides the api\'s information with the provided data.
   * Update api credentials details
   * @param clientId Client ID of the api credential to update
   * @param updateApiCredentialsRequest The request body for updating api credentials
   */
  public async updateApiCredentialsWithHttpInfo(clientId: string, updateApiCredentialsRequest: UpdateApiCredentialsRequest, _options?: Configuration): Promise<HttpInfo<ApiCredentialsProperties>> {
    let requestContextValue = await this.requestFactory.updateApiCredentials(clientId, updateApiCredentialsRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateApiCredentialsWithHttpInfo(responseContext)
  }

  /**
   * Description: This API updates the details of existing api credentials identified by their ID. It overrides the api\'s information with the provided data.
   * Update api credentials details
   * @param clientId Client ID of the api credential to update
   * @param updateApiCredentialsRequest The request body for updating api credentials
   */
  public async updateApiCredentials(clientId: string, updateApiCredentialsRequest: UpdateApiCredentialsRequest, _options?: Configuration): Promise<ApiCredentialsProperties> {
    const responseWithInfo = await this.updateApiCredentialsWithHttpInfo(clientId, updateApiCredentialsRequest, _options);
    return responseWithInfo.data;
  }


}


import { UpdateBackupPolicyApiRequestFactory, UpdateBackupPolicyApiResponseProcessor} from "../apis/UpdateBackupPolicyApi";

export class PromiseUpdateBackupPolicyApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateBackupPolicyApiRequestFactory = new UpdateBackupPolicyApiRequestFactory(configuration),
    private responseProcessor: UpdateBackupPolicyApiResponseProcessor = new UpdateBackupPolicyApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API updates an existing backup policy identified by its ID and project ID.
   * Update a policy
   * @param id ID of the policy to update
   * @param projectId The project ID
   * @param backupPolicyConfig The request body for updating a policy
   */
  public async updatePolicyWithHttpInfo(id: string, projectId: string, backupPolicyConfig: BackupPolicyConfig, _options?: Configuration): Promise<HttpInfo<BackupPolicyConfig>> {
    let requestContextValue = await this.requestFactory.updatePolicy(id, projectId, backupPolicyConfig, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updatePolicyWithHttpInfo(responseContext)
  }

  /**
   * Description: This API updates an existing backup policy identified by its ID and project ID.
   * Update a policy
   * @param id ID of the policy to update
   * @param projectId The project ID
   * @param backupPolicyConfig The request body for updating a policy
   */
  public async updatePolicy(id: string, projectId: string, backupPolicyConfig: BackupPolicyConfig, _options?: Configuration): Promise<BackupPolicyConfig> {
    const responseWithInfo = await this.updatePolicyWithHttpInfo(id, projectId, backupPolicyConfig, _options);
    return responseWithInfo.data;
  }


}


import { UpdateControlApiRequestFactory, UpdateControlApiResponseProcessor} from "../apis/UpdateControlApi";

export class PromiseUpdateControlApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateControlApiRequestFactory = new UpdateControlApiRequestFactory(configuration),
    private responseProcessor: UpdateControlApiResponseProcessor = new UpdateControlApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: API for updating a specific control in a project. Requires the control ID and project ID as path parameters, and the control data as request body. Returns updated control data on success.
   * Update a control
   * @param id ID of the control to update
   * @param projectId The project ID
   * @param control The request body for updating a control
   */
  public async updateControlWithHttpInfo(id: string, projectId: string, control: Control, _options?: Configuration): Promise<HttpInfo<Control>> {
    let requestContextValue = await this.requestFactory.updateControl(id, projectId, control, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateControlWithHttpInfo(responseContext)
  }

  /**
   * Description: API for updating a specific control in a project. Requires the control ID and project ID as path parameters, and the control data as request body. Returns updated control data on success.
   * Update a control
   * @param id ID of the control to update
   * @param projectId The project ID
   * @param control The request body for updating a control
   */
  public async updateControl(id: string, projectId: string, control: Control, _options?: Configuration): Promise<Control> {
    const responseWithInfo = await this.updateControlWithHttpInfo(id, projectId, control, _options);
    return responseWithInfo.data;
  }


}


import { UpdateControlViolationApiRequestFactory, UpdateControlViolationApiResponseProcessor} from "../apis/UpdateControlViolationApi";

export class PromiseUpdateControlViolationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateControlViolationApiRequestFactory = new UpdateControlViolationApiRequestFactory(configuration),
    private responseProcessor: UpdateControlViolationApiResponseProcessor = new UpdateControlViolationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: An operation to update details of a specific control violation identified by ID and project ID.
   * Update a control violation
   * @param id ID of the inventory item
   * @param projectId The project ID
   * @param updateControlViolationInput The request body for updating a control violation
   */
  public async updateControlViolationWithHttpInfo(id: string, projectId: string, updateControlViolationInput: UpdateControlViolationInput, _options?: Configuration): Promise<HttpInfo<ControlViolation>> {
    let requestContextValue = await this.requestFactory.updateControlViolation(id, projectId, updateControlViolationInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateControlViolationWithHttpInfo(responseContext)
  }

  /**
   * Description: An operation to update details of a specific control violation identified by ID and project ID.
   * Update a control violation
   * @param id ID of the inventory item
   * @param projectId The project ID
   * @param updateControlViolationInput The request body for updating a control violation
   */
  public async updateControlViolation(id: string, projectId: string, updateControlViolationInput: UpdateControlViolationInput, _options?: Configuration): Promise<ControlViolation> {
    const responseWithInfo = await this.updateControlViolationWithHttpInfo(id, projectId, updateControlViolationInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateInfectedExcludePathApiRequestFactory, UpdateInfectedExcludePathApiResponseProcessor} from "../apis/UpdateInfectedExcludePathApi";

export class PromiseUpdateInfectedExcludePathApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateInfectedExcludePathApiRequestFactory = new UpdateInfectedExcludePathApiRequestFactory(configuration),
    private responseProcessor: UpdateInfectedExcludePathApiResponseProcessor = new UpdateInfectedExcludePathApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Updates the infected exclude path for the Eon account or resource.
   * Update infected exclude path
   * @param projectId The project ID
   * @param infectedExcludePath The request body for updating the infected exclude path
   */
  public async updateInfectedExcludePathWithHttpInfo(projectId: string, infectedExcludePath: InfectedExcludePath, _options?: Configuration): Promise<HttpInfo<InfectedExcludePath>> {
    let requestContextValue = await this.requestFactory.updateInfectedExcludePath(projectId, infectedExcludePath, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateInfectedExcludePathWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates the infected exclude path for the Eon account or resource.
   * Update infected exclude path
   * @param projectId The project ID
   * @param infectedExcludePath The request body for updating the infected exclude path
   */
  public async updateInfectedExcludePath(projectId: string, infectedExcludePath: InfectedExcludePath, _options?: Configuration): Promise<InfectedExcludePath> {
    const responseWithInfo = await this.updateInfectedExcludePathWithHttpInfo(projectId, infectedExcludePath, _options);
    return responseWithInfo.data;
  }


}


import { UpdateObjectStoreExclusionRuleApiRequestFactory, UpdateObjectStoreExclusionRuleApiResponseProcessor} from "../apis/UpdateObjectStoreExclusionRuleApi";

export class PromiseUpdateObjectStoreExclusionRuleApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateObjectStoreExclusionRuleApiRequestFactory = new UpdateObjectStoreExclusionRuleApiRequestFactory(configuration),
    private responseProcessor: UpdateObjectStoreExclusionRuleApiResponseProcessor = new UpdateObjectStoreExclusionRuleApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: API for updating a specific object store exclusion rule in a project. Requires the rul ID and project ID as path parameters, and the rule data as request body. Returns updated rule data on success.
   * Update a object store exclusion rule
   * @param id ID of the rule to update
   * @param projectId The project ID
   * @param objectStoreExclusionRule The request body for updating a object store exclusion rule
   */
  public async updateObjectStoreExclusionRuleWithHttpInfo(id: string, projectId: string, objectStoreExclusionRule: ObjectStoreExclusionRule, _options?: Configuration): Promise<HttpInfo<ObjectStoreExclusionRule>> {
    let requestContextValue = await this.requestFactory.updateObjectStoreExclusionRule(id, projectId, objectStoreExclusionRule, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateObjectStoreExclusionRuleWithHttpInfo(responseContext)
  }

  /**
   * Description: API for updating a specific object store exclusion rule in a project. Requires the rul ID and project ID as path parameters, and the rule data as request body. Returns updated rule data on success.
   * Update a object store exclusion rule
   * @param id ID of the rule to update
   * @param projectId The project ID
   * @param objectStoreExclusionRule The request body for updating a object store exclusion rule
   */
  public async updateObjectStoreExclusionRule(id: string, projectId: string, objectStoreExclusionRule: ObjectStoreExclusionRule, _options?: Configuration): Promise<ObjectStoreExclusionRule> {
    const responseWithInfo = await this.updateObjectStoreExclusionRuleWithHttpInfo(id, projectId, objectStoreExclusionRule, _options);
    return responseWithInfo.data;
  }


}


import { UpdateProjectRestoreAccountApiRequestFactory, UpdateProjectRestoreAccountApiResponseProcessor} from "../apis/UpdateProjectRestoreAccountApi";

export class PromiseUpdateProjectRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateProjectRestoreAccountApiRequestFactory = new UpdateProjectRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: UpdateProjectRestoreAccountApiResponseProcessor = new UpdateProjectRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to update the details of a specified restore account within a project by providing the account ID and project ID.
   * Update a restore account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param accountUpdateInput The request body for updating a restore account
   */
  public async updateProjectRestoreAccountWithHttpInfo(accountId: string, projectId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateProjectRestoreAccount(accountId, projectId, accountUpdateInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateProjectRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to update the details of a specified restore account within a project by providing the account ID and project ID.
   * Update a restore account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param accountUpdateInput The request body for updating a restore account
   */
  public async updateProjectRestoreAccount(accountId: string, projectId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateProjectRestoreAccountWithHttpInfo(accountId, projectId, accountUpdateInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateProjectSourceAccountApiRequestFactory, UpdateProjectSourceAccountApiResponseProcessor} from "../apis/UpdateProjectSourceAccountApi";

export class PromiseUpdateProjectSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateProjectSourceAccountApiRequestFactory = new UpdateProjectSourceAccountApiRequestFactory(configuration),
    private responseProcessor: UpdateProjectSourceAccountApiResponseProcessor = new UpdateProjectSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation updates the details of a specified source account within a project.
   * Update a source account
   * @param accountId ID of the source account
   * @param projectId The project ID
   * @param accountUpdateInput The request body for updating a source account
   */
  public async updateProjectSourceAccountWithHttpInfo(accountId: string, projectId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateProjectSourceAccount(accountId, projectId, accountUpdateInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateProjectSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the details of a specified source account within a project.
   * Update a source account
   * @param accountId ID of the source account
   * @param projectId The project ID
   * @param accountUpdateInput The request body for updating a source account
   */
  public async updateProjectSourceAccount(accountId: string, projectId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateProjectSourceAccountWithHttpInfo(accountId, projectId, accountUpdateInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateProjectSourceAccountConfigurationApiRequestFactory, UpdateProjectSourceAccountConfigurationApiResponseProcessor} from "../apis/UpdateProjectSourceAccountConfigurationApi";

export class PromiseUpdateProjectSourceAccountConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateProjectSourceAccountConfigurationApiRequestFactory = new UpdateProjectSourceAccountConfigurationApiRequestFactory(configuration),
    private responseProcessor: UpdateProjectSourceAccountConfigurationApiResponseProcessor = new UpdateProjectSourceAccountConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation updates the configuration of a specified source account within a project.
   * Update a source account configuration
   * @param accountId ID of the source account
   * @param projectId The project ID
   * @param body The request body for updating a source account configuration
   */
  public async updateProjectSourceAccountConfigurationWithHttpInfo(accountId: string, projectId: string, body: any, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.updateProjectSourceAccountConfiguration(accountId, projectId, body, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateProjectSourceAccountConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the configuration of a specified source account within a project.
   * Update a source account configuration
   * @param accountId ID of the source account
   * @param projectId The project ID
   * @param body The request body for updating a source account configuration
   */
  public async updateProjectSourceAccountConfiguration(accountId: string, projectId: string, body: any, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.updateProjectSourceAccountConfigurationWithHttpInfo(accountId, projectId, body, _options);
    return responseWithInfo.data;
  }


}


import { UpdateProjectUserOverrideApiRequestFactory, UpdateProjectUserOverrideApiResponseProcessor} from "../apis/UpdateProjectUserOverrideApi";

export class PromiseUpdateProjectUserOverrideApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateProjectUserOverrideApiRequestFactory = new UpdateProjectUserOverrideApiRequestFactory(configuration),
    private responseProcessor: UpdateProjectUserOverrideApiResponseProcessor = new UpdateProjectUserOverrideApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Update user details within a project. Requires admin permissions.
   * Update a user - override
   * @param id ID of the user
   * @param projectId The project ID
   * @param updateUserInput The request body for updating a user
   */
  public async updateProjectUserOverrideWithHttpInfo(id: string, projectId: string, updateUserInput: UpdateUserInput, _options?: Configuration): Promise<HttpInfo<User>> {
    let requestContextValue = await this.requestFactory.updateProjectUserOverride(id, projectId, updateUserInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateProjectUserOverrideWithHttpInfo(responseContext)
  }

  /**
   * Description: Update user details within a project. Requires admin permissions.
   * Update a user - override
   * @param id ID of the user
   * @param projectId The project ID
   * @param updateUserInput The request body for updating a user
   */
  public async updateProjectUserOverride(id: string, projectId: string, updateUserInput: UpdateUserInput, _options?: Configuration): Promise<User> {
    const responseWithInfo = await this.updateProjectUserOverrideWithHttpInfo(id, projectId, updateUserInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateProjectsApiRequestFactory, UpdateProjectsApiResponseProcessor} from "../apis/UpdateProjectsApi";

export class PromiseUpdateProjectsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateProjectsApiRequestFactory = new UpdateProjectsApiRequestFactory(configuration),
    private responseProcessor: UpdateProjectsApiResponseProcessor = new UpdateProjectsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API allows you to update the details of a specific project by providing the project ID and the new data in the request body.
   * Update a project
   * @param projectId ID of the project
   * @param project The request body for updating a project
   */
  public async updateProjectWithHttpInfo(projectId: string, project: Project, _options?: Configuration): Promise<HttpInfo<Project>> {
    let requestContextValue = await this.requestFactory.updateProject(projectId, project, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateProjectWithHttpInfo(responseContext)
  }

  /**
   * Description: This API allows you to update the details of a specific project by providing the project ID and the new data in the request body.
   * Update a project
   * @param projectId ID of the project
   * @param project The request body for updating a project
   */
  public async updateProject(projectId: string, project: Project, _options?: Configuration): Promise<Project> {
    const responseWithInfo = await this.updateProjectWithHttpInfo(projectId, project, _options);
    return responseWithInfo.data;
  }


}


import { UpdatePulumiConfigurationApiRequestFactory, UpdatePulumiConfigurationApiResponseProcessor} from "../apis/UpdatePulumiConfigurationApi";

export class PromiseUpdatePulumiConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdatePulumiConfigurationApiRequestFactory = new UpdatePulumiConfigurationApiRequestFactory(configuration),
    private responseProcessor: UpdatePulumiConfigurationApiResponseProcessor = new UpdatePulumiConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Update Pulumi configuration by id
   * @param id 
   * @param updatePulumiConfigurationRequest 
   */
  public async updatePulumiConfigurationWithHttpInfo(id: string, updatePulumiConfigurationRequest: UpdatePulumiConfigurationRequest, _options?: Configuration): Promise<HttpInfo<PulumiConfiguration>> {
    let requestContextValue = await this.requestFactory.updatePulumiConfiguration(id, updatePulumiConfigurationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updatePulumiConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Update Pulumi configuration by id
   * @param id 
   * @param updatePulumiConfigurationRequest 
   */
  public async updatePulumiConfiguration(id: string, updatePulumiConfigurationRequest: UpdatePulumiConfigurationRequest, _options?: Configuration): Promise<PulumiConfiguration> {
    const responseWithInfo = await this.updatePulumiConfigurationWithHttpInfo(id, updatePulumiConfigurationRequest, _options);
    return responseWithInfo.data;
  }


}


import { UpdateResourceConfigurationApiRequestFactory, UpdateResourceConfigurationApiResponseProcessor} from "../apis/UpdateResourceConfigurationApi";

export class PromiseUpdateResourceConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateResourceConfigurationApiRequestFactory = new UpdateResourceConfigurationApiRequestFactory(configuration),
    private responseProcessor: UpdateResourceConfigurationApiResponseProcessor = new UpdateResourceConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Partially updates the configuration for a specified inventory resource within a given project. Only the provided fields will be updated, while others remain unchanged.
   * Update configuration for an inventory resource
   * @param id ID of the inventory resource
   * @param projectId The project ID
   * @param updateCloudResourceConfigurationRequest Fields to update for the inventory resource configuration
   */
  public async updateResourceConfigurationWithHttpInfo(id: string, projectId: string, updateCloudResourceConfigurationRequest: UpdateCloudResourceConfigurationRequest, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.updateResourceConfiguration(id, projectId, updateCloudResourceConfigurationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateResourceConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: Partially updates the configuration for a specified inventory resource within a given project. Only the provided fields will be updated, while others remain unchanged.
   * Update configuration for an inventory resource
   * @param id ID of the inventory resource
   * @param projectId The project ID
   * @param updateCloudResourceConfigurationRequest Fields to update for the inventory resource configuration
   */
  public async updateResourceConfiguration(id: string, projectId: string, updateCloudResourceConfigurationRequest: UpdateCloudResourceConfigurationRequest, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.updateResourceConfigurationWithHttpInfo(id, projectId, updateCloudResourceConfigurationRequest, _options);
    return responseWithInfo.data;
  }


}


import { UpdateRestoreAccountApiRequestFactory, UpdateRestoreAccountApiResponseProcessor} from "../apis/UpdateRestoreAccountApi";

export class PromiseUpdateRestoreAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateRestoreAccountApiRequestFactory = new UpdateRestoreAccountApiRequestFactory(configuration),
    private responseProcessor: UpdateRestoreAccountApiResponseProcessor = new UpdateRestoreAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation updates the details of an existing restore account identified by the given account ID.
   * Update a restore account
   * @param accountId ID of the restore account
   * @param accountUpdateInput The request body for updating a restore account
   */
  public async updateRestoreAccountWithHttpInfo(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateRestoreAccount(accountId, accountUpdateInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateRestoreAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the details of an existing restore account identified by the given account ID.
   * Update a restore account
   * @param accountId ID of the restore account
   * @param accountUpdateInput The request body for updating a restore account
   */
  public async updateRestoreAccount(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateRestoreAccountWithHttpInfo(accountId, accountUpdateInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateRestoreAccountNameApiRequestFactory, UpdateRestoreAccountNameApiResponseProcessor} from "../apis/UpdateRestoreAccountNameApi";

export class PromiseUpdateRestoreAccountNameApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateRestoreAccountNameApiRequestFactory = new UpdateRestoreAccountNameApiRequestFactory(configuration),
    private responseProcessor: UpdateRestoreAccountNameApiResponseProcessor = new UpdateRestoreAccountNameApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation updates the custom name of a specified restore account within a project.
   * Update restore account custom name
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param name The new name for the restore account. If undefined or empty string, it\&#39;ll unset the custom name.
   */
  public async updateRestoreAccountNameWithHttpInfo(accountId: string, projectId: string, name?: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateRestoreAccountName(accountId, projectId, name, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateRestoreAccountNameWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the custom name of a specified restore account within a project.
   * Update restore account custom name
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param name The new name for the restore account. If undefined or empty string, it\&#39;ll unset the custom name.
   */
  public async updateRestoreAccountName(accountId: string, projectId: string, name?: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateRestoreAccountNameWithHttpInfo(accountId, projectId, name, _options);
    return responseWithInfo.data;
  }


}


import { UpdateRestoreCloudAccountTagsConfigurationApiRequestFactory, UpdateRestoreCloudAccountTagsConfigurationApiResponseProcessor} from "../apis/UpdateRestoreCloudAccountTagsConfigurationApi";

export class PromiseUpdateRestoreCloudAccountTagsConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateRestoreCloudAccountTagsConfigurationApiRequestFactory = new UpdateRestoreCloudAccountTagsConfigurationApiRequestFactory(configuration),
    private responseProcessor: UpdateRestoreCloudAccountTagsConfigurationApiResponseProcessor = new UpdateRestoreCloudAccountTagsConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows users to update the restore cloud account custom tags configuration of a specified restore account within a project by providing the account ID and project ID.
   * Update the cloud account custom tags configuration of a restore account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param customTagsInner The request body for updating a restore account custom tags configuration
   */
  public async updateRestoreCloudAccountTagsConfigurationWithHttpInfo(accountId: string, projectId: string, customTagsInner: Array<CustomTagsInner>, _options?: Configuration): Promise<HttpInfo<Array<CustomTagsInner>>> {
    let requestContextValue = await this.requestFactory.updateRestoreCloudAccountTagsConfiguration(accountId, projectId, customTagsInner, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateRestoreCloudAccountTagsConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows users to update the restore cloud account custom tags configuration of a specified restore account within a project by providing the account ID and project ID.
   * Update the cloud account custom tags configuration of a restore account
   * @param accountId ID of the restore account
   * @param projectId The project ID
   * @param customTagsInner The request body for updating a restore account custom tags configuration
   */
  public async updateRestoreCloudAccountTagsConfiguration(accountId: string, projectId: string, customTagsInner: Array<CustomTagsInner>, _options?: Configuration): Promise<Array<CustomTagsInner>> {
    const responseWithInfo = await this.updateRestoreCloudAccountTagsConfigurationWithHttpInfo(accountId, projectId, customTagsInner, _options);
    return responseWithInfo.data;
  }


}


import { UpdateSamlIdentityProviderConfigOverrideApiRequestFactory, UpdateSamlIdentityProviderConfigOverrideApiResponseProcessor} from "../apis/UpdateSamlIdentityProviderConfigOverrideApi";

export class PromiseUpdateSamlIdentityProviderConfigOverrideApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateSamlIdentityProviderConfigOverrideApiRequestFactory = new UpdateSamlIdentityProviderConfigOverrideApiRequestFactory(configuration),
    private responseProcessor: UpdateSamlIdentityProviderConfigOverrideApiResponseProcessor = new UpdateSamlIdentityProviderConfigOverrideApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation allows an admin to update the SAML identity provider configuration for a specified provider by ID. It requires admin permissions and accepts a JSON request body with the SAML configuration details.
   * Update saml identity provider config - override
   * @param id The ID of the saml identity provider
   * @param updateSamlIdentityProviderInput The request body for updating saml identity provider config.
   */
  public async updateSamlConfigOverrideWithHttpInfo(id: string, updateSamlIdentityProviderInput: UpdateSamlIdentityProviderInput, _options?: Configuration): Promise<HttpInfo<SamlIdentityProvider>> {
    let requestContextValue = await this.requestFactory.updateSamlConfigOverride(id, updateSamlIdentityProviderInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateSamlConfigOverrideWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation allows an admin to update the SAML identity provider configuration for a specified provider by ID. It requires admin permissions and accepts a JSON request body with the SAML configuration details.
   * Update saml identity provider config - override
   * @param id The ID of the saml identity provider
   * @param updateSamlIdentityProviderInput The request body for updating saml identity provider config.
   */
  public async updateSamlConfigOverride(id: string, updateSamlIdentityProviderInput: UpdateSamlIdentityProviderInput, _options?: Configuration): Promise<SamlIdentityProvider> {
    const responseWithInfo = await this.updateSamlConfigOverrideWithHttpInfo(id, updateSamlIdentityProviderInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateScanningAccountApiRequestFactory, UpdateScanningAccountApiResponseProcessor} from "../apis/UpdateScanningAccountApi";

export class PromiseUpdateScanningAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateScanningAccountApiRequestFactory = new UpdateScanningAccountApiRequestFactory(configuration),
    private responseProcessor: UpdateScanningAccountApiResponseProcessor = new UpdateScanningAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation allows updating the details of an existing scanning account using its unique account ID.
   * Update a scanning account
   * @param accountId ID of the scanning account
   * @param accountUpdateInput The request body for updating a scanning account
   */
  public async updateScanningAccountWithHttpInfo(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateScanningAccount(accountId, accountUpdateInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateScanningAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation allows updating the details of an existing scanning account using its unique account ID.
   * Update a scanning account
   * @param accountId ID of the scanning account
   * @param accountUpdateInput The request body for updating a scanning account
   */
  public async updateScanningAccount(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateScanningAccountWithHttpInfo(accountId, accountUpdateInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateSecurityScanPoliciesConfigApiRequestFactory, UpdateSecurityScanPoliciesConfigApiResponseProcessor} from "../apis/UpdateSecurityScanPoliciesConfigApi";

export class PromiseUpdateSecurityScanPoliciesConfigApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateSecurityScanPoliciesConfigApiRequestFactory = new UpdateSecurityScanPoliciesConfigApiRequestFactory(configuration),
    private responseProcessor: UpdateSecurityScanPoliciesConfigApiResponseProcessor = new UpdateSecurityScanPoliciesConfigApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API updates the security scan configuration for the backup policies specified.
   * Update the backup policies security scans configuration
   * @param projectId The project ID
   * @param backupPoliciesSecurityConfig The request body for updating a policy
   */
  public async updateSecurityScanPoliciesConfigWithHttpInfo(projectId: string, backupPoliciesSecurityConfig: BackupPoliciesSecurityConfig, _options?: Configuration): Promise<HttpInfo<ListBackupPolicyResponse>> {
    let requestContextValue = await this.requestFactory.updateSecurityScanPoliciesConfig(projectId, backupPoliciesSecurityConfig, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateSecurityScanPoliciesConfigWithHttpInfo(responseContext)
  }

  /**
   * Description: This API updates the security scan configuration for the backup policies specified.
   * Update the backup policies security scans configuration
   * @param projectId The project ID
   * @param backupPoliciesSecurityConfig The request body for updating a policy
   */
  public async updateSecurityScanPoliciesConfig(projectId: string, backupPoliciesSecurityConfig: BackupPoliciesSecurityConfig, _options?: Configuration): Promise<ListBackupPolicyResponse> {
    const responseWithInfo = await this.updateSecurityScanPoliciesConfigWithHttpInfo(projectId, backupPoliciesSecurityConfig, _options);
    return responseWithInfo.data;
  }


}


import { UpdateShouldFullBackupResourceApiRequestFactory, UpdateShouldFullBackupResourceApiResponseProcessor} from "../apis/UpdateShouldFullBackupResourceApi";

export class PromiseUpdateShouldFullBackupResourceApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateShouldFullBackupResourceApiRequestFactory = new UpdateShouldFullBackupResourceApiRequestFactory(configuration),
    private responseProcessor: UpdateShouldFullBackupResourceApiResponseProcessor = new UpdateShouldFullBackupResourceApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Partially updates the backup settings for a specified inventory resource within a given project. Only the provided fields will be updated, while others remain unchanged.
   * Update if should full backup a resource in the next backup run
   * @param id ID of the inventory resource
   * @param projectId The project ID
   * @param updateShouldFullBackupResourceRequest Fields to update for the inventory resource configuration
   */
  public async updateShouldFullBackupResourceWithHttpInfo(id: string, projectId: string, updateShouldFullBackupResourceRequest: UpdateShouldFullBackupResourceRequest, _options?: Configuration): Promise<HttpInfo<void>> {
    let requestContextValue = await this.requestFactory.updateShouldFullBackupResource(id, projectId, updateShouldFullBackupResourceRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateShouldFullBackupResourceWithHttpInfo(responseContext)
  }

  /**
   * Description: Partially updates the backup settings for a specified inventory resource within a given project. Only the provided fields will be updated, while others remain unchanged.
   * Update if should full backup a resource in the next backup run
   * @param id ID of the inventory resource
   * @param projectId The project ID
   * @param updateShouldFullBackupResourceRequest Fields to update for the inventory resource configuration
   */
  public async updateShouldFullBackupResource(id: string, projectId: string, updateShouldFullBackupResourceRequest: UpdateShouldFullBackupResourceRequest, _options?: Configuration): Promise<void> {
    const responseWithInfo = await this.updateShouldFullBackupResourceWithHttpInfo(id, projectId, updateShouldFullBackupResourceRequest, _options);
    return responseWithInfo.data;
  }


}


import { UpdateSourceAccountApiRequestFactory, UpdateSourceAccountApiResponseProcessor} from "../apis/UpdateSourceAccountApi";

export class PromiseUpdateSourceAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateSourceAccountApiRequestFactory = new UpdateSourceAccountApiRequestFactory(configuration),
    private responseProcessor: UpdateSourceAccountApiResponseProcessor = new UpdateSourceAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation updates the details of a source account identified by a unique accountId.
   * Update a source account
   * @param accountId ID of the source account
   * @param accountUpdateInput The request body for updating a source account
   */
  public async updateSourceAccountWithHttpInfo(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateSourceAccount(accountId, accountUpdateInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateSourceAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the details of a source account identified by a unique accountId.
   * Update a source account
   * @param accountId ID of the source account
   * @param accountUpdateInput The request body for updating a source account
   */
  public async updateSourceAccount(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateSourceAccountWithHttpInfo(accountId, accountUpdateInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateSourceAccountNameApiRequestFactory, UpdateSourceAccountNameApiResponseProcessor} from "../apis/UpdateSourceAccountNameApi";

export class PromiseUpdateSourceAccountNameApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateSourceAccountNameApiRequestFactory = new UpdateSourceAccountNameApiRequestFactory(configuration),
    private responseProcessor: UpdateSourceAccountNameApiResponseProcessor = new UpdateSourceAccountNameApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API operation updates the custom name of a specified source account within a project.
   * Update source account custom name
   * @param accountId ID of the source account
   * @param projectId The project ID
   * @param name The new name for the source account. If undefined or empty string, it\&#39;ll unset the custom name.
   */
  public async updateSourceAccountNameWithHttpInfo(accountId: string, projectId: string, name?: string, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateSourceAccountName(accountId, projectId, name, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateSourceAccountNameWithHttpInfo(responseContext)
  }

  /**
   * Description: This API operation updates the custom name of a specified source account within a project.
   * Update source account custom name
   * @param accountId ID of the source account
   * @param projectId The project ID
   * @param name The new name for the source account. If undefined or empty string, it\&#39;ll unset the custom name.
   */
  public async updateSourceAccountName(accountId: string, projectId: string, name?: string, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateSourceAccountNameWithHttpInfo(accountId, projectId, name, _options);
    return responseWithInfo.data;
  }


}


import { UpdateUserOverrideApiRequestFactory, UpdateUserOverrideApiResponseProcessor} from "../apis/UpdateUserOverrideApi";

export class PromiseUpdateUserOverrideApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateUserOverrideApiRequestFactory = new UpdateUserOverrideApiRequestFactory(configuration),
    private responseProcessor: UpdateUserOverrideApiResponseProcessor = new UpdateUserOverrideApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API updates the details of an existing user identified by their ID. It overrides the user\'s information with the provided data.
   * Update a user - override
   * @param id ID of the user
   * @param updateUserInput The request body for updating a user
   */
  public async updateUserOverrideWithHttpInfo(id: string, updateUserInput: UpdateUserInput, _options?: Configuration): Promise<HttpInfo<User>> {
    let requestContextValue = await this.requestFactory.updateUserOverride(id, updateUserInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateUserOverrideWithHttpInfo(responseContext)
  }

  /**
   * Description: This API updates the details of an existing user identified by their ID. It overrides the user\'s information with the provided data.
   * Update a user - override
   * @param id ID of the user
   * @param updateUserInput The request body for updating a user
   */
  public async updateUserOverride(id: string, updateUserInput: UpdateUserInput, _options?: Configuration): Promise<User> {
    const responseWithInfo = await this.updateUserOverrideWithHttpInfo(id, updateUserInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateVaultAccountApiRequestFactory, UpdateVaultAccountApiResponseProcessor} from "../apis/UpdateVaultAccountApi";

export class PromiseUpdateVaultAccountApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateVaultAccountApiRequestFactory = new UpdateVaultAccountApiRequestFactory(configuration),
    private responseProcessor: UpdateVaultAccountApiResponseProcessor = new UpdateVaultAccountApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API is used to update the details of an existing vault account using its ID.
   * Update a vault account
   * @param accountId ID of the vault account
   * @param accountUpdateInput The request body for updating a vault account
   */
  public async updateVaultAccountWithHttpInfo(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<HttpInfo<Account>> {
    let requestContextValue = await this.requestFactory.updateVaultAccount(accountId, accountUpdateInput, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateVaultAccountWithHttpInfo(responseContext)
  }

  /**
   * Description: This API is used to update the details of an existing vault account using its ID.
   * Update a vault account
   * @param accountId ID of the vault account
   * @param accountUpdateInput The request body for updating a vault account
   */
  public async updateVaultAccount(accountId: string, accountUpdateInput: AccountUpdateInput, _options?: Configuration): Promise<Account> {
    const responseWithInfo = await this.updateVaultAccountWithHttpInfo(accountId, accountUpdateInput, _options);
    return responseWithInfo.data;
  }


}


import { UpdateViewerRoleApiRequestFactory, UpdateViewerRoleApiResponseProcessor} from "../apis/UpdateViewerRoleApi";

export class PromiseUpdateViewerRoleApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateViewerRoleApiRequestFactory = new UpdateViewerRoleApiRequestFactory(configuration),
    private responseProcessor: UpdateViewerRoleApiResponseProcessor = new UpdateViewerRoleApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint allows updating the role of a viewer by specifying a target role ID.
   * Update viewer role
   * @param updateViewerRoleRequest Target role
   */
  public async updateViewerRoleWithHttpInfo(updateViewerRoleRequest: UpdateViewerRoleRequest, _options?: Configuration): Promise<HttpInfo<Message>> {
    let requestContextValue = await this.requestFactory.updateViewerRole(updateViewerRoleRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateViewerRoleWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint allows updating the role of a viewer by specifying a target role ID.
   * Update viewer role
   * @param updateViewerRoleRequest Target role
   */
  public async updateViewerRole(updateViewerRoleRequest: UpdateViewerRoleRequest, _options?: Configuration): Promise<Message> {
    const responseWithInfo = await this.updateViewerRoleWithHttpInfo(updateViewerRoleRequest, _options);
    return responseWithInfo.data;
  }


}


import { UpdateViewerUIPreferencesApiRequestFactory, UpdateViewerUIPreferencesApiResponseProcessor} from "../apis/UpdateViewerUIPreferencesApi";

export class PromiseUpdateViewerUIPreferencesApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: UpdateViewerUIPreferencesApiRequestFactory = new UpdateViewerUIPreferencesApiRequestFactory(configuration),
    private responseProcessor: UpdateViewerUIPreferencesApiResponseProcessor = new UpdateViewerUIPreferencesApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: This API endpoint updates the UI preferences for a viewer based on a specified key.
   * Update viewer UI preferences
   * @param key Key of the preference
   * @param requestBody The request body for updating viewer UI preferences
   */
  public async updateViewerUIPreferencesWithHttpInfo(key: string, requestBody: { [key: string]: any; }, _options?: Configuration): Promise<HttpInfo<Preferences>> {
    let requestContextValue = await this.requestFactory.updateViewerUIPreferences(key, requestBody, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateViewerUIPreferencesWithHttpInfo(responseContext)
  }

  /**
   * Description: This API endpoint updates the UI preferences for a viewer based on a specified key.
   * Update viewer UI preferences
   * @param key Key of the preference
   * @param requestBody The request body for updating viewer UI preferences
   */
  public async updateViewerUIPreferences(key: string, requestBody: { [key: string]: any; }, _options?: Configuration): Promise<Preferences> {
    const responseWithInfo = await this.updateViewerUIPreferencesWithHttpInfo(key, requestBody, _options);
    return responseWithInfo.data;
  }


}


import { ValidateResourceConfigurationApiRequestFactory, ValidateResourceConfigurationApiResponseProcessor} from "../apis/ValidateResourceConfigurationApi";

export class PromiseValidateResourceConfigurationApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: ValidateResourceConfigurationApiRequestFactory = new ValidateResourceConfigurationApiRequestFactory(configuration),
    private responseProcessor: ValidateResourceConfigurationApiResponseProcessor = new ValidateResourceConfigurationApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Validate if a resource is properly configured for backup with Eon.
   * Validate resource configuration
   * @param projectId The project ID
   * @param validateResourceConfigurationRequest 
   */
  public async validateResourceConfigurationWithHttpInfo(projectId: string, validateResourceConfigurationRequest: ValidateResourceConfigurationRequest, _options?: Configuration): Promise<HttpInfo<ValidateResourceConfigurationResponse>> {
    let requestContextValue = await this.requestFactory.validateResourceConfiguration(projectId, validateResourceConfigurationRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.validateResourceConfigurationWithHttpInfo(responseContext)
  }

  /**
   * Description: Validate if a resource is properly configured for backup with Eon.
   * Validate resource configuration
   * @param projectId The project ID
   * @param validateResourceConfigurationRequest 
   */
  public async validateResourceConfiguration(projectId: string, validateResourceConfigurationRequest: ValidateResourceConfigurationRequest, _options?: Configuration): Promise<ValidateResourceConfigurationResponse> {
    const responseWithInfo = await this.validateResourceConfigurationWithHttpInfo(projectId, validateResourceConfigurationRequest, _options);
    return responseWithInfo.data;
  }


}


import { VaultsApiRequestFactory, VaultsApiResponseProcessor} from "../apis/VaultsApi";

export class PromiseVaultsApi {

  public constructor(
    private configuration: Configuration,
    private requestFactory: VaultsApiRequestFactory = new VaultsApiRequestFactory(configuration),
    private responseProcessor: VaultsApiResponseProcessor = new VaultsApiResponseProcessor() 
  ) {
    
  }

  /**
   * Description: Creates an S3 Access Point to allow a Redshift cluster to access backup data in a vault. The access point is created with a policy that grants the specified IAM role read access to the vault\'s S3 bucket. 
   * Create Redshift Access Point
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createRedshiftAccessPointRequest 
   */
  public async createRedshiftAccessPointWithHttpInfo(projectId: string, createRedshiftAccessPointRequest: CreateRedshiftAccessPointRequest, _options?: Configuration): Promise<HttpInfo<CreateRedshiftAccessPointResponse>> {
    let requestContextValue = await this.requestFactory.createRedshiftAccessPoint(projectId, createRedshiftAccessPointRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createRedshiftAccessPointWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates an S3 Access Point to allow a Redshift cluster to access backup data in a vault. The access point is created with a policy that grants the specified IAM role read access to the vault\'s S3 bucket. 
   * Create Redshift Access Point
   * @param projectId ID of the project. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createRedshiftAccessPointRequest 
   */
  public async createRedshiftAccessPoint(projectId: string, createRedshiftAccessPointRequest: CreateRedshiftAccessPointRequest, _options?: Configuration): Promise<CreateRedshiftAccessPointResponse> {
    const responseWithInfo = await this.createRedshiftAccessPointWithHttpInfo(projectId, createRedshiftAccessPointRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Creates a vault in the specified project.  By default, vaults are encrypted using an Eon-managed KMS key. However, if your requirements mandate control over encryption keys, you can use a customer-managed KMS key. For more information, see [About Self-Managed Vault Keys].  <Error> If you use self-managed vault keys, you assume responsibility for securing them from unauthorized access and deletion. </Error>  [About Self-Managed Vault Keys]: /user-guide/backing-up/vaults/about-self-managed-vault-keys 
   * Create Vault
   * @param projectId ID of the project the vault is created in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createVaultRequest 
   */
  public async createVaultWithHttpInfo(projectId: string, createVaultRequest: CreateVaultRequest, _options?: Configuration): Promise<HttpInfo<CreateVaultResponse>> {
    let requestContextValue = await this.requestFactory.createVault(projectId, createVaultRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.createVaultWithHttpInfo(responseContext)
  }

  /**
   * Description: Creates a vault in the specified project.  By default, vaults are encrypted using an Eon-managed KMS key. However, if your requirements mandate control over encryption keys, you can use a customer-managed KMS key. For more information, see [About Self-Managed Vault Keys].  <Error> If you use self-managed vault keys, you assume responsibility for securing them from unauthorized access and deletion. </Error>  [About Self-Managed Vault Keys]: /user-guide/backing-up/vaults/about-self-managed-vault-keys 
   * Create Vault
   * @param projectId ID of the project the vault is created in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param createVaultRequest 
   */
  public async createVault(projectId: string, createVaultRequest: CreateVaultRequest, _options?: Configuration): Promise<CreateVaultResponse> {
    const responseWithInfo = await this.createVaultWithHttpInfo(projectId, createVaultRequest, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a vault.
   * Get Vault
   * @param vaultId ID of the vault to retrieve.
   * @param projectId ID of the project the vault is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getVaultWithHttpInfo(vaultId: string, projectId: string, _options?: Configuration): Promise<HttpInfo<GetVaultResponse>> {
    let requestContextValue = await this.requestFactory.getVault(vaultId, projectId, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.getVaultWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a vault.
   * Get Vault
   * @param vaultId ID of the vault to retrieve.
   * @param projectId ID of the project the vault is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   */
  public async getVault(vaultId: string, projectId: string, _options?: Configuration): Promise<GetVaultResponse> {
    const responseWithInfo = await this.getVaultWithHttpInfo(vaultId, projectId, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Retrieves a list of vaults in the specified project.
   * List Vaults
   * @param projectId ID of the project whose vaults you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listVaultsWithHttpInfo(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<HttpInfo<ListBackupVaultResponse>> {
    let requestContextValue = await this.requestFactory.listVaults(projectId, pageToken, pageSize, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.listVaultsWithHttpInfo(responseContext)
  }

  /**
   * Description: Retrieves a list of vaults in the specified project.
   * List Vaults
   * @param projectId ID of the project whose vaults you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   */
  public async listVaults(projectId: string, pageToken?: string, pageSize?: number, _options?: Configuration): Promise<ListBackupVaultResponse> {
    const responseWithInfo = await this.listVaultsWithHttpInfo(projectId, pageToken, pageSize, _options);
    return responseWithInfo.data;
  }

  /**
   * Description: Updates a vault\'s display name.
   * Update Vault
   * @param vaultId ID of the vault to update.
   * @param projectId ID of the project the vault is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateVaultRequest 
   */
  public async updateVaultWithHttpInfo(vaultId: string, projectId: string, updateVaultRequest: UpdateVaultRequest, _options?: Configuration): Promise<HttpInfo<UpdateVaultResponse>> {
    let requestContextValue = await this.requestFactory.updateVault(vaultId, projectId, updateVaultRequest, _options);
    const responseContext = await requestWithRetries(requestContextValue, this.configuration)
    return await this.responseProcessor.updateVaultWithHttpInfo(responseContext)
  }

  /**
   * Description: Updates a vault\'s display name.
   * Update Vault
   * @param vaultId ID of the vault to update.
   * @param projectId ID of the project the vault is in. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param updateVaultRequest 
   */
  public async updateVault(vaultId: string, projectId: string, updateVaultRequest: UpdateVaultRequest, _options?: Configuration): Promise<UpdateVaultResponse> {
    const responseWithInfo = await this.updateVaultWithHttpInfo(vaultId, projectId, updateVaultRequest, _options);
    return responseWithInfo.data;
  }


}


